import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { Modal, Button, Offcanvas } from "react-bootstrap";
import "react-modal-video/scss/modal-video.scss";
import { sharedSidebarFetch } from "../../core/data/json/horizontalSidebar";
import isEqual from 'lodash.isequal';
import { useIsMobile } from "../../utils/mobile";
import { API_URL } from '../../environment';
import { IDIOMA_PADRAO, precisaTraduzir, valorDoSeletor, opcoesParaContato } from "./idiomas";
import { getSupabaseRealtime, getSupabaseRealtimeCRM } from "../../supabaseRealtime";
import CardViewModal from "../crm/leads/CardViewModal";
import { useSelector } from "react-redux";
import EmojiPicker, { EmojiStyle, Theme as EmojiTheme } from "emoji-picker-react";
import TemplateErrorDetails from "../utils/TemplateErrorDetails";
import { traduzirErroTemplate } from "../utils/templateErrors";
import "./DocsRetencao.css";
import Swal from "sweetalert2";
import { ContactTag, TagChip, TagsManagerModal, HeaderTags } from "./ContactTags";

// Tags de contato: por enquanto só para estes clientes (id_grupo). O backend tem a mesma
// lista (TAGS_CLIENTES_LIBERADOS no api.py) — liberar nos dois.
const TAGS_GRUPOS_LIBERADOS = ["9189"];
const SEM_TAGS: ContactTag[] = [];
const soDigitos = (v: any) => String(v ?? "").replace(/\D/g, "");

// Texto vindo do banco (nome de time/funil/atendente) entra no `html` do Swal.
const escapeHtml = (s: unknown) =>
  String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));


// --- PIX GENERATOR UTILS ---
class PixPayload {
    key: string;
    name: string;
    city: string;
    amount: string;
    txtId: string;

    constructor(key: string, name: string, city: string, amount: string, txtId: string) {
        this.key = key;           
        this.name = name;         
        this.city = city;         
        this.amount = amount;     
        this.txtId = txtId || "***";
    }

    formatData(id: string, value: string) {
        const len = value.length.toString().padStart(2, "0");
        return id + len + value;
    }

    getPayload() {
        const payloadFormat = this.formatData("00", "01");
        
        const merchantAccount = this.formatData("26", this.formatData("00", "br.gov.bcb.pix") + 
                              this.formatData("01", this.key));
        const merchantCat = this.formatData("52", "0000"); 
        const transactionCurrency = this.formatData("53", "986"); // BRL
        const transactionAmount = this.formatData("54", this.amount);
        const countryCode = this.formatData("58", "BR");
        
        const merchantName = this.formatData("59", this.name);
        const merchantCity = this.formatData("60", this.city);
        
        const additionalData = this.formatData("62", this.formatData("05", this.txtId));
        
        const payloadRaw = payloadFormat + merchantAccount + merchantCat + 
                         transactionCurrency + transactionAmount + countryCode + 
                         merchantName + merchantCity + additionalData + "6304";
        
        return payloadRaw + this.getCRC16(payloadRaw);
    }

    getCRC16(payload: string) {
        let crc = 0xFFFF;
        for (let i = 0; i < payload.length; i++) {
            crc ^= payload.charCodeAt(i) << 8;
            for (let j = 0; j < 8; j++) {
                if ((crc & 0x8000) !== 0) {
                    crc = (crc << 1) ^ 0x1021;
                } else {
                    crc = crc << 1;
                }
            }
        }
        return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, "0");
    }
}

const PIX_CONFIG = {
    CHAVE:  "27913046000158",
    NOME:   "BARCOPIRATACANAS",
    CIDADE: "FLORIANOPOLIS"
};

// id_times (localStorage.id_cliente) habilitados a usar o botão PIX.
// Todos compartilham a chave PIX_CONFIG acima — só habilitar times do MESMO
// beneficiário. 134 e 147 são os dois times de Passeios Florianópolis (grupo 151).
const PIX_ENABLED_TIMES = ["134", "147"];
// ---------------------------

let isChatFullyInitialized = false;

// Popover do motivo da falha de um template (coluna chats.motivo_template_wp).
// Envolve o indicador de falha ("Falhou" / ícone). Ao CLICAR, abre um balão vermelho
// acima do gatilho com o erro "traduzido" (título amigável + resumo + botão ⓘ com a
// causa provável e o que fazer + a mensagem original do WhatsApp recolhida).
// No hover, o atributo title dá uma prévia rápida (título + resumo). Sempre abre,
// mesmo sem motivo — o TemplateErrorDetails cai num texto genérico.
const FailedTemplateTooltip: React.FC<{
  motivo?: string | null;
  isDark?: boolean;
  children: React.ReactNode;
}> = ({ motivo, isDark, children }) => {
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const info = traduzirErroTemplate(motivo);

  // Fecha ao clicar fora ou apertar Esc.
  useEffect(() => {
    if (!aberto) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setAberto(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [aberto]);

  return (
    <span
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex", alignItems: "center", pointerEvents: "auto" }}
    >
      <span
        title={`${info.titulo} — ${info.resumo}`}
        onClick={(e) => {
          e.stopPropagation();
          setAberto((v) => !v);
        }}
        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      >
        {children}
      </span>
      {aberto && (
        <span
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            right: 0,
            zIndex: 50,
            width: "300px",
            maxWidth: "80vw",
            textAlign: "left",
            whiteSpace: "normal",
            wordBreak: "break-word",
            background: isDark ? "#2a1416" : "#fff5f5",
            color: isDark ? "#fecaca" : "#7f1d1d",
            border: "1px solid #ef4444",
            borderRadius: "8px",
            padding: "10px 12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            cursor: "default",
          }}
        >
          <TemplateErrorDetails motivo={motivo} isDark={isDark} fontSize={12} />
          {/* setinha do balão apontando para o gatilho */}
          <span
            style={{
              position: "absolute",
              top: "100%",
              right: "12px",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #ef4444",
            }}
          />
        </span>
      )}
    </span>
  );
};

// Formatter puro (espelha o formatLocalTime interno) em nível de módulo, p/ a linha memoizada.
const fmtLocalTime = (ts: number | string | undefined): string => {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
};

// Data local em YYYY-MM-DD (sem usar toISOString, que é UTC e pode pular o dia).
const ymdLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Traduz a escolha de data do filtro em um intervalo [inicio, fim] (YYYY-MM-DD)
// enviado ao backend. O /contacts_full retorna TODAS as conversas com ao menos uma
// mensagem no período — não só as ~200 mais recentes, nem só as cuja ÚLTIMA msg cai
// no período. Cada limite pode vir vazio (só início, só fim, ou ambos).
const computeDateRange = (
  choice: string,
  start: string,
  end: string,
): { inicio: string; fim: string } => {
  if (!choice) return { inicio: "", fim: "" };
  if (choice === "custom") return { inicio: start || "", fim: end || "" };
  const hoje = new Date();
  if (choice === "today") {
    const s = ymdLocal(hoje);
    return { inicio: s, fim: s };
  }
  if (choice === "yesterday") {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const s = ymdLocal(y);
    return { inicio: s, fim: s };
  }
  // Últimos N dias (incluindo hoje).
  const n = Number(choice);
  if (!Number.isFinite(n) || n <= 0) return { inicio: "", fim: "" };
  const ini = new Date();
  ini.setDate(ini.getDate() - (n - 1));
  return { inicio: ymdLocal(ini), fim: ymdLocal(hoje) };
};

// Prévia da última mensagem no card: se a última msg for mídia, mostra ícone + tipo
// (ex.: "🎤 Áudio") em vez da URL crua do bucket. Tipo de mídia → ícone (Tabler) + rótulo.
const PREVIEW_MIDIA: Record<string, { icon: string; label: string }> = {
  audio: { icon: "ti-microphone", label: "Áudio" },
  image: { icon: "ti-photo", label: "Imagem" },
  video: { icon: "ti-video", label: "Vídeo" },
  document: { icon: "ti-file-text", label: "Documento" },
  contact: { icon: "ti-user", label: "Contato" },
  sticker: { icon: "ti-sticker", label: "Figurinha" },
};

// Usa o `type` da última mensagem real (não-anotação); como fallback, infere o tipo pela
// pasta/extensão da URL (cobre mídia recebida cujo type venha diferente).
const previewUltimaMsg = (contact: any): { icon: string | null; texto: string } => {
  const msgs = contact?.messages || [];
  let tipo: string | undefined;
  for (let i = msgs.length - 1; i >= 0; i--) {
    // Ignora anotações, transferências E uploads internos (tnpupload): a prévia
    // reflete a última mensagem REAL, não um evento interno da conversa.
    if (msgs[i]?.type !== "anotacao" && msgs[i]?.type !== "transfer" && msgs[i]?.type !== "tnpupload" && msgs[i]?.type !== "call") { tipo = msgs[i]?.type; break; }
  }
  const lm = contact?.last_message || "";
  if (!tipo || !PREVIEW_MIDIA[tipo]) {
    if (/^(https?:\/\/|blob:|\/storage\/)/i.test(lm)) {
      if (/\/audio\/|\.(ogg|opus|mp3|m4a|aac|amr)(\?|$)/i.test(lm)) tipo = "audio";
      else if (/\/imagem\/|\.(jpe?g|png|webp|gif)(\?|$)/i.test(lm)) tipo = "image";
      else if (/\/video\/|\.(mp4|3gp|mov|webm)(\?|$)/i.test(lm)) tipo = "video";
      else if (/\/document\/|\.(pdf|docx?|xlsx?|pptx?|txt|csv)(\?|$)/i.test(lm)) tipo = "document";
    }
  }
  if (tipo && PREVIEW_MIDIA[tipo]) return { icon: PREVIEW_MIDIA[tipo].icon, texto: PREVIEW_MIDIA[tipo].label };
  return { icon: null, texto: lm };
};

// Ícone de "transferido sem resposta" no card: verdadeiro quando o contato tem uma
// mensagem type='transfer' e, DEPOIS dela, nenhuma mensagem enviada (status='send').
// Ou seja: o lead foi transferido e ainda não foi respondido pelo novo atendente.
// Usa timestamp (não posição) para ser robusto a qualquer ordenação da lista.
const precisaIconeTransfer = (contact: any): boolean => {
  // Card LEVE (lista sem histórico): o backend já manda a resposta pronta em
  // `transfer_sem_resposta`, calculada no mesmo GROUP BY do resumo. Antes isto
  // varria `contact.messages` — e por isso o ícone sumia na busca, que sempre
  // veio sem mensagens. O cálculo local abaixo continua valendo para quem tem o
  // histórico em memória (conversa aberta) e para respostas antigas em cache.
  if (typeof contact?.transfer_sem_resposta === "boolean") return contact.transfer_sem_resposta;
  const msgs = contact?.messages || [];
  let ultimoTransferTs: number | null = null;
  for (const m of msgs) {
    if (m?.type === "transfer") {
      const t = new Date(m.timestamp).getTime();
      if (!isNaN(t) && (ultimoTransferTs === null || t > ultimoTransferTs)) ultimoTransferTs = t;
    }
  }
  if (ultimoTransferTs === null) return false; // nunca foi transferido
  for (const m of msgs) {
    if (m?.status === "send") {
      const t = new Date(m.timestamp).getTime();
      if (!isNaN(t) && t > ultimoTransferTs) return false; // já houve resposta após o transfer
    }
  }
  return true;
};

// Tamanho humano (B/KB/MB) a partir de bytes.
// Converte \n / \r\n LITERAIS (a string "barra-n", não o caractere) em quebra de linha real.
// Com o wrapper em white-space: pre-wrap, vira quebra visual; newlines REAIS não são afetados
// (a regex só pega a sequência escapada).
const comQuebras = (txt?: string | null): string =>
  (txt ?? "").replace(/\\r\\n|\\r|\\n/g, "\n");

const formatBytes = (n: number): string => {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Cor por tipo de arquivo (estilo dos apps: PDF vermelho, Word azul, Excel verde…).
const FILE_COLORS: Record<string, string> = {
  pdf: "#e2574c",
  doc: "#2b7cd3", docx: "#2b7cd3",
  xls: "#1d9d5d", xlsx: "#1d9d5d", csv: "#1d9d5d",
  ppt: "#d35230", pptx: "#d35230",
  zip: "#f0ad4e", rar: "#f0ad4e", "7z": "#f0ad4e",
  txt: "#607d8b",
  jpg: "#8e44ad", jpeg: "#8e44ad", png: "#8e44ad", webp: "#8e44ad", gif: "#8e44ad",
};

// Ícone de arquivo: página (canto dobrado) + faixa colorida com a extensão escrita.
const FileTypeIcon = ({ ext }: { ext: string }) => {
  const cor = FILE_COLORS[ext] || "#007e94";
  const label = (ext || "FILE").toUpperCase().slice(0, 4);
  return (
    <svg width="42" height="42" viewBox="0 0 40 40" style={{ flex: "0 0 auto", display: "block" }}>
      <path d="M10 4 H26 L32 10 V34 A2 2 0 0 1 30 36 H10 A2 2 0 0 1 8 34 V6 A2 2 0 0 1 10 4 Z" fill="#fff" stroke="#d9dee3" strokeWidth="1.4" />
      <path d="M26 4 V8 A2 2 0 0 0 28 10 H32 Z" fill="#eef1f4" stroke="#d9dee3" strokeWidth="1" />
      <rect x="6" y="20" width="26" height="12" rx="2.5" fill={cor} />
      <text x="19" y="29" fontSize={label.length > 3 ? 7 : 8.5} fontWeight="700" fill="#fff" textAnchor="middle" fontFamily="system-ui, Arial, sans-serif">{label}</text>
    </svg>
  );
};

// Arrastar uma mídia da PRÓPRIA conversa (documento/imagem) para a área de anexo: o navegador
// leva só o link (ou um arquivo de 0 bytes, no caso da <img> do storage) — o anexo saía vazio
// (TNP retenção, 28/10/2026). A bolha grava a URL + nome neste tipo e o drop baixa o arquivo.
const MIDIA_DRAG_TYPE = "application/x-conversai-midia";
const iniciarArrasteMidia = (e: React.DragEvent, url: string, nome: string) => {
  if (!/^https?:\/\//i.test(url)) return;
  e.dataTransfer.setData(MIDIA_DRAG_TYPE, JSON.stringify({ url, nome }));
  e.dataTransfer.effectAllowed = "copy";
};

// Bolha de documento estilo WhatsApp: ícone do tipo + nome + (TIPO · tamanho) + baixar.
// O tamanho vem por HEAD (Content-Length) quando o storage permite; senão é omitido.
// Mesmo <a download> de antes — clique baixa/abre igual.
const DocumentBubble = ({ href, fileName, isDark }: { href: string; fileName: string; isDark: boolean }) => {
  const ext = (fileName.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const [size, setSize] = useState<string>("");
  useEffect(() => {
    let cancel = false;
    if (!/^https?:\/\//i.test(href)) return;
    fetch(href, { method: "HEAD" })
      .then((r) => {
        const len = r.headers.get("content-length");
        if (!cancel && len) setSize(formatBytes(Number(len)));
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [href]);
  const subtitle = [ext ? ext.toUpperCase() : "", size].filter(Boolean).join(" · ");
  return (
    <a
      href={href}
      download={fileName}
      target="_blank"
      rel="noopener noreferrer"
      title={fileName}
      onDragStart={(e) => iniciarArrasteMidia(e, href, fileName)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        textDecoration: "none",
        color: "inherit",
        padding: "8px 12px 8px 8px",
        borderRadius: 10,
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
        maxWidth: 290,
        minWidth: 190,
      }}
    >
      <FileTypeIcon ext={ext} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fileName}
        </div>
        {subtitle && <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <i className="ti ti-download" style={{ fontSize: 18, opacity: 0.5, flex: "0 0 auto" }} />
    </a>
  );
};

// Linha de contato MEMOIZADA. Como aplicarMensagemRealtime preserva a referência dos
// contatos não-alterados, uma mensagem nova re-renderiza só a linha afetada — não a lista
// inteira. Props devem ser estáveis: isDark/stages (state) e onSelect (useCallback).
// Botão do header para etapa de "atendimento finalizado" (29/10/2026): finaliza o atendimento
// (o card sai da lista) ou, se já está finalizado, reabre (volta para a etapa anterior).
const BotaoFinalizarAtendimento = ({ finalizado, onFinalizar, onReabrir }: {
  finalizado: boolean; onFinalizar: () => void; onReabrir: () => void;
}) => (
  <button
    type="button"
    className={`btn btn-sm d-flex align-items-center gap-1 ${finalizado ? "btn-outline-secondary" : "btn-outline-success"}`}
    style={{ height: 32, whiteSpace: "nowrap", fontWeight: 600 }}
    onClick={finalizado ? onReabrir : onFinalizar}
    title={finalizado ? "Atendimento finalizado — clique para reabrir" : "Finalizar o atendimento deste contato"}
  >
    <i className={`ti ${finalizado ? "ti-rotate-clockwise" : "ti-circle-check"}`} />
    {finalizado ? "Reabrir atendimento" : "Finalizar atendimento"}
  </button>
);

const ContactRow = React.memo(function ContactRow({ contact, isActive, isDark, stages, onSelect, onPrefetch, tags = SEM_TAGS }: {
  contact: any; isActive: boolean; isDark: boolean; stages: any[];
  onSelect: (c: any) => void; onPrefetch?: (c: any) => void; tags?: ContactTag[];
}) {
  const matchedStage = stages.find((s: any) => s.funil === contact.funil && s.nome_funil === contact.nome_funil);
  const isHuman = matchedStage?.atendimento_humano === "Sim";
  const isBot = matchedStage?.closer === "Sim" && !isHuman;
  const hasUnread = !!(contact.unread_count && contact.unread_count > 0);
  const prevUltima = previewUltimaMsg(contact);
  // Ícone de "transferido sem resposta" — só para o grupo (id_cliente) 192.
  const showTransferIcon =
    (localStorage.getItem("id_grupo") || "") === "192" && precisaIconeTransfer(contact);

  return (
    <div
      className={`chat-list chat-card ${isActive ? "active" : ""}`}
      onClick={() => onSelect(contact)}
      // O mouse chega ao card antes do clique — normalmente algumas centenas de ms.
      // Buscar o histórico aqui costuma ser suficiente para o clique parecer instantâneo.
      // `carregarConversa` é idempotente (conversasCarregadasRef), então passar o mouse
      // várias vezes não gera request repetido.
      onMouseEnter={() => onPrefetch?.(contact)}
      style={{ marginBottom: "4px", borderRadius: "12px", width: "100%", border: "none", overflow: "hidden", padding: 0, transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)", cursor: "pointer" }}
    >
      <div
        className={`chat-user-list d-flex ${isHuman ? "card-human" : ""} ${isBot ? "card-closer" : ""}`}
        style={{ minHeight: "64px", alignItems: "center", padding: "10px 12px 10px 14px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <h6
              style={{
                margin: 0,
                fontWeight: contact.unread_count && contact.unread_count > 0 ? 700 : 600,
                fontSize: 14,
                letterSpacing: "-0.1px",
                color: contact.unread_count && contact.unread_count > 0 ? (isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)") : (isDark ? "#D8DFEE" : "#1a1a2e"),
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {contact.nome}
              {isHuman && (<i className="ti ti-headset text-success" style={{ fontSize: "14px" }} />)}
              {isBot && (<i className="ti ti-robot text-primary" style={{ fontSize: "14px" }} />)}
              {showTransferIcon && (
                <span
                  title="Transferido sem resposta"
                  style={{
                    flex: "0 0 auto",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 17,
                    height: 17,
                    borderRadius: "50%",
                    background: "#4dabf7",
                    color: "#fff",
                  }}
                >
                  <i className="ti ti-arrows-exchange-2" style={{ fontSize: "11px", fontWeight: 700 }} />
                </span>
              )}
            </h6>

            {contact.lastTimestamp && (
              <span style={{ fontSize: 11, color: isDark ? "#666" : "#8a94a6", whiteSpace: "nowrap", flex: "0 0 auto", textAlign: "right", maxWidth: 76, fontWeight: 500 }}>
                {(() => {
                  const d = new Date(contact.lastTimestamp);
                  const now = new Date();
                  const diffDays = Math.floor((now.setHours(0, 0, 0, 0) - d.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24));
                  if (diffDays === 0) {
                    return fmtLocalTime(contact.lastTimestamp);
                  } else if (diffDays === 1) {
                    return "Ontem";
                  } else {
                    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
                  }
                })()}
              </span>
            )}
          </div>

          <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{ fontSize: 12.5, color: hasUnread ? (isDark ? "#cfd8e6" : "#374151") : (isDark ? "#7a8494" : "#6b7280"), fontWeight: hasUnread ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1, lineHeight: "1.4" }}
              title={prevUltima.texto}
            >
              {prevUltima.icon && (
                <i className={`ti ${prevUltima.icon}`} style={{ marginRight: 4, fontSize: 13, verticalAlign: "-1px", opacity: 0.85 }} />
              )}
              {prevUltima.texto}
            </div>
            {hasUnread && (
              <span
                title={`${contact.unread_count} mensagem${contact.unread_count === 1 ? "" : "s"} não lida${contact.unread_count === 1 ? "" : "s"}`}
                style={{ flex: "0 0 auto", background: "#25d366", color: "#fff", fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, padding: "0 6px", borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
              >
                {contact.unread_count! > 99 ? "99+" : contact.unread_count}
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: "4px", marginTop: "4px", flexWrap: "wrap" }}>
            {contact.funil && (
              <span style={{ fontSize: "10px", backgroundColor: isDark ? "rgba(77,216,232,0.1)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.08)", padding: "2px 8px", borderRadius: "6px", color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" as const }}>
                {contact.funil}
              </span>
            )}
            {contact.nome_funil && (
              <span style={{ fontSize: "10px", backgroundColor: isDark ? "rgba(171,71,188,0.12)" : "rgba(171,71,188,0.08)", padding: "2px 8px", borderRadius: "6px", color: isDark ? "#ce93d8" : "#7b1fa2", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" as const }}>
                {contact.nome_funil}
              </span>
            )}
            {tags.map((t) => (
              <TagChip key={t.id} tag={t} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

const Chat = () => {
  const isDark = useSelector((state: any) => state.themeSetting.dataTheme) === "dark";
  const isFetchingContacts = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollbarsRef = useRef<Scrollbars>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<string>("");
  const today = new Date().toISOString().slice(0, 10);
  const isFirstLoad = useRef(true);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const ultimoSomRef = useRef<number>(0); // throttle do som de notificação (timestamp ms)
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false); // busca de msg no header mobile
  const isMobile = useIsMobile();
  const [mobileHeight, setMobileHeight] = useState("calc(100vh - 90px)");

  // --- PIX STATES & HANDLERS ---
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixAmount, setPixAmount] = useState("");
  const [sendingPix, setSendingPix] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<"pix" | "mercadopago" | "stripe">("pix");
  const [paymentDescription, setPaymentDescription] = useState("Reserva do barco pirata");

  const handleSendPixConfirm = async () => {
    if (!selectedContact || !pixAmount || parseFloat(pixAmount) <= 0) {
      alert("Por favor, insira um valor válido.");
      return;
    }

    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact || !contact.fone) return;

    setSendingPix(true);
    try {
      const id_cliente = localStorage.getItem("id_cliente");
      const id_user = localStorage.getItem("id_user");
      let instanceName = localStorage.getItem("instanceName");

      // Garante instância
      if (!instanceName && id_cliente !== "146") {
         throw new Error("Instância não conectada ou não encontrada.");
      }

      // 1. Gerar Payload PIX
      const valorFormatado = parseFloat(pixAmount).toFixed(2);
      const pix = new PixPayload(
          PIX_CONFIG.CHAVE, 
          PIX_CONFIG.NOME, 
          PIX_CONFIG.CIDADE, 
          valorFormatado, 
          "RESERVA"
      );
      const code = pix.getPayload();

      // 2. Enviar Msg 1: Valor
      const msgValor = `R$ ${valorFormatado} referente a ${paymentDescription}`;
      await fetch(`${API_URL}/enviarmensagem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instanceName,
          fone: contact.fone,
          text: msgValor,
          id_cliente,
          id_user
        }),
      });

      // 3. Buscar e Enviar Msg 2: QR Code (Imagem)
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(code)}`;
      const qrRes = await fetch(qrUrl);
      const qrBlob = await qrRes.blob();
      const qrFile = new File([qrBlob], "qrcode-pix.png", { type: "image/png" });

      // Reutiliza lógica de envio de mídia
      const form = new FormData();
      form.append("file", qrFile);
      form.append("mediatype", "image");
      form.append("number", contact.fone);
      form.append("id_cliente", id_cliente || "");
      if (id_user) form.append("id_user", id_user);
      if (instanceName) form.append("instanceName", instanceName);

      const endpoint = id_cliente === "129" ? `${API_URL}/enviarmidiameta` : `${API_URL}/enviarmidia`;
      await fetch(endpoint, { method: "POST", body: form });

      // 4. Enviar Msg 3: Instruções
      const msgInstrucao = `Código Copia e Cola:\n\n1. Copie o código abaixo\n2. Abra seu banco\n3. Escolha PIX Copia e Cola`;
      await fetch(`${API_URL}/enviarmensagem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instanceName,
          fone: contact.fone,
          text: msgInstrucao,
          id_cliente,
          id_user
        }),
      });

      // 5. Enviar Msg 4: Código Puro
      await fetch(`${API_URL}/enviarmensagem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instanceName,
          fone: contact.fone,
          text: code,
          id_cliente,
          id_user
        }),
      });

      // Sucesso
      setPixAmount("");
      setShowPixModal(false);
      alert("Sequência de PIX enviada com sucesso!");

      // Atualiza chat (opcional, pode levar um tempo para o back processar)
      fetchContactsFull(); 

    } catch (err) {
      console.error("Erro ao enviar PIX:", err);
      alert("Erro ao enviar PIX. Verifique a conexão e tente novamente.");
    } finally {
            isFetchingContacts.current = false;
      setSendingPix(false);
    }
  };

  const handleSendPaymentConfirm = async () => {
    if (paymentProvider === "pix") {
      handleSendPixConfirm();
      return;
    }
    if (!selectedContact || !pixAmount || parseFloat(pixAmount) <= 0) {
      alert("Por favor, insira um valor válido.");
      return;
    }
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact || !contact.fone) return;
    setSendingPix(true);
    try {
      const id_cliente = localStorage.getItem("id_cliente");
      const id_user = localStorage.getItem("id_user");
      let instanceName = localStorage.getItem("instanceName");
      let instanciaoficial = localStorage.getItem("instanciaoficial");
      const res = await fetch(`${API_URL}/criar_link_pagamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: paymentProvider,
          valor: pixAmount,
          descricao: paymentDescription
        })
      });
      if (!res.ok) throw new Error("Erro ao gerar link de pagamento");
      const data = await res.json();
      const paymentLink = data.link;
      const msgText = `Segue o link para pagamento via ${paymentProvider === "stripe" ? "Stripe (Cartão)" : "Mercado Pago"}:\n\n${paymentDescription}\nValor: R$ ${parseFloat(pixAmount).toFixed(2)}\n\nLink: ${paymentLink}`;
      const sendEndpoint = instanciaoficial && instanciaoficial !== "0"
        ? `${API_URL}/enviarmensagem_admin`
        : `${API_URL}/enviarmensagem`;
      await fetch(sendEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instanceName,
          fone: contact.fone,
          text: msgText,
          id_cliente,
          id_user,
          instanciaoficial
        }),
      });
      setPixAmount("");
      setShowPixModal(false);
      alert("Link de pagamento enviado com sucesso!");
      fetchContactsFull();
    } catch (err) {
      console.error("Erro ao enviar link de pagamento:", err);
      alert("Erro ao enviar pagamento. Verifique a conexão.");
    } finally {
            isFetchingContacts.current = false;
      setSendingPix(false);
    }
  };


  // -----------------------------

  const isMounted = useRef(true);
  // Tamanho da PRIMEIRA carga da lista. Era 200, o que no admin master do grupo 192
  // (7 times) dava ~4 MB e 11 s só no servidor: em rede lenta a busca estourava o
  // timeout de 30 s e a lista nunca aparecia. Com 50 a primeira dobra chega rápido e
  // o resto vem pelo scroll infinito, que já sobe de 200 em 200 (LOTE_CONTATOS).
  const LIMITE_INICIAL_CONTATOS = 50;
  const LOTE_CONTATOS = 200;
  const [contactsLimit, setContactsLimit] = useState(LIMITE_INICIAL_CONTATOS);
  const isLoadingMore = useRef(false);
  const hasMoreContacts = useRef(true);
  const contactsListScrollRef = useRef<Scrollbars>(null);
  const fetchController = useRef<AbortController | null>(null); // 🎮 Controle de cancelamento
  // Geração da busca de contatos: cada fetchContactsFull incrementa e captura o seu número.
  // Ao retornar, se já começou um fetch mais novo, o resultado é DESCARTADO — evita que
  // fetches concorrentes (race) se sobrescrevam e a lista pisque/some durante a busca.
  const fetchGenRef = useRef(0);

  // Busca server-side: searchTerm é o input cru; searchTermDebounced é o que
  // efetivamente vai para o backend (300ms de delay). A ref espelha o valor
  // atual p/ ser lido dentro de fetchContactsFull sem stale closure.
  const [searchTermDebounced, setSearchTermDebounced] = useState<string>("");
  const searchTermDebouncedRef = useRef<string>("");
  // Indicador visual do fetch de busca server-side. Liga durante o request
  // (depois do debounce); o `pending` derived cobre a janela do debounce.
  const [isSearchingRemote, setIsSearchingRemote] = useState(false);
  // Busca bateu no teto de resultados do backend (X-Busca-Truncada). Mostra a dica
  // "refine a busca" no rodapé da lista.
  const [buscaTruncada, setBuscaTruncada] = useState(false);
  // Deep-link `?fone=`: spinner de tela cheia enquanto o /contato_por_fone resolve
  // QUAL contato abrir (ainda não há conversa selecionada). Só isso — o histórico da
  // conversa já aberta é o `chaveConversaCarregando` abaixo.
  const [carregandoConversa, setCarregandoConversa] = useState(false);
  // QUAL conversa está buscando o histórico (chave `fone|id_time`), ou null.
  //
  // 🔴 Era o booleano `carregandoConversa` acima que mandava neste spinner, e ele
  // ficava preso em `true`: o effect da conversa sob demanda depende de `contacts`,
  // que muda a cada polling/realtime — o cleanup marcava `cancelado`, o `finally`
  // então NÃO desligava o spinner, e a re-execução saía cedo pelo
  // `conversasCarregadasRef`, sem nunca mais desligar. Numa conversa COM mensagens
  // isso passava batido (o spinner só é exibido enquanto a lista está vazia); num
  // contato SEM nenhuma mensagem — o time 5159 tem 129 assim — o spinner ficava
  // girando para sempre. Por chave, cada conversa liga e desliga o seu.
  const [chaveConversaCarregando, setChaveConversaCarregando] = useState<string | null>(null);
  // Chave estável de uma conversa: mesmo par usado no conversasCarregadasRef.
  const chaveConversa = (fone?: string | number | null, idTime?: string | number | null) =>
    `${fone ?? ""}|${idTime ?? ""}`;
  // VEREDITO do servidor sobre cada conversa (chave `fone|id_time`):
  //   "vazia" → o backend respondeu 200 com `messages: []`. NÃO existem mensagens.
  //   "erro"  → a busca falhou. Não sabemos se existem.
  //   ausente → ainda não sabemos (nunca buscamos, ou a busca está em voo).
  //
  // 🔴 Existe porque o corpo da conversa dizia "Nenhuma mensagem nesta conversa ainda"
  // em situação de DÚVIDA. O teste era `allMsgs.length === 0 && !carregandoHistorico`,
  // e `carregandoHistorico` só é true no fetch COM indicador: no prefetch de hover
  // (que marca a conversa como carregada e busca sem indicador), clicar durante o voo
  // fazia o `carregarConversa` sair cedo, sem ligar spinner nenhum — e o operador lia
  // "não tem mensagem" numa conversa cheia. O mesmo valia para o intervalo entre
  // selecionar o contato e o efeito de carga rodar.
  //
  // A regra agora é: o aviso de conversa vazia SÓ aparece com veredito "vazia". Em
  // qualquer outro caso de lista vazia — inclusive não sabermos ainda — mostra-se o
  // carregamento. Na dúvida, nunca afirmar que não há mensagens.
  const [veredictoConversa, setVeredictoConversa] = useState<Record<string, "vazia" | "erro">>({});
  const marcarVeredicto = useCallback((chave: string, v: "vazia" | "erro" | null) => {
    setVeredictoConversa((prev) => {
      if (v === null) {
        if (!(chave in prev)) return prev;          // nada mudou: não re-renderiza
        const { [chave]: _fora, ...resto } = prev;
        return resto;
      }
      if (prev[chave] === v) return prev;
      return { ...prev, [chave]: v };
    });
  }, []);
  // Fones cuja conversa já foi carregada sob demanda — evita refetch a cada clique.
  const conversasCarregadasRef = useRef<Set<string>>(new Set());
  // Última mensagem (lastTimestamp do card) que já motivou uma rebusca de cada
  // conversa, na rede de segurança do realtime (ver fetchContactsFull). Existe pelo
  // caso patológico: se algum dia o `lastTimestamp` do card e o que o
  // /contato_por_fone devolve discordarem de forma permanente — um tipo de mensagem
  // que um conta e o outro não —, a divergência nunca fecharia e a conversa seria
  // rebuscada a CADA polling, para sempre. Guardando o timestamp já tentado, a
  // segunda tentativa pela MESMA mensagem não acontece; uma mensagem realmente nova
  // (timestamp maior) continua sendo buscada na hora, sem espera.
  const divergenciaJaTentadaRef = useRef<Map<string, number>>(new Map());
  // Último snapshot da lista "modo normal" (sem search). Usado para restaurar
  // os contatos instantaneamente quando o operador apaga o campo de busca —
  // evita ver a lista quase vazia enquanto o refetch normal está em andamento.
  const normalContactsRef = useRef<Contact[] | null>(null);

  // Intervalo do filtro de data (YYYY-MM-DD) em ref, p/ ser lido dentro de
  // fetchContactsFull sem stale closure. Mantido em sincronia por um useEffect
  // que também dispara o re-fetch quando o filtro muda.
  const dateFilterRef = useRef<{ inicio: string; fim: string }>({ inicio: "", fim: "" });

  // Filtros de funil/etapa/time em ref, pelo MESMO motivo do dateFilterRef: são lidos
  // dentro de fetchContactsFull, que é chamado também pelo polling (closure congelado).
  const funnelFilterRef = useRef<{ etapa: string | null; funis: string[]; times: string[]; tags: number[]; finalizados: boolean }>({
    etapa: null, funis: [], times: [], tags: [], finalizados: false,
  });
  // Limite atual em ref. O polling de 30s vive num useEffect com deps [] e por isso
  // congelava `contactsLimit` no valor inicial (200): a cada tick ele refazia a busca
  // com limit=200 e, como o setContacts SUBSTITUI a lista, tudo que o scroll infinito
  // tinha carregado desaparecia da tela. Lendo da ref, o polling respeita o limite atual.
  const contactsLimitRef = useRef(LIMITE_INICIAL_CONTATOS);
  useEffect(() => { contactsLimitRef.current = contactsLimit; }, [contactsLimit]);
  // Ponteiro sempre atualizado para a última versão de fetchContactsFull, para quem é
  // chamado de dentro de closures congelados (polling) não usar uma versão velha.
  const fetchContactsFullRef = useRef<((isPolling?: boolean) => void) | null>(null);
  // Sinaliza que estamos buscando MAIS contatos (scroll/refill), para o spinner de
  // "Carregando mais..." aparecer sem depender de o operador ter rolado a lista.
  const [carregandoMais, setCarregandoMais] = useState(false);
  // Espelho reativo de isFetchingContacts (ver comentario no fetchContactsFull).
  const [buscandoContatos, setBuscandoContatos] = useState(false);
  // Quantos contatos sobraram DEPOIS dos filtros no último render — usado pelo refill
  // automático. Ref (e não state) porque só é lido dentro de effect, sem re-renderizar.
  const visibleContactsCountRef = useRef(0);
  // Guarda para pular a primeira execucao do effect de filtros (ver comentario la).
  const filtrosInicializados = useRef(false);
  // Refill automático: alvo mínimo de itens na lista e teto de segurança para não
  // varrer o grupo inteiro num filtro que quase não casa (o 192 tem ~8900 conversas).
  const MIN_CONTATOS_VISIVEIS = 30;
  const LIMITE_MAX_AUTO = 2000;

  // Optimistic overrides: mantém alterações pendentes (etapa/funil) enquanto
  // o backend ainda não commitou — evita que o polling sobrescreva a UI.
  const pendingOverridesRef = useRef<Map<string, { nome_funil?: string; funil?: string; expiraEm: number }>>(new Map());
  const PENDING_OVERRIDE_TTL_MS = 15000;

  const setPendingOverride = (fone: string, partial: { nome_funil?: string; funil?: string }) => {
    const existing = pendingOverridesRef.current.get(String(fone)) || { expiraEm: 0 };
    pendingOverridesRef.current.set(String(fone), {
      ...existing,
      ...partial,
      expiraEm: Date.now() + PENDING_OVERRIDE_TTL_MS,
    });
  };

  const clearPendingOverride = (fone: string) => {
    pendingOverridesRef.current.delete(String(fone));
  };

  const applyPendingOverrides = <T extends { fone?: string | number; nome_funil?: string; funil?: string }>(list: T[]): T[] => {
    if (pendingOverridesRef.current.size === 0) return list;
    const now = Date.now();
    // Purge expirados
    Array.from(pendingOverridesRef.current.entries()).forEach(([k, v]) => {
      if (v.expiraEm <= now) pendingOverridesRef.current.delete(k);
    });
    if (pendingOverridesRef.current.size === 0) return list;
    return list.map((c) => {
      const ov = pendingOverridesRef.current.get(String(c.fone));
      if (!ov) return c;
      return {
        ...c,
        ...(ov.nome_funil !== undefined ? { nome_funil: ov.nome_funil } : {}),
        ...(ov.funil !== undefined ? { funil: ov.funil } : {}),
      };
    });
  };


  useEffect(() => {
    if (!isMobile) return;
    const updateHeight = () => {
      // Calcula altura real visível - 90px de margem/header
      setMobileHeight(`${window.innerHeight - 90}px`);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isMobile]);
  const [showChatMobile, setShowChatMobile] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Toggle do bloco de filtros/dropdowns no DESKTOP. Por padrão fechado p/
  // admin (libera espaço da lista de contatos); o botão "Funções" alterna.
  const [funcoesOpen, setFuncoesOpen] = useState(false);
  // Emoji picker (ao lado do botão de enviar). Click fora fecha.
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  // Header de contato: recolhe/expande os botões de ação (Atendimento, Cards,
  // Funil, Etapa, Info, busca, etc.) p/ não tampar o nome. Recolhido por padrão.
  const [headerActionsExpanded, setHeaderActionsExpanded] = useState(false);
  // Ref do wrapper de ações do header — usado p/ recolher o popover "Informações"
  // ao clicar fora dele.
  const headerActionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!headerActionsExpanded) return;
    const onClickAway = (e: MouseEvent) => {
      if (headerActionsRef.current && !headerActionsRef.current.contains(e.target as Node)) {
        setHeaderActionsExpanded(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [headerActionsExpanded]);
  useEffect(() => {
    if (!showEmojiPicker) return;
    const onClickAway = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [showEmojiPicker]);

  // Insere o emoji na posição do cursor do textarea — preserva texto antes/depois.
  const insertEmoji = (emoji: string) => {
    const ta = messageInputRef.current;
    if (!ta) {
      setNewMessage((prev) => prev + emoji);
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const next = before + emoji + after;
    setNewMessage(next);
    // Restaura cursor após o emoji inserido + auto-resize
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    });
  };
  const [isEditingName, setIsEditingName] = useState(false);
  const [nomeEditado, setNomeEditado] = useState("");
  // Modal de criação manual de contato
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactFunnel, setNewContactFunnel] = useState("");
  const [newContactStage, setNewContactStage] = useState("");
  // Número (instância) por onde o lead vai ser atendido. Vira funil_fluxo.cliente_ins e é o
  // que o roteamento de envio usa depois — a janela de 24h da Meta é POR NÚMERO.
  const [instanciasDoTime, setInstanciasDoTime] = useState<{ ins_nome: number; ins_numero: number }[]>([]);
  const [newContactIns, setNewContactIns] = useState("");
  // DDI separado do numero: o operador digitava 55 junto e errava (ou esquecia). Agora o
  // campo de telefone recebe SO DDD + numero, e o DDI vem de um seletor, 55 por padrao.
  const [newContactDDI, setNewContactDDI] = useState("55");
  // Admin master escolhe o time de destino; os demais criam no proprio time. Funis, etapas
  // e numeros do modal seguem ESTE time, nao o do login.
  const [newContactTime, setNewContactTime] = useState<string>("");
  const [modalFunnels, setModalFunnels] = useState<{ id: number; nome_funil: string }[]>([]);
  const [modalStages, setModalStages] = useState<any[]>([]);
  // Seletor de numero e etapa-receptora sao opt-in por grupo. Fora do opt-in a tela fica
  // exatamente como era antes (operador escolhe funil e etapa, sem seletor de numero).
  const grupoUsaReceptora = localStorage.getItem("id_grupo") === "192";   // TNP
  // 🔴 SAO DUAS REGRAS DIFERENTES, e juntar as duas num flag so foi o erro de 15/09:
  // a ETAPA RECEPTORA (que ESCONDE funil/etapa) segue opt-in da TNP, mas o SELETOR DE
  // NUMERO e consequencia de ter MAIS DE UM numero no time -- vale para qualquer grupo
  // (18/09: nascimento martins, ins 63 + 64). Com um numero so nao ha o que perguntar e
  // o seletor NAO aparece, nem na TNP (que ate 18/09 mostrava um select desabilitado).
  // Fora do opt-in da receptora o operador segue escolhendo funil e etapa normalmente.
  const mostraSeletorNumero = instanciasDoTime.length > 1;
  const [receptora, setReceptora] = useState<{ funil: string; etapa: string; boasVindas?: boolean } | null>(null);

  // Carrega os números do time ao abrir o modal. Time com 1 número só já vem escolhido
  // (não faz sentido perguntar); com vários, a escolha passa a ser obrigatória.
  // Ao abrir o modal, define o time de destino: o do login por padrao.
  useEffect(() => {
    if (!showNewContactModal) return;
    setNewContactTime((atual) => atual || localStorage.getItem("id_cliente") || "");
  }, [showNewContactModal]);

  // Numeros, funis e etapas seguem o TIME ESCOLHIDO. Recarrega quando ele muda — sem isso,
  // o admin master criava lead em outro time com o funil/etapa do time dele.
  useEffect(() => {
    if (!showNewContactModal || !newContactTime) return;
    const id_time = newContactTime;
    (async () => {
      try {
        // id_user: no time "numero por dono" (AFH 159) o backend devolve so o numero do operador.
        const res = await fetch(`${API_URL}/instancias_time`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_time, id_user: localStorage.getItem("id_user") }),
        });
        const lista = res.ok ? await res.json() : [];
        const arr = Array.isArray(lista) ? lista : [];
        setInstanciasDoTime(arr);
        setNewContactIns(arr.length === 1 ? String(arr[0].ins_nome) : "");
      } catch (e) {
        console.error("instancias_time:", e);
        setInstanciasDoTime([]);
        setNewContactIns("");
      }
      try {
        const [rf, re] = await Promise.all([
          fetch(`${API_URL}/pegarpaginas`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id_cliente: id_time }) }),
          fetch(`${API_URL}/pegaretapaschat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id_cliente: id_time }) }),
        ]);
        const df = rf.ok ? await rf.json() : [];
        const de = re.ok ? await re.json() : [];
        setModalFunnels((Array.isArray(df) ? df : []).map((i: any, idx: number) => ({ id: idx + 1, nome_funil: i.page })));
        setModalStages(Array.isArray(de) ? de : []);
      } catch (e) {
        console.error("funis/etapas do modal:", e);
        setModalFunnels([]); setModalStages([]);
      }
      setNewContactFunnel(""); setNewContactStage("");
      // Etapa receptora do time (so nos grupos com opt-in). Resolvida no backend para a
      // regra viver num lugar so. Sem receptora, o operador escolhe como sempre.
      if (grupoUsaReceptora) {
        try {
          const rr = await fetch(`${API_URL}/etapa_receptora`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_time }),
          });
          const dr = rr.ok ? await rr.json() : {};
          if (dr?.funil && dr?.etapa) {
            setReceptora({ funil: dr.funil, etapa: dr.etapa, boasVindas: !!dr.dispara_boas_vindas });
            setNewContactFunnel(dr.funil);
            setNewContactStage(dr.etapa);
          } else {
            setReceptora(null);
          }
        } catch (e) {
          console.error("etapa_receptora:", e);
          setReceptora(null);
        }
      } else {
        setReceptora(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewContactModal, newContactTime]);

  // Formata 554137952654 -> +55 41 3795-2654 (só para exibir no seletor)
  const rotuloNumero = (n: number | string) => {
    const d = String(n).replace(/\D/g, "");
    if (d.length < 12) return String(n);
    const ddd = d.slice(2, 4), resto = d.slice(4);
    const meio = resto.length > 8 ? resto.slice(0, 5) : resto.slice(0, 4);
    return `+${d.slice(0, 2)} ${ddd} ${meio}-${resto.slice(meio.length)}`;
  };
  // Estado de criação: loading no botão + mensagem de erro específica do back
  const [creatingContact, setCreatingContact] = useState(false);
  const [createContactError, setCreateContactError] = useState<string | null>(null);







  interface Contact {
    id: number;
    nome: string;
    fone: string;
    lastTimestamp?: number;
    last_message?: string;
    profilePictureUrl?: string;
    unread_count?: number;
    nome_funil?: string; // etapa do funil
    funil?: string; // funil
    observacao?: string;
    messages?: Message[];
    idioma?: string;
    id_time?: number;
    id_user?: number | null; // usuário (atendente) dono atual do contato
  }

  interface Message {
    id: number | string;
    fone: string;
    status: 'send' | 'received' | 'note' | 'upload' | 'call';
    mensagem: string;
    timestamp: string;
    read: boolean;
    type: string;
    traduzido?: string;
    audio_transc?: string;
    audio_trad?: string; // ✅ esse é o novo campo que está faltando no tipo
    msg_original?: string;
    template?: boolean; // true quando esta mensagem enviada é um template (NÃO reabre a janela 24h)
    status_template_wp?: string | null; // status de entrega do template na API Oficial: 'sent' | 'delivered' | 'read' | 'failed' (só preenchido p/ templates)
    motivo_template_wp?: string | null; // motivo do erro do template (só preenchido quando status_template_wp = 'failed')
    id_user?: number | null; // autor da mensagem (usado p/ resolver nome em anotações)
    ins?: number | string | null; // instância (ins_nome) que registrou a mensagem — usado p/ filtro por número (time 159)
    clientKey?: string; // key React estável herdada do temp ao confirmar (evita remount/pulo na transição temp→real)
    template_nome?: string | null;   // nome do template (resolve mídia/botões via templatesInfo)
    template_midia?: string | null;  // URL da imagem/vídeo do header do template (bolha unificada)
    template_botoes?: { type?: string; text?: string; url?: string; phone_number?: string }[] | null; // botões do template
    doc_name?: string | null;        // nome original do documento (type='document'); mostrado no lugar do nome do bucket
    doc_validado?: boolean | null;   // validação do anexo pela operadora: null = não avaliado, true = aceito, false = recusado
  }

  // ── Anotações internas ──────────────────────────────────────────────
  // Usuário do grupo (para menção @ e p/ resolver o nome do autor da anotação)
  interface UsuarioGrupo {
    id: number;
    nome: string;
    id_time?: number;
    email?: string;
  }

  // Quebra o texto da anotação (com marcadores @[Nome](id)) em segmentos de
  // texto e menção, para renderizar as menções destacadas.
  type NotaSegmento = { tipo: "texto"; valor: string } | { tipo: "mencao"; valor: string };
  const MENCAO_RE = /@\[([^\]]+)\]\((\d+)\)/g;
  const parseNotaSegmentos = (texto: string): NotaSegmento[] => {
    const segs: NotaSegmento[] = [];
    let lastIndex = 0;
    const re = new RegExp(MENCAO_RE);
    let m: RegExpExecArray | null;
    while ((m = re.exec(texto)) !== null) {
      if (m.index > lastIndex) segs.push({ tipo: "texto", valor: texto.slice(lastIndex, m.index) });
      segs.push({ tipo: "mencao", valor: m[1] });
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < texto.length) segs.push({ tipo: "texto", valor: texto.slice(lastIndex) });
    return segs;
  };
  // Texto plano (sem marcadores) — usado para o textarea-fantasma de medição, etc.
  const notaTextoPlano = (texto: string): string =>
    (texto || "").replace(/@\[([^\]]+)\]\(\d+\)/g, "$1");

  interface Stage {
    id: number;
    nome_funil: string;
    funil: string;
    atendimento_humano?: string;
    atendimento_finalizado?: string | null;
    closer?: string;
  }

  const routerLocation = useLocation() as { state: { phone?: string } };

  const formatLocalTime = (ts: number | string | undefined): string => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  

  const useBodyClass = (className: string) => {
    const location = useLocation();

    useEffect(() => {
      if (location.pathname === "/chat") {
        document.body.classList.add(className);
      } else {
        document.body.classList.remove(className);
      }
      return () => {
        document.body.classList.remove(className);
      };
    }, [location.pathname, className]);
  };
  useBodyClass("app-chat");

  const routes = all_routes;
  const [contacts, setContacts] = useState<Contact[]>(() => {
    try {
      const id = localStorage.getItem("id_cliente");
      const cached = id ? localStorage.getItem(`contacts_cache_${id}`) : null;
      return cached ? JSON.parse(cached) : [];
    } catch (e) { return []; }
  });
  /*
   * Multi-conta: quando este /chat roda dentro de um frame de conta (ver
   * public/session-shim.js), avisa o shell o total de não lidas para ele pintar o
   * badge na aba desta conta. Fora do multi-conta `__CRM_SID__` não existe e o
   * efeito não faz nada.
   */
  useEffect(() => {
    const sid = (window as any).__CRM_SID__;
    if (!sid || window.parent === window) return;
    const total = contacts.reduce((soma, c) => soma + (c.unread_count ?? 0), 0);
    try {
      window.parent.postMessage({ tipo: "crm:nao-lidas", sid, total }, window.location.origin);
    } catch (e) { /* shell de outra origem: badge apenas não aparece */ }
  }, [contacts]);

  const [filterFunnels, setFilterFunnels] = useState<string[]>([]);
  const [isFunnelDropdownOpen, setIsFunnelDropdownOpen] = useState<boolean>(false);

  // ---- Tags de contato (só TAGS_GRUPOS_LIBERADOS) ----
  // Tags e vínculos vêm num GET só (/tags). `tagsPorFone` guarda arrays estáveis por fone:
  // a ContactRow é memoizada e só re-renderiza quando as tags daquele contato mudam.
  const tagsIdGrupo = localStorage.getItem("id_grupo") || "";
  const tagsLiberadas = TAGS_GRUPOS_LIBERADOS.includes(tagsIdGrupo);
  // Por time (29/10/2026): o operador vê as tags do PRÓPRIO time; o admin master vê todas
  // e, no contato, só as do time do contato. Tag sem time (antiga) vale para todos.
  const tagsMeuTime = localStorage.getItem("id_cliente") || "";
  const tagsAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  const [tagsCliente, setTagsCliente] = useState<ContactTag[]>([]);
  const [tagVinculos, setTagVinculos] = useState<{ fone: string; id_tag: number }[]>([]);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [filterTags, setFilterTags] = useState<number[]>([]);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const tagDoTime = (t: ContactTag, idTime: any) => t.id_time == null || String(t.id_time) === String(idTime ?? "");
  const tagsVisiveis = useMemo(
    () => (tagsAdminMaster ? tagsCliente : tagsCliente.filter((t) => tagDoTime(t, tagsMeuTime))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tagsCliente, tagsAdminMaster, tagsMeuTime]
  );
  const tagsDoTime = (idTime: any) => tagsCliente.filter((t) => tagDoTime(t, idTime));

  const carregarTags = useCallback(async () => {
    if (!tagsLiberadas) return;
    try {
      const r = await fetch(`${API_URL}/tags?id_grupo=${encodeURIComponent(tagsIdGrupo)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setTagsCliente(j.tags || []);
      setTagVinculos((j.vinculos || []).map((v: any) => ({ fone: soDigitos(v.fone), id_tag: Number(v.id_tag) })));
    } catch (e) {
      console.error("Erro ao carregar tags:", e);
    }
  }, [tagsLiberadas, tagsIdGrupo]);

  useEffect(() => { carregarTags(); }, [carregarTags]);

  // Chave "time|fone": o mesmo fone em dois times mostra, em cada um, só as tags daquele time.
  // Tag sem time entra em "*|fone" e é somada na leitura (tagsDoContato).
  const tagsPorFone = useMemo(() => {
    const porId = new Map(tagsCliente.map((t) => [t.id, t]));
    const mapa: Record<string, ContactTag[]> = {};
    for (const v of tagVinculos) {
      const t = porId.get(v.id_tag);
      if (!t) continue;
      const k = `${t.id_time ?? "*"}|${v.fone}`;
      (mapa[k] = mapa[k] || []).push(t);
    }
    Object.values(mapa).forEach((lista) => lista.sort((a, b) => a.nome.localeCompare(b.nome)));
    return mapa;
  }, [tagsCliente, tagVinculos]);
  const tagsDoContato = (idTime: any, fone: any): ContactTag[] | undefined => {
    const f = soDigitos(fone);
    const doTime = tagsPorFone[`${idTime ?? ""}|${f}`];
    const semTime = tagsPorFone[`*|${f}`];
    if (!semTime) return doTime;
    return doTime ? [...doTime, ...semTime] : semTime;
  };

  const usoPorTag = useMemo(() => {
    const uso: Record<number, number> = {};
    for (const v of tagVinculos) uso[v.id_tag] = (uso[v.id_tag] || 0) + 1;
    return uso;
  }, [tagVinculos]);

  // Devolve a mensagem de erro (para o modal mostrar) ou null quando deu certo.
  const chamarTags = async (url: string, method: string, body?: any): Promise<string | null> => {
    try {
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return j?.error || `Erro ${r.status}`;
      return null;
    } catch (e: any) {
      return e?.message || "Falha de conexão";
    }
  };

  // Quem fez a ação: vai em toda chamada e fica no log das tags (contato_tag_log).
  const tagsQuem = () => ({
    id_user: localStorage.getItem("id_user") || "",
    login: localStorage.getItem("user_email") || "",
  });

  const criarTag = async (nome: string, cor: string, idTime: string) => {
    const erro = await chamarTags(`${API_URL}/tags`, "POST", { id_grupo: tagsIdGrupo, id_time: idTime, nome, cor, ...tagsQuem() });
    if (!erro) await carregarTags();
    return erro;
  };

  const editarTag = async (id: number, nome: string, cor: string) => {
    const erro = await chamarTags(`${API_URL}/tags/${id}`, "PUT", { id_grupo: tagsIdGrupo, nome, cor, ...tagsQuem() });
    if (!erro) await carregarTags();
    return erro;
  };

  const apagarTag = async (id: number) => {
    const q = new URLSearchParams({ id_grupo: tagsIdGrupo, ...tagsQuem() });
    const erro = await chamarTags(`${API_URL}/tags/${id}?${q.toString()}`, "DELETE");
    if (!erro) await carregarTags();
    return erro;
  };

  // Marca/desmarca na hora (otimista) e desfaz se o backend recusar.
  const alternarTagContato = async (fone: string, tag: ContactTag, ativo: boolean) => {
    const f = soDigitos(fone);
    if (!f) return;
    const antes = tagVinculos;
    setTagVinculos((vs) => ativo
      ? (vs.some((v) => v.fone === f && v.id_tag === tag.id) ? vs : [...vs, { fone: f, id_tag: tag.id }])
      : vs.filter((v) => !(v.fone === f && v.id_tag === tag.id)));
    const erro = await chamarTags(`${API_URL}/tags/vincular`, "POST",
      { id_grupo: tagsIdGrupo, fone: f, id_tag: tag.id, ativo, ...tagsQuem() });
    if (erro) {
      setTagVinculos(antes);
      Swal.fire({ icon: "error", title: "Não foi possível salvar a tag", text: erro });
    }
  };

  const [selectedContact, setSelectedContact] = useState<number | null>(null);
  // Espelha `selectedContact` em ref para uso dentro de callbacks pegados por
  // setInterval do polling (evita stale closure). Permite "grudar" o contato
  // atualmente aberto na lista de contatos mesmo quando ele cai fora dos 200
  // primeiros do refetch normal (ex: após uma busca server-side).
  const selectedContactRef = useRef<number | null>(null);
  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);
  // Espelho de `contacts` pelo MESMO motivo do selectedContactRef: o polling e os
  // listeners de janela têm closure congelado e precisam da lista de agora — sem
  // virar dependência de efeito (o que os faria remontar a cada refetch).
  const contactsRef = useRef<Contact[]>([]);
  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessages, setNewMessages] = useState<typeof messages | null>(null); // Temporário
  const [newMessage, setNewMessage] = useState<string>("");
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [errorDetails, setErrorDetails] = useState<string | null>(null); // 🆕 Novo estado para detalhes do erro
  const [showErrorDetails, setShowErrorDetails] = useState(false); // 🆕 Controle de visibilidade dos detalhes
  //const [qrCode, setQrCode] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  // Inicializa isConnected com o valor salvo no localStorage ("true" ou "false")
  const [isConnected, setIsConnected] = useState<boolean>(() => {
    return localStorage.getItem("isConnected") === "true";
  }); 
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(true);
  const [pendingMessages, setPendingMessages] = useState<Message[]>([]);
  // `pendingMessages` é global de propósito: um upload iniciado numa conversa
  // continua em voo depois de o operador trocar de chat (era o que travava o
  // atendimento — ver sendMediaFile). Como a fila passou a ser global, TODO ponto
  // que combina temps com a conversa aberta precisa filtrar pelo fone dela, senão
  // o balão de um envio aparece na conversa errada.
  const tempsDoFone = useCallback(
    (pend: Message[], fone?: string | number | null): Message[] =>
      fone == null ? [] : pend.filter((m) => String(m.fone) === String(fone)),
    [],
  );
  const [newMessageIndicator, setNewMessageIndicator] = useState(false);

  // ── Aviso de versão nova do front ────────────────────────────────────────
  // A aba NÃO recarrega sozinha quando um bundle novo sobe: quem deixa o /chat
  // aberto por dias segue rodando o JS do dia em que abriu. Isso já produziu bug
  // fantasma — em 10/09/2026 a duplicata de mensagens foi reportada com o fix
  // dela JÁ em produção desde 03/09, e uma aba antiga explica exatamente isso.
  // Aqui a aba compara o bundle que ELA carregou com o que o servidor serve agora.
  // 🔴 Nunca recarrega sozinha: o operador pode estar com mensagem digitada.
  const [novaVersaoDisponivel, setNovaVersaoDisponivel] = useState(false);

  useEffect(() => {
    const el = document.querySelector('script[src*="/static/js/main."]') as HTMLScriptElement | null;
    const casa = el?.src?.match(/main\.[a-z0-9]+\.js/i);
    const bundleDestaAba = casa ? casa[0] : null;
    if (!bundleDestaAba) return;   // dev server: não há bundle com hash

    let vivo = true;
    const checar = async () => {
      try {
        // `no-store` + querystring: sem isso o próprio cache do navegador
        // devolveria o index antigo e o aviso nunca apareceria.
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(/main\.[a-z0-9]+\.js/i);
        if (vivo && m && m[0] !== bundleDestaAba) setNovaVersaoDisponivel(true);
      } catch {
        /* offline ou soluço de rede: tenta de novo no próximo ciclo */
      }
    };
    const id = window.setInterval(checar, 10 * 60 * 1000);
    return () => { vivo = false; window.clearInterval(id); };
  }, []);

  // Mapa persistente id-real-da-mensagem → { key, ts } herdados do temp que ela
  // confirmou. Mantém a key React e o timestamp ESTÁVEIS entre pollings, evitando
  // que a mensagem "pule" (remonte/reordene) quando o temp vira mensagem real do
  // backend (que traria id novo e timestamp do servidor).
  const sentMsgStableRef = useRef<Map<string, { key: string; ts: string }>>(new Map());

  // ── Anotações internas (habilitado para todos os grupos) ──────────
  const ANOTACOES_HABILITADO = !!localStorage.getItem("id_grupo");
  const [usuariosDoGrupo, setUsuariosDoGrupo] = useState<UsuarioGrupo[]>([]);
  const [showNoteComposer, setShowNoteComposer] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
  const noteComposerRef = useRef<HTMLDivElement>(null);
  // Mapa id_user -> nome p/ resolver autor das anotações exibidas
  const nomePorUserId = React.useMemo(() => {
    const map: Record<number, string> = {};
    usuariosDoGrupo.forEach((u) => { map[u.id] = u.nome; });
    return map;
  }, [usuariosDoGrupo]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [funnels, setFunnels] = useState<Stage[]>([]);

  // Etapas de TODOS os times do grupo — só o admin master carrega. É o que
  // permite mover um lead para o funil/etapa de outro time direto do /chat
  // (a mudança de time viaja junto: ver /mover_lead_funil_etapa no api.py).
  interface GroupStage {
    id_time: number;
    nome_time: string;
    id_funil_pai: number;
    funil: string;
    id_etapa: number;
    nome_funil: string;
    ordem: number;
    atendimento_finalizado?: string | null;
  }
  const [groupStages, setGroupStages] = useState<GroupStage[]>([]);
  const [initialPhone, setInitialPhone] = useState<string | undefined>(undefined);
  // Feedback visual do botão "Compartilhar" (deep-link da conversa).
  const [shareCopied, setShareCopied] = useState(false);
  // Feedback visual do botão "Marcar como não lido" (disponível p/ todos).
  const [naoLidoMarcado, setNaoLidoMarcado] = useState(false);
  // Enquanto a conversa aberta foi marcada MANUALMENTE como não-lida, suprime a
  // auto-marcação de "lido" (scroll até o fim / "ver novas mensagens"). O flag só é
  // resetado ao abrir a conversa de novo (fresh open), no useEffect de [selectedContact].
  const suprimirAutoLeituraRef = useRef(false);

  // Deep-link: copia um link que abre o /chat já nesta conversa (?fone=...).
  // Funciona em qualquer PC desde que a pessoa tenha login (o chat carrega os
  // contatos dela e auto-seleciona este fone).
  const compartilharConversa = (fone?: string) => {
    if (!fone) return;
    const link = `${window.location.origin}${window.location.pathname}?fone=${encodeURIComponent(fone)}`;
    const marcarCopiado = () => {
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(marcarCopiado).catch(() => { window.prompt("Copie o link da conversa:", link); });
    } else {
      window.prompt("Copie o link da conversa:", link);
    }
  };

  // Marca a conversa como NÃO lida (disponível p/ todos os usuários). Só as ÚLTIMAS
  // mensagens recebidas contam — o bloco de 'received' que veio DEPOIS do nosso último
  // envio. Mensagens recebidas antes de uma resposta nossa não voltam a não-lido. O
  // filtro (último 'send' -> só 'received' posteriores) é feito no backend
  // (/marcarnaolidos), que também devolve o total de não-lidas atualizado p/ o badge.
  // Ao marcar, liga o flag que impede a conversa aberta de ser re-marcada como lida.
  const marcarComoNaoLido = async () => {
    const fone = selectedContactData?.fone;
    if (!fone) return;
    try {
      const resp = await fetch(`${API_URL}/marcarnaolidos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // id_grupo (escopo real da contagem) + id_time como fallback p/ api.py antigo,
        // mesmo motivo do markMessagesAsRead.
        body: JSON.stringify({
          fone,
          id_grupo: localStorage.getItem("id_grupo"),
          id_time: selectedContactData?.id_time,
        }),
      });
      if (!resp.ok) throw new Error("Falha ao marcar como não lido.");
      const data = await resp.json().catch(() => ({} as any));
      const novoUnread = Number(data?.unread_count) || 0;
      // Impede que a conversa (que continua aberta) seja re-marcada como lida.
      suprimirAutoLeituraRef.current = true;
      setContacts((prev) =>
        prev.map((contact) =>
          contact.id === selectedContact ? { ...contact, unread_count: novoUnread } : contact
        )
      );
      setNaoLidoMarcado(true);
      window.setTimeout(() => setNaoLidoMarcado(false), 2500);
    } catch (error) {
      console.error("Erro ao marcar mensagens como não lidas", error);
    }
  };
  const normalizePhone = (phone: any) => {
    if (!phone) return "";
    return String(phone).replace(/\D/g, "");
  };

  // ── Realtime (Supabase): aplica UMA mensagem (row da tabela chats) recebida via
  // WebSocket direto no estado, sem esperar o polling. Reusa o dedup por id + a key
  // estável (igual à reconciliação do polling) p/ não duplicar nem "pular".
  const aplicarMensagemRealtime = useCallback((row: any) => {
    if (!row || row.fone == null) return;
    const novaMsg: Message = {
      id: row.id,
      fone: String(row.fone),
      status: row.status,                       // 'send' | 'received' | 'note' | 'upload' (já bate)
      mensagem: row.mensagem ?? "",
      timestamp: row.created_at,
      read: row.read ?? false,
      type: row.type ?? "conversation",
      id_user: row.id_user ?? null,
      ins: row.ins ?? null,
      template: row.template ?? undefined,
      template_nome: row.template_nome ?? null,
      template_midia: row.template_midia ?? null,
      template_botoes: Array.isArray(row.template_botoes) ? row.template_botoes : null,
      doc_name: row.doc_name ?? null,
      doc_validado: row.doc_validado ?? null,
    };
    const foneNorm = normalizePhone(novaMsg.fone);
    setContacts((prev) => {
      let achou = false;
      const out = prev.map((c) => {
        if (normalizePhone(c.fone) !== foneNorm) return c;
        achou = true;
        const msgs = c.messages || [];
        // já temos essa mensagem real (id do banco)? então ignora (idempotente)
        if (msgs.some((mm) => String(mm.id) === String(novaMsg.id))) return c;
        // remove UM temp correspondente (mesma msg+status) e doa sua key+timestamp
        // p/ a mensagem real — evita duplicar com o otimista e evita o "pulo".
        let removeuTemp = false;
        const filtradas = msgs.filter((mm) => {
          if (removeuTemp || !String(mm.id).startsWith("temp-")) return true;
          if (mm.mensagem === novaMsg.mensagem && mm.status === novaMsg.status) {
            removeuTemp = true;
            sentMsgStableRef.current.set(String(novaMsg.id), {
              key: mm.clientKey ?? String(mm.id),
              ts: mm.timestamp,
            });
            return false;
          }
          return true;
        });
        const st = sentMsgStableRef.current.get(String(novaMsg.id));
        const msgFinal = st ? { ...novaMsg, clientKey: st.key, timestamp: st.ts } : novaMsg;
        const aberto = selectedContactRef.current === c.id;
        // 🔊 Som de notificação: mensagem recebida nova num contato DESTE atendente (já
        // matched aqui) + conversa NÃO aberta. Throttle evita duplicar (StrictMode/burst);
        // enviadas e contatos de outro atendente não tocam.
        if (novaMsg.status === "received" && !aberto) {
          const _agora = Date.now();
          const _a = notificationSoundRef.current;
          if (_a && _agora - ultimoSomRef.current > 1200) {
            ultimoSomRef.current = _agora;
            _a.currentTime = 0;
            _a.play().catch(() => {});
          }
        }
        return {
          ...c,
          messages: [...filtradas, msgFinal],
          // Anotação, transferência e upload interno NÃO viram prévia (mantém a última
          // msg real); porém 'transfer' ATUALIZA o lastTimestamp abaixo (card p/ o topo).
          last_message: (novaMsg.type === "anotacao" || novaMsg.type === "transfer" || novaMsg.type === "tnpupload" || novaMsg.type === "call") ? c.last_message : novaMsg.mensagem,
          // Upload interno não reordena a lista (mesma regra do /contacts_full, que
          // exclui 'tnpupload' do last_timestamp) — senão o card pula p/ o topo aqui
          // e volta pro lugar no próximo fetch.
          lastTimestamp: (novaMsg.type === "tnpupload" || novaMsg.type === "call") ? c.lastTimestamp : new Date(msgFinal.timestamp).getTime(),
          unread_count:
            novaMsg.status === "received" && !aberto
              ? (c.unread_count ?? 0) + 1
              : (c.unread_count ?? 0),
        } as typeof c;
      });
      // contato ainda não está na lista carregada → deixa o polling-fallback trazer
      if (!achou) return prev;
      return [...out].sort((a, b) => ((b.lastTimestamp ?? 0) - (a.lastTimestamp ?? 0)));
    });
  }, []);

  // Realtime: aplica um UPDATE de mensagem já existente (read/visto ✓✓, status de
  // template, motivo de erro) — atualiza a msg no thread sem esperar o polling.
  const aplicarUpdateMensagemRealtime = useCallback((row: any) => {
    if (!row || row.id == null) return;
    const foneNorm = normalizePhone(String(row.fone));
    setContacts((prev) => prev.map((c) => {
      if (normalizePhone(c.fone) !== foneNorm) return c;
      const msgs = c.messages || [];
      let mudou = false;
      const novas = msgs.map((mm) => {
        if (String(mm.id) !== String(row.id)) return mm;
        mudou = true;
        return {
          ...mm,
          read: row.read ?? mm.read,
          status: row.status ?? mm.status,
          status_template_wp: row.status_template_wp ?? mm.status_template_wp,
          motivo_template_wp: row.motivo_template_wp ?? mm.motivo_template_wp,
          mensagem: row.mensagem ?? mm.mensagem,
          // Sem `??` aqui: null é um estado válido (validação desfeita) e o `??`
          // o confundiria com "campo ausente", travando a bolha no valor antigo.
          doc_validado: 'doc_validado' in row ? row.doc_validado : mm.doc_validado,
        } as Message;
      });
      return mudou ? { ...c, messages: novas } : c;
    }));
  }, []);

  // Realtime: contato NOVO (1ª msg de um número inédito) entra na lista na hora.
  // Respeita a visibilidade: admin vê todos do time/grupo; atendente só os seus
  // (id_user) — evita exibir contato de outro atendente momentaneamente.
  const aplicarContatoRealtime = useCallback((row: any) => {
    if (!row || row.fone == null) return;
    const ehAdmin =
      localStorage.getItem("isAdminMaster") === "true" ||
      localStorage.getItem("isAdmin") === "true";
    const meuIdUser = localStorage.getItem("id_user");
    if (!ehAdmin && String(row.id_user ?? "") !== String(meuIdUser ?? "")) return;
    const foneNorm = normalizePhone(String(row.fone));
    setContacts((prev) => {
      if (prev.some((c) => normalizePhone(c.fone) === foneNorm && String(c.id_time) === String(row.id_time))) {
        return prev;
      }
      const novo: Contact = {
        id: row.id,
        nome: row.nome ?? "",
        fone: String(row.fone),
        id_time: row.id_time,
        id_user: row.id_user ?? null,
        messages: [],
        last_message: "",
        lastTimestamp: row.data_entrada ? new Date(row.data_entrada).getTime() : Date.now(),
        unread_count: 0,
        observacao: row.observacao ?? "",
        idioma: row.idioma ?? IDIOMA_PADRAO,
        profilePictureUrl: row.profpic ?? undefined,
      };
      return [novo, ...prev];
    });
  }, []);

  // Seleção de contato extraída p/ callback ESTÁVEL — exigido pelo React.memo da
  // ContactRow (se mudasse de referência a cada render, a memoização não pegaria).
  const handleSelectContact = useCallback((contact: any) => {
    setSelectedContact(contact.id);
    setMessages(contact.messages || []);
    // Só descarta os temps DESTA conversa: os de um upload em andamento em OUTRA
    // conversa continuam vivos (ver comentário em sendMediaFile).
    setPendingMessages((prev) => prev.filter((m) => String(m.fone) !== String(contact.fone)));
    if (isMobile) setShowChatMobile(true);
  }, [isMobile]);

  // ── Conversa sob demanda + prefetch ───────────────────────────────────────
  // A lista chega SEM o histórico (`sem_mensagens=1`), então o histórico de cada
  // conversa é buscado aqui, no máximo uma vez por sessão. O `conversasCarregadasRef`
  // é a trava, e ela vale para os três caminhos que chamam esta função: abrir a
  // conversa, passar o mouse no card e o prefetch de fundo.
  // O histórico buscado em SEGUNDO PLANO fica AQUI, não em `contacts`. Escrever cada
  // resposta no state recriaria o array e re-renderizaria a lista de 50 cards, 15 vezes;
  // num ref isso custa zero render, e o histórico só entra no state quando o operador
  // abre a conversa.
  // ⚠️ Medido com CPU 6x mais lenta (lista visível, 3 amostras de cada): 8959 ms sem
  // prefetch × 8975 ms com prefetch — 16 ms, ou seja, ruído. Este cache NÃO foi o que
  // salvou o boot; ele é só a forma mais barata de fazer a mesma coisa. Uma medição
  // anterior sugeriu 1,4 s de atraso, mas era artefato do teste (ponto zero no login,
  // que varia, e interceptação do Playwright em só um dos lados).
  const historicoPrefetchRef = useRef<Map<string, Message[]>>(new Map());

  const aplicarHistorico = useCallback((c: Contact, msgs: Message[]) => {
    // Mescla em vez de substituir: enquanto a busca estava em voo, o realtime (ou um
    // envio otimista) pode ter anexado mensagens que ainda não existiam no histórico
    // que o servidor montou. Sobrescrever o array as apagaria da tela.
    setContacts((prev) =>
      prev.map((x) => {
        if (x.id !== c.id) return x;
        const doServidor = new Set(msgs.map((m) => String(m.id)));
        const extras = (x.messages || []).filter((m) => !doServidor.has(String(m.id)));
        return { ...x, messages: extras.length ? [...msgs, ...extras] : msgs };
      }),
    );
  }, []);

  const carregarConversa = useCallback((c: Contact, comIndicador: boolean,
                                        emSegundoPlano = false) => {
    if (!c || !c.fone) return;
    // 🔴 NÃO usar `c.messages.length > 0` como "já tem histórico". Numa lista leve o
    // array nasce vazio, e o realtime (aplicarMensagemRealtime) ANEXA a mensagem que
    // acabou de chegar — o contato fica com 1 item e o teste concluía, errado, que a
    // conversa estava carregada. Resultado: o painel mostrava só o que chegou depois de
    // a página abrir e o histórico nunca era buscado. Atinge em cheio o admin master,
    // cujo realtime escuta o GRUPO inteiro e por isso "toca" contatos que ele nem abriu.
    // O único critério confiável é o registro explícito de que já buscamos.
    const chave = chaveConversa(c.fone, c.id_time);
    // Já buscado em segundo plano? Aplica na hora — é isto que torna o clique instantâneo.
    const doCache = historicoPrefetchRef.current.get(chave);
    if (doCache) {
      if (emSegundoPlano) return;                       // já está guardado, nada a fazer
      historicoPrefetchRef.current.delete(chave);
      aplicarHistorico(c, doCache);
      // O prefetch também produz veredito: se ele voltou vazio, a conversa é vazia
      // mesmo — sem isto o corpo ficaria em carregamento eterno, já que este caminho
      // não dispara fetch nenhum.
      marcarVeredicto(chave, doCache.length === 0 ? "vazia" : null);
      return;
    }
    if (conversasCarregadasRef.current.has(chave)) return;   // já tentou (inclusive conversa vazia)
    conversasCarregadasRef.current.add(chave);

    // O indicador é só do clique. No prefetch a busca é invisível de propósito: ela
    // acontece antes de o operador pedir qualquer coisa.
    if (comIndicador) setChaveConversaCarregando(chave);
    const params = new URLSearchParams({ fone: String(c.fone) });
    if (c.id_time != null) params.append("id_time", String(c.id_time));
    const grupo = localStorage.getItem("id_grupo");
    if (grupo) params.append("id_cliente", grupo);
    fetch(`${API_URL}/contato_por_fone?${params.toString()}`)
      // 🔴 Uma resposta não-2xx NÃO é exceção do fetch. Antes ela virava `null`, o
      // `.then` seguinte saía calado e o `.catch` (o único que devolve a chave ao
      // conversasCarregadasRef) nunca rodava — a conversa ficava presa em "Nenhuma
      // mensagem nesta conversa ainda" até um F5. Acontece de verdade: o gunicorn
      // recicla worker a cada 500 requests (~75×/dia) e reseta requisições em voo,
      // o que o nginx devolve como 502.
      .then((r) => {
        if (!r.ok) throw new Error(`/contato_por_fone HTTP ${r.status}`);
        return r.json();
      })
      .then((full) => {
        // Corpo inesperado também precisa liberar a chave. `messages: []` é resposta
        // válida (conversa realmente vazia) e mantém a chave, sem refetch em loop.
        if (!full || !Array.isArray(full.messages)) throw new Error("resposta sem `messages`");
        const ehAAberta = selectedContactRef.current === c.id;
        if (emSegundoPlano && !ehAAberta) {
          // Guarda sem tocar no state: nenhum re-render durante o boot.
          historicoPrefetchRef.current.set(chave, full.messages);
          return;
        }
        // Veredito do servidor: 200 com array vazio é a ÚNICA prova de que a conversa
        // não tem mensagens. É o que libera o aviso no corpo da conversa.
        marcarVeredicto(chave, full.messages.length === 0 ? "vazia" : null);
        aplicarHistorico(c, full.messages);
        // O painel só é preenchido se ESTA conversa for a que está aberta agora — escrever
        // ali no prefetch jogaria histórico de outro contato dentro da conversa aberta.
        if (ehAAberta) setMessages((prev) => (prev.length === 0 ? full.messages : prev));
      })
      .catch((e) => {
        conversasCarregadasRef.current.delete(chave);   // deixa tentar de novo
        // Falha NÃO é conversa vazia: o corpo passa a dizer que não conseguiu
        // carregar, em vez de afirmar que o lead nunca escreveu.
        marcarVeredicto(chave, "erro");
        console.error("Erro ao carregar a conversa:", e);
      })
      // Desliga SEMPRE — só não pisa numa carga mais nova.
      .finally(() => setChaveConversaCarregando((atual) => (atual === chave ? null : atual)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aplicarHistorico, marcarVeredicto]);

  // Abrir a conversa: carrega com indicador visível.
  useEffect(() => {
    if (selectedContact == null) return;
    const c = contacts.find((x) => x.id === selectedContact);
    if (c) carregarConversa(c, true);
  }, [selectedContact, contacts, carregarConversa]);

  // Handler estável para o hover. O ContactRow é memoizado, então uma função nova a
  // cada render anularia a memoização e re-renderizaria a lista inteira.
  const prefetchConversa = useCallback((c: Contact) => carregarConversa(c, false, true),
    [carregarConversa]);

  // Rebusca a conversa ABERTA ignorando o conversasCarregadasRef. Serve para os
  // momentos em que há motivo para desconfiar do realtime — que é a única fonte de
  // mensagem nova do corpo da conversa desde a lista leve (ver a rede de segurança
  // no fetchContactsFull). O throttle existe porque os dois gatilhos abaixo costumam
  // disparar juntos: voltar para a aba reconecta o socket.
  const ultimaRevalidacaoRef = useRef(0);
  const REVALIDACAO_INTERVALO_MS = 5000;
  const revalidarConversaAberta = useCallback((motivo: string) => {
    const agora = Date.now();
    if (agora - ultimaRevalidacaoRef.current < REVALIDACAO_INTERVALO_MS) return;
    const id = selectedContactRef.current;
    if (id == null) return;
    const c = contactsRef.current.find((x) => x.id === id);
    if (!c || !c.fone) return;
    ultimaRevalidacaoRef.current = agora;
    const chave = chaveConversa(c.fone, c.id_time);
    conversasCarregadasRef.current.delete(chave);
    historicoPrefetchRef.current.delete(chave);
    console.log(`🔄 [DEBUG] revalidando a conversa aberta (${motivo})`);
    carregarConversa(c, false);   // sem indicador: a conversa já tem conteúdo na tela
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarConversa]);

  // Aba em segundo plano: o Chrome congela timers e deixa o socket mudo, então nem o
  // polling nem o realtime necessariamente correram enquanto o operador estava em
  // outra janela. Ao voltar, rebusca em vez de assumir que nada passou.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === "visible") revalidarConversaAberta("aba voltou ao foco");
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => document.removeEventListener("visibilitychange", aoVoltar);
  }, [revalidarConversaAberta]);

  // Primeira subscrição do canal do realtime não conta como reconexão (a conversa
  // acabou de ser carregada) — só as seguintes.
  const jaSubscreveuRealtimeRef = useRef(false);

  // Prefetch de fundo: logo depois de a lista aparecer, busca o histórico das
  // primeiras conversas para que o clique nelas seja instantâneo. Sem isto, cada
  // primeira abertura paga ~750 ms — foi o preço de deixar a lista leve em 01/09.
  //
  // 🔴 Sequencial e espaçado de propósito. Disparar 15 fetches de uma vez castiga um
  // backend que já recicla worker a cada 500 requests (e devolve 502 quando corta uma
  // requisição em voo). Espalhado, some no meio do polling que já roda a cada 5 s.
  // Quantas conversas vêm da API com o histórico já dentro (`msgs_primeiras`). O
  // prefetch abaixo continua existindo para as SEGUINTES — ele pula sozinho as que já
  // chegaram prontas, porque a chave delas entra no `conversasCarregadasRef`.
  const CONVERSAS_PRONTAS_NA_LISTA = 12;
  const PREFETCH_CONVERSAS = 15;
  const PREFETCH_INTERVALO_MS = 400;
  // Espera o boot assentar antes de começar: o parse do JSON das respostas disputa a
  // thread principal com a pintura dos 50 cards, que é o momento mais caro numa máquina
  // fraca. Medição com CPU 6x não acusou diferença (ver acima), então isto é precaução,
  // não correção de um problema observado.
  const PREFETCH_ATRASO_MS = 2500;
  const prefetchFeitoRef = useRef(false);
  // 🔴 A dependência é o BOOLEANO "a lista já apareceu", não `contacts`. Com `contacts`
  // o efeito re-executa a cada polling (5 s) e o cleanup mata o setInterval no meio —
  // só ~12 dos 15 prefetches chegariam a rodar. Assim ele dispara uma vez só, quando a
  // lista sai do vazio, e o `contacts` capturado no closure é justamente essa 1ª lista.
  const listaPronta = contacts.length > 0;
  useEffect(() => {
    if (prefetchFeitoRef.current || !listaPronta) return;
    prefetchFeitoRef.current = true;
    const alvos = contacts.slice(0, PREFETCH_CONVERSAS);
    let i = 0;
    let timer: ReturnType<typeof setInterval> | null = null;
    const inicio = setTimeout(() => {
    timer = setInterval(() => {
      if (i >= alvos.length) { if (timer) clearInterval(timer); return; }
      // Aba em segundo plano não gasta request: o operador não vai clicar agora.
      if (document.visibilityState === "visible") carregarConversa(alvos[i], false, true);
      i += 1;
    }, PREFETCH_INTERVALO_MS);
    }, PREFETCH_ATRASO_MS);
    return () => { clearTimeout(inicio); if (timer) clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaPronta, carregarConversa]);

  // Subscription do realtime: escuta a `chats` (INSERT=msg nova, UPDATE=visto/status)
  // e a `contatos` (INSERT=contato novo), filtrado por tenant (a policy RLS garante o
  // isolamento; o filtro reduz o tráfego). Atendente: o próprio time; admin master: o
  // grupo todo. Reconecta quando o token/login muda.
  useEffect(() => {
    const supabase = getSupabaseRealtime();
    if (!supabase) return;
    const id_time = localStorage.getItem("id_cliente");
    const id_grupo = localStorage.getItem("id_grupo");
    const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
    const filtro = isAdminMaster ? `id_cliente=eq.${id_grupo}` : `id_time=eq.${id_time}`;
    const channel = supabase
      .channel(`rt-chat-${id_time || "x"}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chats", filter: filtro },
        (payload) => aplicarMensagemRealtime(payload.new))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chats", filter: filtro },
        (payload) => aplicarUpdateMensagemRealtime(payload.new))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "contatos", filter: filtro },
        (payload) => aplicarContatoRealtime(payload.new))
      .subscribe((status) => {
        // SUBSCRIBED de novo = o socket caiu e voltou. O Supabase NÃO reenvia o que
        // passou durante a queda, e o polling só reidrata o card — então a conversa
        // aberta ficaria com um buraco silencioso. Rebusca.
        if (status !== "SUBSCRIBED") return;
        if (!jaSubscreveuRealtimeRef.current) { jaSubscreveuRealtimeRef.current = true; return; }
        revalidarConversaAberta("realtime re-subscrito");
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [aplicarMensagemRealtime, aplicarUpdateMensagemRealtime, aplicarContatoRealtime, revalidarConversaAberta]);

  // Realtime da ETAPA/FUNIL do contato (funil_fluxo @ banco CRM): quando o lead muda
  // de etapa (no leads-list, automação ou outro atendente), o /chat reflete na hora.
  // Mapeia id_funil→nome via `stages` (ref p/ versão atual). Etapa fora do cache (ex:
  // admin multi-time) cai no polling de 30s.
  const stagesChatRef = useRef<Stage[]>([]);
  useEffect(() => { stagesChatRef.current = stages; }, [stages]);

  const aplicarLeadFunilRealtime = useCallback((payload: any) => {
    const row = payload?.new;
    if (payload?.eventType !== "UPDATE" || !row || row.fone == null) return;
    const stage = stagesChatRef.current.find((s) => Number(s.id) === Number(row.id_funil));
    if (!stage) return;
    const foneNorm = normalizePhone(String(row.fone));
    setContacts((prev) => prev.map((c) =>
      normalizePhone(c.fone) === foneNorm
        ? ({ ...c, nome_funil: stage.nome_funil, funil: stage.funil } as typeof c)
        : c
    ));
  }, []);

  useEffect(() => {
    const supabase = getSupabaseRealtimeCRM();
    if (!supabase) return;
    const id_time = localStorage.getItem("id_cliente");
    const id_grupo = localStorage.getItem("id_grupo");
    const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
    const filtro = isAdminMaster ? `id_cliente=eq.${id_grupo}` : `id_time=eq.${id_time}`;
    const channel = supabase
      .channel(`rt-chat-leads-${id_time || "x"}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "funil_fluxo", filter: filtro },
        (payload) => aplicarLeadFunilRealtime(payload))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [aplicarLeadFunilRealtime]);
    const isFetchingFunnels = useRef(false);
    const isFetchingStages = useRef(false);
    const isFetchingGroupStages = useRef(false);
    const isFetchingMessages = useRef(false);
    const [temIdiomas, setTemIdiomas] = useState<boolean>(false);
    const isConnecting = useRef(false); // 🔒 Lock para evitar loop
    const isTranslating = useRef(false); // 🔒 Lock para tradução periódica
  
    const [loadingContacts, setLoadingContacts] = useState(() => {
      return !((window as any)._last_contacts_data?.length > 0);
    });
  
  
    const [showCombinedDropdown, setShowCombinedDropdown] = useState(false);
    const [combinedDropdownStyle, setCombinedDropdownStyle] = useState<React.CSSProperties>({});
    const combinedDropdownButtonRef = useRef<HTMLButtonElement>(null);
    const [isEditingObs, setIsEditingObs] = useState(false);
    const [obsValue, setObsValue] = useState<string>("");
    const [savingObs, setSavingObs] = useState(false);
    const prevContacts = useRef<Contact[]>([]);
    const selected = contacts.find(c => c.id === selectedContact);
    const msgs = [
      ... (selected?.messages || []),
      ...tempsDoFone(pendingMessages, selected?.fone)
    ].sort((a,b)=> new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [showMediaMenu, setShowMediaMenu] = useState(false);
    const [pendingUploadType, setPendingUploadType] = useState<"image" | "video" | "document" | "audio" | null>(null);
    // Arrastar-e-soltar arquivos na área de mensagens. O drop NÃO envia direto: abre
    // uma prévia com a lista de arquivos e só envia quando o usuário confirma.
    // dragDepthRef conta enter/leave dos filhos — sem isso o overlay pisca ao passar
    // o cursor por cima de cada bolha da conversa.
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const dragDepthRef = useRef(0);
    const [dropPreview, setDropPreview] = useState<{ file: File; previewUrl: string | null }[]>([]);
    // Biblioteca de mídias (modal no chat que puxa mídias reutilizáveis do grupo)
    const [showBibliotecaModal, setShowBibliotecaModal] = useState(false);
    const [bibliotecaItens, setBibliotecaItens] = useState<any[]>([]);
    const [bibliotecaLoading, setBibliotecaLoading] = useState(false);
    const [bibliotecaAba, setBibliotecaAba] = useState<"image" | "video" | "audio" | "document">("image");
    const [bibliotecaPastas, setBibliotecaPastas] = useState<any[]>([]);
    const [bibliotecaPasta, setBibliotecaPasta] = useState<any | null>(null);
    // Respostas rápidas: digitar "/" no campo de mensagem abre o picker
    const [respostasRapidas, setRespostasRapidas] = useState<any[]>([]);
    const [respostasCarregadas, setRespostasCarregadas] = useState(false);
    const [showRespostas, setShowRespostas] = useState(false);
    // Mídia aberta em tela cheia (modal) a partir de uma mensagem do chat.
    // `msgId` só vem nas imagens RECEBIDAS: é o que permite girar de dentro do modal
    // (e repintar a imagem em tela quando o backend devolve a versão girada).
    const [fullscreenMedia, setFullscreenMedia] = useState<{ tipo: "image" | "video"; src: string; msgId?: string } | null>(null);
    // Mapa template_nome -> {headerFormat, headerImage, botoes} de TODOS os templates do grupo.
    // Usado p/ identificar mídia/botões de QUALQUER mensagem de template pelo template_nome
    // (manual, cadência, boas-vindas) — independente de o envio ter gravado template_midia/botoes.
    const [templatesInfo, setTemplatesInfo] = useState<Record<string, { headerFormat?: string; headerImage?: string; botoes?: { type?: string; text?: string; url?: string; phone_number?: string }[] }>>({});
    // Envios de mídia em andamento, POR CONVERSA (fone).
    //
    // 🔴 Isto era um booleano global (`uploading`). Enquanto um documento subia,
    // o campo de texto, o botão de enviar e o de áudio ficavam desabilitados em
    // TODAS as conversas — o operador trocava de chat e continuava sem conseguir
    // escrever para ninguém até o upload terminar. Era a queixa "quando manda um
    // documento não dá pra mandar mensagem pra outras pessoas". Agora o bloqueio
    // vale só para a conversa que está enviando; as outras seguem livres.
    const [fonesEnviando, setFonesEnviando] = useState<string[]>([]);
    // Progresso da fila de envio múltiplo ("enviando 2 de 5"), por conversa.
    const [progressoEnvio, setProgressoEnvio] = useState<{ fone: string; feitos: number; total: number } | null>(null);
    const marcarEnviando = useCallback((fone: string | number | null | undefined, ligado: boolean) => {
      const f = String(fone ?? "");
      if (!f) return;
      setFonesEnviando((prev) =>
        ligado ? (prev.includes(f) ? prev : [...prev, f]) : prev.filter((x) => x !== f),
      );
    }, []);
    const [mostrarTraducaoAudio, setMostrarTraducaoAudio] = useState<Record<string, boolean>>({});
    const [mostrarTranscricaoAudio, setMostrarTranscricaoAudio] = useState<Record<string, boolean>>({});
    // Transcrição sob demanda: texto devolvido pelo /chat/transcrever_audio e estado de carregamento,
    // por id da mensagem (a lista de mensagens vem do servidor e não é tocada aqui).
    const [transcricoesAudio, setTranscricoesAudio] = useState<Record<string, string>>({});
    const [transcrevendoAudio, setTranscrevendoAudio] = useState<Record<string, boolean>>({});
    const transcreverAudio = async (idMsg: number | string) => {
      setTranscrevendoAudio(prev => ({ ...prev, [idMsg]: true }));
      try {
        const res = await fetch(`${API_URL}/chat/transcrever_audio`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: idMsg }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.audio_transc) {
          alert(body.error || `Não foi possível transcrever (HTTP ${res.status}).`);
          return;
        }
        setTranscricoesAudio(prev => ({ ...prev, [idMsg]: body.audio_transc }));
        setMostrarTranscricaoAudio(prev => ({ ...prev, [idMsg]: true }));
      } catch (err: any) {
        alert(`Erro de conexão: ${err?.message || "tente novamente"}`);
      } finally {
        setTranscrevendoAudio(prev => ({ ...prev, [idMsg]: false }));
      }
    };
    const [filterStage, setFilterStage] = useState<string | null>(null);
    // Filtro de data baseado na data da última mensagem (lastTimestamp).
    // dateChoice: "" = qualquer data | "today" = hoje | "yesterday" = ontem |
    // "7"/"15"/"30" = últimos N dias (calendário, incluindo hoje) |
    // "custom" = intervalo personalizado (usa dateStart/dateEnd, YYYY-MM-DD; cada limite é opcional).
    const [dateChoice, setDateChoice] = useState<string>("");
    const [dateStart, setDateStart] = useState<string>("");
    const [dateEnd, setDateEnd] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    // Filtro Todos / Não lidas (botões acima dos cards de contato).
    const [filtroNaoLidas, setFiltroNaoLidas] = useState<boolean>(false);
    // "Finalizados" (29/10/2026): card em etapa de atendimento finalizado sai da lista e só
    // aparece com este filtro (o servidor já filtra; ver /contacts_full `finalizados`).
    const [filtroFinalizados, setFiltroFinalizados] = useState<boolean>(false);
    // Desktop: controles (busca, novo contato, filtros) recolhíveis pela seta acima da lista,
    // para devolver altura aos contatos. A escolha fica lembrada neste navegador.
    const [controlesRecolhidos, setControlesRecolhidos] = useState<boolean>(() => {
      try { return localStorage.getItem("chat_controles_recolhidos") === "1"; } catch { return false; }
    });
    const alternarControles = () => {
      setControlesRecolhidos((v) => {
        const novo = !v;
        try { localStorage.setItem("chat_controles_recolhidos", novo ? "1" : "0"); } catch { /* sem storage */ }
        return novo;
      });
    };

    // Debounce 300ms do termo de busca para evitar 1 fetch por tecla.
    // Quando o termo final tem 3+ chars vai ao backend; senão volta ao modo normal.
    useEffect(() => {
      const trimmed = searchTerm.trim();
      // Restauração instantânea: quando o operador apaga (ou diminui abaixo de
      // 3 chars) ESTANDO em modo busca, traz os 200 normais imediatamente —
      // sem esperar o debounce nem o próximo fetch. Mantém o sticky aplicado.
      if (
        trimmed.length < 3 &&
        searchTermDebouncedRef.current &&
        normalContactsRef.current
      ) {
        setContacts((prev) => {
          const snapshot = normalContactsRef.current as Contact[];
          // O snapshot do modo normal fica CONGELADO enquanto a busca está ativa
          // (só é regravado quando !activeSearch — ver fetchContactsFull). Se o
          // operador mandou mensagem durante a busca, ela existe em `prev` mas não
          // no snapshot: restaurar cru fazia a bolha sumir até o refetch seguinte.
          // Reaproveita do estado atual toda mensagem que o snapshot não tem (por
          // id — cobre temp-* e confirmadas, e também recebidas que chegaram
          // durante a busca). `prev` é sempre o estado mais novo, então mensagem
          // apagada de propósito não ressuscita.
          const prevById = new Map(prev.map((c) => [c.id, c]));
          let restored = snapshot.map((c) => {
            const atual = prevById.get(c.id);
            if (!atual) return c;
            const msgsSnapshot = c.messages || [];
            const idsSnapshot = new Set(msgsSnapshot.map((m) => String(m.id)));
            const faltantes = (atual.messages || []).filter((m) => !idsSnapshot.has(String(m.id)));
            return faltantes.length > 0 ? { ...c, messages: [...msgsSnapshot, ...faltantes] } : c;
          });
          const selectedId = selectedContactRef.current;
          if (selectedId != null && !restored.some((c) => c.id === selectedId)) {
            const sticky = prev.find((c) => c.id === selectedId);
            if (sticky) restored = [sticky, ...restored];
          }
          return restored;
        });
      }

      const handle = setTimeout(() => {
        const t2 = searchTerm.trim();
        const next = t2.length >= 3 ? t2 : "";
        if (next !== searchTermDebouncedRef.current) {
          searchTermDebouncedRef.current = next;
          setSearchTermDebounced(next);
        }
      }, 300);
      return () => clearTimeout(handle);
    }, [searchTerm]);
    const [showRetryConnection, setShowRetryConnection] = useState(false);
    // --- Estados de busca interna no chat ---
    const [chatSearchTerm, setChatSearchTerm] = useState("");
    const [chatSearchMatches, setChatSearchMatches] = useState<(string | number)[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  
    // States for CardViewModal
    const [cardModalShow, setCardModalShow] = useState(false);
    const [selectedLeadPhone, setSelectedLeadPhone] = useState("");
    const [selectedLeadName, setSelectedLeadName] = useState("");
    const [selectedFunnelName, setSelectedFunnelName] = useState("");
  
    // --- Admin Master: filtro por time/usuário ---
    const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
    // Admin de time (admin local). Soma com adminMaster p/ permissões de transferência.
    const isAdminTime = localStorage.getItem("isAdmin") === "true";
    const isAnyAdmin = isAdminMaster || isAdminTime;
    // Login com API Oficial (Meta) conectada → exibe o status de entrega (✓/✓✓) de
    // TODAS as mensagens enviadas, não só templates. (Admin master vê vários times,
    // também habilita.) O status vem de status_template_wp (push do webhook).
    const _instOficial = localStorage.getItem("instanciaoficial");
    const temApiOficial = isAdminMaster || (!!_instOficial && _instOficial !== "0");
    // Criação manual de contato: REABILITADA para todos, inclusive o grupo 192 (TNP), em
    // 14/09/2026. Ficava desligada só para o 192 por hardcode. Agora o modal exige também
    // escolher a INSTÂNCIA (número) quando o time tem mais de uma — ver `instanciasDoTime`.
    const bloqueiaContatoManual = false;
    interface TimeInfo { id: string; nome: string; has_official: boolean; has_unofficial: boolean; }
    const [timesDoGrupo, setTimesDoGrupo] = useState<TimeInfo[]>([]);
    const timesDoGrupoRef = useRef<TimeInfo[]>([]);
    // Filtro por time aceita MÚLTIPLOS times. Lista vazia = todos os times.
    const [idsTimesSelecionados, setIdsTimesSelecionados] = useState<string[]>([]);
    const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState<boolean>(false);
    const [usuariosDoTime, setUsuariosDoTime] = useState<{ id: number; nome: string; fone?: number | string | null; ins?: number | string | null; id_time?: number }[]>([]);
    // Filtro por atendente aceita MÚLTIPLOS usuários. Lista vazia = todos.
    const [idsUsuariosSelecionados, setIdsUsuariosSelecionados] = useState<string[]>([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState<boolean>(false);

    // Opções do seletor "filtrar por atendente". A lista bruta (`usuariosDoTime`)
    // vem de /pegarusuariosgrupo, que devolve os atendentes do GRUPO inteiro —
    // necessário p/ achar dono cross-time no seletor de dono. Aqui recortamos:
    //   - admin master: os times marcados no filtro (nenhum marcado = todos);
    //   - admin de time: só o próprio time, senão o dropdown listaria nomes de
    //     times irmãos que não filtram nada na lista dele.
    const meuIdTime = localStorage.getItem("id_cliente");
    const opcoesUsuarioFiltro = isAdminMaster
      ? (idsTimesSelecionados.length === 0
          ? usuariosDoTime
          : usuariosDoTime.filter((u) => idsTimesSelecionados.includes(String(u.id_time ?? ""))))
      : usuariosDoTime.filter((u) => String(u.id_time ?? "") === String(meuIdTime ?? ""));

    // Com 2+ times marcados os nomes vêm de times diferentes: agrupa por time
    // (mesma estrutura que o filtro de Funil usa p/ as etapas). Times sem
    // atendente carregado não viram cabeçalho vazio.
    const agrupaUsuariosPorTime = isAdminMaster && idsTimesSelecionados.length > 1;
    const gruposUsuarioFiltro = agrupaUsuariosPorTime
      ? idsTimesSelecionados
          .map((idT) => ({
            idTime: idT,
            nomeTime: timesDoGrupo.find((t) => String(t.id) === idT)?.nome || `Time ${idT}`,
            usuarios: opcoesUsuarioFiltro.filter((u) => String(u.id_time ?? "") === idT),
          }))
          .filter((g) => g.usuarios.length > 0)
      : [];

    // Checkbox de um atendente — usado na lista plana e dentro de cada grupo.
    const renderCheckUsuario = (u: (typeof usuariosDoTime)[number]) => (
      <div key={u.id} className="form-check">
        <input
          id={`user-${u.id}`}
          className="form-check-input"
          type="checkbox"
          checked={idsUsuariosSelecionados.includes(String(u.id))}
          onChange={() =>
            setIdsUsuariosSelecionados(prev =>
              prev.includes(String(u.id))
                ? prev.filter(x => x !== String(u.id))
                : [...prev, String(u.id)]
            )
          }
        />
        <label htmlFor={`user-${u.id}`} className="form-check-label">
          {u.nome}
        </label>
      </div>
    );

    // === Time 159: envio pelo número do DONO do lead + indicador/filtro de instância ===
    // No time 159 há vários usuários divididos em 2 números oficiais (ins 20 e 27) +
    // um legado (57). O envio passa a sair pelo número do usuário DONO do lead; o
    // header mostra qual número atende e, se o contato tem histórico em +de 1 instância,
    // um filtro client-side por número.
    const TIME_NUMERO_POR_DONO = "159";
    const ehTimeNumeroPorDono = (idTime: number | string | null | undefined) =>
      String(idTime ?? "") === TIME_NUMERO_POR_DONO;
    // Filtro de instância no header do contato (null = todas as instâncias)
    const [filtroInstancia, setFiltroInstancia] = useState<string | null>(null);
    // Formata número WhatsApp p/ exibição (ex: 554796381284 -> (47) 9638-1284; 0800)
    const formatNumeroWhats = (fone?: number | string | null): string => {
      if (fone == null) return "";
      let d = String(fone).replace(/\D/g, "");
      if (!d) return "";
      if (d.startsWith("55")) d = d.slice(2);
      if (/^800\d{7}$/.test(d)) return `0800 ${d.slice(3, 6)}-${d.slice(6)}`;
      if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return d;
    };
    // Resolve o id_user de ENVIO: cada login envia SEMPRE pelo número do PRÓPRIO
    // usuário logado (inclusive admin). Não há roteamento por dono do lead nem
    // escolha manual de número.
    const resolveEnvioIdUser = (_contact?: { id_time?: number | null; id_user?: number | null } | null): string | null => {
      return localStorage.getItem("id_user");
    };
    // Rótulo de uma instância (ins_nome) p/ o filtro: número do usuário que a usa, ou genérico
    const labelInstancia = (ins?: number | string | null): string => {
      const insStr = String(ins ?? "");
      if (!insStr) return "Sem instância";
      const u = usuariosDoTime.find((x) => String(x.ins ?? "") === insStr);
      if (u?.fone) return formatNumeroWhats(u.fone);
      return `Instância ${insStr}`;
    };
    // === Número vinculado ao lead no header (instância do lead) ===
    // Diferente do time 159 (que resolve o número pelo DONO/id_user), aqui os usuários não têm
    // número próprio — o número vem da INSTÂNCIA do lead (chats.ins -> instancias.ins_numero).
    // Era só a TNP (192); desde 30/09/2026 vale para QUALQUER grupo com mais de um número
    // (Buchele Auxílio 61/65, nascimento martins 63/64...). Com um número só não há o que mostrar.
    // O time 159 (AFH) fica de fora: ele já mostra o número do DONO e teria dois selos.
    const GRUPO_NUMERO_NO_HEADER = "192";
    const [grupoMultiNumero, setGrupoMultiNumero] = useState(false);
    const ehGrupoNumeroNoHeader = () =>
      localStorage.getItem("id_grupo") === GRUPO_NUMERO_NO_HEADER ||
      (grupoMultiNumero && !ehTimeNumeroPorDono(localStorage.getItem("id_cliente")));
    // === Documentos / retenção (TNP, grupo 192) ===
    // Três superfícies gated por este grupo: validação do anexo na bolha (✓/✗),
    // modal Documentos do cliente (checklist) e modal Registrar promessa.
    const GRUPO_DOCS_RETENCAO = "192";
    const ehGrupoDocsRetencao = () => localStorage.getItem("id_grupo") === GRUPO_DOCS_RETENCAO;
    // Tipos de anexo que aceitam o ✓/✗ da bolha. O cliente costuma mandar foto ou
    // vídeo do documento em vez do PDF, então imagem e vídeo valem tanto quanto
    // `document`. Áudio e texto ficam de fora (não são "arquivo que serve ou não").
    // `imagem` e `image` convivem: o banco grava em PT, o front aceita os dois.
    const DOCS_TIPOS_VALIDAVEIS = ["document", "imagem", "image", "video"];
    const ehAnexoValidavel = (tipo?: string | null) =>
      DOCS_TIPOS_VALIDAVEIS.includes(String(tipo ?? ""));
    // A PROMESSA é mais restrita que a validação de anexo: só os times de retenção
    // do TNP registram promessa (5138 = RETENÇÃO - Auxílio, 5139 = RETENÇÃO - Isir).
    // Comparado com `id_cliente`, que no localStorage guarda o id do TIME do login
    // (mesmo padrão de PIX_ENABLED_TIMES).
    // Recebe o time do LEAD (ver `timeDoLeadAtual`); sem argumento, cai no time do
    // login — que é o mesmo valor para todo perfil que não seja admin master.
    const TIMES_PROMESSA = ["5138", "5139"];
    const ehTimeRetencao = (idTimeLead?: string | number | null) =>
      TIMES_PROMESSA.includes(String(idTimeLead ?? localStorage.getItem("id_cliente") ?? ""));
    // === Uploads internos (TNP, grupo 192) ===
    // Arquivo anexado à conversa que NÃO vai para o WhatsApp: sobe no mesmo bucket
    // do envio de mídia e grava em `chats` com type='tnpupload' / status='upload'
    // (mensagem = URL, doc_name = nome original). Aparece como balão CINZA no
    // thread — o mesmo formato da anotação, que é amarela — e numa lista própria
    // no header do contato. O cliente não recebe nada.
    const GRUPO_TNP_UPLOAD = "192";
    const ehGrupoTnpUpload = () => localStorage.getItem("id_grupo") === GRUPO_TNP_UPLOAD;
    // === Anotações no popover "Informações" (TNP, grupo 192) ===
    // Reúne no header todas as notas internas do lead, que hoje só existem
    // espalhadas pelo thread. Sai das mensagens já carregadas (o /contacts_full e o
    // /contato_por_fone trazem as anotações do fone no GRUPO inteiro, sem LIMIT),
    // então não custa request nenhum nem depende do id_time da conversa.
    const GRUPO_ANOTACOES_INFO = "192";
    const ehGrupoAnotacoesInfo = () => localStorage.getItem("id_grupo") === GRUPO_ANOTACOES_INFO;
    type TnpUpload = {
      id: number | string;
      mensagem: string;              // URL do arquivo no bucket
      doc_name: string | null;       // nome original
      id_user: number | null;        // quem subiu
      timestamp: string;
    };
    const tnpFileInputRef = useRef<HTMLInputElement | null>(null);
    const [tnpUploading, setTnpUploading] = useState(false);
    const [showTnpUploads, setShowTnpUploads] = useState(false);
    const [tnpUploads, setTnpUploads] = useState<TnpUpload[]>([]);
    const [tnpUploadsLoading, setTnpUploadsLoading] = useState(false);
    // === Registro de LIGACAO (TNP, grupo 192) ===
    // Tipo PROPRIO: grava em `chats` com type='call' / status='call'. Nao e
    // WhatsApp e nao e 'transfer' -- so herda dele o formato de tag central.
    // A data/hora e sempre de BRASILIA, independente do fuso do PC do operador
    // e do servidor (que roda em UTC).
    const GRUPO_LIGACAO = "192";
    const ehGrupoLigacao = () => localStorage.getItem("id_grupo") === GRUPO_LIGACAO;
    const [salvandoLigacao, setSalvandoLigacao] = useState(false);

    // === Relogio de envios programados (header do contato) ====================
    // O que o lead AINDA VAI RECEBER se ficar parado na etapa: cadencia, follow,
    // boas-vindas e a cobranca da promessa. Quem decide o que entra e o nivel de
    // alerta e o backend (`/lead/agendamentos`) — a tela so pinta, senao a regra
    // passa a existir em dois lugares e um dia diverge do worker.
    type ItemAgenda = {
      tipo: string; quando: string | null; estimado?: boolean; canal?: string;
      titulo?: string; template?: string | null; texto?: string | null;
      observacao?: string; etapa?: string | null; inativo?: boolean;
      documentos?: any;
    };
    // "2026-09-22 09:00" -> "22-09-2026 09:00". O backend manda ISO DE PROPOSITO e continua
    // mandando: ele ORDENA os itens pela string (`itens.sort`) e compara com o agora pra
    // decidir o nivel do relogio (`strptime(i['quando'][:16], "%Y-%m-%d %H:%M")`). Em
    // dia-mes-ano as duas coisas quebram — a ordenacao vira alfabetica pelo DIA e o parse
    // estoura. Entao a inversao e so de exibicao, aqui na tela.
    const dataAgendaBR = (quando?: string | null) => {
      if (!quando) return null;
      const m = /^(\d{4})-(\d{2})-(\d{2})(.*)$/.exec(quando);
      return m ? `${m[3]}-${m[2]}-${m[1]}${m[4]}` : quando;   // formato inesperado: mostra cru
    };
    const [agenda, setAgenda] = useState<{ nivel: string; itens: ItemAgenda[]; canal?: string } | null>(null);
    const [agendaCarregando, setAgendaCarregando] = useState(false);
    const [showAgendaModal, setShowAgendaModal] = useState(false);

    // Mapa ins_nome -> ins_numero (carregado do backend p/ o grupo); resolve a ins do lead pro número
    const [mapaInstanciaNumero, setMapaInstanciaNumero] = useState<Record<string, string>>({});
    // ins_nome -> saude (verde/amarelo/vermelho) da Meta, p/ colorir o número no header
    const [mapaInstanciaSaude, setMapaInstanciaSaude] = useState<Record<string, string>>({});
    // Reseta o filtro de instância ao trocar de contato
    useEffect(() => {
      setFiltroInstancia(null);
    }, [selectedContact]);
    // Modo de API por time: "official" ou "unofficial"

    // Templates da janela 24h (Meta). Carregados da WABA da instância do usuário.
    // `categoria` é o campo da META (MARKETING/UTILITY/AUTHENTICATION). Não vem do
    // /chat/templates_db (que não fala com a Meta) e sim do /chat/templates_categorias,
    // carregado em 2º plano — por isso é opcional.
    type ChatTemplate = { name: string; language: string; body: string; headerText: string; headerFormat: string; headerImage: string; vars: string[]; headerVars?: string[]; botoes?: { type?: string; text?: string; url?: string; phone_number?: string }[]; categoria?: string; status?: string };
    const [sendingTemplate, setSendingTemplate] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templatesList, setTemplatesList] = useState<ChatTemplate[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTpl, setSelectedTpl] = useState<ChatTemplate | null>(null);
    const [tplVars, setTplVars] = useState<Record<string, string>>({});
    const [templateSearch, setTemplateSearch] = useState("");
    // Fallback: times historicamente liberados para template. A regra principal
    // agora é "login com nº oficial conectado" (ver shouldUseTemplate).
    const TEMPLATE_TIME_IDS = ["146", "159"];
    // Janela de 24h da Meta, com 10 min de folga. A folga existe porque contamos
    // pelo chats.created_at (hora em que NÓS gravamos), não pelo timestamp da Meta.
    // Atraso medido em 14/08/2026 cruzando webhook_inbound_log × chats: p99 = 12s,
    // máximo = 14s. 10 min cobre ~50x o pior caso observado. Era 23h (1h de folga),
    // o que bloqueava o envio livre por 1 hora a mais e empurrava o operador para um
    // template pago antes da hora.
    const WINDOW_HOURS = 23 + 50 / 60; // 23h50min

    // ─── Gravação de áudio (estilo WhatsApp Web) ───────────────────
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const recordingCanceledRef = useRef(false);
    const audioMimeRef = useRef<string>("");

    const handleOpenCardModal = () => {
      // selectedContactData is derived from selectedContact ID
      const contact = contacts.find(c => c.id === selectedContact);
      if (!contact) return;
      
      setSelectedLeadPhone(contact.fone || "");
      setSelectedLeadName(contact.nome || "");
      setSelectedFunnelName(contact.funil || "");
      setCardModalShow(true);
    };

    const preFetchCardData = async (fone: string, funnelName: string) => {
      const id_cliente = localStorage.getItem("id_cliente");
      if (!id_cliente || !fone) return;

      // 1. Funnels (Global-ish)
      const funnelsKey = `card_funnels_${id_cliente}`;
      let funnelId: number | null = null;
      
      try {
        let funnelsData = [];
        const cachedFunnels = localStorage.getItem(funnelsKey);
        if (cachedFunnels) {
          funnelsData = JSON.parse(cachedFunnels);
        } else {
          const res = await fetch(`${API_URL}/card/funnels?id_cliente=${id_cliente}`);
          if (res.ok) {
            funnelsData = await res.json();
            localStorage.setItem(funnelsKey, JSON.stringify(funnelsData));
          }
        }
        const match = funnelsData.find((f: any) => f.nome === funnelName);
        if (match) funnelId = match.id;
        else if (funnelsData.length > 0) funnelId = funnelsData[0].id;
      } catch (e) { console.error("Prefetch funnels error", e); }

      // 2. Values (Specific to contact)
      const valuesKey = `card_values_${id_cliente}_${fone}`;
      try {
        const res = await fetch(`${API_URL}/card/values?id_cliente=${id_cliente}&fone=${fone}`);
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem(valuesKey, JSON.stringify(data));
        }
      } catch (e) { console.error("Prefetch values error", e); }

      // 3. Structure (Specific to funnel)
      if (funnelId) {
        const structKey = `card_structure_${id_cliente}_${funnelId}`;
        const cachedStruct = localStorage.getItem(structKey);
        if (!cachedStruct) {
          try {
            const res = await fetch(`${API_URL}/card/groups?id_funil_pai=${funnelId}&id_cliente=${id_cliente}`);
            if (res.ok) {
              const groupsData = await res.json();
              const groupsWithFields = await Promise.all(groupsData.map(async (g: any) => {
                const fieldsRes = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
                const fields = fieldsRes.ok ? await fieldsRes.json() : [];
                return { ...g, fields };
              }));
              localStorage.setItem(structKey, JSON.stringify(groupsWithFields));
            }
          } catch (e) { console.error("Prefetch structure error", e); }
        }
      }
    };
  
    // Helper para scrollar até a mensagem
    const scrollToMessage = (msgId: string | number) => {
      const el = document.getElementById(`msg-${msgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "background 0.5s";
        el.style.backgroundColor = "#fffde7"; // Destaque temporário (amarelo claro)
        setTimeout(() => {
          el.style.backgroundColor = "";
        }, 2000);
      }
    };

    useEffect(() => {
      const id_cliente = localStorage.getItem('id_cliente');
      if (!id_cliente) return;

      // Usando sharedSidebarFetch para evitar que Sidebar e Chat peçam idiomas ao mesmo tempo
      sharedSidebarFetch(`${API_URL}/tem_idiomas`, { id_time: id_cliente })
        .then(data => {
          if (data && data.tem_idiomas) {
            setTemIdiomas(true);
          }
        })
        .catch(err => console.error('Erro ao verificar idiomas:', err));
    }, []);
  
    // Efeito para buscar mensagens quando o termo muda
    useEffect(() => {
      if (!chatSearchTerm || !messages.length) {
        setChatSearchMatches([]);
        setCurrentMatchIndex(-1);
        return;
      }
      const lower = chatSearchTerm.toLowerCase();
      // Filtra e guarda os IDs das mensagens que batem
      const matches = messages
        .filter(m => m.mensagem && String(m.mensagem).toLowerCase().includes(lower))
        .map(m => m.id);
      
      setChatSearchMatches(matches);
      if (matches.length > 0) {
        setCurrentMatchIndex(0);
        scrollToMessage(matches[0]); // Vai para o primeiro
      } else {
        setCurrentMatchIndex(-1);
      }
    }, [chatSearchTerm, messages]);
  
    // Navegação Próximo/Anterior
    const nextMatch = () => {
      if (chatSearchMatches.length === 0) return;
      const nextIndex = (currentMatchIndex + 1) % chatSearchMatches.length;
      setCurrentMatchIndex(nextIndex);
      scrollToMessage(chatSearchMatches[nextIndex]);
    };
  
    const prevMatch = () => {
      if (chatSearchMatches.length === 0) return;
      const prevIndex = (currentMatchIndex - 1 + chatSearchMatches.length) % chatSearchMatches.length;
      setCurrentMatchIndex(prevIndex);
      scrollToMessage(chatSearchMatches[prevIndex]);
    };
  
  
  
      
  
  
    
    
  
  
  
    const contactInitialized = useRef(false);
  
    useEffect(() => {
      if (
        !contactInitialized.current &&
        routerLocation.state?.phone &&
        contacts.length > 0
      ) {
        const normalized = normalizePhone(routerLocation.state.phone);
        const found = contacts.find(c => normalizePhone(c.fone) === normalized);
        if (found) {
          setSelectedContact(found.id);
          contactInitialized.current = true;
          return;
        }
        // 🔴 Não achou na lista: ANTES o efeito simplesmente desistia, em silêncio.
        // A lista traz só os mais recentes (limit 50), então clicar no nome de um
        // lead ANTIGO em /leads-list levava ao /chat com a lista cheia e NENHUMA
        // conversa aberta — sem erro, sem spinner, sem explicação. Atinge em cheio o
        // admin master, cuja lista é o grupo inteiro (8.920 conversas no 192): na
        // bateria de 03/09 o link nunca abriu a conversa nesse login, 37 tentativas.
        //
        // O deep-link por `?fone=` já resolvia isso — busca a conversa pelo
        // /contato_por_fone e injeta o contato na lista. Em vez de duplicar aquela
        // lógica aqui, este caminho passa a alimentar o MESMO `initialPhone`, e o
        // efeito de baixo faz o resto (inclusive o aviso honesto quando falha).
        contactInitialized.current = true;
        setInitialPhone(routerLocation.state.phone);
      }
    }, [contacts, routerLocation.state?.phone]);

    // Deep-link por URL (?fone=...): abre o /chat já numa conversa específica a
    // partir de um link colado em outro dispositivo/PC. Diferente do `state` do
    // React Router (que vive só em memória e não vai na URL), o query param
    // sobrevive ao compartilhamento do link. Roda uma vez no mount; o efeito de
    // `initialPhone` abaixo seleciona o contato assim que a lista carrega.
    useEffect(() => {
      const q = new URLSearchParams(window.location.search);
      // Aceita as três grafias: quem monta o link (dash de retenção, planilha,
      // mensagem colada) nem sempre usa `fone`, e um parâmetro não reconhecido
      // abre o /chat mudo, sem dizer que ignorou o telefone.
      const foneParam = q.get("fone") || q.get("telefone") || q.get("phone");
      if (foneParam) setInitialPhone(foneParam);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
  
  
  
    
  
  
  
    // Deep-link: acha o contato na lista OU vai buscá-lo.
    //
    // 🔴 Antes este efeito só olhava a lista já carregada. Como a lista traz os N
    // mais recentes, clicar num nome no dash de retenção só abria a conversa se o
    // lead estivesse entre eles; nos demais casos o /chat ficava parado sem abrir
    // nada e sem dizer por quê ("às vezes não carrega o contato"). Agora, quando
    // não está na lista, busca a conversa avulsa e injeta — mesmo caminho que o
    // deep-link de anotação já usava.
    const deepLinkFoneBuscado = useRef<string | null>(null);
    useEffect(() => {
      if (!initialPhone) return;
      const normalizedInitial = normalizePhone(initialPhone);
      const foundContact = contacts.find(
        contact => normalizePhone(contact.fone) === normalizedInitial
      );
      if (foundContact) {
        setSelectedContact(foundContact.id);
        setInitialPhone(undefined); // Limpa para permitir clique manual depois
        return;
      }
      // 🔴 ANTES havia aqui um `if (contacts.length === 0) return;` — o deep-link
      // ficava ESPERANDO a lista para só então buscar a conversa. Os dois pedidos são
      // independentes, e a lista é o lado lento: no waterfall de 03/09 o
      // /contacts_full levou 2,8 a 4,3 s (disputando as ~6 conexões do navegador com
      // as outras 12 requisições do boot) enquanto o /contato_por_fone leva ~0,9 s.
      // Quem chega pelo link do dashboard quer VER A CONVERSA; ela agora é buscada em
      // paralelo com a lista, e não depois dela. Se a lista chegar antes e já tiver o
      // contato, o `foundContact` acima resolve e nem chega aqui.
      if (deepLinkFoneBuscado.current === normalizedInitial) return;
      deepLinkFoneBuscado.current = normalizedInitial;

      const grupo = localStorage.getItem("id_grupo");
      const idTimeUrl = new URLSearchParams(window.location.search).get("id_time");
      if (!grupo && !idTimeUrl) { setInitialPhone(undefined); return; }
      // Manda só os dígitos: o link pode vir com máscara ("(41) 99856-1270") e o
      // banco guarda o número limpo.
      const params = new URLSearchParams({ fone: normalizedInitial || String(initialPhone) });
      if (idTimeUrl) params.append("id_time", idTimeUrl);
      if (grupo) params.append("id_cliente", grupo);
      setCarregandoConversa(true);
      fetch(`${API_URL}/contato_por_fone?${params.toString()}`)
        // 🔴 Não-2xx NÃO é exceção do fetch. Antes virava `null` e caía no ramo de
        // "não encontrada" abaixo — a tela dizia que a conversa não existe quando o
        // que houve foi o servidor falhar (502 do reciclo de worker do gunicorn).
        // Mensagem errada manda quem investiga para o lado errado.
        .then((r) => {
          if (!r.ok) throw new Error(`/contato_por_fone HTTP ${r.status}`);
          return r.json();
        })
        .then((c) => {
          if (!c || !c.id) {
            setModalMessage("Conversa não encontrada para este telefone.");
            setShowModal(true);
            return;
          }
          setContacts((prev) => {
            if (!prev.some((x) => x.id === c.id)) return [c, ...prev];
            // 🔴 Desde que a lista virou LEVE (messages_lazy, carga inicial sem
            // histórico), devolver `prev` inalterado aqui descartava justamente as
            // mensagens que ESTE fetch acabou de trazer — e o `add` abaixo ainda
            // marcava a conversa como carregada, então o carregador sob demanda se
            // recusava a buscar de novo. A conversa ficava em "Nenhuma mensagem nesta
            // conversa ainda" até um F5. Injeta só o histórico: o resto do card
            // continua sendo o da lista, que vem enriquecido pelo fetchContactsFull.
            const novas = (Array.isArray(c.messages) ? c.messages : []) as Message[];
            if (novas.length === 0) return prev;
            return prev.map((x) => (x.id === c.id ? { ...x, messages: novas } : x));
          });
          setSelectedContact(c.id);
          // Só conta como carregada se veio histórico de verdade. Sem isto, um
          // deep-link que voltasse vazio (ou com erro) travava a conversa vazia.
          if (Array.isArray(c.messages) && c.messages.length > 0) {
            conversasCarregadasRef.current.add(`${c.fone}|${c.id_time ?? ""}`);
          }
        })
        .catch((e) => {
          // Libera a trava: sem isto um 502 queimava o link para sempre (o ref guarda
          // o fone e o efeito nunca mais tenta), e o operador ficava sem entender.
          if (deepLinkFoneBuscado.current === normalizedInitial) deepLinkFoneBuscado.current = null;
          console.error("Erro ao abrir conversa do deep-link:", e);
          setModalMessage("Não foi possível abrir a conversa agora (falha no servidor). Tente novamente.");
          setShowModal(true);
        })
        .finally(() => {
          setCarregandoConversa(false);
          setInitialPhone(undefined);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPhone, contacts]);

    // ── Anotações: carrega os usuários do grupo (p/ menção @ e nome do autor) ──
    useEffect(() => {
      if (!ANOTACOES_HABILITADO) return;
      const id_grupo = localStorage.getItem("id_grupo");
      if (!id_grupo) return;
      fetch(`${API_URL}/pegarusuariosgrupo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_grupo }),
      })
        .then((r) => r.json())
        .then((d) => setUsuariosDoGrupo(Array.isArray(d) ? d : []))
        .catch((e) => console.error("Erro ao carregar usuários do grupo:", e));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ANOTACOES_HABILITADO]);

    // ── Anotações: deep-link da notificação. Abre a conversa marcada mesmo que
    // o contato não pertença ao usuário (busca avulsa + injeção na lista). ──────
    const anotacaoDeepLinkFetched = useRef(false);
    // Carregando a conversa vinda da notificação de anotação? (começa true se há
    // deep-link pendente — mostra spinner já na abertura, antes mesmo dos contatos)
    const [carregandoDeepLink, setCarregandoDeepLink] = useState<boolean>(
      () => !!localStorage.getItem("open_anotacao_fone")
    );
    useEffect(() => {
      if (!ANOTACOES_HABILITADO) { setCarregandoDeepLink(false); return; }
      const fone = localStorage.getItem("open_anotacao_fone");
      const idTime = localStorage.getItem("open_anotacao_id_time");
      if (!fone) { setCarregandoDeepLink(false); return; }
      const norm = normalizePhone(fone);
      const found = contacts.find(
        (c) => normalizePhone(c.fone) === norm && (!idTime || String(c.id_time) === String(idTime))
      );
      if (found) {
        setSelectedContact(found.id);
        localStorage.removeItem("open_anotacao_fone");
        localStorage.removeItem("open_anotacao_id_time");
        setCarregandoDeepLink(false);
        return;
      }
      // Não está na lista do usuário → busca avulsa (ignora posse) e injeta.
      if (contacts.length > 0 && idTime && !anotacaoDeepLinkFetched.current) {
        anotacaoDeepLinkFetched.current = true;
        setCarregandoDeepLink(true);
        // 🔴 `id_cliente` é OBRIGATÓRIO aqui. Esta era a única chamada de
        // /contato_por_fone que mandava só fone+id_time, e era por ela que entrava a
        // conversa de OUTRO cliente: sem o grupo, o backend resolvia o tenant pelo
        // telefone (o dono da mensagem mais recente). O backend já não aceita mais
        // esse caminho cego, mas mandar o grupo é o que mantém a rota barata — sem
        // ele o servidor gasta um round-trip só para redescobrir o que o front sabe.
        const paramsAnot = new URLSearchParams({ fone: String(fone), id_time: String(idTime) });
        const grupoAnot = localStorage.getItem("id_grupo");
        if (grupoAnot) paramsAnot.append("id_cliente", grupoAnot);
        fetch(`${API_URL}/contato_por_fone?${paramsAnot.toString()}`)
          // Mesmo tratamento do deep-link principal: não-2xx tem de virar exceção,
          // senão o `.catch` (único lugar que destrava o ref) nunca roda.
          .then((r) => {
            if (!r.ok) throw new Error(`/contato_por_fone HTTP ${r.status}`);
            return r.json();
          })
          .then((c) => {
            if (c && c.id) {
              setContacts((prev) => {
                if (!prev.some((x) => x.id === c.id)) return [c, ...prev];
                // A lista é LEVE: o card já lá não tem histórico. Devolver `prev`
                // inalterado jogaria fora as mensagens que este fetch trouxe.
                const novas = (Array.isArray(c.messages) ? c.messages : []) as Message[];
                if (novas.length === 0) return prev;
                return prev.map((x) => (x.id === c.id ? { ...x, messages: novas } : x));
              });
              setSelectedContact(c.id);
            }
          })
          .catch((e) => {
            // Destrava para o próximo clique no sino poder tentar de novo.
            anotacaoDeepLinkFetched.current = false;
            console.error("Erro ao abrir conversa da anotação:", e);
          })
          .finally(() => {
            localStorage.removeItem("open_anotacao_fone");
            localStorage.removeItem("open_anotacao_id_time");
            setCarregandoDeepLink(false);
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts, ANOTACOES_HABILITADO]);
  
    const [idiomaSelecionado, setIdiomaSelecionado] = useState<string | null>(null);
  
    const idiomaNomePorCodigo: { [key: string]: string } = {
      "DE": "Alemão",
      "EN": "Inglês",
      "FR": "Francês",
      "ES": "Espanhol",
      "IT": "Italiano",
      "JA": "Japonês",
      "ZH": "Chinês",
      "PT-BR": "Português",
    };
  
  
    const bandeiras: { [key: string]: string } = {
      "Alemao": "🇩🇪",
      "Ingles": "🇺🇸",
      "Frances": "🇫🇷",
      "Espanhol": "🇪🇸",
      "Italiano": "🇮🇹",
      "Japones": "🇯🇵",
      "Chines": "🇨🇳",
      "Portugues": "🇧🇷",
    };
  
  
    
  
  
  
  
        const fetchContactsFull = async (isPolling = false) => {
          // Polling: pula se já tem fetch em andamento (evita duplicar)
          if (isPolling && isFetchingContacts.current) return;
          // Não-polling: pula SOMENTE se outra chamada não-polling está em andamento
          if (!isPolling && isFetchingContacts.current) return;
          isFetchingContacts.current = true;
          const myGen = ++fetchGenRef.current; // geração desta chamada (descarta se ficar stale)

          const id_cliente = localStorage.getItem("id_cliente");
          if (!id_cliente) {
             isFetchingContacts.current = false;
             return;
          }

          // Admin: aguarda os times carregarem antes de buscar
          if (isAdminMaster && timesDoGrupoRef.current.length === 0) {
             isFetchingContacts.current = false;
             return;
          }
          // `isFetchingContacts` é REF: mudá-la não re-renderiza, então um spinner que
          // dependa dela some assim que qualquer outra coisa causa render. Medido: ao
          // aplicar o filtro a lista ficava ~9s vazia e SEM nenhum indicador, que é
          // exatamente o que se lê como "sumiu". Este state espelha o ref para a UI.
          if (!isPolling) setBuscandoContatos(true);

          // Ativa o loading somente se não houver contatos na tela (carga inicial)
          if (contacts.length === 0 && !isPolling) {
              console.log("🔄 [DEBUG] Ligando Spinner (Lista vazia)");
              setLoadingContacts(true);
          } else {
              console.log("⏭️ [DEBUG] Pulando Spinner (Já temos contatos ou é polling)");
          }

          // Admin sempre busca TODOS os contatos de todos os times (usa ref para ter valor atualizado)
          const times = timesDoGrupoRef.current;
          const isAllTeams = isAdminMaster && times.length > 0;
          const rawOficial = localStorage.getItem("instanciaoficial");
          const rawInstance = localStorage.getItem("instanceName");
          // "0" não é instância válida — tratar como null
          let instanceName = (rawOficial && rawOficial !== "0") ? rawOficial : (rawInstance || null);

          // Recuperação de Instância (não necessária para "Todos os Times")
          if (!instanceName && !isAllTeams) {
             try {
                 const controller = new AbortController();
                 const timeout = setTimeout(() => controller.abort(), 10000);
                 const instRes = await fetch(`${API_URL}/instanciaatual`, {
                     method: "POST",
                     headers: { "Content-Type": "application/json" },
                     body: JSON.stringify({ id_cliente: id_cliente }),
                     signal: controller.signal,
                 });
                 clearTimeout(timeout);
                 if (instRes.ok) {
                     const instData = await instRes.json();
                     instanceName = instData.instanceName || instData.instancename;
                     if (instanceName && !isAdminMaster) localStorage.setItem("instanceName", instanceName);
                 }
             } catch (e) {
                 console.warn("⚠️ [DEBUG] /instanciaatual falhou ou timeout:", e);
             }
          }

          try {
            const params = new URLSearchParams();
            const endpoint = `${API_URL}/contacts_full`;

            // Busca server-side: quando o operador digita 3+ chars, manda `search`
            // e o backend ignora o limit (devolve TODOS os matches por nome/fone).
            const activeSearch = searchTermDebouncedRef.current;
            if (activeSearch) {
              params.append("search", activeSearch);
              // Lista LEVE na busca: só o card do contato, sem o histórico de
              // mensagens de cada um. Pesquisar "silva" no grupo 192 casava 1143
              // contatos e o backend mandava as mensagens dos 1143 — 14,3 MB de
              // JSON. Era o motivo de a pesquisa "demorar pra aparecer": não era o
              // banco, era o navegador engasgando com o payload. Agora vêm ~130 KB
              // e a conversa escolhida carrega sozinha (ver carregarConversaSobDemanda).
              params.append("sem_mensagens", "1");
              setIsSearchingRemote(true);
            } else {
              // Limitar contatos para evitar payloads gigantes (28MB+ sem limite).
              // Lê da ref: ver contactsLimitRef (o polling tem closure congelado).
              params.append("limit", String(contactsLimitRef.current));
              // As N conversas mais recentes vêm com o histórico JÁ EMBUTIDO. É o
              // meio-caminho entre a lista pesada de antes de 01/09 (2,2 MB para 50
              // conversas, que travava máquina fraca) e a leve pura (53 KB, mas TODA
              // conversa aberta paga um /contato_por_fone de ~0,9 s e depende do
              // prefetch ter alcançado): quem chega no /chat abre justamente as mais
              // recentes, e essas passam a estar prontas na hora.
              //
              // Medido no servidor: 12 conversas = 516 KB e +100 ms no endpoint (o
              // custo é o round-trip, não o volume). O teto no backend é 20.
              // ⛔ NÃO vai em modo busca — ali a lista tem de continuar leve, senão
              // volta o problema de pesquisar "silva" e trazer 1.143 históricos.
              // ⛔ Só na CARGA INICIAL. No polling de 30 s isso reenviaria o histórico
              // das 12 conversas a cada ciclo — 516 KB de parse e um array novo por
              // conversa, o que reconstrói as bolhas na tela sem necessidade. Depois do
              // boot, quem mantém a conversa em dia é o realtime, e a rede de segurança
              // acima cobre o que ele perder.
              if (!isPolling) params.append("msgs_primeiras", String(CONVERSAS_PRONTAS_NA_LISTA));
              // Lista LEVE também na carga normal, não só na busca.
              //
              // 🔴 Medido em 01/09/2026 no escopo do login luana.lopes (time 5138,
              // 846 contatos): 50 conversas com o histórico embutido dão **2,6 MB**.
              // Na rede vai comprimido pelo gzip, mas o `JSON.parse` e o render são
              // CPU do navegador — é a parte que separa uma máquina boa de uma fraca,
              // e o que fazia o clique do dash de retenção levar ~10s. A busca já
              // pedia `sem_mensagens=1` e devolvia 667 bytes para o mesmo trabalho.
              // A conversa aberta carrega sozinha pelo /contato_por_fone (~758 ms em
              // produção) — o mesmo caminho que a busca usa desde 28/08.
              params.append("sem_mensagens", "1");
            }

            // Filtro de funil/etapa/time — SERVER-SIDE. Antes era só client-side, em
            // cima da janela de recência que o limit já tinha cortado: no grupo 192
            // (8920 conversas) uma etapa com 34 leads mostrava 1. Mandando ao backend,
            // o limite passa a valer PARA O FILTRO — a lista vem cheia da etapa.
            const { etapa: etapaFiltro, funis: funisFiltro, times: timesFiltro, tags: tagsFiltro, finalizados: soFinalizados } = funnelFilterRef.current;
            if (etapaFiltro) params.append("etapa", etapaFiltro);
            // Só um funil selecionado dá para resolver no SQL; com vários, o backend
            // devolve o conjunto e o client-side (visibleContacts) faz a interseção.
            if (funisFiltro.length === 1) params.append("funil", funisFiltro[0]);
            if (timesFiltro.length > 0) params.append("times", timesFiltro.join(","));
            // Tags de contato: o backend devolve só quem tem alguma das tags (antes do limit).
            if (tagsFiltro.length > 0) params.append("tags", tagsFiltro.join(","));
            if (soFinalizados) params.append("finalizados", "1");

            // Filtro de data — SERVER-SIDE. Manda o intervalo (YYYY-MM-DD) e o backend
            // retorna TODAS as conversas com mensagem no período, ignorando o limite de
            // recência. Compõe com a busca (ambos aplicados via AND no /contacts_full).
            const { inicio: dataInicioFiltro, fim: dataFimFiltro } = dateFilterRef.current;
            if (dataInicioFiltro) params.append("data_inicio", dataInicioFiltro);
            if (dataFimFiltro) params.append("data_fim", dataFimFiltro);

            // Time 159: o admin vê SOMENTE os contatos do PRÓPRIO número. Em vez de
            // ver todos os times, manda o id_user + group_by_number=1 e o backend
            // resolve cliente_usuario(id_user).ins e filtra por todos os usuários
            // daquele número (admin 1284 vê o grupo do 1284; admin 2708 o do 2708).
            const admin159 = isAdminMaster && ehTimeNumeroPorDono(id_cliente);
            if (admin159) {
              params.append("id_cliente", TIME_NUMERO_POR_DONO);
              const id_user = localStorage.getItem("id_user");
              if (id_user) params.append("id_user", id_user);
              params.append("group_by_number", "1");
            } else if (isAllTeams) {
              // Admin Master — sempre busca TODOS os times
              const allIds = times.map(t => t.id).join(",");
              params.append("id_cliente", allIds);
            } else {
              params.append("id_cliente", id_cliente);
              if (instanceName) params.append("instanceName", instanceName);
              // Visibilidade por usuário: envia o operador logado. O backend só
              // filtra os contatos por id_user se esse usuário tiver número próprio
              // em cliente_usuario (senão, mantém todos os contatos do time).
              const id_user = localStorage.getItem("id_user");
              if (id_user) params.append("id_user", id_user);
            }

            console.log(`📡 [DEBUG] Buscando contatos: admin=${isAdminMaster}, id_cliente=${id_cliente}, instanceName=${instanceName}`);

            // Timeout de 30s para o fetch de contatos
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 30000);
            const res = await fetch(`${endpoint}?${params.toString()}`, { signal: controller.signal });
            clearTimeout(timeout);
            if (!res.ok) throw new Error("Erro na API");

            // Backend avisa quando a busca bateu no teto de resultados — a lista
            // mostra "refine a busca" em vez de fingir que ali está tudo.
            const truncada = res.headers.get("X-Busca-Truncada") === "1";
            const data: Contact[] = await res.json();
            console.log(`📡 [DEBUG] Contatos recebidos: ${data.length}, limit: ${contactsLimit}`);
            if (isMounted.current) setBuscaTruncada(!!activeSearch && truncada);

            // Race guard: se um fetch mais novo já começou, descarta ESTE resultado —
            // não mexe na lista (evita sobrescrever/zerar/piscar os contatos na busca).
            if (fetchGenRef.current !== myGen) {
              return;
            }

            // Detecta se há mais contatos para carregar (apenas no modo normal —
            // quando search está ativo, o backend já mandou todos os matches).
            hasMoreContacts.current = !activeSearch && Array.isArray(data) && data.length >= contactsLimitRef.current;

            if (Array.isArray(data) && data.length > 0) {

            // Enriquecimento e Formatação
            const enriched = data.map(contact => {
              const lastMessage = contact.last_message || "";
              let lastTimestamp: number | undefined;
              if (contact.lastTimestamp) {
                lastTimestamp = new Date(contact.lastTimestamp).getTime();
              } else if ((contact as any).last_timestamp) {
                lastTimestamp = new Date((contact as any).last_timestamp).getTime();
              } else if (contact.messages?.length) {
                const validTimes = contact.messages
                  .map(m => new Date(m.timestamp).getTime())
                  .filter(t => !isNaN(t));
                lastTimestamp = validTimes.length > 0 ? Math.max(...validTimes) : undefined;
              }
              return { ...contact, last_message: lastMessage, lastTimestamp, unread_count: contact.unread_count ?? 0 };
            });

            enriched.sort((a, b) => (b.lastTimestamp ?? 0) - (a.lastTimestamp ?? 0));

            // ── Rede de segurança do realtime ────────────────────────────────
            // Desde a lista leve (`sem_mensagens=1`, 01/09), este polling atualiza o
            // CARD (prévia, hora, badge) mas PRESERVA as mensagens que já estão em
            // memória — ver o `messages_lazy` no merge abaixo. O corpo da conversa
            // passou a depender exclusivamente do realtime, e um evento perdido
            // (socket caído, notebook suspenso, aba dormindo) congelava a conversa
            // até um F5: em 02/09/2026 o card do lead mostrava "Documento 17:54" e
            // as bolhas paravam no ☺️ das 10:02 — o banco tinha 11 mensagens no meio.
            // Sair e voltar na conversa não resolvia, porque o conversasCarregadasRef
            // impede o refetch.
            //
            // O próprio card denuncia a divergência: `lastTimestamp` vem do backend e
            // a última mensagem em memória é conhecida. Quando o backend está à
            // frente, a chave é solta — o efeito de abertura rebusca a conversa ABERTA
            // no mesmo ciclo (silencioso: o spinner só aparece com a conversa vazia) e
            // as demais rebuscam quando o operador as abrir. Custo zero em dia normal:
            // nenhuma requisição a mais enquanto o realtime estiver entregando.
            const TOLERANCIA_DIVERGENCIA_MS = 2000;  // relógio do servidor + timestamps estáveis herdados de temp
            // Só entram aqui as conversas que JÁ têm histórico em memória (a aberta e as
            // do prefetch) — as outras não têm o que reidratar, e invalidá-las geraria
            // fetch para quem nem foi aberto. São poucas, então o índice sai barato e
            // evita varrer a lista inteira uma vez por contato.
            const maisNovaEmMemoriaPorId = new Map<string | number, number>();
            for (const antigo of contactsRef.current) {
              const msgs = antigo.messages;
              if (!msgs || msgs.length === 0) continue;
              let maisNova = 0;
              for (const m of msgs) {
                const t = new Date(m.timestamp).getTime();
                if (!Number.isNaN(t) && t > maisNova) maisNova = t;
              }
              if (maisNova > 0) maisNovaEmMemoriaPorId.set(antigo.id, maisNova);
            }
            for (const fresh of enriched) {
              const backendTs = fresh.lastTimestamp ?? 0;
              if (!backendTs) continue;
              const maisNovaEmMemoria = maisNovaEmMemoriaPorId.get(fresh.id);
              // Conversa ABERTA que ficou sem nenhuma mensagem em memória, mas cujo card
              // diz que existe conversa: é o "Nenhuma mensagem nesta conversa ainda" numa
              // conversa que tem mensagens. Vale reidratar — para as fechadas, seguir em
              // frente evita disparar fetch para quem nunca foi aberto.
              const abertaEVazia = selectedContactRef.current === fresh.id && !maisNovaEmMemoria;
              if (!maisNovaEmMemoria && !abertaEVazia) continue;
              if (maisNovaEmMemoria && backendTs <= maisNovaEmMemoria + TOLERANCIA_DIVERGENCIA_MS) continue;
              const chaveDivergente = chaveConversa(fresh.fone, fresh.id_time);
              // Uma rebusca por mensagem: se já tentamos por causa DESTE timestamp e a
              // divergência continua, não insistimos (ver divergenciaJaTentadaRef).
              if (divergenciaJaTentadaRef.current.get(chaveDivergente) === backendTs) continue;
              divergenciaJaTentadaRef.current.set(chaveDivergente, backendTs);
              conversasCarregadasRef.current.delete(chaveDivergente);
              historicoPrefetchRef.current.delete(chaveDivergente);   // cache do prefetch também está velho
            }

            // Conversa que chegou com o histórico embutido (`msgs_primeiras`) já está
            // carregada — registrar isso evita que o prefetch e o clique gastem um
            // /contato_por_fone para buscar o que já está na tela. É o que faz o
            // clique nas primeiras conversas ser instantâneo em vez de pagar ~0,9 s.
            for (const fresh of enriched) {
              if (!Array.isArray((fresh as any).messages) || (fresh as any).messages.length === 0) continue;
              conversasCarregadasRef.current.add(chaveConversa(fresh.fone, fresh.id_time));
            }

            // Cache (protegido contra quota exceeded). Só o modo normal alimenta o
            // cache: gravar o resultado de uma busca faria a próxima abertura do /chat
            // começar mostrando os contatos de "silva" como se fossem a lista.
            if (!activeSearch) {
              try {
                  localStorage.setItem(`contacts_cache_${id_cliente}`, JSON.stringify(enriched));
              } catch (e) {
                  console.warn("⚠️ Cache não salvo (localStorage cheio):", e);
              }
            }

            if (isMounted.current) {
                setContacts((prevContacts) => {
                  // Duas classes de mensagem local precisam sobreviver ao merge (o
                  // refetch SUBSTITUI contact.messages pelo que o backend devolveu):
                  //
                  //  a) temp-*  — otimistas ainda não confirmadas. Dedup por conteúdo
                  //     mais abaixo (uma msg do backend confirma no máximo um temp).
                  //
                  //  b) enviadas recentes JÁ confirmadas (id real). O realtime entrega
                  //     a versão real e descarta o temp (ver aplicarMensagemRealtime);
                  //     a partir daí a mensagem ficava sem nenhuma proteção. Se o
                  //     refetch seguinte não a trouxesse (latência de commit/replicação),
                  //     ela SUMIA da tela e voltava no polling seguinte — o "some e
                  //     aparece depois". Preservar por id resolve p/ texto, mídia,
                  //     áudio e template de uma vez, seja qual for a origem da ausência.
                  //
                  // A janela evita segurar para sempre uma mensagem que o banco de fato
                  // nunca gravou (falha pós-provedor já é avisada por
                  // avisarSeNaoSalvouHistorico); depois dela, o backend é a verdade.
                  const PRESERVAR_ENVIADA_MS = 5 * 60 * 1000;
                  const agoraMs = Date.now();
                  const tempMessagesByContact = new Map<string | number, Message[]>();
                  const enviadasRecentesByContact = new Map<string | number, Message[]>();
                  // Resposta leve (messages_lazy): o backend NÃO mandou o histórico.
                  // "Sem a chave" é diferente de "conversa vazia" — sem este mapa, um
                  // refetch em modo busca apagaria a conversa aberta.
                  const msgsAnterioresByContact = new Map<string | number, Message[]>();
                  for (const prev of prevContacts) {
                    const prevMsgs = (prev.messages || []) as Message[];
                    if (prevMsgs.length > 0) msgsAnterioresByContact.set(prev.id, prevMsgs);
                    const tempMsgs: Message[] = [];
                    const enviadas: Message[] = [];
                    for (const m of prevMsgs) {
                      if (String(m.id).startsWith("temp-")) {
                        tempMsgs.push(m);
                      } else if (m.status === "send" || m.status === "note" || m.status === "upload") {
                        // 'send' = enviada por nós (texto, mídia, áudio, template, doc);
                        // 'note' = anotação interna (api.py grava type='anotacao'/status='note');
                        // 'upload' = arquivo interno TNP (type='tnpupload').
                        // Todas nascem de uma ação do operador — nenhuma pode piscar.
                        // 'received' fica de fora: vem do backend, não temos versão local.
                        const ts = new Date(m.timestamp).getTime();
                        if (!Number.isNaN(ts) && agoraMs - ts <= PRESERVAR_ENVIADA_MS) enviadas.push(m);
                      }
                    }
                    if (tempMsgs.length > 0) tempMessagesByContact.set(prev.id, tempMsgs);
                    if (enviadas.length > 0) enviadasRecentesByContact.set(prev.id, enviadas);
                  }

                  // Aplica pending overrides (mudanças otimistas que o backend ainda não commitou)
                  // ANTES de qualquer return, para garantir que cada caminho preserva os overrides.
                  // Limpa overrides cujo valor já foi confirmado pelo backend (igualdade exata).
                  if (pendingOverridesRef.current.size > 0) {
                    Array.from(pendingOverridesRef.current.entries()).forEach(([foneKey, ov]) => {
                      const fresh = enriched.find((c) => String(c.fone) === foneKey);
                      if (!fresh) return;
                      const matchEtapa = ov.nome_funil === undefined || fresh.nome_funil === ov.nome_funil;
                      const matchFunil = ov.funil === undefined || (fresh as any).funil === ov.funil;
                      if (matchEtapa && matchFunil) {
                        pendingOverridesRef.current.delete(foneKey);
                      }
                    });
                  }
                  let enrichedWithOverrides = applyPendingOverrides(enriched as any) as typeof enriched;

                  // Sticky: garante que o contato atualmente aberto NUNCA suma do
                  // array, mesmo quando o refetch (modo normal) só trouxe os 200
                  // mais recentes. Preserva a entrada antiga do prevContacts caso
                  // ela não esteja no resultado novo.
                  const selectedId = selectedContactRef.current;
                  if (selectedId != null && !enrichedWithOverrides.some((c) => c.id === selectedId)) {
                    const sticky = prevContacts.find((c) => c.id === selectedId);
                    if (sticky) {
                      // Normaliza para o mesmo shape do enriched (last_message
                      // string, unread_count number) — evita erro de tipo no
                      // setContacts (que infere o tipo do enriched).
                      const stickyNormalized = {
                        ...sticky,
                        last_message: sticky.last_message ?? "",
                        unread_count: sticky.unread_count ?? 0,
                      } as (typeof enrichedWithOverrides)[number];
                      enrichedWithOverrides = [stickyNormalized, ...enrichedWithOverrides];
                    }
                  }

                  const TEMP_DEDUP_WINDOW_MS = 2 * 60 * 1000;

                  // Aplica key+timestamp ESTÁVEIS (herdados do temp) às mensagens já
                  // confirmadas em pollings anteriores. O backend devolve a mensagem
                  // com id novo e timestamp do servidor a cada polling; sem isso a
                  // bolha trocaria de key (remonta) e de posição (reordena) → "pulo".
                  const aplicarEstaveis = (msgs: Message[]): Message[] => {
                    if (sentMsgStableRef.current.size === 0) return msgs;
                    let mudou = false;
                    const out = msgs.map((m) => {
                      const st = sentMsgStableRef.current.get(String(m.id));
                      if (st && (m.clientKey !== st.key || m.timestamp !== st.ts)) {
                        mudou = true;
                        return { ...m, clientKey: st.key, timestamp: st.ts };
                      }
                      return m;
                    });
                    return mudou ? out : msgs;
                  };

                  const finalContacts = enrichedWithOverrides.map((contact) => {
                    // Lista leve: mantém o que já estava em memória para este contato.
                    const backendMsgs = ((contact as any).messages_lazy
                      ? (msgsAnterioresByContact.get(contact.id) || [])
                      : (contact.messages || [])) as Message[];

                    // (b) Reinsere as enviadas recentes que o backend não devolveu.
                    // Casamento por ID (não por conteúdo): elas já têm id real, então
                    // "veio do backend" é uma pergunta exata. Entra ANTES do dedup dos
                    // temps p/ que um temp ainda pendente possa casar com a reinserida
                    // e não gerar bolha duplicada.
                    const enviadasRecentes = enviadasRecentesByContact.get(contact.id);
                    let faltantes: Message[] = [];
                    if (enviadasRecentes && enviadasRecentes.length > 0) {
                      const idsBackend = new Set(backendMsgs.map((m) => String(m.id)));
                      faltantes = enviadasRecentes.filter((m) => !idsBackend.has(String(m.id)));
                    }
                    const baseMsgs = faltantes.length > 0 ? [...backendMsgs, ...faltantes] : backendMsgs;

                    const tempMsgs = tempMessagesByContact.get(contact.id);

                    // Dedup 1:1 por (texto, status, janela ±2min): cada mensagem do
                    // backend confirma NO MÁXIMO UM temp (Set `consumidos`). Sem isso,
                    // dois temps idênticos (a MESMA msg enviada 2x) casavam ambos com a
                    // ÚNICA já confirmada e os DOIS sumiam. Ao confirmar, o temp doa sua
                    // key+timestamp p/ a msg real (sentMsgStableRef), p/ não pular.
                    let stillPending: Message[] = [];
                    if (tempMsgs && tempMsgs.length > 0) {
                      const consumidos = new Set<number>();
                      stillPending = tempMsgs.filter((t) => {
                        const tempTs = new Date(t.timestamp).getTime();
                        const idx = baseMsgs.findIndex((m, i) => {
                          if (consumidos.has(i)) return false;
                          if (m.mensagem !== t.mensagem) return false;
                          if (m.status !== t.status) return false;
                          const ts = new Date(m.timestamp).getTime();
                          if (Number.isNaN(ts) || Number.isNaN(tempTs)) return false;
                          return Math.abs(ts - tempTs) <= TEMP_DEDUP_WINDOW_MS;
                        });
                        if (idx >= 0) {
                          consumidos.add(idx);
                          const real = baseMsgs[idx];
                          if (real && real.id != null) {
                            sentMsgStableRef.current.set(String(real.id), {
                              key: t.clientKey ?? String(t.id),
                              ts: t.timestamp,
                            });
                          }
                          return false; // temp confirmado → remove
                        }
                        return true;    // sem correspondente → preserva
                      });
                    }

                    // `msgsEstaveis === backendMsgs` também cobre o caso (b): com
                    // faltantes, baseMsgs é um array novo, então a identidade quebra
                    // e o contato é reconstruído em vez de retornar inalterado.
                    const msgsEstaveis = aplicarEstaveis(baseMsgs);
                    // Na lista leve o `contact` vem SEM a chave `messages` — devolvê-lo
                    // inalterado apagaria a conversa. Aqui sempre remonta com o que foi
                    // preservado.
                    if (!(contact as any).messages_lazy && stillPending.length === 0 && msgsEstaveis === backendMsgs) return contact;
                    return { ...contact, messages: [...msgsEstaveis, ...stillPending] };
                  });

                  // Salva snapshot do modo normal — usado p/ restaurar a lista
                  // instantaneamente quando o operador apaga o campo de busca,
                  // sem esperar o próximo refetch.
                  if (!activeSearch) {
                    normalContactsRef.current = finalContacts as Contact[];
                  }
                  return finalContacts;
                });
                setLoadingContacts(false);
            }
            (window as any)._chat_init_done = true;

          } else {
              console.warn(`⚠️ [DEBUG] API retornou 0 contatos para id_cliente=${id_cliente}`);
              // Busca server-side SEM matches: zera a lista (preservando só a conversa
              // aberta). Sem isso, os contatos antigos ficavam e a contagem mostrava um
              // número errado (ex.: "50 resultados" sem nenhum match real de "giovana").
              if (activeSearch && isMounted.current) {
                  const selId = selectedContactRef.current;
                  setContacts((prev) => {
                      const sticky = selId != null ? prev.find((c) => c.id === selId) : undefined;
                      return sticky ? [sticky] : [];
                  });
              }
          }

          } catch (err: any) {
            if (err.name === 'AbortError') {
                console.error("⏱️ [DEBUG] fetchContactsFull timeout após 30s");
            } else {
                console.error("Erro fetchContactsFull:", err);
            }
          } finally {
            isFetchingContacts.current = false;
            // Só o fetch MAIS NOVO desliga o spinner/loading — senão um fetch stale apagava
            // o "Buscando…" enquanto o atual ainda roda (piscada do spinner).
            if (isMounted.current && fetchGenRef.current === myGen) {
                setLoadingContacts(false);
                setIsSearchingRemote(false);
                setCarregandoMais(false);
                setBuscandoContatos(false);
            }
          }
        };

      // Infinite scroll: carrega mais contatos ao chegar no final da lista
      const loadMoreContacts = () => {
        if (isLoadingMore.current || !hasMoreContacts.current || isFetchingContacts.current) return;
        isLoadingMore.current = true;
        setCarregandoMais(true);
        setContactsLimit(prev => prev + LOTE_CONTATOS);
      };

      const handleContactsScroll = (values: { scrollTop: number; scrollHeight: number; clientHeight: number }) => {
        const { scrollTop, scrollHeight, clientHeight } = values;
        // Quando falta 100px ou menos para o final, carrega mais
        if (scrollHeight - scrollTop - clientHeight < 100) {
          loadMoreContacts();
        }
      };

      const fetchStages = async () => {
        if (isFetchingStages.current) return;
        isFetchingStages.current = true;
        const id_cliente = localStorage.getItem('id_cliente');
        try {
          const data = await sharedSidebarFetch(`${API_URL}/pegaretapaschat`, { id_cliente });
          setStages(data);
        } catch (err) {
          console.error('Erro ao buscar etapas:', err);
        } finally {
          isFetchingStages.current = false;
        }
      };

      const fetchFunnels = async () => {
        if (isFetchingFunnels.current) return;
        isFetchingFunnels.current = true;
        const id_cliente = localStorage.getItem('id_cliente');
        try {
          const data = await sharedSidebarFetch(`${API_URL}/pegarpaginas`, { id_cliente });
          const mappedFunnels = data.map((item: { page: string }, index: number) => ({
            id: index + 1,
            nome_funil: item.page,
          }));
          setFunnels(mappedFunnels);
        } catch (err) {
          console.error('Erro ao buscar funis:', err);
        } finally {
          isFetchingFunnels.current = false;
        }
      };

      // Admin master: funis + etapas de todos os times do grupo, numa chamada só.
      // Diferente de fetchStages/fetchFunnels, o param aqui é o id_grupo
      // (cliente_cadastro.id), não o id_time que o resto do /chat chama de id_cliente.
      const fetchGroupStages = async () => {
        if (!isAdminMaster || isFetchingGroupStages.current) return;
        isFetchingGroupStages.current = true;
        const id_grupo = localStorage.getItem('id_grupo');
        try {
          const data = await sharedSidebarFetch(`${API_URL}/pegarfunisetapas_grupo`, { id_grupo });
          setGroupStages(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error('Erro ao buscar funis/etapas do grupo:', err);
        } finally {
          isFetchingGroupStages.current = false;
        }
      };
    
  
    // Atendimento finalizado: move o card para a etapa marcada (ele sai da lista; volta pelo
    // filtro "Finalizados"). Reabrir volta para a etapa anterior, igual ao switch de humano.
    const finalizarAtendimento = async (etapaFinalizada: string) => {
      const fone = selectedContactData?.fone;
      if (!fone) return;
      const ok = await Swal.fire({
        icon: "question",
        title: "Finalizar atendimento?",
        text: "O contato sai da lista do chat e fica no filtro \"Finalizados\".",
        showCancelButton: true,
        confirmButtonText: "Finalizar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#0f9d58",
      });
      if (!ok.isConfirmed) return;
      await updateFunnelStage(fone, etapaFinalizada);
    };

    const reabrirAtendimento = async () => {
      const id_cliente = localStorage.getItem("id_cliente");
      const fone = selectedContactData?.fone;
      if (!fone || !id_cliente) return;
      try {
        const res = await fetch(`${API_URL}/voltar_etapa_anterior`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone, id_cliente }),
        });
        const data = await res.json();
        if (!data.found) {
          // Sem histórico (card criado direto na etapa): volta para a 1ª etapa do mesmo funil.
          const primeira = stages.find(
            (s) => s.funil === selectedContactData?.funil && s.atendimento_finalizado !== "Sim" && s.atendimento_humano !== "Sim"
          );
          if (primeira) { updateFunnelStage(fone, primeira.nome_funil); return; }
          Swal.fire({ icon: "info", title: "Escolha a etapa", text: "Não achei a etapa anterior. Selecione a etapa no seletor de Etapa." });
          return;
        }
        const targetFunnel = data.funnel_name as string | undefined;
        if (targetFunnel && targetFunnel !== selectedContactData?.funil) {
          updateFunnelCombined(fone, data.stage_name, targetFunnel);
        } else {
          updateFunnelStage(fone, data.stage_name);
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "Não foi possível reabrir o atendimento" });
      }
    };

    const updateFunnelStage = async (fone: string, newStage: string) => {
      // Admin: usa id_time do contato; Não-admin: usa localStorage
      const contact = contacts.find(c => String(c.fone) === String(fone));
      const id_cliente = (isAdminMaster && contact?.id_time) ? String(contact.id_time) : localStorage.getItem("id_cliente");
  
      // 🔹 Validação de Travas (Card)
      try {
        const valRes = await fetch(`${API_URL}/card/validate_move`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente: id_cliente, fone: fone }),
        });
        if (valRes.ok) {
          const valData = await valRes.json();
          if (!valData.allowed) {
            alert(`🚫 Ação bloqueada!\n\nPara avançar desta etapa, preencha:\n\n- ${valData.missing_fields.join("\n- ")}`);
            return;
          }
        }
      } catch (e) {
        console.error("Erro validação:", e);
      }
  
      // ⚡ Optimistic Update: Atualiza a UI imediatamente
      setPendingOverride(String(fone), { nome_funil: newStage });
      setContacts((prev) =>
        prev.map((contact) =>
          contact.fone === fone ? { ...contact, nome_funil: newStage } : contact
        )
      );

      try {
        const response = await fetch(`${API_URL}/atualizaretapa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: String(fone), nova_etapa: newStage, idcliente: id_cliente }),
        });
        if (!response.ok) throw new Error("Erro ao atualizar a etapa");
      } catch (err) {
        console.error(err);
        // ❌ Revert em caso de erro
        clearPendingOverride(String(fone));
        fetchContactsFull();
      }
    };
    
    const updateFunnelPage = async (fone: string, newPage: string) => {
      const contact = contacts.find(c => String(c.fone) === String(fone));
      const id_cliente = (isAdminMaster && contact?.id_time) ? String(contact.id_time) : localStorage.getItem("id_cliente");
  
      // 🔹 Validação de Travas (Card)
      try {
        const valRes = await fetch(`${API_URL}/card/validate_move`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente: id_cliente, fone: fone }),
        });
        if (valRes.ok) {
          const valData = await valRes.json();
          if (!valData.allowed) {
            alert(`🚫 Ação bloqueada!\n\nPara sair deste funil/etapa, preencha:\n\n- ${valData.missing_fields.join("\n- ")}`);
            return;
          }
        }
      } catch (e) {
        console.error("Erro validação:", e);
      }
  
      // Primeira etapa do novo funil (stages já vem ordenado por ordem ASC do backend)
      const firstStage = stages.find((s: any) => s.funil === newPage);
      const firstStageName = firstStage?.nome_funil;

      // ⚡ Optimistic Update: Atualiza a UI imediatamente (funil + primeira etapa)
      setPendingOverride(String(fone), {
        funil: newPage,
        ...(firstStageName ? { nome_funil: firstStageName } : {}),
      });
      setContacts((prev) =>
        prev.map((contact) =>
          contact.fone === fone
            ? { ...contact, funil: newPage, ...(firstStageName ? { nome_funil: firstStageName } : {}) }
            : contact
        )
      );

      try {
        // Se temos a primeira etapa, usa o endpoint combinado para já atualizar funil + etapa no backend
        const endpoint = firstStageName ? `${API_URL}/atualizarjuntochat` : `${API_URL}/atualizarpaginachat`;
        const body = firstStageName
          ? { fone: String(fone), nova_etapa: firstStageName, novo_funil: newPage, id_cliente }
          : { fone, nova_pagina: newPage, id_cliente };
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error("Erro ao atualizar a página");
      } catch (err) {
        console.error(err);
        // ❌ Revert em caso de erro
        clearPendingOverride(String(fone));
        fetchContactsFull();
      }
    };
  
    const handleScheduleSubmit = () => {
      if (!selectedContactData?.fone || !scheduleDate) return;
      // Fecha o modal na hora:
      setShowScheduleModal(false);
      alert("Retorno agendado para " + scheduleDate);
          // Dispara a requisição em segundo plano:
      const id_cliente = localStorage.getItem("id_cliente");
      fetch(`${API_URL}/marcaragendamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone: selectedContactData.fone,
          data_agendamento: scheduleDate,
          id_cliente,
        }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Erro ao agendar retorno");
          return res.json();
        })
        .catch((err) => {
          console.error(err);
          alert("Erro ao agendar retorno");
        });
      };
  
    const updateFunnelCombined = async (fone: string, newStage: string, newFunnel: string) => {
      const contact = contacts.find(c => String(c.fone) === String(fone));
      const id_cliente = (isAdminMaster && contact?.id_time) ? String(contact.id_time) : localStorage.getItem("id_cliente");

      // ⚡ Optimistic Update
      setPendingOverride(String(fone), { nome_funil: newStage, funil: newFunnel });
      setContacts((prev) =>
        prev.map((c) =>
          String(c.fone) === String(fone) ? { ...c, nome_funil: newStage, funil: newFunnel } : c
        )
      );

      try {
        const response = await fetch(`${API_URL}/atualizarjuntochat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: String(fone), nova_etapa: newStage, novo_funil: newFunnel, id_cliente }),
        });
        if (!response.ok) {
          throw new Error("Erro ao atualizar funil e etapa");
        }
      } catch (error) {
        console.error(error);
        // ❌ Revert em caso de erro
        clearPendingOverride(String(fone));
        fetchContactsFull();
      }
    };

    // ══════════════════════════════════════════════════════════════════════
    // Seletor Funil → Etapa do lead
    // ══════════════════════════════════════════════════════════════════════
    // Duas regras aqui:
    //  1. Trocar o FUNIL não grava nada — só recarrega a lista de etapas. O lead
    //     só se move quando a ETAPA é escolhida (antes, trocar de funil jogava o
    //     lead na primeira etapa automaticamente).
    //  2. Admin master enxerga os funis/etapas de TODOS os times do grupo; escolher
    //     um de outro time TRANSFERE o lead para lá (id_time, atendente, histórico).
    // Por isso a chave de funil carrega o time: "LEADS" existe em vários times.

    const funnelKeyOf = (idTime: any, funil: string) =>
      `${isAdminMaster ? (idTime ?? "") : ""}|${funil || ""}`;

    // Fonte única das etapas do seletor. Sem admin master, é o `stages` do próprio
    // time (que já traz funil.id em `id`); com, é o grupo inteiro.
    const stageOptions = React.useMemo(() => {
      if (isAdminMaster && groupStages.length > 0) {
        return groupStages.map((s) => ({
          id_etapa: s.id_etapa, funil: s.funil, nome_funil: s.nome_funil,
          id_time: s.id_time, nome_time: s.nome_time,
        }));
      }
      return (stages || []).map((s: any) => ({
        id_etapa: s.id, funil: s.funil, nome_funil: s.nome_funil,
        id_time: null as number | null, nome_time: null as string | null,
      }));
    }, [isAdminMaster, groupStages, stages]);

    // Funis derivados das etapas, preservando a ordem em que vieram do backend
    // (time → funil → ordem da etapa). Um funil sem etapa nenhuma não aparece:
    // não haveria para onde mover o lead.
    const funnelOptions = React.useMemo(() => {
      const vistos = new Set<string>();
      const out: { key: string; funil: string; id_time: number | null; nome_time: string | null }[] = [];
      stageOptions.forEach((o) => {
        const key = funnelKeyOf(o.id_time, o.funil);
        if (vistos.has(key)) return;
        vistos.add(key);
        out.push({ key, funil: o.funil, id_time: o.id_time, nome_time: o.nome_time });
      });
      return out;
    }, [stageOptions, isAdminMaster]);

    // Funil escolhido mas ainda não aplicado (aguardando a etapa). null = mostra o
    // funil atual do lead.
    const [pendingFunnelKey, setPendingFunnelKey] = useState<string | null>(null);

    // currentFunnelKey / stageSelectValue dependem de selectedContactData, que só é
    // derivado bem mais abaixo — ficam junto do renderFunilEtapaSelects.

    const moverLeadParaEtapa = async (fone: string, idEtapa: number | string) => {
      const destino = stageOptions.find((o) => String(o.id_etapa) === String(idEtapa));
      if (!fone || !destino) return;

      const contact = contacts.find((c) => String(c.fone) === String(fone));
      const idTimeAtual = (isAdminMaster && contact?.id_time)
        ? String(contact.id_time)
        : localStorage.getItem("id_cliente");
      const trocaTime = destino.id_time != null && String(destino.id_time) !== String(idTimeAtual);

      // 🔹 Validação de Travas (Card) — mesma regra dos fluxos antigos
      try {
        const valRes = await fetch(`${API_URL}/card/validate_move`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente: idTimeAtual, fone }),
        });
        if (valRes.ok) {
          const valData = await valRes.json();
          if (!valData.allowed) {
            alert(`🚫 Ação bloqueada!\n\nPara sair desta etapa, preencha:\n\n- ${valData.missing_fields.join("\n- ")}`);
            return;
          }
        }
      } catch (e) {
        console.error("Erro validação:", e);
      }

      // Mudar o lead de time é destrutivo o bastante para pedir confirmação.
      if (trocaTime) {
        const confirma = await Swal.fire({
          title: "Mover lead de time?",
          html:
            `O lead vai para <b>${escapeHtml(destino.nome_time)}</b>, em ` +
            `<b>${escapeHtml(destino.funil)} › ${escapeHtml(destino.nome_funil)}</b>.` +
            `<div style="margin-top:10px;font-size:13px;color:#64748b">` +
            `O atendimento e o histórico de mensagens passam para esse time, e o lead sai do time atual.</div>`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sim, mover",
          cancelButtonText: "Cancelar",
        });
        if (!confirma.isConfirmed) return;
      }

      // ⚡ Optimistic update
      setPendingOverride(String(fone), { funil: destino.funil, nome_funil: destino.nome_funil });
      setContacts((prev) =>
        prev.map((c) =>
          String(c.fone) === String(fone)
            ? {
                ...c,
                funil: destino.funil,
                nome_funil: destino.nome_funil,
                ...(trocaTime ? { id_time: destino.id_time as any } : {}),
              }
            : c
        )
      );
      setPendingFunnelKey(null);

      try {
        const response = await fetch(`${API_URL}/mover_lead_funil_etapa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: String(fone), id_time: idTimeAtual, id_etapa: destino.id_etapa }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err?.error || "Erro ao mover o lead");
        }
        if (trocaTime) {
          // O backend devolve o atendente que recebeu o lead no time destino. Aplica
          // aqui: o fetchContactsFull abaixo pode ser pulado se já houver outra busca
          // em andamento, e aí o header ficava com o atendente antigo até o F5.
          const resultado = await response.json().catch(() => ({}));
          setContacts((prev) =>
            prev.map((c) =>
              String(c.fone) === String(fone)
                ? {
                    ...c,
                    id_time: (resultado?.id_time ?? destino.id_time) as any,
                    ...(resultado?.id_user != null ? { id_user: resultado.id_user } : {}),
                  }
                : c
            )
          );
          // O lead saiu do time atual: a lista precisa refletir isso de verdade.
          clearPendingOverride(String(fone));
          fetchContactsFull();
          Swal.fire({
            icon: "success",
            title: "Lead movido",
            html:
              `Agora em <b>${escapeHtml(destino.nome_time)}</b> › ${escapeHtml(destino.nome_funil)}` +
              (resultado?.id_user_nome ? `<br>Atendente: <b>${escapeHtml(resultado.id_user_nome)}</b>` : ""),
            timer: 2500,
            showConfirmButton: false,
          });
        }
      } catch (error: any) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Não foi possível mover o lead", text: error?.message || "Erro ao mover o lead" });
        clearPendingOverride(String(fone));
        fetchContactsFull();
      }
    };


        console.log("👀 Renderizando Chat. Contatos:", contacts.length, "Loading:", loadingContacts);
        
        // Etapas de atendimento finalizado ("time|funil|etapa"): as do próprio time (stages) e,
        // no admin master, as de todos os times (groupStages).
        const meuTimeFin = localStorage.getItem("id_cliente") || "";
        const chavesFinalizadas = new Set<string>([
          ...stages.filter((s) => s.atendimento_finalizado === "Sim").map((s) => `${meuTimeFin}|${s.funil}|${s.nome_funil}`),
          ...groupStages.filter((s) => s.atendimento_finalizado === "Sim").map((s) => `${s.id_time}|${s.funil}|${s.nome_funil}`),
        ]);
        const existeEtapaFinalizada = chavesFinalizadas.size > 0;
        const estaFinalizado = (c: any) => chavesFinalizadas.has(`${c.id_time ?? meuTimeFin}|${c.funil}|${c.nome_funil}`);

        const visibleContacts = contacts.filter(c => {
          // Filtro por time (client-side para admin): múltipla seleção, vazio = todos
          if (isAdminMaster && idsTimesSelecionados.length > 0) {
            if (!idsTimesSelecionados.includes(String(c.id_time ?? ""))) return false;
          }

          const matchFunnel = filterFunnels.length === 0 || filterFunnels.includes(c.funil || "");
          const matchStage = !filterStage || c.nome_funil === filterStage;

          // Filtro de data agora é SERVER-SIDE (ver fetchContactsFull + /contacts_full):
          // o backend já devolve exatamente as conversas com ao menos uma mensagem no
          // período. O antigo filtro client-side comparava só a ÚLTIMA mensagem
          // (lastTimestamp) e, além de ver só as ~200 recentes, descartava conversas
          // ainda ativas que tinham mensagens no período — por isso foi removido.
          const matchDate = true;

          const lowerSearch = searchTerm.toLowerCase();
          const matchSearch = !searchTerm ||
            (c.nome && String(c.nome).toLowerCase().includes(lowerSearch)) ||
            (c.fone && String(c.fone).includes(lowerSearch)) ||
            (c.messages && c.messages.some(m => m.mensagem && String(m.mensagem).toLowerCase().includes(lowerSearch)));

          // Filtro do dropdown "Seleção de Usuário": múltipla seleção, compara
          // como string (state é string[]; c.id_user é number). Vazio = todos.
          const matchUser =
            idsUsuariosSelecionados.length === 0 ||
            idsUsuariosSelecionados.includes(String(c.id_user ?? ""));

          // Filtro "Não lidas": só contatos com mensagens recebidas não lidas.
          const matchNaoLidas = !filtroNaoLidas || !!(c.unread_count && c.unread_count > 0);

          // Filtro de tags (o servidor já filtra; aqui garante na lista em memória e no polling).
          const matchTags = filterTags.length === 0 ||
            (tagsDoContato(c.id_time, c.fone) || SEM_TAGS).some((t) => filterTags.includes(t.id));

          // Finalizados: só com o filtro; a busca acha os dois (o servidor também libera na busca).
          const matchFinalizado = filtroFinalizados ? estaFinalizado(c) : (!estaFinalizado(c) || !!searchTerm);

          return matchFunnel && matchStage && matchSearch && matchUser && matchDate && matchNaoLidas && matchTags && matchFinalizado;
        });

        // Alimenta o refill automático (ver useEffect de MIN_CONTATOS_VISIVEIS).
        visibleContactsCountRef.current = visibleContacts.length;

        if (contacts.length > 0 && visibleContacts.length === 0) {
            console.log("⚠️ visibleContacts vazio! Filtros:", { filterFunnels, filterStage, searchTerm });
        }

  
  
  
  


  const sendMessage = async () => {
    if (!selectedContact || !newMessage.trim()) return;

    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;

    const fone = contact.fone || "";

    // Admin master: usa id_time do contato, ou do time selecionado (se não for "todos")
    const adminTimeId = isAdminMaster
      ? (contact.id_time?.toString() || (idsTimesSelecionados.length === 1 ? idsTimesSelecionados[0] : null))
      : null;

    const instanceName = localStorage.getItem("instanceName");
    const id_cliente = localStorage.getItem("id_cliente");
    const id_user = localStorage.getItem("id_user");
    const instanciaOficial = localStorage.getItem("instanciaoficial");

    // Time 159: o envio sai pelo número do DONO do lead (id_user do dono).
    const envioIdUser = resolveEnvioIdUser(contact);
    const envioIns = ehTimeNumeroPorDono(contact.id_time)
      ? (usuariosDoTime.find((u) => String(u.id) === String(envioIdUser))?.ins ?? null)
      : null;

    const hasOfficial = instanciaOficial && instanciaOficial !== "0";
    const hasEvolution = instanceName;

    if (!id_cliente || (!hasOfficial && !hasEvolution && !isAdminMaster)) {
      setModalMessage("Erro: Instância ou ID do cliente não encontrados.");
      setShowModal(true);
      return;
    }

    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      status: "send",
      mensagem: newMessage,
      timestamp: new Date().toISOString(),
      type: "conversation",
      read: true,
      fone,
      ins: envioIns,
    };

    setContacts((prevContacts) =>
      prevContacts.map((c) =>
        c.id === selectedContact
          ? { ...c, messages: [...(c.messages || []), tempMessage] }
          : c
      )
    );

    setNewMessage("");
    if (messageInputRef.current) {
      messageInputRef.current.style.height = "40px";
    }

    try {
      // Sem `api_mode`: o backend auto-detecta pelo que o time tem cadastrado
      // (`use_evolution = has_unofficial and not has_official`), priorizando a oficial —
      // exatamente o default que o switch removido já aplicava.
      if (isAdminMaster && adminTimeId) {
        // Admin master: usa endpoint admin com id_time do contato/time selecionado
        const messageData = {
          fone,
          text: newMessage,
          id_time: adminTimeId,
          id_user: envioIdUser,
        };

        const response = await fetch(`${API_URL}/enviarmensagem_admin`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(messageData),
        });

        if (!response.ok) throw new Error(await motivoFalhaEnvio(response, "Erro ao enviar a mensagem."));
        await avisarSeNaoSalvouHistorico(response);
      } else {
        // Não-admin: endpoint unificado com detecção automática
        const messageData = {
          fone,
          text: newMessage,
          id_cliente,
          id_user: envioIdUser,
          instanceName,
        };

        const response = await fetch(`${API_URL}/enviarmensagem`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(messageData),
        });

        if (!response.ok) throw new Error(await motivoFalhaEnvio(response, "Erro ao enviar a mensagem."));
        await avisarSeNaoSalvouHistorico(response);
      }
    } catch (err: any) {
      console.error(err);
      setModalMessage(err?.message || "Erro ao enviar mensagem.");
      setShowModal(true);
      // Remove a mensagem da lista se falhar
      setContacts((prevContacts) =>
        prevContacts.map((c) =>
          c.id === selectedContact
            ? {
                ...c,
                messages: (c.messages || []).filter((msg) => msg.id !== tempMessage.id),
              }
            : c
        )
      );
    }
  };

  // ─── Anotações internas: criação, menção e exclusão ──────────────────
  // Extrai os ids de usuário mencionados de um texto com marcadores @[Nome](id)
  const mencoesIdsNoTexto = (texto: string): number[] => {
    const ids: number[] = [];
    const re = /@\[[^\]]+\]\((\d+)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(texto)) !== null) ids.push(parseInt(m[1], 10));
    return ids;
  };

  // Lista de usuários filtrada pelo texto digitado após o "@"
  const usuariosMencaoFiltrados = (): UsuarioGrupo[] => {
    const q = mentionQuery.trim().toLowerCase();
    const lista = q
      ? usuariosDoGrupo.filter((u) => u.nome.toLowerCase().includes(q))
      : usuariosDoGrupo;
    return lista.slice(0, 8);
  };

  const handleNoteTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNoteText(val);
    const ta = e.target;
    const caret = ta.selectionStart ?? val.length;
    const before = val.slice(0, caret);
    const match = before.match(/@([^\s@]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionQuery("");
    }
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  };

  const selecionarMencao = (u: UsuarioGrupo) => {
    const ta = noteTextareaRef.current;
    const caret = ta?.selectionStart ?? noteText.length;
    const before = noteText.slice(0, caret);
    const after = noteText.slice(caret);
    const newBefore = before.replace(/@([^\s@]*)$/, `@[${u.nome}](${u.id}) `);
    const novo = newBefore + after;
    setNoteText(novo);
    setMentionOpen(false);
    setMentionQuery("");
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        const pos = newBefore.length;
        ta.setSelectionRange(pos, pos);
      }
    });
  };

  const saveNote = async () => {
    if (!selectedContact || !noteText.trim() || noteSaving) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;

    const fone = contact.fone || "";
    const id_grupo = localStorage.getItem("id_grupo");
    const id_user = localStorage.getItem("id_user");
    const instanceName = localStorage.getItem("instanceName");
    const id_time = contact.id_time ?? Number(localStorage.getItem("id_cliente"));
    const autor_nome =
      (id_user && nomePorUserId[Number(id_user)]) ||
      (localStorage.getItem("user_email") || "").split("@")[0] ||
      "Usuário";
    const texto = noteText.trim();
    const ids = mencoesIdsNoTexto(texto);
    const mencionados = usuariosDoGrupo
      .filter((u) => ids.includes(u.id))
      .map((u) => ({ id: u.id, nome: u.nome }));

    setNoteSaving(true);
    const tempNote: Message = {
      id: `temp-${Date.now()}`,
      status: "note",
      mensagem: texto,
      timestamp: new Date().toISOString(),
      type: "anotacao",
      read: true,
      fone,
      id_user: id_user ? Number(id_user) : null,
    };
    setContacts((prev) =>
      prev.map((c) =>
        c.id === selectedContact ? { ...c, messages: [...(c.messages || []), tempNote] } : c
      )
    );
    setNoteText("");
    setShowNoteComposer(false);
    setMentionOpen(false);

    try {
      const res = await fetch(`${API_URL}/criar_anotacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone,
          id_grupo,
          id_time,
          instanceName,
          id_user,
          autor_nome,
          contato_nome: contact.nome,
          texto,
          mencionados,
        }),
      });
      if (!res.ok) throw new Error("Falha ao salvar anotação");
    } catch (err) {
      console.error(err);
      setModalMessage("Erro ao salvar anotação.");
      setShowModal(true);
      setContacts((prev) =>
        prev.map((c) =>
          c.id === selectedContact
            ? { ...c, messages: (c.messages || []).filter((m) => m.id !== tempNote.id) }
            : c
        )
      );
    } finally {
      setNoteSaving(false);
    }
  };

  const excluirAnotacao = async (message: Message) => {
    if (!selectedContact) return;
    if (!window.confirm("Excluir esta anotação?")) return;
    const id_user = localStorage.getItem("id_user");
    setContacts((prev) =>
      prev.map((c) =>
        c.id === selectedContact
          ? { ...c, messages: (c.messages || []).filter((m) => m.id !== message.id) }
          : c
      )
    );
    try {
      await fetch(`${API_URL}/excluir_anotacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_chat: message.id, id_user }),
      });
    } catch (e) {
      console.error("Erro ao excluir anotação:", e);
    }
  };

  // ─── Uploads internos (TNP, grupo 192) ──────────────────────────────
  // Nome a exibir: doc_name (nome original) quando existe; senão o fim da URL
  // (nome gerado no bucket), que é o que sobra em linhas antigas/manuais.
  const tnpNomeArquivo = (m: { mensagem?: string; doc_name?: string | null }): string => {
    if (m?.doc_name) return m.doc_name;
    const semQuery = (m?.mensagem || "").split("?")[0];
    try {
      return decodeURIComponent(semQuery.split("/").pop() || "arquivo");
    } catch {
      return semQuery.split("/").pop() || "arquivo";
    }
  };

  // Sobe os arquivos escolhidos (fila sequencial) e grava cada um em `chats`
  // como type='tnpupload'. Nenhum provedor é acionado — é anexo interno.
  const enviarTnpUploads = async (selecionados: File[]) => {
    // Arquivo de 0 bytes (ex.: arrastado de app que não entrega o conteúdo) virava anexo vazio.
    const vazios = selecionados.filter((f) => f.size === 0);
    const files = selecionados.filter((f) => f.size > 0);
    if (vazios.length > 0) {
      setModalMessage(
        `${vazios.length === 1 ? `"${vazios[0].name}" está vazio` : `${vazios.length} arquivos estão vazios`} (0 bytes) e não foi anexado. Salve o arquivo no computador e anexe de lá.`
      );
      setShowModal(true);
    }
    if (!selectedContact || files.length === 0) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;

    const id_grupo = localStorage.getItem("id_grupo");
    const id_user = localStorage.getItem("id_user");
    const instanceName = localStorage.getItem("instanceName");
    const id_time = contact.id_time ?? Number(localStorage.getItem("id_cliente"));
    if (!id_grupo) {
      setModalMessage("Erro: grupo do usuário não encontrado.");
      setShowModal(true);
      return;
    }

    setTnpUploading(true);
    try {
      for (const file of files) {
        // Balão otimista (mesmo padrão da anotação): entra na hora e, no sucesso,
        // vira a linha real (id do banco) — o fetch seguinte casa pelo id e não duplica.
        const tempId = `temp-tnp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const temp: Message = {
          id: tempId,
          status: "upload",
          mensagem: URL.createObjectURL(file),
          timestamp: new Date().toISOString(),
          type: "tnpupload",
          read: true,
          fone: contact.fone || "",
          id_user: id_user ? Number(id_user) : null,
          doc_name: file.name,
        };
        setContacts((prev) =>
          prev.map((c) =>
            c.id === selectedContact ? { ...c, messages: [...(c.messages || []), temp] } : c
          )
        );

        try {
          const form = new FormData();
          form.append("file", file, file.name);
          form.append("fone", contact.fone || "");
          form.append("id_grupo", id_grupo);
          form.append("id_time", String(id_time ?? ""));
          if (instanceName) form.append("instanceName", instanceName);
          if (id_user) form.append("id_user", id_user);

          const res = await fetch(`${API_URL}/tnp_upload`, { method: "POST", body: form });
          if (!res.ok) throw new Error("Falha no upload");
          const dados = await res.json();

          // Troca o balão temp pela linha real. Se o refetch/realtime já tiver
          // trazido essa mesma linha enquanto o upload estava em voo, ela é
          // descartada aqui — senão ficariam duas bolhas com o MESMO id.
          setContacts((prev) =>
            prev.map((c) =>
              c.id === selectedContact
                ? {
                    ...c,
                    messages: (c.messages || [])
                      .filter(
                        (m: Message) =>
                          m.id === tempId || dados.id == null || String(m.id) !== String(dados.id)
                      )
                      .map((m: Message) =>
                        m.id === tempId
                          ? {
                              ...m,
                              id: dados.id ?? tempId,
                              mensagem: dados.url || m.mensagem,
                              timestamp: dados.timestamp || m.timestamp,
                              doc_name: dados.doc_name || m.doc_name,
                              clientKey: tempId, // key estável: temp→real sem remount
                            }
                          : m
                      ),
                  }
                : c
            )
          );
        } catch (err) {
          console.error("Erro no upload TNP:", err);
          setModalMessage(`Erro ao subir o arquivo "${file.name}".`);
          setShowModal(true);
          // Remove só o balão deste arquivo (os demais da fila seguem).
          setContacts((prev) =>
            prev.map((c) =>
              c.id === selectedContact
                ? { ...c, messages: (c.messages || []).filter((m: Message) => m.id !== tempId) }
                : c
            )
          );
        }
      }
    } finally {
      setTnpUploading(false);
    }
  };

  // Lista os uploads do contato aberto. Vem do banco (e não das mensagens já
  // carregadas) p/ não depender do filtro de data nem da paginação da conversa.
  const carregarTnpUploads = async () => {
    const contact = contacts.find((c) => c.id === selectedContact);
    const id_grupo = localStorage.getItem("id_grupo");
    if (!contact?.fone || !id_grupo) return;
    setTnpUploadsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/tnp_uploads?fone=${encodeURIComponent(contact.fone)}&id_grupo=${id_grupo}`
      );
      setTnpUploads(res.ok ? await res.json() : []);
    } catch (e) {
      console.error("Erro ao carregar uploads TNP:", e);
      setTnpUploads([]);
    } finally {
      setTnpUploadsLoading(false);
    }
  };

  const abrirTnpUploads = () => {
    setShowTnpUploads(true);
    carregarTnpUploads();
  };

  // -- Registro de ligacao (TNP, grupo 192) ---------------------------------
  // Data/hora em horario de BRASILIA, forcado pelo timeZone: nao depende do fuso
  // configurado no PC do operador. Formato "DD/MM/AAAA HH:MM" -- o mesmo do
  // fallback do backend e o que o render da tag le de volta por regex.
  const agoraEmBrasilia = (): string => {
    const partes = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date());
    const p: Record<string, string> = {};
    partes.forEach((x) => { p[x.type] = x.value; });
    return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
  };

  const registrarLigacao = async () => {
    const fone = selectedContactData?.fone;
    const id_grupo = localStorage.getItem("id_grupo") || "";
    if (!fone || !id_grupo || salvandoLigacao) return;
    const quando = agoraEmBrasilia();
    setSalvandoLigacao(true);
    try {
      const res = await fetch(`${API_URL}/registrar_ligacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone,
          id_grupo,
          id_time: timeDoLeadAtual(),
          instanceName: localStorage.getItem("instanceName") || "",
          id_user: localStorage.getItem("id_user") || null,
          quando,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.error) {
        setModalMessage(`Falha ao registrar a ligação: ${body?.error || `HTTP ${res.status}`}`);
        setShowModal(true);
        return;
      }
      // Insere na conversa aberta sem refetch (o realtime tambem entrega; o dedup
      // por id no render cobre a corrida entre os dois caminhos).
      const nova: Message = {
        id: body.id,
        fone: String(fone),
        status: "call",
        mensagem: body.mensagem || `Ligacao realizada em ${quando}`,
        timestamp: body.timestamp || new Date().toISOString(),
        read: true,
        type: "call",
        id_user: Number(localStorage.getItem("id_user")) || null,
        ins: body.ins ?? null,
      };
      setContacts((prev) =>
        prev.map((c) =>
          String(c.fone) === String(fone)
            ? { ...c, messages: [...(c.messages || []), nova] }
            : c
        )
      );
    } catch (err) {
      console.error("Erro ao registrar ligacao:", err);
      setModalMessage("Erro ao registrar a ligação.");
      setShowModal(true);
    } finally {
      setSalvandoLigacao(false);
    }
  };

  // Fecha o compositor de anotação ao clicar fora
  useEffect(() => {
    if (!showNoteComposer) return;
    const handler = (e: MouseEvent) => {
      if (noteComposerRef.current && !noteComposerRef.current.contains(e.target as Node)) {
        setShowNoteComposer(false);
        setMentionOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNoteComposer]);

  // ─── Janela 24h + Templates ──────────────────────────────────────
  // ms da última mensagem RECEBIDA (da pessoa) do contato, ou null.
  // Considera qualquer mensagem recebida, independente da instância/número.
  const getLastReceivedMs = (contact?: Contact | null): number | null => {
    if (!contact) return null;
    const times = (contact.messages || [])
      .filter((m) => m.status === "received")
      .map((m) => (m.timestamp ? new Date(m.timestamp).getTime() : NaN))
      .filter((t) => isFinite(t));
    if (times.length) return Math.max(...times);
    // Card LEVE: sem o histórico em memória, cai no timestamp que o backend manda
    // no resumo. Sem isto, uma conversa DENTRO da janela de 24h parece fora dela e
    // o rodapé mostra "Enviar template" até a conversa terminar de carregar —
    // empurrando o operador para um template pago sem necessidade.
    const ts = (contact as any).ultima_recebida_ts;
    if (ts) {
      const t = new Date(ts).getTime();
      if (isFinite(t)) return t;
    }
    return null;
  };

  // True se a conversa está FORA da janela (precisa enviar template).
  // A janela só é (re)aberta pela última mensagem RECEBIDA da pessoa — de QUALQUER
  // número/instância. Nem mensagens normais nem TEMPLATES que NÓS enviamos abrem a
  // janela — apenas uma resposta do cliente reinicia a contagem das 24h.
  const isOutOf24h = (contact?: Contact | null): boolean => {
    const recebidaMs = getLastReceivedMs(contact);
    if (recebidaMs == null) return true; // nunca recebeu mensagem do cliente
    return Date.now() - recebidaMs > WINDOW_HOURS * 60 * 60 * 1000;
  };

  // Resolve o id_time atual considerando admin master.
  const getResolvedTimeId = (): string | null => {
    const id_cliente = localStorage.getItem("id_cliente");
    const contact = contacts.find((c) => c.id === selectedContact);
    const adminTimeId = isAdminMaster
      ? (contact?.id_time?.toString() || (idsTimesSelecionados.length === 1 ? idsTimesSelecionados[0] : null))
      : null;
    return adminTimeId || id_cliente;
  };

  // True se deve bloquear digitação e exibir o botão de template.
  // Habilitado para QUALQUER login com nº oficial (Meta) conectado
  // (localStorage.instanciaoficial válido), fora da janela de 24h. Os times
  // historicamente liberados (146/159) continuam funcionando como fallback,
  // mesmo que o nº oficial não seja detectado no login.
  // True se ESTE LOGIN pode enviar template, independente da janela de 24h: nº
  // oficial (Meta) conectado, ou um dos times historicamente liberados.
  const canSendTemplate = (): boolean => {
    const t = getResolvedTimeId();
    if (!t) return false;
    const instanciaOficial = localStorage.getItem("instanciaoficial");
    const hasOfficial = !!instanciaOficial && instanciaOficial !== "0";
    return hasOfficial || TEMPLATE_TIME_IDS.includes(String(t));
  };

  const shouldUseTemplate = (): boolean => {
    if (!canSendTemplate()) return false;
    const contact = contacts.find((c) => c.id === selectedContact);
    return isOutOf24h(contact);
  };

  // Carrega templates do /chat direto do BANCO (rápido, sem Meta). O backend já
  // filtra p/ 'Manual' ou sem classificação e devolve o body com variáveis
  // posicionais ({{1}}...). As imagens (headerImage) carregam em 2º plano (lazy).
  // Carrega o mapa nome->{mídia,botões} de TODOS os templates do grupo ao abrir o chat,
  // pra identificar mensagens de template (manual/cadência/boas-vindas) pelo template_nome.
  useEffect(() => {
    const id_time = getResolvedTimeId();
    if (!id_time) return;
    fetch(`${API_URL}/chat/templates_map`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time }),
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((map: any) => { if (map && typeof map === "object") setTemplatesInfo(map); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selo de categoria do template (campo da Meta). MARKETING é cobrado como conversa
  // de marketing e tem regra de opt-out; UTILITY é transacional. O operador precisa
  // ver a diferença ANTES de enviar — é o único uso deste dado no /chat.
  const seloCategoria = (categoria?: string) => {
    const c = (categoria || "").toUpperCase();
    if (c === "MARKETING") return { texto: "MARKETING", cor: "#b45309", fundo: "rgba(245,158,11,0.16)", icone: "ti-speakerphone" };
    if (c === "UTILITY") return { texto: "UTILITY", cor: "#2563eb", fundo: "rgba(37,99,235,0.12)", icone: "ti-tool" };
    if (c) return { texto: c, cor: "#64748b", fundo: "rgba(100,116,139,0.14)", icone: "ti-tag" };
    return null;
  };

  // Selo de STATUS do template na Meta. A lista do modal sai da tabela LOCAL, que não tem
  // status: template recém-criado aparece igualzinho a um aprovado e o envio falha. APPROVED
  // não ganha selo de propósito — aprovado é o caso normal e poluir a lista com um selo em
  // cada item tiraria a força justamente de quem precisa chamar atenção.
  const seloStatus = (status?: string) => {
    const st = (status || "").toUpperCase();
    if (st === "PENDING" || st === "IN_APPEAL" || st === "PENDING_DELETION")
      return { texto: "EM ANÁLISE", cor: "#b45309", fundo: "rgba(245,158,11,0.18)", icone: "ti-clock-hour-4",
               aviso: "Este template ainda está em análise pela Meta. Enquanto não for aprovado, o envio falha." };
    if (st === "REJECTED")
      return { texto: "REJEITADO", cor: "#b91c1c", fundo: "rgba(220,38,38,0.14)", icone: "ti-ban",
               aviso: "A Meta rejeitou este template. Ele não pode ser enviado — corrija o texto e crie um novo, com outro nome." };
    if (st === "PAUSED")
      return { texto: "PAUSADO", cor: "#b91c1c", fundo: "rgba(220,38,38,0.14)", icone: "ti-player-pause",
               aviso: "A Meta pausou este template por qualidade baixa. O envio falha até ser reativado." };
    if (st === "DISABLED")
      return { texto: "DESATIVADO", cor: "#b91c1c", fundo: "rgba(220,38,38,0.14)", icone: "ti-circle-off",
               aviso: "Este template foi desativado pela Meta e não pode ser enviado." };
    return null;   // APPROVED (e desconhecido) seguem sem selo, como era antes
  };

  // Números das variáveis {{n}} de um texto, sem repetição, em ordem.
  const varsDoTexto = (txt: string): string[] =>
    Array.from(new Set((txt.match(/\{\{(\d+)\}\}/g) || []).map((m) => m.replace(/\D/g, ""))))
      .sort((a, b) => Number(a) - Number(b));

  // Nome do lead para pré-preencher {{1}} (primeiro nome, capitalizado se veio todo maiúsculo/minúsculo).
  const primeiroNomeDoLead = (): string => {
    const contact = contacts.find((c) => c.id === selectedContact);
    let nome = (contact?.nome || "").trim().split(/\s+/)[0] || "Cliente";
    if (nome.length > 1 && (nome === nome.toUpperCase() || nome === nome.toLowerCase())) {
      nome = nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase();
    }
    return nome;
  };

  // Variáveis do TÍTULO ficam em tplVars com prefixo "h" ("h1"), porque o título e o corpo
  // numeram as variáveis de forma independente (os dois podem ter {{1}}).
  useEffect(() => {
    const hv = selectedTpl?.headerVars || [];
    if (!hv.length) return;
    setTplVars((prev) => {
      const faltam = hv.filter((n) => prev["h" + n] === undefined);
      if (!faltam.length) return prev;
      const novo = { ...prev };
      faltam.forEach((n) => { novo["h" + n] = n === "1" ? primeiroNomeDoLead() : ""; });
      return novo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTpl?.name, selectedTpl?.headerVars?.join(",")]);

  const loadAllTemplates = async (): Promise<ChatTemplate[]> => {
    const id_time = getResolvedTimeId();
    // MESMOS parâmetros que `sendSelectedTemplate` manda pro /enviar_template — a lista
    // tem que sair do número por onde a mensagem vai sair, senão o operador escolhe um
    // template que não existe na WABA de destino (Meta 132001).
    const contact = contacts.find((c) => c.id === selectedContact);
    const res = await fetch(`${API_URL}/chat/templates_db`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // fone: o backend resolve a instância VINCULADA AO LEAD (funil_fluxo.cliente_ins →
      // chats.ins) e lista só os templates dela. id_user/ins_nome ficam como FALLBACK
      // (lead novo, sem instância própria) e saem do mesmo `resolveEnvioIdUser` do envio,
      // pra lista e envio nunca divergirem se essa regra voltar a mudar.
      body: JSON.stringify({
        id_time,
        fone: contact?.fone || "",
        id_user: contact ? resolveEnvioIdUser(contact) : localStorage.getItem("id_user"),
        ins_nome: localStorage.getItem("instanciaoficial"),
      }),
    });
    if (!res.ok) throw new Error("Falha ao carregar templates");
    const data: any[] = await res.json();
    return data.map((t: any): ChatTemplate => {
      const body: string = t.body || "";
      const vars: string[] = Array.from(
        new Set((body.match(/\{\{(\d+)\}\}/g) || []).map((m: string) => m.replace(/\D/g, "")))
      ).sort((a, b) => Number(a) - Number(b));
      return {
        name: t.name,
        language: t.language || "pt_BR",
        body,
        headerText: "",
        headerFormat: t.headerFormat || "",
        headerImage: t.headerImage || "",
        vars,
        botoes: Array.isArray(t.botoes) ? t.botoes : [],
      };
    });
  };

  // Categorias (MARKETING/UTILITY) da WABA, em 2º plano: o modal abre com a lista do
  // banco na hora e o selo entra quando o mapa chega. Manda os MESMOS parâmetros da
  // lista/envio para o selo sair do número por onde a mensagem vai sair. Falha = sem
  // selo, nunca bloqueia o envio.
  const carregarCategoriasTemplates = async () => {
    const id_time = getResolvedTimeId();
    if (!id_time) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    try {
      const res = await fetch(`${API_URL}/chat/templates_categorias`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_time,
          fone: contact?.fone || "",
          id_user: contact ? resolveEnvioIdUser(contact) : localStorage.getItem("id_user"),
          ins_nome: localStorage.getItem("instanciaoficial"),
          v: 2,   // pede {categoria, status}; sem isso o back devolve só a categoria (forma antiga)
        }),
      });
      if (!res.ok) return;
      const bruto: Record<string, any> = await res.json();
      if (!bruto || typeof bruto !== "object") return;
      // Aceita as duas formas: string (back antigo) e objeto (v:2). Assim um deploy só do
      // front, ou um rollback só do back, não apaga o selo nem quebra a lista.
      const mapa: Record<string, { categoria?: string; status?: string; header?: string | null }> = {};
      Object.keys(bruto).forEach((k) => {
        const v = bruto[k];
        mapa[k] = typeof v === "string" ? { categoria: v } : (v || {});
      });
      // O título (header TEXTO) só existe na Meta — a lista local guarda só o corpo. Com
      // variável no título ({{1}}), a Meta exige o parâmetro; sem ele o envio dá 132000.
      const aplica = (t: ChatTemplate): ChatTemplate => {
        const m = mapa[t.name];
        if (!m) return t;
        const header = m.header || "";
        return {
          ...t,
          categoria: m.categoria ?? t.categoria,
          status: m.status ?? t.status,
          ...(header && !["IMAGE", "VIDEO", "DOCUMENT"].includes(t.headerFormat)
            ? { headerText: header, headerVars: varsDoTexto(header) } : {}),
        };
      };
      setTemplatesList((prev) => prev.map(aplica));
      setSelectedTpl((prev) => (prev ? aplica(prev) : prev));
    } catch (err) {
      console.warn("Categorias de template indisponíveis:", err);
    }
  };

  // Abre o modal de seleção de template (carrega a lista sob demanda).
  const openTemplateModal = async () => {
    if (!selectedContact) return;
    setSelectedTpl(null);
    setTplVars({});
    setTemplateSearch("");
    setShowTemplateModal(true);
    setLoadingTemplates(true);
    try {
      setTemplatesList(await loadAllTemplates());
      carregarCategoriasTemplates();   // 2º plano: pinta o selo quando a Meta responder
    } catch (err) {
      console.error(err);
      setShowTemplateModal(false);
      setModalMessage("Erro ao carregar os templates da conta.");
      setShowModal(true);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Seleciona um template e pré-preenche {{1}} com o primeiro nome do contato.
  const selectTemplate = (tpl: ChatTemplate) => {
    setSelectedTpl(tpl);
    const nomeLead = primeiroNomeDoLead();
    const init: Record<string, string> = {};
    tpl.vars.forEach((n) => { init[n] = n === "1" ? nomeLead : ""; });
    (tpl.headerVars || []).forEach((n) => { init["h" + n] = n === "1" ? nomeLead : ""; });
    setTplVars(init);
  };

  // Envia o template selecionado no modal, com as variáveis preenchidas.
  const sendSelectedTemplate = async () => {
    if (sendingTemplate || !selectedContact || !selectedTpl) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;
    const fone = contact.fone || "";
    const id_time = getResolvedTimeId();
    const id_user = localStorage.getItem("id_user");
    // Time 159: envia pelo número do dono do lead
    const envioIdUser = resolveEnvioIdUser(contact);
    const envioIns = ehTimeNumeroPorDono(contact.id_time)
      ? (usuariosDoTime.find((u) => String(u.id) === String(envioIdUser))?.ins ?? null)
      : null;
    if (!id_time) {
      setModalMessage("ID do time não encontrado.");
      setShowModal(true);
      return;
    }

    const headerVarNums = selectedTpl.headerVars || [];
    const faltando = [
      ...headerVarNums.filter((n) => !(tplVars["h" + n] || "").trim()).map((n) => `título {{${n}}}`),
      ...selectedTpl.vars.filter((n) => !(tplVars[n] || "").trim()).map((n) => `{{${n}}}`),
    ];
    if (faltando.length) {
      setModalMessage(`Preencha as variáveis: ${faltando.join(", ")}`);
      setShowModal(true);
      return;
    }

    const subst = (txt: string) => txt.replace(/\{\{(\d+)\}\}/g, (_m, n) => tplVars[n] ?? `{{${n}}}`);
    const renderedBody = subst(selectedTpl.body);

    // Componentes Meta: variáveis do body, na ordem.
    const bodyVarNums = Array.from(
      new Set((selectedTpl.body.match(/\{\{(\d+)\}\}/g) || []).map((m) => m.replace(/\D/g, "")))
    ).sort((a, b) => Number(a) - Number(b));
    const components: any[] = [];
    // Header de mídia (imagem): reutiliza a imagem aprovada do próprio template.
    if (selectedTpl.headerFormat === "IMAGE" && selectedTpl.headerImage) {
      components.push({ type: "header", parameters: [{ type: "image", image: { link: selectedTpl.headerImage } }] });
    } else if (headerVarNums.length) {
      // Título em texto com variável: a Meta exige o parâmetro (senão 132000).
      components.push({ type: "header", parameters: headerVarNums.map((n) => ({ type: "text", text: tplVars["h" + n] || "" })) });
    }
    if (bodyVarNums.length) {
      components.push({ type: "body", parameters: bodyVarNums.map((n) => ({ type: "text", text: tplVars[n] || "" })) });
    }

    setSendingTemplate(true);
    // Mensagem otimista ÚNICA: texto + imagem/vídeo do header + botões na MESMA bolha
    // (não cria mais a "linha de imagem" separada).
    const tempTs = Date.now();
    const ehMidia = (selectedTpl.headerFormat === "IMAGE" || selectedTpl.headerFormat === "VIDEO") && !!selectedTpl.headerImage;
    const tempMessage: Message = {
      id: `temp-${tempTs}`,
      status: "send",
      mensagem: renderedBody,
      timestamp: new Date(tempTs).toISOString(),
      type: "conversation",
      read: true,
      fone,
      template: true,
      ins: envioIns,
      template_midia: ehMidia ? selectedTpl.headerImage : null,
      template_botoes: (selectedTpl.botoes && selectedTpl.botoes.length) ? selectedTpl.botoes : null,
    };
    const tempMessages: Message[] = [tempMessage];
    setContacts((prev) =>
      prev.map((c) =>
        c.id === selectedContact
          ? { ...c, messages: [...(c.messages || []), ...tempMessages], lastTimestamp: tempTs }
          : c
      )
    );

    try {
      const res = await fetch(`${API_URL}/enviar_template`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone,
          id_time,
          id_user: envioIdUser,
          template_name: selectedTpl.name,
          language: selectedTpl.language,
          components,
          rendered_body: renderedBody,
        }),
      });
      if (!res.ok) throw new Error(await motivoFalhaEnvio(res, "Erro ao enviar o template."));
      await avisarSeNaoSalvouHistorico(res);
      setShowTemplateModal(false);
      setSelectedTpl(null);
      setTplVars({});
    } catch (err: any) {
      console.error(err);
      setModalMessage(err?.message || "Erro ao enviar template.");
      setShowModal(true);
      const tempIds = new Set(tempMessages.map((m) => m.id));
      setContacts((prev) =>
        prev.map((c) =>
          c.id === selectedContact
            ? { ...c, messages: (c.messages || []).filter((m) => !tempIds.has(m.id)) }
            : c
        )
      );
    } finally {
      setSendingTemplate(false);
    }
  };

  // ─── Gravação de áudio (push-to-talk) ──────────────────────────
  const formatRecordingTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const startRecordingTimer = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = setInterval(() => {
      setRecordingDuration((d) => d + 1);
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const cleanupRecording = () => {
    stopRecordingTimer();
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
  };

  const startRecording = async () => {
    if (isRecording || uploading || shouldUseTemplate()) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setModalMessage("Seu navegador não suporta gravação de áudio.");
      setShowModal(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      recordingCanceledRef.current = false;

      // Preferir OGG/OPUS (nota de voz nativa); fallback para WebM/OPUS.
      const candidates = [
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/webm",
      ];
      const mimeType = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      audioMimeRef.current = mimeType;

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const wasCanceled = recordingCanceledRef.current;
        const chunks = [...audioChunksRef.current];
        const usedMime = audioMimeRef.current || "audio/webm";
        cleanupRecording();
        if (wasCanceled || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: usedMime });
        const ext = usedMime.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
        sendMediaFile(file, "audio");
      };

      recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      startRecordingTimer();
    } catch (err) {
      console.error("Erro ao iniciar gravação:", err);
      setModalMessage("Permissão de microfone negada ou erro ao iniciar.");
      setShowModal(true);
      cleanupRecording();
    }
  };

  const pauseRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording || isPaused) return;
    try {
      r.pause();
      setIsPaused(true);
      stopRecordingTimer();
    } catch (e) {
      console.error("Erro ao pausar gravação:", e);
    }
  };

  const resumeRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording || !isPaused) return;
    try {
      r.resume();
      setIsPaused(false);
      startRecordingTimer();
    } catch (e) {
      console.error("Erro ao retomar gravação:", e);
    }
  };

  const sendRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording) return;
    recordingCanceledRef.current = false;
    try {
      if (isPaused) r.resume(); // garante que o último chunk seja emitido
      r.stop(); // dispara onstop → envia
    } catch (e) {
      console.error("Erro ao parar/enviar gravação:", e);
      cleanupRecording();
    }
  };

  const cancelRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording) return;
    recordingCanceledRef.current = true;
    try {
      if (isPaused) r.resume();
      r.stop(); // dispara onstop → descarta porque canceled=true
    } catch (e) {
      console.error("Erro ao cancelar gravação:", e);
      cleanupRecording();
    }
  };

  // Cleanup do mic ao desmontar o componente
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());
      try { mediaRecorderRef.current?.stop(); } catch {}
    };
  }, []);


  const markMessagesAsRead = async () => {
    // Se o usuário marcou esta conversa como NÃO lida manualmente, não re-marcar como
    // lida enquanto ela continua aberta (scroll até o fim / "ver novas mensagens"). O
    // flag só é resetado ao reabrir a conversa (useEffect de [selectedContact]).
    if (suprimirAutoLeituraRef.current) return;
    const contatoAberto = contacts.find((contact) => contact.id === selectedContact);
    const selectedContactFone = contatoAberto?.fone;
    if (!selectedContactFone) return;
    // Modo "espiar": com ?nao_ler=1 na URL (ex.: /chat?fone=55...&nao_ler=1) abrir conversas NÃO
    // marca nada como lido — quem supervisiona olha sem consumir o não-lido do atendente.
    // Vale enquanto o parâmetro estiver na URL. /marcarlidos é a única rota que marca lido.
    const qs = new URLSearchParams(window.location.search);
    if (["1", "true", "sim"].includes((qs.get("nao_ler") || "").toLowerCase())) return;
    // Admin (de time ou master) marca como lido APENAS os leads que são dele — ou seja,
    // quando o dono do contato (contact.id_user) é o próprio admin. Abrir o lead de outro
    // atendente não "consome" o não-lido dele, que é o ponto: o admin espia sem atrapalhar.
    // Antes, admin não marcava NADA — nem a própria carteira, o que deixava os leads dele
    // sempre em negrito.
    // Lead sem dono (id_user null) segue sem ser marcado: não é do admin.
    // EXCEÇÃO mantida: time 159 — ali o admin marca qualquer lead ao abrir (id_cliente = id_time).
    const ehTime159 = localStorage.getItem("id_cliente") === "159";
    const ehAdmin = localStorage.getItem("isAdmin") === "true" || localStorage.getItem("isAdminMaster") === "true";
    if (ehAdmin && !ehTime159) {
      const meuIdUser = localStorage.getItem("id_user");
      const donoDoLead = contatoAberto?.id_user;
      const ehMeuLead = donoDoLead != null && meuIdUser != null && String(donoDoLead) === String(meuIdUser);
      if (!ehMeuLead) return;
    }
    try {
      // Escopo por GRUPO: a contagem de não-lidas do /contacts_full é por fone no
      // grupo inteiro, então marcar lido só no id_time deixava não-lidas presas em
      // outro time do mesmo grupo (badge voltava no refetch, sem mensagem nova).
      // id_time vai junto de propósito: se o api.py em produção ainda for o antigo,
      // ele ignora id_grupo e usa id_time — sem esse fallback cairia no fone-only,
      // que marca lido em TODOS os tenants com o mesmo fone.
      const resp = await fetch(`${API_URL}/marcarlidos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone: selectedContactData?.fone || "",
          id_grupo: localStorage.getItem("id_grupo"),
          id_time: selectedContactData?.id_time,
        }),
      });
      // Só zera o badge se o banco confirmou. Antes zerava sempre: com o read-only
      // intermitente do pooler (a rota mais atingida), o badge sumia da tela e
      // voltava no refetch seguinte, parecendo mensagem nova.
      if (!resp.ok) throw new Error(`/marcarlidos falhou (${resp.status})`);
      setContacts((prev) =>
        prev.map((contact) =>
          contact.id === selectedContact ? { ...contact, unread_count: 0 } : contact
        )
      );
    } catch (error) {
      console.error("Erro ao marcar mensagens como lidas", error);
    }
  };

  const createNewContact = async (name: string, phone: string) => {
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) {
      console.error("ID do cliente não encontrado");
      return;
    }
    try {
      const response = await fetch(`${API_URL}/criarcontatonovo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente, name, phone }),
      });
      if (!response.ok) {
        throw new Error("Erro ao criar contato.");
      }
      const data = await response.json();
      // Aqui você pode atualizar a UI ou mostrar uma mensagem de sucesso se desejar.
    } catch (error) {
      console.error("Erro ao criar contato:", error);
    }
  };

  const handleCreateManualContact = async () => {
    // Time de DESTINO: o escolhido pelo admin master, senao o do login.
    const id_cliente = newContactTime || localStorage.getItem("id_cliente");
    const id_user = localStorage.getItem("id_user");
    setCreateContactError(null);
    if (!id_cliente || !newContactName || !newContactPhone || !newContactFunnel || !newContactStage) {
      setCreateContactError("Preencha todos os campos.");
      return;
    }
    // DDI + (DDD + numero). Tudo so digito; o back ainda normaliza pela uazapi.
    const ddi = (newContactDDI || "55").replace(/\D/g, "");
    const local = newContactPhone.replace(/\D/g, "");
    if (!ddi) { setCreateContactError("Informe o código do país."); return; }
    if (local.length < 10) {
      setCreateContactError("Telefone incompleto — informe DDD + número (sem o código do país).");
      return;
    }
    // Guarda contra o erro classico: colar o numero ja com o 55 na frente.
    if (ddi === "55" && local.length > 11) {
      setCreateContactError("Telefone longo demais. Aqui vai só DDD + número — o 55 já está no campo ao lado.");
      return;
    }
    const foneCompleto = ddi + local;
    // Com mais de um número no time, a escolha é obrigatória: deixar o back adivinhar pelo
    // login é o que gera lead com cliente_ins errado e, depois, recusa da Meta por janela.
    if (instanciasDoTime.length > 1 && !newContactIns) {
      setCreateContactError("Selecione o número de atendimento.");
      return;
    }

    setCreatingContact(true);
    try {
      const response = await fetch(`${API_URL}/criar_contato_manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente,
          id_user,
          nome: newContactName,
          fone: foneCompleto,
          funil: newContactFunnel,
          etapa: newContactStage,
          // Só manda quando há escolha — o back valida contra as instâncias do grupo e,
          // se vier vazio, mantém o comportamento antigo (deriva do login).
          ...(newContactIns ? { cliente_ins: newContactIns } : {}),
        }),
      });

      // Tenta parsear o JSON sempre — backend retorna {error, code} em todos os erros
      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        // Mensagens específicas por código (definidos no backend)
        const code = data?.code;
        let msg: string;
        switch (code) {
          case "no_whatsapp":
            msg = `Esse número não tem WhatsApp${data?.fone ? ` (${data.fone})` : ""}.`;
            break;
          case "duplicate":
            msg = "Esse número já está cadastrado neste time.";
            break;
          case "funnel_not_found":
            msg = data?.error || "Funil não encontrado.";
            break;
          case "stage_not_found":
            msg = data?.error || "Etapa não encontrada para o funil selecionado.";
            break;
          case "missing_fields":
            msg = "Preencha todos os campos obrigatórios.";
            break;
          default:
            // Fallback: usa o `error` do backend ou mensagem por status HTTP
            if (data?.error) msg = data.error;
            else if (response.status >= 500) msg = "Erro interno no servidor. Tente novamente.";
            else msg = `Falha ao criar o contato (HTTP ${response.status}).`;
        }
        setCreateContactError(msg);
        return;
      }

      // Sucesso: limpa formulário e fecha modal
      setShowNewContactModal(false);
      setNewContactName("");
      setNewContactPhone("");
      setNewContactFunnel("");
      setNewContactStage("");
      setNewContactIns("");
      setNewContactDDI("55");

      // Atualiza lista de contatos
      fetchContactsFull();
    } catch (error: any) {
      console.error("Erro ao criar contato:", error);
      setCreateContactError(
        error?.message ? `Erro de conexão: ${error.message}` : "Erro de conexão ao criar contato."
      );
    } finally {
      setCreatingContact(false);
    }
  };


  const updateObservacao = async (phone: string, observation: string) => {
    try {
      const id_cliente = localStorage.getItem("id_cliente");
      const payload = {
        fone: phone,
        observacao: observation,
        id_cliente,
      };
  
      const response = await fetch(`${API_URL}/marcarobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Erro ao atualizar observação");
      }
      const data = await response.json();
      alert("Observação atualizada com sucesso!");
    } catch (error) {
      console.error("Erro ao atualizar observação:", error);
      alert("Erro ao atualizar observação");
    }
  };  


  const handleCombinedDropdownClick = () => {
    if (combinedDropdownButtonRef.current) {
      const rect = combinedDropdownButtonRef.current.getBoundingClientRect();
      setCombinedDropdownStyle({
        position: "fixed",
        top: rect.bottom + 5, // 5px abaixo do botão
        left: rect.left,
        zIndex: 1100,
      });
    }
    setShowCombinedDropdown(true);
  };

  const combinedOptions = funnels.map(funnel => ({
    funnel: funnel.nome_funil,
    stages: stages.filter(stage => stage.funil === funnel.nome_funil)
  }));

  const atualizarIdioma = async (fone: string, idioma: string) => {
    try {
      await fetch(`${API_URL}/atualizar_idioma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // id_time do CONTATO, nao do login: o mesmo telefone existe em mais de um time
        // (medido na base: 567 fones em mais de um time, 421 em mais de um GRUPO) e o
        // UPDATE sem escopo trocava o idioma do contato de outros clientes junto.
        body: JSON.stringify({ fone, idioma, id_time: selectedContactData?.id_time }),
      });
      // Atualizar estado ou feedback opcional
    } catch (err) {
      console.error("Erro ao atualizar idioma:", err);
    }
  };




  useEffect(() => {
    fetchStages();
    fetchFunnels();
    fetchGroupStages();
  }, []);
  
  


  useEffect(() => {
    // Verifica se já está conectado (armazenado no localStorage)
    const alreadyConnected = localStorage.getItem("isConnected") === "true";
    if (alreadyConnected) {
      setIsConnected(true);
    }
  }, []);
  
  // (Removido efeito de sessionStorage para evitar cache obsoleto e problemas de hidratação)
  
  
  
  


  const genericAvatar = "assets/img/default-avatar.png"; // Avatar genérico


  const selectedContactData = contacts.find(
    (contact) => contact.id === selectedContact
  );

  // "Está subindo mídia NESTA conversa?" — usado para o aviso de progresso no rodapé
  // e para as ações que competem pelo MESMO upload (gravar/reenviar áudio, confirmar
  // a prévia). Escrever e enviar TEXTO não entra nessa lista: o operador precisa
  // seguir conversando enquanto o documento sobe. Um upload em outra conversa não
  // toca nesta (ver fonesEnviando).
  const uploading = !!selectedContactData?.fone && fonesEnviando.includes(String(selectedContactData.fone));

  // Time DONO do lead aberto — mesmo padrão já usado nas ações do header.
  // Só o admin master vê leads de outros times; para ele, o card e a promessa
  // precisam seguir o time do LEAD, senão leem e GRAVAM no time do login (e o
  // valor some da vista de quem loga no time real). Atendente e admin de time
  // sempre operam no próprio time, então o fallback os deixa inalterados.
  const timeDoLeadAtual = () =>
    (isAdminMaster && selectedContactData?.id_time)
      ? String(selectedContactData.id_time)
      : (localStorage.getItem("id_cliente") || "");

  // ── Seletor Funil → Etapa (parte que depende do lead selecionado) ──────────
  // Ver o bloco `stageOptions` / `moverLeadParaEtapa` acima para as duas regras:
  // trocar o funil não grava, e admin master pode escolher funil de outro time.
  const currentFunnelKey = pendingFunnelKey
    ?? funnelKeyOf(selectedContactData?.id_time, selectedContactData?.funil || "");

  // Com um funil pendente, a etapa fica em branco — é ela que dispara a gravação.
  const stageSelectValue = pendingFunnelKey !== null
    ? ""
    : String(stageOptions.find(
        (o) => funnelKeyOf(o.id_time, o.funil) === currentFunnelKey
               && o.nome_funil === (selectedContactData?.nome_funil || "")
      )?.id_etapa ?? "");

  // Trocar de lead descarta a escolha de funil pendente.
  useEffect(() => {
    setPendingFunnelKey(null);
  }, [selectedContactData?.fone]);

  // Selects Funil + Etapa — usados no header e no modal Info, com estilos próprios.
  const renderFunilEtapaSelects = (selectStyle: React.CSSProperties, className: string) => {
    const etapasDoFunil = stageOptions.filter(
      (o) => funnelKeyOf(o.id_time, o.funil) === currentFunnelKey
    );
    const fone = selectedContactData?.fone || "";
    // Agrupa por time só quando há mais de um time em jogo (admin master).
    const agrupaPorTime = isAdminMaster && groupStages.length > 0;
    const funisPorTime: { nome_time: string; itens: typeof funnelOptions }[] = [];
    if (agrupaPorTime) {
      funnelOptions.forEach((f) => {
        const nome = f.nome_time || "—";
        const grupo = funisPorTime.find((g) => g.nome_time === nome);
        if (grupo) grupo.itens.push(f);
        else funisPorTime.push({ nome_time: nome, itens: [f] });
      });
    }

    return (
      <>
        {/* Funil — trocar aqui NÃO grava; só recarrega as etapas */}
        <select
          className={className}
          style={selectStyle}
          value={currentFunnelKey}
          onChange={(e) => setPendingFunnelKey(e.target.value)}
          title="Funil"
        >
          <option value="|" disabled>Funil</option>
          {agrupaPorTime
            ? funisPorTime.map((g) => (
                <optgroup key={g.nome_time} label={g.nome_time}>
                  {g.itens.map((f) => (
                    <option key={f.key} value={f.key}>{f.funil}</option>
                  ))}
                </optgroup>
              ))
            : funnelOptions.map((f) => (
                <option key={f.key} value={f.key}>{f.funil}</option>
              ))}
        </select>

        {/* Etapa — é a escolha que efetivamente move o lead */}
        <select
          className={className}
          style={selectStyle}
          value={stageSelectValue}
          onChange={(e) => moverLeadParaEtapa(fone, e.target.value)}
          title={pendingFunnelKey !== null ? "Escolha a etapa de destino" : "Etapa"}
        >
          <option value="" disabled>
            {pendingFunnelKey !== null ? "Escolha a etapa…" : "Etapa"}
          </option>
          {etapasDoFunil.map((o) => (
            <option key={o.id_etapa} value={o.id_etapa}>{o.nome_funil}</option>
          ))}
        </select>
      </>
    );
  };

  // ── Documentos / retenção (TNP, grupo 192) ─────────────────────────────────
  // Duas coisas diferentes, propositalmente separadas:
  //   validação do ANEXO  -> "este arquivo serve?"   (chats.doc_validado)
  //   checklist do LEAD   -> "este documento chegou?" (campo tipo checklist do CARD)
  // O ✓/✗ da bolha NÃO classifica a que item do checklist o arquivo pertence.
  //
  // O checklist NÃO tem armazenamento próprio: vem dos cards (card_campos tipo
  // 'checklist' + card_valores), configurados pelo /card-builder. O CardViewModal
  // entrega os pendentes por callback ao clicar em "Registrar promessa".
  const [showPromessaModal, setShowPromessaModal] = useState(false);
  const [promessaPendentes, setPromessaPendentes] = useState<string[]>([]);
  const [promessaRecebidos, setPromessaRecebidos] = useState(0);
  const [promessaSelecionados, setPromessaSelecionados] = useState<string[]>([]);
  const [promessaData, setPromessaData] = useState("");
  const [promessaCarregando, setPromessaCarregando] = useState(false);
  const [salvandoPromessa, setSalvandoPromessa] = useState(false);
  // Promessa em aberto que o lead JÁ tem (GET /docs/promessa). Serve para avisar a
  // operadora antes de registrar outra, já que salvar substitui a anterior.
  const [promessaAtual, setPromessaAtual] = useState<any>(null);
  // Confirmação de entrega dos documentos prometidos (fecha a promessa).
  const [confirmandoEntrega, setConfirmandoEntrega] = useState(false);
  const [promessaCumpridaAviso, setPromessaCumpridaAviso] = useState("");
  // ids de mensagem com validação em voo (evita clique duplo)
  const [validandoAnexo, setValidandoAnexo] = useState<string[]>([]);
  // Giro de imagem recebida: ids das mensagens com giro em andamento (trava o duplo clique)
  const [girandoImagem, setGirandoImagem] = useState<string[]>([]);

  const docsIdGrupo = localStorage.getItem("id_grupo") || "";
  const docsIdUser = localStorage.getItem("id_user") || "";

  // Data de hoje em BRT, no formato do <input type="date">
  const docsHojeISO = () => {
    const agora = new Date();
    const brt = new Date(agora.getTime() - agora.getTimezoneOffset() * 60000);
    return brt.toISOString().slice(0, 10);
  };

  const docsSomarDias = (dias: number) => {
    const d = new Date(`${docsHojeISO()}T12:00:00`);
    d.setDate(d.getDate() + dias);
    return d.toISOString().slice(0, 10);
  };

  const docsFormatarBR = (iso: string) =>
    iso ? iso.split("-").reverse().join("/") : "";

  // O GET /docs/promessa serializa data_prometida via Flask, que pode devolver
  // "YYYY-MM-DD" ou o formato RFC ("Fri, 25 Jul 2026 00:00:00 GMT"). Formata os
  // dois em dd/mm/aaaa lendo em UTC (a data é pura, sem hora, não deslocar o dia).
  const docsFmtPromessaData = (valor: any) => {
    if (!valor) return "";
    const d = new Date(valor);
    if (!isNaN(d.getTime())) {
      const dia = String(d.getUTCDate()).padStart(2, "0");
      const mes = String(d.getUTCMonth() + 1).padStart(2, "0");
      return `${dia}/${mes}/${d.getUTCFullYear()}`;
    }
    return docsFormatarBR(String(valor).slice(0, 10));
  };

  // Busca a promessa em aberto do lead. Falha silenciosa: é só um aviso, não pode
  // travar a abertura do modal se a rota estiver fora do ar.
  const carregarPromessaAtual = async (fone: string) => {
    if (!docsIdGrupo || !fone) { setPromessaAtual(null); return; }
    try {
      const res = await fetch(
        `${API_URL}/docs/promessa?id_cliente=${encodeURIComponent(docsIdGrupo)}&fone=${encodeURIComponent(fone)}`
      );
      const row = res.ok ? await res.json() : null;
      setPromessaAtual(row && row.id ? row : null);
    } catch {
      setPromessaAtual(null);
    }
  };

  // Envios programados do lead selecionado. Roda a cada troca de contato; falha
  // de rede deixa o relogio fosco em vez de quebrar o header.
  const carregarAgenda = async (fone?: string | null, idTime?: string | number | null) => {
    if (!fone || !idTime) { setAgenda(null); return; }
    setAgendaCarregando(true);
    try {
      const res = await fetch(
        `${API_URL}/lead/agendamentos?id_time=${encodeURIComponent(String(idTime))}&fone=${encodeURIComponent(String(fone))}`
      );
      const d = res.ok ? await res.json() : null;
      setAgenda(d && Array.isArray(d.itens) ? d : null);
    } catch {
      setAgenda(null);
    } finally {
      setAgendaCarregando(false);
    }
  };

  useEffect(() => {
    if (!selectedContactData?.fone) { setAgenda(null); return; }
    carregarAgenda(selectedContactData.fone, selectedContactData.id_time || timeDoLeadAtual());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact, selectedContactData?.fone, selectedContactData?.id_time, selectedContactData?.nome_funil]);

  // Valida um anexo recebido: true = aceito, false = recusado, null = desfaz.
  // Atualiza a bolha na hora e reverte se o backend recusar.
  const validarAnexo = async (
    message: Message,
    valor: boolean | null
  ) => {
    const chaveMsg = String(message.id);
    if (validandoAnexo.includes(chaveMsg)) return;
    const anterior = message.doc_validado ?? null;
    // Clicar de novo no botão já aceso volta ao estado neutro.
    const novo = anterior === valor ? null : valor;

    setValidandoAnexo((prev) => [...prev, chaveMsg]);
    const aplicar = (v: boolean | null) =>
      setContacts((prev) =>
        prev.map((c) => {
          if (c.id !== selectedContact) return c;
          return {
            ...c,
            messages: (c.messages || []).map((mm) =>
              String(mm.id) === chaveMsg ? { ...mm, doc_validado: v } : mm
            ),
          };
        })
      );

    aplicar(novo);
    try {
      const res = await fetch(`${API_URL}/chat/validar_anexo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: docsIdGrupo,      // grupo
          fone: message.fone,
          mensagem_id: message.id,
          validacao: novo,
        }),
      });
      if (!res.ok) throw new Error("Falha ao validar anexo");
    } catch (err) {
      console.error(err);
      aplicar(anterior);               // devolve a bolha ao estado anterior
      setModalMessage("Erro ao registrar a validação do anexo.");
      setShowModal(true);
    } finally {
      setValidandoAnexo((prev) => prev.filter((k) => k !== chaveMsg));
    }
  };

  // ── Girar imagem recebida ───────────────────────────────────────────────
  // Foto de documento chega deitada quando o lead fotografa com o celular virado.
  // Girar aqui é definitivo: o backend grava a versão girada como o novo arquivo da
  // mensagem, então todo mundo da equipe passa a ver a foto em pé. Só o grupo 192
  // (TNP) tem os botões — mesmo gate do ícone de transferência (linha ~394).
  const podeGirarImagem = docsIdGrupo === "192";

  const girarImagem = async (message: Message, sentido: "horario" | "antihorario") => {
    const chaveMsg = String(message.id);
    if (girandoImagem.includes(chaveMsg)) return;

    setGirandoImagem((prev) => [...prev, chaveMsg]);
    try {
      const res = await fetch(`${API_URL}/girar_imagem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: docsIdGrupo,
          mensagem_id: message.id,
          sentido,
        }),
      });
      const dados = res.ok ? await res.json() : null;
      if (!res.ok || !dados?.url) throw new Error("Falha ao girar a imagem");
      const nova: string = dados.url;

      // Atualiza as DUAS fontes. `messages` é o que está na tela; `contacts` é de
      // onde o polling repõe a conversa. A assinatura do sync (~linha 5505) não
      // olha `mensagem`, então mexer só em `contacts` não repintaria a bolha aberta
      // — e mexer só em `messages` voltaria atrás ao trocar de conversa.
      setMessages((prev) =>
        prev.map((mm) => (String(mm.id) === chaveMsg ? { ...mm, mensagem: nova } : mm))
      );
      setContacts((prev) =>
        prev.map((c) => {
          if (c.id !== selectedContact) return c;
          return {
            ...c,
            messages: (c.messages || []).map((mm: Message) =>
              String(mm.id) === chaveMsg ? { ...mm, mensagem: nova } : mm
            ),
          };
        })
      );
      // Se a imagem está aberta em tela cheia, troca ali também (senão o operador
      // gira e continua vendo a versão antiga até fechar o modal).
      setFullscreenMedia((prev) =>
        prev && prev.msgId === chaveMsg ? { ...prev, src: nova } : prev
      );
    } catch (err) {
      console.error(err);
      setModalMessage("Erro ao girar a imagem.");
      setShowModal(true);
    } finally {
      setGirandoImagem((prev) => prev.filter((k) => k !== chaveMsg));
    }
  };

  // Varre TODOS os checklists do card do lead (todos os funis e grupos) e separa
  // o que já foi marcado do que ainda falta. Mesma fonte que o CardViewModal usa:
  // card_campos tipo 'checklist' + card_valores.
  const carregarChecklistsDoCard = async (fone: string) => {
    const idCliente = timeDoLeadAtual();   // time do LEAD, igual ao que o card usa
    const pendentes: string[] = [];
    const jaMarcados: string[] = [];
    if (!idCliente) return { pendentes, recebidos: 0 };

    const [funis, valores] = await Promise.all([
      fetch(`${API_URL}/card/funnels?id_cliente=${idCliente}`).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API_URL}/card/values?id_cliente=${idCliente}&fone=${fone}`).then((r) => (r.ok ? r.json() : [])),
    ]);

    const valorPorCampo = new Map<number, string>();
    (valores || []).forEach((v: any) => valorPorCampo.set(v.id_campo, v.valor));

    // Um mesmo grupo pode estar ligado a vários funis — busca cada um só uma vez.
    const gruposVistos = new Set<number>();
    const listasDeGrupos = await Promise.all(
      (funis || []).map((f: any) =>
        fetch(`${API_URL}/card/groups?id_funil_pai=${f.id}&id_cliente=${idCliente}`)
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => [])
      )
    );
    const gruposUnicos: any[] = [];
    listasDeGrupos.flat().forEach((g: any) => {
      if (g && !gruposVistos.has(g.id)) {
        gruposVistos.add(g.id);
        gruposUnicos.push(g);
      }
    });

    const camposPorGrupo = await Promise.all(
      gruposUnicos.map((g) =>
        fetch(`${API_URL}/card/fields?id_grupo=${g.id}`)
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => [])
      )
    );

    camposPorGrupo.flat().forEach((campo: any) => {
      if (!campo || campo.tipo !== "checklist") return;
      const opcoes = String(campo.opcoes || "").split(",").map((o: string) => o.trim()).filter(Boolean);
      let marcados: string[] = [];
      try {
        const p = JSON.parse(valorPorCampo.get(campo.id) || "[]");
        if (Array.isArray(p)) marcados = p;
      } catch { marcados = []; }
      opcoes.forEach((opt: string) => {
        // O mesmo documento pode aparecer em mais de um grupo — não duplica na lista.
        if (marcados.includes(opt)) {
          if (!jaMarcados.includes(opt)) jaMarcados.push(opt);
        } else if (!pendentes.includes(opt)) {
          pendentes.push(opt);
        }
      });
    });

    // Marcado em qualquer grupo conta como recebido, e some dos pendentes.
    return {
      pendentes: pendentes.filter((p) => !jaMarcados.includes(p)),
      recebidos: jaMarcados.length,
    };
  };

  // Abre a promessa com os pendentes já em mãos (vindo do CardViewModal).
  const abrirPromessaComDados = (pendentes: string[], recebidos: number) => {
    setPromessaPendentes(pendentes);
    setPromessaRecebidos(recebidos);
    setPromessaSelecionados([]);
    setPromessaData("");
    setPromessaCarregando(false);
    setPromessaAtual(null);
    setPromessaCumpridaAviso("");
    if (selectedContactData?.fone) carregarPromessaAtual(selectedContactData.fone);
    setShowPromessaModal(true);
  };

  // Botão Promessa do header: busca os checklists do card antes de abrir.
  const abrirPromessaModal = async () => {
    if (!selectedContactData) return;
    setPromessaPendentes([]);
    setPromessaRecebidos(0);
    setPromessaSelecionados([]);
    setPromessaData("");
    setPromessaAtual(null);
    setPromessaCumpridaAviso("");
    setPromessaCarregando(true);
    setShowPromessaModal(true);
    carregarPromessaAtual(selectedContactData.fone);
    try {
      const { pendentes, recebidos } = await carregarChecklistsDoCard(selectedContactData.fone);
      setPromessaPendentes(pendentes);
      setPromessaRecebidos(recebidos);
    } catch (err) {
      console.error(err);
      setModalMessage("Erro ao carregar os documentos do card.");
      setShowModal(true);
    } finally {
      setPromessaCarregando(false);
    }
  };

  // Confirma que os documentos prometidos chegaram: fecha a promessa em aberto
  // (status 'cumprida') e o lead deixa de ter data de promessa — some do aviso aqui,
  // do GET /docs/promessa e da cobrança do promessa-worker (que só olha 'aberta').
  // Não marca o checklist do card: documento recebido continua sendo marcado no card.
  const confirmarEntregaPromessa = async () => {
    if (!selectedContactData || !promessaAtual) return;
    setConfirmandoEntrega(true);
    try {
      const res = await fetch(`${API_URL}/docs/promessa/cumprir`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: docsIdGrupo,
          fone: selectedContactData.fone,
          // Trava de corrida: fecha ESTA promessa, não uma registrada no meio-tempo.
          id_promessa: promessaAtual.id,
          id_user: docsIdUser,
        }),
      });
      if (!res.ok) {
        const erro = await res.json().catch(() => ({}));
        throw new Error(erro.error || "Falha ao confirmar a entrega dos documentos");
      }
      setPromessaCumpridaAviso(
        `Entrega confirmada. A promessa de ${docsFmtPromessaData(promessaAtual.data_prometida)} foi encerrada e este cliente não tem mais data de promessa.`
      );
      setPromessaAtual(null);
      // Limpar é limpar tudo: some a promessa e o formulário volta ao zero, senão
      // sobrariam documentos marcados/data digitada sugerindo uma promessa que não
      // existe mais.
      setPromessaSelecionados([]);
      setPromessaData("");
    } catch (err: any) {
      console.error(err);
      setModalMessage(err?.message || "Erro ao confirmar a entrega dos documentos.");
      setShowModal(true);
    } finally {
      setConfirmandoEntrega(false);
    }
  };

  const salvarPromessa = async () => {
    if (!selectedContactData || !promessaData || promessaSelecionados.length === 0) return;
    setSalvandoPromessa(true);
    try {
      const res = await fetch(`${API_URL}/docs/promessa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: docsIdGrupo,
          fone: selectedContactData.fone,
          data: promessaData,
          documentos: promessaSelecionados,
          id_user: docsIdUser,
        }),
      });
      if (!res.ok) {
        const erro = await res.json().catch(() => ({}));
        throw new Error(erro.error || "Falha ao salvar promessa");
      }
      setShowPromessaModal(false);
      setPromessaSelecionados([]);
      setPromessaData("");
    } catch (err: any) {
      console.error(err);
      setModalMessage(err?.message || "Erro ao registrar a promessa.");
      setShowModal(true);
    } finally {
      setSalvandoPromessa(false);
    }
  };

  // 📜 Auto-scroll effect
  useEffect(() => {
    if (selectedContact && scrollbarsRef.current) {
      setTimeout(() => {
        scrollbarsRef.current?.scrollToBottom();
      }, 100);
    }
  }, [selectedContact, selectedContactData?.messages?.length, pendingMessages.length]);



  // ADD: util p/ inferir mimetype quando faltar
  const MIME_POR_EXTENSAO: Record<string, string> = {
    pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", mp4: "video/mp4",
    mp3: "audio/mpeg", ogg: "audio/ogg", oga: "audio/ogg", opus: "audio/opus", m4a: "audio/mp4",
    aac: "audio/aac", amr: "audio/amr", wav: "audio/wav", webm: "audio/webm",
    doc: "application/msword", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint", pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain", csv: "text/csv",
  };
  const guessMime = (file: File) =>
    file.type || MIME_POR_EXTENSAO[(file.name.split(".").pop() || "").toLowerCase()] || "application/octet-stream";

  // Arquivo de áudio (pelo tipo ou pela extensão): pode ir como áudio gravado (voz).
  const ehArquivoDeAudio = (file: File) => {
    const mime = guessMime(file).toLowerCase();
    return mime.startsWith("audio/") || /\.(mp3|ogg|oga|opus|m4a|aac|amr|wav|wma|flac)$/i.test(file.name);
  };

  // Áudio pré-gravado (29/10/2026): o atendente escolhe mandar como ÁUDIO GRAVADO (padrão —
  // chega como se tivesse gravado no celular, mesmo caminho do botão de gravar) ou como ARQUIVO.
  const perguntarModoAudio = async (qtd: number): Promise<"audio" | "document" | null> => {
    const r = await Swal.fire({
      title: qtd === 1 ? "Como enviar este áudio?" : `Como enviar estes ${qtd} áudios?`,
      input: "radio",
      inputOptions: {
        audio: "🎙️ Áudio gravado (como se tivesse gravado no celular)",
        document: "📎 Arquivo",
      },
      inputValue: "audio",
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0f9d58",
    });
    if (!r.isConfirmed) return null;
    return r.value === "document" ? "document" : "audio";
  };

  // ADD: envia mídia para seu backend, que retransmite à API externa
  // ── Falha de envio: motivo real na tela ─────────────────────────────────────
  // O aviso era sempre genérico ("Erro ao enviar mídia."), então o operador via a
  // bolha sumir sem saber por quê — o motivo só existia no log do servidor. O
  // backend devolve o corpo do provedor (Meta: {error:{message,code}}; uazapi:
  // {error:"..."}), e é isso que mostramos aqui. A mesma falha vai para o
  // api_audit_log com categoria 'envio_falha'.
  const motivoFalhaEnvio = async (res: Response, prefixo: string) => {
    try {
      const body = await res.clone().json();
      const err = body?.error;
      const msg =
        (typeof err === "string" ? err : err?.message) ||
        body?.message ||
        body?.detalhes?.error?.message;
      const code = typeof err === "object" ? err?.code : undefined;
      if (msg) return `${prefixo}\n\nMotivo: ${msg}${code ? ` (código ${code})` : ""}`;
    } catch { /* corpo não-JSON: cai no HTTP puro */ }
    return `${prefixo}\n\n(HTTP ${res.status})`;
  };

  // Enviado ao cliente MAS não gravado no histórico (ex.: banco em read-only).
  // Sem este aviso a mensagem chega no WhatsApp e simplesmente não aparece no CRM.
  const avisarSeNaoSalvouHistorico = async (res: Response) => {
    try {
      const body = await res.clone().json();
      if (body && body.salvo_no_historico === false) {
        setModalMessage(
          "A mensagem FOI ENVIADA ao cliente, mas não foi salva no histórico do CRM " +
          "(falha no banco). Ela não vai aparecer nesta conversa — não reenvie, o " +
          "cliente já recebeu."
        );
        setShowModal(true);
        return true;
      }
    } catch { /* sem corpo JSON = nada a avisar */ }
    return false;
  };

  const sendMediaFile = async (
    file: File,
    mediatype: "image" | "video" | "document" | "audio",
    tempIdExterno?: string,   // se vier, o balão temp já foi criado (fila) — não cria de novo
  ) => {
    if (!selectedContact) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;

    const instanceName = localStorage.getItem("instanceName");
    const id_cliente = localStorage.getItem("id_cliente");
    const id_user = localStorage.getItem("id_user");
    const instanciaOficial = localStorage.getItem("instanciaoficial");

    // Time 159: envia pelo número do dono do lead
    const envioIdUser = resolveEnvioIdUser(contact);
    const envioIns = ehTimeNumeroPorDono(contact.id_time)
      ? (usuariosDoTime.find((u) => String(u.id) === String(envioIdUser))?.ins ?? null)
      : null;

    // Admin master: usa id_time do contato, ou do time selecionado (se não for "todos")
    const adminTimeId = isAdminMaster
      ? (contact.id_time?.toString() || (idsTimesSelecionados.length === 1 ? idsTimesSelecionados[0] : null))
      : null;

    if (!id_cliente) {
      setModalMessage("Erro: ID do cliente não encontrado.");
      setShowModal(true);
      return;
    }

    const tempId = tempIdExterno ?? `temp-media-${Date.now()}`;
    try {
      if (!tempIdExterno) marcarEnviando(contact.fone, true);

      if (!tempIdExterno) {
        const temp: Message = {
          id: tempId,
          status: "send",
          mensagem: URL.createObjectURL(file),
          timestamp: new Date().toISOString(),
          type: mediatype === "document" ? "document" : mediatype,
          read: true,
          fone: selectedContactData?.fone || "",
          ins: envioIns,
          // nome original do documento na bolha otimista (antes do real chegar do banco)
          doc_name: mediatype === "document" ? file.name : undefined,
        };
        setPendingMessages((prev) => [...prev, temp]);
      }

      const form = new FormData();
      form.append("file", file, file.name);
      form.append("mediatype", mediatype);
      form.append("number", contact.fone || "");

      let endpoint = "";

      if (isAdminMaster && adminTimeId) {
        // Admin master: envia pela instância do time selecionado
        form.append("id_time", adminTimeId);
        if (envioIdUser) form.append("id_user", envioIdUser);
        form.append("mimetype", guessMime(file));
        if (mediatype === "document") form.append("caption", "");
        endpoint = `${API_URL}/enviarmidiameta_admin`;
      }
      // 🏛️ Lógica Oficial (Uazapi / InstanciaOficial)
      else if (instanciaOficial && instanciaOficial !== "0") {
        form.append("id_time", id_cliente);
        if (envioIdUser) form.append("id_user", envioIdUser);
        form.append("mimetype", guessMime(file));
        if (mediatype === "document") form.append("caption", "");
        endpoint = `${API_URL}/enviarmidiameta_admin`;
      }
      // 🦕 Lógica Legada (Evolution)
      else {
        form.append("id_cliente", id_cliente);
        if (envioIdUser) form.append("id_user", envioIdUser);

        if (id_cliente !== "146") {
            if (!instanceName) throw new Error("Conectando número...");
            form.append("instanceName", instanceName);
        }

        if (mediatype === "document") {
            form.append("caption", "");
            form.append("mimetype", guessMime(file));
        }

        endpoint = id_cliente === "129"
            ? `${API_URL}/enviarmidiameta`
            : `${API_URL}/enviarmidia`;
      }

      const res = await fetch(endpoint, {
        method: "POST",
        body: form
      });

      if (!res.ok) {
        throw new Error(await motivoFalhaEnvio(res, `Erro ao enviar ${mediatype === "audio" ? "o áudio" : "a mídia"}.`));
      }

      await avisarSeNaoSalvouHistorico(res);

      const responseJson = await res.json();
      console.log("📦 Resposta do backend:", responseJson);

      // Sucesso: NÃO remove o balão temp (blob) da conversa ABERTA. O combine do
      // render esconde a versão real (banco) equivalente enquanto o temp existe →
      // sem sumiço/piscada; ele sai quando o refetch traz a versão persistida.
      //
      // Se o operador já trocou de conversa, porém, ninguém mais consome este temp
      // (o consumo acontece na conversa aberta): sem esta limpeza ele ficaria em
      // memória até recarregar a página.
      if (String(selectedContactRef.current ?? "") !== String(contact.id)) {
        setPendingMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    } catch (err: any) {
      console.error(err);
      setModalMessage(err?.message || "Erro ao enviar mídia.");
      setShowModal(true);
      // Erro: remove só ESTE temp (a mídia não foi enviada), preservando outros em voo.
      setPendingMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      // (Removido daqui um `isFetchingContacts.current = false`: o envio de mídia
      // não abre fetch de contatos nenhum, e zerar o guard liberava um segundo
      // fetch por cima do que estava em andamento — duas listas concorrentes
      // sobrescrevendo o `contacts`, que é como um contato clicado some da lista
      // e o clique "não abre".)
      if (!tempIdExterno) marcarEnviando(contact.fone, false);
    }
  };

  // Envia VÁRIOS arquivos do mesmo tipo como FILA: cria TODOS os balões temp de uma vez
  // (os N aparecem juntos e ficam até o real chegar — sem sumir/piscar) e envia um a um.
  // O dedup do render casa cada temp com seu real 1:1 (por tipo+fone), então não duplica.
  // ── Arrastar-e-soltar arquivos na conversa ─────────────────────────────────
  // Só a área de mensagens aceita o drop. O resto do painel (header/rodapé) apenas
  // cancela o comportamento padrão do navegador, que abriria o arquivo numa aba e
  // derrubaria a conversa. O drop NÃO envia: abre a prévia e espera confirmação.
  const tipoMidiaDoArquivo = (file: File): "image" | "video" | "document" => {
    const mime = (file.type || "").toLowerCase();
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("video/")) return "video";
    return "document";
  };

  const arrastaArquivos = (e: React.DragEvent): boolean => {
    const tipos = Array.from(e.dataTransfer?.types || []);
    return tipos.includes("Files") || tipos.includes(MIDIA_DRAG_TYPE);
  };

  // Mídia arrastada da própria conversa: baixa do storage e vira File (com o nome da bolha).
  const baixarMidiaArrastada = async (url: string, nome: string): Promise<File | null> => {
    try {
      const r = await fetch(url);
      if (!r.ok) return null;
      const blob = await r.blob();
      if (blob.size === 0) return null;
      return new File([blob], nome || "arquivo", { type: blob.type || "application/octet-stream" });
    } catch {
      return null;
    }
  };

  // Limite de tamanho por tipo. Os números são os da Meta Cloud API; o teto de 50 MB
  // é do próprio backend (MAX_CONTENT_LENGTH no api.py) e vale para todos. Sem esta
  // conferência, um arquivo grande subia inteiro para só então voltar 413 — o
  // operador esperava o upload de um envio que nunca ia acontecer.
  const LIMITE_BACKEND_MB = 50;
  const LIMITES_MB: Record<string, number> = { image: 5, video: 16, audio: 16, document: LIMITE_BACKEND_MB };
  const problemaDoArquivo = (file: File): string | null => {
    if (file.size === 0) return "arquivo vazio";
    const tipo = tipoMidiaDoArquivo(file);
    const limite = Math.min(LIMITES_MB[tipo] ?? LIMITE_BACKEND_MB, LIMITE_BACKEND_MB);
    if (file.size > limite * 1024 * 1024) {
      return `acima do limite de ${limite} MB para ${tipo === "image" ? "imagem" : tipo === "video" ? "vídeo" : "documento"}`;
    }
    return null;
  };

  // Janela de 24h fechada: o cliente não pode receber, mas o upload interno da TNP (que não
  // vai ao cliente) continua valendo — a prévia abre com só esse botão ativo.
  const podeReceberDrop = (): boolean =>
    !!selectedContact && !isRecording && (!shouldUseTemplate() || ehGrupoTnpUpload());

  const limparPreviaDrop = () => {
    setDropPreview((prev) => {
      prev.forEach((p) => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
      return [];
    });
  };

  const removerArquivoDrop = (idx: number) => {
    setDropPreview((prev) => {
      const alvo = prev[idx];
      if (alvo?.previewUrl) URL.revokeObjectURL(alvo.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDropAreaEnter = (e: React.DragEvent) => {
    if (!arrastaArquivos(e) || !podeReceberDrop()) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleDropAreaOver = (e: React.DragEvent) => {
    if (!arrastaArquivos(e) || !podeReceberDrop()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDropAreaLeave = (e: React.DragEvent) => {
    if (!arrastaArquivos(e)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFiles(false);
  };

  const handleDropAreaDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (!podeReceberDrop()) return;
    const midiaDaConversa = e.dataTransfer?.getData(MIDIA_DRAG_TYPE) || "";
    let arquivos = Array.from(e.dataTransfer?.files || []);
    if (midiaDaConversa) {
      // O File que o navegador anexa a uma <img> arrastada vem com 0 bytes: usa o do storage.
      let alvo: { url?: string; nome?: string } = {};
      try { alvo = JSON.parse(midiaDaConversa); } catch { /* ignora */ }
      const baixado = alvo.url ? await baixarMidiaArrastada(alvo.url, alvo.nome || "") : null;
      if (!baixado) {
        setModalMessage("Não foi possível baixar esse arquivo da conversa. Baixe-o e anexe pelo botão.");
        setShowModal(true);
        return;
      }
      arquivos = [baixado];
    }
    if (arquivos.length === 0) return; // arrastou texto/link, não arquivo
    setDropPreview((prev) => [
      ...prev,
      ...arquivos.map((file) => ({
        file,
        previewUrl: /^(image|video)\//i.test(file.type || "") ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  // Envia os arquivos da prévia pelo mesmo caminho do clipe. Agrupa em blocos
  // consecutivos do mesmo tipo p/ preservar a ordem em que aparecem na lista.
  const enviarPreviaDrop = async () => {
    if (shouldUseTemplate()) return; // janela fechada: só o upload interno vale
    // Só os válidos vão; os que estouram o limite ficam de fora com aviso (a lista
    // já marca cada um em vermelho na prévia).
    const itens = dropPreview.filter((p) => !problemaDoArquivo(p.file));
    const recusados = dropPreview.length - itens.length;
    if (itens.length === 0) {
      setModalMessage("Nenhum arquivo pôde ser enviado: todos estão acima do limite de tamanho.");
      setShowModal(true);
      return;
    }
    limparPreviaDrop();
    if (recusados > 0) {
      setModalMessage(`${recusados} arquivo(s) não foram enviados por estarem acima do limite de tamanho. Os demais foram para a fila.`);
      setShowModal(true);
    }
    const blocos: { tipo: "image" | "video" | "document"; files: File[] }[] = [];
    for (const { file } of itens) {
      const tipo = tipoMidiaDoArquivo(file);
      const ultimo = blocos[blocos.length - 1];
      if (ultimo && ultimo.tipo === tipo) ultimo.files.push(file);
      else blocos.push({ tipo, files: [file] });
    }
    for (const bloco of blocos) await enfileirarMidias(bloco.files, bloco.tipo);
  };

  // Grupo TNP (192): mesma prévia, mas grava como type='tnpupload' — fica na
  // conversa e NÃO vai pro cliente.
  const enviarPreviaDropInterno = async () => {
    // Upload interno não passa pela Meta (é anexo no storage), então só vale o teto
    // do backend — mas ele vale, e um 413 aqui também é upload perdido.
    const files = dropPreview
      .map((p) => p.file)
      .filter((f) => f.size > 0 && f.size <= LIMITE_BACKEND_MB * 1024 * 1024);
    const recusados = dropPreview.length - files.length;
    const vazios = dropPreview.filter((p) => p.file.size === 0).length;
    const motivo = vazios > 0
      ? `${vazios} arquivo(s) chegaram vazios (0 bytes) — baixe para o computador e anexe de lá`
      : `o limite é ${LIMITE_BACKEND_MB} MB por arquivo`;
    if (files.length === 0) {
      setModalMessage(`Nenhum arquivo pôde ser anexado: ${motivo}.`);
      setShowModal(true);
      return;
    }
    limparPreviaDrop();
    if (recusados > 0) {
      setModalMessage(`${recusados} arquivo(s) ficaram de fora (${motivo}). Os demais foram anexados.`);
      setShowModal(true);
    }
    await enviarTnpUploads(files);
  };

  useEffect(() => {
    if (dropPreview.length === 0) return;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") limparPreviaDrop(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dropPreview.length]);

  // Arrasto abortado fora da área (ESC, soltar em outra janela) não dispara o
  // dragleave do container — sem este reset o overlay tracejado ficaria preso.
  useEffect(() => {
    const resetArrasto = () => { dragDepthRef.current = 0; setIsDraggingFiles(false); };
    window.addEventListener("dragend", resetArrasto);
    window.addEventListener("drop", resetArrasto);
    return () => {
      window.removeEventListener("dragend", resetArrasto);
      window.removeEventListener("drop", resetArrasto);
    };
  }, []);

  // Trocar de conversa descarta a prévia: os arquivos foram soltos para o contato
  // anterior e enviá-los na conversa nova seria envio no lugar errado.
  useEffect(() => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    limparPreviaDrop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact]);

  // ── Envio pela Meta: formatos aceitos (testado em 28/10/2026) ──────────────────
  // A Meta recusa ZIP, WAV, GIF, MOV, HEIC e arquivo vazio, e o operador via só "Erro no
  // upload"; WEBP como imagem passa no upload e FALHA na entrega, sem aviso. Aqui: WEBP/GIF/BMP
  // viram JPG, foto acima de 5 MB é comprimida, e o resto que a Meta não aceita é barrado
  // com o motivo antes de subir.
  const META_MIMES_DOC = [
    "application/pdf", "application/msword", "application/vnd.ms-excel", "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/plain", "text/csv", "image/jpeg", "image/png", "video/mp4", "video/3gpp",
    "audio/aac", "audio/mp4", "audio/x-m4a", "audio/mpeg", "audio/amr", "audio/ogg", "audio/opus",
  ];
  const vaiPelaMeta = () => {
    const io = localStorage.getItem("instanciaoficial");
    return isAdminMaster || (!!io && io !== "0");
  };
  const paraJpeg = async (file: File, maxLado = 2560, qualidade = 0.85): Promise<File | null> => {
    try {
      const bmp = await createImageBitmap(file);
      const escala = Math.min(1, maxLado / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bmp.width * escala);
      canvas.height = Math.round(bmp.height * escala);
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#fff"; // GIF/PNG com transparência: fundo branco em vez de preto
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const blob: Blob | null = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", qualidade));
      if (!blob || blob.size === 0) return null;
      return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
      return null;
    }
  };
  const prepararParaMeta = async (files: File[], mediatype: string) => {
    const aceitos: File[] = [];
    const recusados: string[] = [];
    for (const file of files) {
      const mime = guessMime(file).toLowerCase();
      const nome = file.name;
      if (file.size === 0) { recusados.push(`${nome}: arquivo vazio (0 bytes)`); continue; }
      if (mediatype === "image") {
        if (/^image\/(webp|gif|bmp)$/.test(mime) || (/^image\/(jpeg|png)$/.test(mime) && file.size > 5 * 1024 * 1024)) {
          const jpg = await paraJpeg(file);
          if (jpg && jpg.size <= 5 * 1024 * 1024) aceitos.push(jpg);
          else recusados.push(`${nome}: não foi possível converter a imagem para JPG`);
        } else if (/^image\/(jpeg|png)$/.test(mime)) aceitos.push(file);
        else recusados.push(`${nome}: formato de imagem não aceito pelo WhatsApp (use JPG ou PNG)`);
        continue;
      }
      if (mediatype === "video") {
        if (/^video\/(mp4|3gpp)$/.test(mime)) aceitos.push(file);
        else recusados.push(`${nome}: formato de vídeo não aceito pelo WhatsApp (use MP4)`);
        continue;
      }
      if (mediatype === "audio") {
        // O backend reencoda qualquer áudio para OGG/Opus (igual ao gravado no navegador).
        if (file.size > 16 * 1024 * 1024) recusados.push(`${nome}: áudio acima de 16 MB`);
        else if (ehArquivoDeAudio(file) || mime === "audio/webm") aceitos.push(file);
        else recusados.push(`${nome}: não é um arquivo de áudio`);
        continue;
      }
      if (META_MIMES_DOC.includes(mime)) aceitos.push(file);
      else recusados.push(`${nome}: tipo de arquivo não aceito pelo WhatsApp`);
    }
    return { aceitos, recusados };
  };

  const enfileirarMidias = async (
    selecionados: File[],
    mediatype: "image" | "video" | "document" | "audio",
  ) => {
    if (!selectedContact || selecionados.length === 0) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;
    if (!localStorage.getItem("id_cliente")) {
      setModalMessage("Erro: ID do cliente não encontrado.");
      setShowModal(true);
      return;
    }
    // Áudios mandados como documento (clipe "Documento" ou arrastados): pergunta o formato.
    if (mediatype === "document") {
      const audios = selecionados.filter(ehArquivoDeAudio);
      if (audios.length > 0) {
        const modo = await perguntarModoAudio(audios.length);
        if (modo === null) return;
        if (modo === "audio") {
          const outros = selecionados.filter((f) => !ehArquivoDeAudio(f));
          if (outros.length > 0) await enfileirarMidias(outros, "document");
          await enfileirarMidias(audios, "audio");
          return;
        }
      }
    }
    let files = selecionados;
    if (vaiPelaMeta()) {
      const { aceitos, recusados } = await prepararParaMeta(selecionados, mediatype);
      if (recusados.length > 0) {
        setModalMessage(
          `${recusados.length === 1 ? "Este arquivo não foi enviado" : `${recusados.length} arquivos não foram enviados`}:\n\n` +
          recusados.map((r) => `• ${r}`).join("\n") +
          "\n\nO WhatsApp aceita PDF, Word, Excel, PowerPoint, TXT, imagens JPG/PNG, vídeo MP4 e áudio MP3/OGG/M4A/AAC."
        );
        setShowModal(true);
      }
      files = aceitos;
      if (files.length === 0) return;
    }
    const envioIdUser = resolveEnvioIdUser(contact);
    const envioIns = ehTimeNumeroPorDono(contact.id_time)
      ? (usuariosDoTime.find((u) => String(u.id) === String(envioIdUser))?.ins ?? null)
      : null;

    // 1) cria TODOS os temps e adiciona de uma vez (aparecem juntos na ordem da seleção)
    const base = Date.now();
    const fila = files.map((file, idx) => {
      const tempId = `temp-media-${base}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
      const temp: Message = {
        id: tempId,
        status: "send",
        mensagem: URL.createObjectURL(file),
        timestamp: new Date(base + idx).toISOString(), // +idx mantém a ordem da seleção
        type: mediatype === "document" ? "document" : mediatype,
        read: true,
        fone: contact.fone || "",
        ins: envioIns,
        doc_name: mediatype === "document" ? file.name : undefined,
      };
      return { file, tempId, temp };
    });
    setPendingMessages((prev) => [...prev, ...fila.map((f) => f.temp)]);

    // 2) envia um a um (fila sequencial), mantendo todos os temps na tela.
    //    O bloqueio da UI é da conversa DESTE contato — não do sistema inteiro.
    marcarEnviando(contact.fone, true);
    setProgressoEnvio({ fone: String(contact.fone || ""), feitos: 0, total: fila.length });
    try {
      let feitos = 0;
      for (const { file, tempId } of fila) {
        await sendMediaFile(file, mediatype, tempId);
        feitos += 1;
        setProgressoEnvio({ fone: String(contact.fone || ""), feitos, total: fila.length });
      }
    } finally {
      marcarEnviando(contact.fone, false);
      setProgressoEnvio(null);
    }
  };

  // Reenvia um áudio JÁ enviado: rebusca o arquivo da própria URL e manda de novo pelo
  // mesmo fluxo (sendMediaFile → conversão webm->ogg no backend). Pro caso de áudios que
  // não chegaram corretamente, sem precisar gravar de novo. Cria uma nova bolha (reenvio).
  const reenviarAudio = async (message: Message) => {
    const url = message?.mensagem;
    if (!url) return;
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("download do áudio falhou");
      const blob = await resp.blob();
      const ext = (url.split("?")[0].match(/\.(\w+)$/)?.[1] || "ogg").toLowerCase();
      const file = new File([blob], `audio-reenvio-${Date.now()}.${ext}`, { type: blob.type || `audio/${ext}` });
      await sendMediaFile(file, "audio");
    } catch (e) {
      console.error("Erro ao reenviar áudio:", e);
      setModalMessage("Não foi possível reenviar o áudio.");
      setShowModal(true);
    }
  };

  // Abre o modal da Biblioteca e carrega as mídias do grupo.
  // Carrega as pastas de um tipo da biblioteca (do grupo).
  const carregarBibliotecaPastas = async (tipo: string) => {
    try {
      const id_grupo = localStorage.getItem("id_grupo") || "";
      const res = await fetch(`${API_URL}/biblioteca/pastas?id_cliente=${id_grupo}&tipo=${tipo}`);
      setBibliotecaPastas(res.ok ? await res.json() : []);
    } catch (err) { console.error(err); setBibliotecaPastas([]); }
  };

  // Carrega as mídias de um tipo + pasta (null = raiz).
  const carregarBibliotecaMidias = async (tipo: string, pasta: any | null) => {
    setBibliotecaLoading(true);
    try {
      const id_grupo = localStorage.getItem("id_grupo") || "";
      const ip = pasta ? String(pasta.id) : "null";
      const res = await fetch(`${API_URL}/biblioteca?id_cliente=${id_grupo}&tipo=${tipo}&id_pasta=${ip}`);
      setBibliotecaItens(res.ok ? await res.json() : []);
    } catch (err) { console.error(err); setBibliotecaItens([]); }
    finally { setBibliotecaLoading(false); }
  };

  const abrirBiblioteca = async () => {
    if (!selectedContact) return;
    setShowMediaMenu(false);
    setShowBibliotecaModal(true);
    setBibliotecaPasta(null);
    carregarBibliotecaPastas(bibliotecaAba);
    carregarBibliotecaMidias(bibliotecaAba, null);
  };

  // Troca de tipo no modal: volta p/ raiz e recarrega pastas + mídias.
  const trocarAbaBiblioteca = (tipo: "image" | "video" | "audio" | "document") => {
    setBibliotecaAba(tipo);
    setBibliotecaPasta(null);
    carregarBibliotecaPastas(tipo);
    carregarBibliotecaMidias(tipo, null);
  };

  const entrarPastaBiblioteca = (p: any) => {
    setBibliotecaPasta(p);
    carregarBibliotecaMidias(bibliotecaAba, p);
  };

  // Envia uma mídia da biblioteca (por URL, sem re-upload) para o contato aberto.
  const enviarMidiaBiblioteca = async (m: any) => {
    if (!selectedContact) return;
    const contact = contacts.find((c) => c.id === selectedContact);
    if (!contact) return;
    const id_time = isAdminMaster
      ? (contact.id_time?.toString() || (idsTimesSelecionados.length === 1 ? idsTimesSelecionados[0] : null))
      : localStorage.getItem("id_cliente");
    if (!id_time) {
      setModalMessage("ID do time não encontrado.");
      setShowModal(true);
      return;
    }
    const envioIdUser = resolveEnvioIdUser(contact);
    const envioIns = ehTimeNumeroPorDono(contact.id_time)
      ? (usuariosDoTime.find((u) => String(u.id) === String(envioIdUser))?.ins ?? null)
      : null;
    const fone = contact.fone || "";
    const tipo = m.tipo === "document" ? "document" : m.tipo;

    const tempId = `temp-bib-${Date.now()}`;
    setPendingMessages((prev) => [...prev, {
      id: tempId, status: "send", mensagem: m.url, timestamp: new Date().toISOString(),
      type: tipo, read: true, fone, ins: envioIns,
    } as Message]);
    setShowBibliotecaModal(false);

    try {
      const res = await fetch(`${API_URL}/enviarmidia_url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone, id_time, id_user: envioIdUser, url: m.url, tipo: m.tipo }),
      });
      if (!res.ok) throw new Error("falha");
      setPendingMessages((prev) => prev.filter((p) => p.id !== tempId));
    } catch (err) {
      console.error(err);
      setPendingMessages((prev) => prev.filter((p) => p.id !== tempId));
      setModalMessage("Erro ao enviar a mídia da biblioteca.");
      setShowModal(true);
    }
  };

  // Respostas rápidas: carrega 1x (do grupo) ao abrir o picker pela 1ª vez.
  const carregarRespostasRapidas = async () => {
    if (respostasCarregadas) return;
    setRespostasCarregadas(true);
    try {
      const id_grupo = localStorage.getItem("id_grupo") || "";
      const id_user = localStorage.getItem("id_user") || "";
      const res = await fetch(`${API_URL}/respostas_rapidas?id_cliente=${id_grupo}&id_user=${id_user}`);
      if (res.ok) setRespostasRapidas(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  // Insere o texto da resposta rápida no campo (substitui o "/...") e fecha o picker.
  const inserirRespostaRapida = (r: any) => {
    setNewMessage(r.texto || "");
    setShowRespostas(false);
    setTimeout(() => {
      const ta = messageInputRef.current;
      if (ta) {
        ta.focus();
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
      }
    }, 0);
  };


  useEffect(() => {
    if (newMessages) {
      const foneAberto = selectedContactData?.fone;
      const combined = [...newMessages, ...tempsDoFone(pendingMessages, foneAberto)].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      setMessages(combined);
      // Consome só os temps DESTA conversa; os de um envio em curso noutra ficam.
      setPendingMessages((prev) => prev.filter((m) => String(m.fone) !== String(foneAberto)));

      const hasUnread = combined.some((msg) => msg.read === false);

      // 🔍 Se houver mensagens recebidas, dispara tradução
      const contato = contacts.find(c => c.id === selectedContact);
      const idioma = contato?.idioma || IDIOMA_PADRAO;
      const fone = contato?.fone || "";

      const receivedMsgs = newMessages.filter(m => m.status === "received");

      // (Som de notificação fica SÓ no realtime — aplicarMensagemRealtime — pra tocar 1x por
      // mensagem recebida nova, igual à notificação visual. O gatilho daqui era espúrio:
      // re-tocava em todo poll da conversa aberta mesmo sem mensagem nova.)

      if (scrollbarsRef.current) {
        scrollbarsRef.current.scrollToBottom();
      }

      setNewMessageIndicator(!!(selectedContact && hasUnread));
      setNewMessages(null);
    }
  }, [newMessages, pendingMessages, selectedContact, selectedContactData]);

  useEffect(() => {
    const handleUserInteraction = () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.volume = 0;
        notificationSoundRef.current.play().catch(() => {});
        notificationSoundRef.current.pause();
        notificationSoundRef.current.currentTime = 0;
        notificationSoundRef.current.volume = 1;
        window.removeEventListener("click", handleUserInteraction);
      }
    };

    window.addEventListener("click", handleUserInteraction);
    return () => window.removeEventListener("click", handleUserInteraction);
  }, []);




  // Sync messages with contact data (polling updates)
  useEffect(() => {
    if (selectedContactData?.messages) {
      setMessages((prev) => {
        const dbMessages = selectedContactData.messages || [];
        const combined = [...dbMessages, ...tempsDoFone(pendingMessages, selectedContactData.fone)].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        // Verifica se há mudança real para evitar loops.
        //
        // 🔴 Era JSON.stringify dos DOIS arrays inteiros, a cada polling e a cada
        // mudança de pendingMessages. Numa conversa de 800 mensagens isso serializa
        // ~1 MB duas vezes por comparação, na thread da UI: em máquina fraca é
        // exatamente o congelamento que o operador sente ao abrir conversa longa.
        // A assinatura abaixo compara só o que de fato muda numa mensagem.
        const assinatura = (arr: Message[]) => {
          let s = String(arr.length);
          for (const m of arr) {
            s += `|${m.id}:${m.read ? 1 : 0}:${m.status ?? ""}:${m.status_template_wp ?? ""}:${m.traduzido ? 1 : 0}:${m.doc_validado ?? ""}`;
          }
          return s;
        };
        if (assinatura(prev) !== assinatura(combined)) {
            return combined;
        }
        return prev;
      });
    }
  }, [selectedContactData, pendingMessages]);

  const handleChatScroll = () => {
    if (scrollbarsRef.current) {
      const scrollTop = scrollbarsRef.current.getScrollTop();
      const clientHeight = scrollbarsRef.current.getClientHeight();
      const scrollHeight = scrollbarsRef.current.getScrollHeight();
      // Se o scroll chegar ao final (offset de 10px)...
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        // Verifica se existem mensagens não lidas
        const hasUnread = messages.some((msg) => msg.read === false);
        if (hasUnread) {
          setNewMessageIndicator(false);
          markMessagesAsRead();
        }
      }
    }
  };


  {/*
  useEffect(() => {
    fetchContactsFull();
    handleTraduzirAgora();
    const id = setInterval(fetchContactsFull, 5000);
    return () => clearInterval(id);
  }, []);
  */}

  // 🆕 Efeito de Auto-Retry Inteligente (Até 3 tentativas automáticas)
  
  //useEffect(() => {
    // Só tenta o retry se NÃO estiver carregando, se NÃO tiver contatos E se NÃO estiver buscando agora
  //  if (isConnected && contacts.length === 0 && !loadingContacts && !isCheckingConnection && !isFetchingContacts.current) {
  //    if (retryCount < 3) {
  //      const timer = setTimeout(() => {
  //        console.log(`🔄 Auto-Retry ${retryCount + 1}/3 disparado...`);
  //        setRetryCount(prev => prev + 1);
  //        fetchContactsFull();
  //      }, 8000); 
  //      return () => clearTimeout(timer);
  //    }
  //  }
  //}, [isConnected, contacts.length, loadingContacts, isCheckingConnection, retryCount]);
  

  // --- Admin Master: busca times do grupo ao montar ---
  useEffect(() => {
    if (!isAdminMaster) return;
    const id_grupo = localStorage.getItem("id_grupo");
    if (!id_grupo) return;

    fetch(`${API_URL}/pegartimecompletochat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_cliente: id_grupo }),
    })
      .then((res) => res.json())
      .then((data) => {
        const lista: TimeInfo[] = Array.isArray(data) ? data : [];
        setTimesDoGrupo(lista);
        timesDoGrupoRef.current = lista;
        // Busca contatos agora que temos os times (ref já atualizado)
        if (lista.length > 0) {
          fetchContactsFull();
        }
      })
      .catch((err) => console.error("Erro ao buscar times do grupo:", err));
  }, []);

  // --- Admin Master: busca usuários quando muda a seleção de times ---
  useEffect(() => {
    if (!isAdminMaster) return;

    // Nenhum time marcado = "todos os times". Não há contexto único: limpa o
    // filtro de usuário e não toca no id_cliente.
    if (idsTimesSelecionados.length === 0) {
      setUsuariosDoTime([]);
      setIdsUsuariosSelecionados([]);
      return;
    }

    // Só com UM time marcado existe contexto único para funis/etapas, que são
    // buscados por id_cliente. Com 2+ o id_cliente fica como está — o envio não
    // depende dele (usa o id_time do próprio lead, ver adminTimeId).
    if (idsTimesSelecionados.length === 1) {
      localStorage.setItem("id_cliente", idsTimesSelecionados[0]);

      // Re-busca funis e etapas do novo time
      isFetchingFunnels.current = false;
      isFetchingStages.current = false;
      fetchFunnels();
      fetchStages();
    }

    // /pegarusuariosgrupo (e não /pegarusuariostime) porque só ele devolve
    // id_time — necessário p/ recortar os atendentes pelos times marcados e p/
    // agrupar os nomes no dropdown. Uma chamada só, independente de quantos
    // times estejam marcados.
    const idGrupo = localStorage.getItem("id_grupo");
    fetch(`${API_URL}/pegarusuariosgrupo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(idGrupo ? { id_grupo: idGrupo } : { id_time: idsTimesSelecionados[0] }),
    })
      .then((res) => res.json())
      .then((data) => {
        setUsuariosDoTime(Array.isArray(data) ? data : []);
        setIdsUsuariosSelecionados([]);
      })
      .catch((err) => console.error("Erro ao buscar usuários dos times:", err));
    // Filtragem por time é feita client-side em visibleContacts
  }, [idsTimesSelecionados]);

  // Quando o termo de busca (debounced) muda, dispara um re-fetch. Ao entrar
  // em modo busca, o backend devolve todos os matches independente do limit.
  // Ao voltar para vazio, recarrega a lista normal (200 contatos).
  // 🔴 `isMounted` nasce `true` (useRef(true)), então o `if (!isMounted.current)`
  // NÃO protege a montagem: este efeito rodava no boot com o termo ainda vazio,
  // zerava o guard e disparava um SEGUNDO /contacts_full de 64 KB em paralelo com o
  // da carga inicial. Medido em 03/09 no waterfall do deep-link: as duas chamadas
  // saíam juntas aos 908 ms e terminavam em 2,8 s e 4,3 s, disputando as ~6 conexões
  // do navegador com as outras 12 requisições do boot — e a conversa só aparece
  // depois disso. O efeito do filtro de funil já pulava a 1ª execução por este mesmo
  // motivo; busca e data ficaram sem.
  const buscaInicializada = useRef(false);
  useEffect(() => {
    if (!isMounted.current) return;
    if (!buscaInicializada.current) { buscaInicializada.current = true; return; }
    isFetchingContacts.current = false; // permite refetch imediato
    fetchContactsFull();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTermDebounced]);

  // Filtro de data (SERVER-SIDE): quando muda, recalcula o intervalo, guarda no ref
  // (lido sem stale closure dentro do fetch) e dispara re-fetch. O backend passa a
  // devolver TODAS as conversas com mensagem no período — não só as ~200 recentes.
  // Mesmo caso do efeito de busca acima: no boot o intervalo nasce vazio e não há
  // o que refazer — só o request duplicado. O `dateFilterRef` continua sendo
  // atualizado sempre, inclusive na 1ª passada; o que se pula é o fetch.
  const dataInicializada = useRef(false);
  useEffect(() => {
    dateFilterRef.current = computeDateRange(dateChoice, dateStart, dateEnd);
    if (!isMounted.current) return;
    if (!dataInicializada.current) { dataInicializada.current = true; return; }
    isFetchingContacts.current = false; // permite refetch imediato
    fetchContactsFull();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateChoice, dateStart, dateEnd]);

  // Carrega usuariosDoTime sempre que o contato selecionado pertence a um
  // time cujos usuários ainda não foram buscados. Necessário p/ admin de
  // time (que nunca dispara o fluxo via idsTimesSelecionados) e p/ admin master
  // abrindo contato de time diferente.
  const lastLoadedUsersTimeRef = useRef<string | null>(null);
  useEffect(() => {
    const contactTime = selectedContactData?.id_time
      ? String(selectedContactData.id_time)
      : localStorage.getItem("id_cliente");
    // Carrega p/ admins (qualquer time) e p/ o time 159 (precisa do mapa dono→número
    // mesmo p/ usuários não-admin, p/ indicador e filtro de instância).
    if (!isAnyAdmin && !ehTimeNumeroPorDono(contactTime)) return;
    if (!contactTime) return;
    if (lastLoadedUsersTimeRef.current === contactTime && usuariosDoTime.length > 0) return;
    lastLoadedUsersTimeRef.current = contactTime;
    // Carrega os atendentes de TODOS os times da mesma empresa (grupo) do contato.
    // Um contato pode estar atribuído a um atendente de outro time da mesma empresa
    // (ex.: contato do time 164 com dono do time 168); usar só o time do contato
    // deixaria o seletor sem encontrar o dono (estado "cinza").
    fetch(`${API_URL}/pegarusuariosgrupo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time: contactTime }),
    })
      .then((res) => res.json())
      .then((data) => setUsuariosDoTime(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erro ao carregar usuariosDoTime:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyAdmin, selectedContact]);

  // Carrega o mapa ins_nome -> ins_numero (números do grupo) p/ exibir no header. Roda para todo
  // grupo: é a própria lista que diz se o grupo tem mais de um número (grupoMultiNumero).
  useEffect(() => {
    const id_grupo = localStorage.getItem("id_grupo");
    if (!id_grupo) return;
    fetch(`${API_URL}/instancias_grupo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_grupo }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const mapa: Record<string, string> = {};
        data.forEach((i: any) => {
          if (i?.ins_nome != null) mapa[String(i.ins_nome)] = String(i.ins_numero ?? "");
        });
        setMapaInstanciaNumero(mapa);
        const multi = Object.keys(mapa).length > 1;
        setGrupoMultiNumero(multi);
        if (multi || id_grupo === GRUPO_NUMERO_NO_HEADER) carregarSaudeNumeros();
      })
      .catch((err) => console.error("Erro ao carregar instancias_grupo:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saúde de cada número (Meta) p/ colorir o badge — só para grupo que mostra o número (consulta a
  // Meta por número; não vale a pena para quem tem um só).
  const carregarSaudeNumeros = () => {
    fetch(`${API_URL}/status_numeros_oficiais`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_email: localStorage.getItem("user_email"),
        id_cliente: localStorage.getItem("id_cliente"),
      }),
    })
      .then((res) => res.json())
      .then((j) => {
        const nums = j && Array.isArray(j.numeros) ? j.numeros : [];
        const mapa: Record<string, string> = {};
        nums.forEach((n: any) => {
          if (n?.ins_nome != null) mapa[String(n.ins_nome)] = String(n.saude || "");
        });
        setMapaInstanciaSaude(mapa);
      })
      .catch((err) => console.error("Erro ao carregar saude dos numeros:", err));
  };

  // Nome do time pelo id (lista timesDoGrupo; fallback "Time {id}").
  const nomeTimeChat = (id?: number | null) =>
    id != null ? (timesDoGrupo.find((t) => String(t.id) === String(id))?.nome || `Time ${id}`) : "";

  // Rótulo do atendente no seletor: anexa o time quando ele difere do time do contato
  // atual (deixa claro que a escolha vai transferir o lead para outro time).
  const rotuloAtendenteChat = (u: { id: number; nome: string; id_time?: number }) => {
    const contatoTime = selectedContactData?.id_time;
    if (u.id_time != null && contatoTime != null && String(u.id_time) !== String(contatoTime)) {
      return `${u.nome} · ${nomeTimeChat(u.id_time)}`;
    }
    return u.nome;
  };

  // Handler de transferência de atendente (admin only). Mesmo time OU outro time do
  // mesmo grupo (o time destino vem do atendente escolhido). Atualiza contatos +
  // funil_fluxo + cod_reg no backend (que remapeia a etapa ao trocar de time).
  const transferirAtendente = async (novoUserId: number, novoUserNome: string, novoIdTime?: number) => {
    if (!selectedContactData) return;
    const origemTime =
      selectedContactData.id_time != null
        ? String(selectedContactData.id_time)
        : localStorage.getItem("id_cliente");
    if (!origemTime) {
      alert("ID do time não encontrado.");
      return;
    }
    const destinoTime = novoIdTime != null ? String(novoIdTime) : origemTime;
    const trocaTime = destinoTime !== origemTime;
    if (selectedContactData.id_user === novoUserId && !trocaTime) return;
    const nomeAtual =
      usuariosDoTime.find((u) => u.id === selectedContactData.id_user)?.nome ||
      "ninguém";
    const alvo = trocaTime ? `${novoUserNome} (${nomeTimeChat(novoIdTime)})` : novoUserNome;
    const ok = window.confirm(
      `Transferir "${selectedContactData.nome || selectedContactData.fone}" de ${nomeAtual} para ${alvo}?`
    );
    if (!ok) return;

    // Atualização otimista local
    setContacts((prev) =>
      prev.map((c) =>
        c.id === selectedContactData.id
          ? { ...c, id_user: novoUserId, id_time: novoIdTime != null ? novoIdTime : c.id_time }
          : c
      )
    );

    try {
      const res = await fetch(`${API_URL}/transferir_usuario`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_time: origemTime,
          novo_id_time: destinoTime, // time destino (mesmo grupo) — backend remapeia a etapa
          fone: selectedContactData.fone,
          novo_id_user: novoUserId,
          de_id_user: selectedContactData.id_user, // origem: move só a linha deste atendente
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(`Falha ao transferir: ${body.error || `HTTP ${res.status}`}`);
        fetchContactsFull();
        return;
      }
      // Sucesso: refetch para garantir consistência
      fetchContactsFull();
    } catch (err: any) {
      console.error("Erro ao transferir atendente:", err);
      alert(`Erro de conexão: ${err?.message || "tente novamente"}`);
      fetchContactsFull();
    }
  };

  // Contatos são filtrados client-side — não precisa re-fetchar ao trocar usuário

  // Re-fetch quando o limite aumenta (infinite scroll)
  useEffect(() => {
    if (contactsLimit > LIMITE_INICIAL_CONTATOS) {
      fetchContactsFull();
      isLoadingMore.current = false;
    }
  }, [contactsLimit]);

  // Mantém o ponteiro do fetch sempre na versão mais recente (ver fetchContactsFullRef).
  useEffect(() => { fetchContactsFullRef.current = fetchContactsFull; });

  // Troca de filtro de funil/etapa/time: atualiza as refs, volta o limite ao inicial
  // (a lista filtrada começa do zero) e refaz a busca — agora com o filtro no servidor,
  // então os 200 primeiros são 200 DA ETAPA, não 200 do grupo inteiro.
  useEffect(() => {
    if (!isMounted.current) return;
    funnelFilterRef.current = {
      etapa: filterStage,
      funis: filterFunnels,
      times: isAdminMaster ? idsTimesSelecionados : [],
      tags: filterTags,
      finalizados: filtroFinalizados,
    };
    // Na montagem os filtros já nascem vazios e a carga inicial é feita pelo effect de
    // mount (ou, no admin master, depois que os times chegam). Disparar aqui também só
    // geraria um request duplicado — e, no admin master, um request sem times.
    if (!filtrosInicializados.current) {
      filtrosInicializados.current = true;
      return;
    }
    hasMoreContacts.current = true;
    isLoadingMore.current = false;
    isFetchingContacts.current = false; // permite refetch imediato
    if (contactsLimit !== LIMITE_INICIAL_CONTATOS) {
      setContactsLimit(LIMITE_INICIAL_CONTATOS);   // o effect acima NÃO dispara ao voltar p/ o inicial
      contactsLimitRef.current = LIMITE_INICIAL_CONTATOS;
    }
    fetchContactsFull();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStage, filterFunnels, idsTimesSelecionados, filterTags, filtroFinalizados]);

  // Preenche a tela sem exigir scroll: se a lista filtrada rendeu menos itens do que
  // cabe na primeira dobra e ainda há mais no servidor, busca o próximo lote sozinho.
  // Sem isto o operador via uma lista curta achando que "acabou" e só descobria que
  // havia mais ao rolar — que era exatamente a queixa do filtro de etapa.
  useEffect(() => {
    if (loadingContacts || isFetchingContacts.current || isLoadingMore.current) return;
    if (!hasMoreContacts.current) return;
    if (visibleContactsCountRef.current >= MIN_CONTATOS_VISIVEIS) return;
    if (contactsLimit >= LIMITE_MAX_AUTO) return;   // trava de segurança
    isLoadingMore.current = true;
    setCarregandoMais(true);
    setContactsLimit(prev => prev + LOTE_CONTATOS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, filterStage, filterFunnels, idsTimesSelecionados, filterTags, filtroFinalizados, loadingContacts]);

  // (Removido useEffect automático de isConnected para evitar race conditions)

  // Polling FALLBACK (30s). O tempo real (Supabase Realtime) já traz as mensagens
  // na hora; o polling fica só como rede de segurança (se o WebSocket cair ou o
  // realtime não estiver disponível) e p/ pegar updates não cobertos (read/status).
  useEffect(() => {
        const id = setInterval(() => {
           // Admin: só faz polling se já tiver os times carregados
           if (isAdminMaster && timesDoGrupoRef.current.length === 0) return;
           if (!isFetchingContacts.current) {
               // Pela ref: este useEffect tem deps [], então a `fetchContactsFull` que
               // ele capturaria seria a do primeiro render — com contactsLimit=200 e os
               // filtros vazios congelados no closure. Era isso que, a cada 30s, refazia
               // a busca com limit=200 e apagava da tela tudo que o scroll tinha trazido.
               (fetchContactsFullRef.current ?? fetchContactsFull)(true);
           }
        }, 30000);

        return () => clearInterval(id);
    }, []);


  useEffect(() => {
    isMounted.current = true;
    // Reset do guard para evitar trava por double-mount (React Strict Mode)
    isFetchingContacts.current = false;
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) return;

    // Admin: contatos são buscados após carregar os times (no useEffect de pegartimecompletochat)
    if (!isAdminMaster) {
      fetchContactsFull();
    }
    
    // 🕵️‍♂️ VIGIA DE EMERGÊNCIA: Se os dados chegarem e o componente \"piscar\", 
    // ele tenta ler o cache de novo em 1.5 segundos (antes do polling de 10s).
    const emergencyTimer = setTimeout(() => {
        // 🔴 Era `contacts.length === 0` — e este efeito tem deps [], então o `contacts`
        // do closure é o do PRIMEIRO render: sempre vazio. A condição era sempre
        // verdadeira e o resgate rodava em TODO boot que tivesse cache, mesmo com a
        // lista já carregada. Isso substituía o `contacts` inteiro pelo cache — que,
        // desde a lista leve (01/09), é salvo SEM as mensagens. Quem tivesse aberto uma
        // conversa nesses 1,5 s (o deep-link do dash de retenção abre sozinho) via o
        // histórico recém-carregado ser apagado e a conversa virar "Nenhuma mensagem
        // nesta conversa ainda" — sem volta, porque o conversasCarregadasRef já tinha
        // marcado a conversa como carregada. Pelo ref, o resgate só age quando a lista
        // está de fato vazia, que é o que ele se propõe a fazer.
        if (isMounted.current && contactsRef.current.length === 0) {
            const cached = localStorage.getItem(`contacts_cache_${id_cliente}`);
            if (cached) {
                console.log("🆘 [DEBUG] Resgate de emergência via Cache!");
                setContacts(JSON.parse(cached));
                setLoadingContacts(false);
            }
        }
    }, 1500);
    
    
    // Lógica de conexão
    const instanciaOficial = localStorage.getItem("instanciaoficial");
    if (id_cliente === "129" || (instanciaOficial && instanciaOficial !== "0")) {
      setIsConnected(true);
      setIsCheckingConnection(false);
    }


    return () => { 
        isMounted.current = false; 
        clearTimeout(emergencyTimer);
    };
  }, []);

  useEffect(() => {
        const checkInitialConnection = async () => {
            const instanceName = localStorage.getItem("instanceName");
            if (!instanceName) {
                setIsCheckingConnection(false);
                return;
            }

            try {
                const res = await fetch(`${API_URL}/pegarinstancias`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ instanceName }),
                });

                if (res.ok) {
                    const data = await res.json();
                    const status = data[0]?.connectionStatus || "unknown";
                    const open = (status === "open" || status === "connected");
                    setIsConnected(open);
                    localStorage.setItem("isConnected", open ? "true" : "false");
                }
            } catch (err) {
                console.error("Erro ao verificar conexao no chat:", err);
            } finally {
                setIsCheckingConnection(false);
            }
        };

        checkInitialConnection();
    }, []);

  
  
  
  

  

  useEffect(() => {
    setIsEditingObs(false);
    if (scrollbarsRef.current) {
      scrollbarsRef.current.scrollToBottom();
    }
    if (selectedContact) {
      // Fresh open: libera a auto-marcação de lido (desfaz a supressão do "não lido").
      suprimirAutoLeituraRef.current = false;
      markMessagesAsRead(); // marca como lidas ao entrar no chat
      
      // 🚀 Pre-fetch card data
      const contact = contacts.find(c => c.id === selectedContact);
      if (contact && contact.fone) {
          preFetchCardData(contact.fone, contact.funil || "");
      }
    }
  }, [selectedContact]);


  const avatarSrc =
    selectedContactData && selectedContactData.profilePictureUrl
      ? encodeURI(selectedContactData.profilePictureUrl)
      : genericAvatar;


  
  (window as any).contacts = contacts;


      return (
        <div
          className="page-wrapper"
          style={{
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 400,
            color: "#222",
          }}
        >
          <div className="content">
            <div className="chat-wrapper">
              {/* Chats Sidebar */}
              {(!isMobile || !showChatMobile) && (
                <div className="sidebar-group">
                  <div id="chats" className="sidebar-content active slimscroll" style={{ display: "flex", flexDirection: "column" }}>
                    {/* Desktop: busca, novo contato, filtros e Todos/Não lidas ficam acima da lista e podem
                        ser recolhidos pela seta, devolvendo a altura toda para os contatos. */}
                    {!isMobile && (
                      <button
                        type="button"
                        className="chats-toggle-controles"
                        onClick={alternarControles}
                        title={controlesRecolhidos ? "Mostrar busca e filtros" : "Recolher busca e filtros"}
                        aria-label={controlesRecolhidos ? "Mostrar busca e filtros" : "Recolher busca e filtros"}
                        aria-expanded={!controlesRecolhidos}
                      >
                        <i className={`ti ti-chevrons-${controlesRecolhidos ? "down" : "up"}`} />
                        <span>{controlesRecolhidos ? "Busca e filtros" : "Recolher"}</span>
                      </button>
                    )}
                    <div className="chats-col-controles" style={{ display: !isMobile && controlesRecolhidos ? "none" : "contents" }}>
                      <div className="chat-search-header" style={{ flexShrink: 0 }}>
                        <div className="p-3 d-flex flex-column gap-3">
                          {/* 1. Pesquisa */}
                          {(() => {
                            const trimmed = searchTerm.trim();
                            const searchPending = trimmed.length >= 3 && trimmed !== searchTermDebounced;
                            const searchActive = searchPending || isSearchingRemote;
                            return (
                              <>
                                <div className="input-group">
                                  <span className="input-group-text bg-white border-end-0">
                                    {searchActive ? (
                                      <span
                                        className="spinner-border spinner-border-sm text-primary"
                                        role="status"
                                        aria-label="Buscando"
                                      />
                                    ) : (
                                      <i className="ti ti-search" />
                                    )}
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Pesquisar contato, telefone ou mensagem..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                  />
                                </div>
                                {searchActive && (
                                  <div className="text-muted small d-flex align-items-center gap-1 ps-1" style={{ fontSize: 12 }}>
                                    <i className="ti ti-database-search" />
                                    <span>Buscando na base inteira…</span>
                                  </div>
                                )}
                                {!searchActive && trimmed.length >= 3 && searchTermDebounced && (
                                  <div className="text-muted small ps-1" style={{ fontSize: 12 }}>
                                    {visibleContacts.length} resultado{visibleContacts.length === 1 ? "" : "s"} para “{searchTermDebounced}”
                                    {buscaTruncada && (
                                      <span className="d-block" style={{ color: "#b26a00" }}>
                                        <i className="ti ti-alert-triangle me-1" />
                                        muitos resultados — escreva mais letras para achar o certo
                                      </span>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          {/* Botão Novo Contato — oculto p/ grupo 192 (criação manual desabilitada) */}
                          {!bloqueiaContatoManual && (
                            <button
                              className="btn btn-success w-100"
                              onClick={() => setShowNewContactModal(true)}
                            >
                              ➕ Novo Contato
                            </button>
                          )}

                          {/* Tags de contato (só clientes liberados): abre o modal de criar/editar */}
                          {tagsLiberadas && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100 d-flex align-items-center gap-2"
                              onClick={() => setShowTagsModal(true)}
                              title="Criar e editar as tags dos contatos"
                            >
                              <i className="ti ti-tags" />
                              <span>Tags</span>
                              <span className="d-flex gap-1 ms-auto overflow-hidden" style={{ minWidth: 0 }}>
                                {tagsVisiveis.slice(0, 4).map((t) => (
                                  <span key={t.id} title={t.nome} style={{ width: 10, height: 10, borderRadius: "50%", background: t.cor, flex: "0 0 auto" }} />
                                ))}
                                <small className="text-muted">{tagsVisiveis.length}</small>
                              </span>
                            </button>
                          )}

                          {/* Mobile: botão toggle para filtros */}
                          {isMobile && (
                            <button
                              type="button"
                              onClick={() => setMobileFiltersOpen(v => !v)}
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                                width: "100%", padding: "6px 0", border: "none",
                                background: "transparent", color: isDark ? "#8696a0" : "#8a94a6",
                                fontSize: "12px", fontWeight: 600, cursor: "pointer",
                              }}
                            >
                              <i className={`ti ti-chevron-${mobileFiltersOpen ? "up" : "down"}`} style={{ fontSize: "14px" }} />
                              {mobileFiltersOpen ? "Ocultar filtros" : "Filtros e opções"}
                            </button>
                          )}

                          {/* Desktop + admin (Master ou de time): botão "Filtros" toggle */}
                          {!isMobile && isAnyAdmin && (
                            <button
                              type="button"
                              onClick={() => setFuncoesOpen(v => !v)}
                              className="btn d-flex align-items-center justify-content-center gap-2 w-100"
                              style={{
                                background: funcoesOpen
                                  ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
                                  : "linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #006473 100%)",
                                color: "#ffffff",
                                fontWeight: 700,
                                fontSize: 13,
                                padding: "8px 12px",
                                border: "none",
                                borderRadius: 10,
                                boxShadow: "0 2px 6px rgba(var(--brand-primary-rgb, 0, 126, 148), 0.25)",
                                letterSpacing: 0.3,
                              }}
                            >
                              <i className={`ti ti-${funcoesOpen ? "x" : "filter"}`} style={{ fontSize: 16 }} />
                              <span>{funcoesOpen ? "Ocultar filtros" : "Filtros"}</span>
                              <i className={`ti ti-chevron-${funcoesOpen ? "up" : "down"}`} style={{ fontSize: 14 }} />
                            </button>
                          )}

                          {/* Wrapper colapsável:
                              - mobile: usa mobileFiltersOpen
                              - desktop + admin: usa funcoesOpen (default fechado)
                              - desktop + não-admin: sempre visível */}
                          <div style={
                            isMobile
                              ? {
                                  // overflow vira 'visible' qd o dropdown de filtro abre,
                                  // senão ele é cortado pelo overflow do wrapper colapsável.
                                  overflow: (isFunnelDropdownOpen || isUserDropdownOpen || isTeamDropdownOpen || isTagFilterOpen) ? "visible" : "hidden",
                                  maxHeight: mobileFiltersOpen ? "600px" : "0px",
                                  opacity: mobileFiltersOpen ? 1 : 0,
                                  transition: "max-height 0.3s ease, opacity 0.2s ease",
                                  display: "flex", flexDirection: "column", gap: "12px",
                                }
                              : isAnyAdmin
                                ? {
                                    // overflow vira 'visible' qd o dropdown de filtro abre,
                                    // senão ele é cortado pelo overflow do wrapper colapsável.
                                    overflow: (isFunnelDropdownOpen || isUserDropdownOpen || isTeamDropdownOpen || isTagFilterOpen) ? "visible" : "hidden",
                                    maxHeight: funcoesOpen ? "800px" : "0px",
                                    opacity: funcoesOpen ? 1 : 0,
                                    transition: "max-height 0.3s ease, opacity 0.2s ease",
                                    display: "flex", flexDirection: "column", gap: "12px",
                                  }
                                : { display: "contents" }
                          }>

                          {/* 2. Seleção de Time (Admin Master) — múltipla.
                              Mesmo padrão do filtro de Funil/Etapa logo abaixo. */}
                          {isAdminMaster && timesDoGrupo.length > 0 && (
                            <div className="position-relative">
                              <button
                                type="button"
                                className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                                onClick={() => setIsTeamDropdownOpen(open => !open)}
                              >
                                <span className="text-truncate" style={{ maxWidth: "90%" }}>
                                  {idsTimesSelecionados.length === 0
                                    ? "— Todos os Times —"
                                    : idsTimesSelecionados.length === 1
                                      ? (timesDoGrupo.find(t => String(t.id) === idsTimesSelecionados[0])?.nome || "1 time")
                                      : `${idsTimesSelecionados.length} times`}
                                </span>
                                <i className="ti ti-chevron-down ms-1"></i>
                              </button>

                              {isTeamDropdownOpen && (
                                <div
                                  className="dropdown-menu show"
                                  style={{ maxHeight: 300, overflowY: "auto", position: "absolute", zIndex: 1050, left: 0, right: 0, padding: "8px 12px 8px 20px" }}
                                >
                                  {/* “Todos” limpa a seleção */}
                                  <div className="form-check">
                                    <input
                                      id="time-all"
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={idsTimesSelecionados.length === 0}
                                      onChange={() => setIdsTimesSelecionados([])}
                                    />
                                    <label htmlFor="time-all" className="form-check-label fw-bold">
                                      — Todos os Times —
                                    </label>
                                  </div>
                                  <hr className="my-2" />

                                  {timesDoGrupo.map((t) => (
                                    <div key={t.id} className="form-check">
                                      <input
                                        id={`time-${t.id}`}
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={idsTimesSelecionados.includes(String(t.id))}
                                        onChange={() =>
                                          setIdsTimesSelecionados(prev =>
                                            prev.includes(String(t.id))
                                              ? prev.filter(x => x !== String(t.id))
                                              : [...prev, String(t.id)]
                                          )
                                        }
                                      />
                                      <label htmlFor={`time-${t.id}`} className="form-check-label">
                                        {t.nome}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* 3. Seleção de Usuário (admin master e admin de time) — múltipla.
                              Mesmo padrão do filtro de Funil/Etapa logo abaixo. */}
                          {isAnyAdmin && opcoesUsuarioFiltro.length > 0 && (
                            <div className="position-relative">
                              <button
                                type="button"
                                className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                                onClick={() => setIsUserDropdownOpen(open => !open)}
                              >
                                <span className="text-truncate" style={{ maxWidth: "90%" }}>
                                  {idsUsuariosSelecionados.length === 0
                                    ? "— Todos Usuários —"
                                    : idsUsuariosSelecionados.length === 1
                                      ? (opcoesUsuarioFiltro.find(u => String(u.id) === idsUsuariosSelecionados[0])?.nome || "1 usuário")
                                      : `${idsUsuariosSelecionados.length} usuários`}
                                </span>
                                <i className="ti ti-chevron-down ms-1"></i>
                              </button>

                              {isUserDropdownOpen && (
                                <div
                                  className="dropdown-menu show"
                                  style={{ maxHeight: 300, overflowY: "auto", position: "absolute", zIndex: 1050, left: 0, right: 0, padding: "8px 12px 8px 20px" }}
                                >
                                  {/* “Todos” limpa a seleção */}
                                  <div className="form-check">
                                    <input
                                      id="user-all"
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={idsUsuariosSelecionados.length === 0}
                                      onChange={() => setIdsUsuariosSelecionados([])}
                                    />
                                    <label htmlFor="user-all" className="form-check-label fw-bold">
                                      — Todos Usuários —
                                    </label>
                                  </div>
                                  <hr className="my-2" />

                                  {/* 2+ times marcados: agrupa por time. Senão, lista plana. */}
                                  {agrupaUsuariosPorTime
                                    ? gruposUsuarioFiltro.map((g) => (
                                        <div key={g.idTime} style={{ marginBottom: "10px" }}>
                                          <div className="fw-bold">{g.nomeTime}</div>
                                          <div style={{ marginLeft: "20px", marginTop: "4px" }}>
                                            {g.usuarios.map(renderCheckUsuario)}
                                          </div>
                                        </div>
                                      ))
                                    : opcoesUsuarioFiltro.map(renderCheckUsuario)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* 4.5 Filtro de Data (baseado na data da última mensagem) */}
                          <select
                            className="form-select w-100"
                            value={dateChoice}
                            onChange={(e) => setDateChoice(e.target.value)}
                            title="Filtra os contatos pela data da última mensagem"
                          >
                            <option value="">— Qualquer data —</option>
                            <option value="today">Hoje</option>
                            <option value="yesterday">Ontem</option>
                            <option value="7">Últimos 7 dias</option>
                            <option value="15">Últimos 15 dias</option>
                            <option value="30">Últimos 30 dias</option>
                            <option value="custom">Intervalo personalizado…</option>
                          </select>

                          {/* 4.6 Intervalo personalizado de datas (de / até) */}
                          {dateChoice === "custom" && (
                            <div className="d-flex gap-1 w-100">
                              <input
                                type="date"
                                className="form-control"
                                value={dateStart}
                                max={dateEnd || undefined}
                                onChange={(e) => setDateStart(e.target.value)}
                                title="Data inicial"
                                aria-label="Data inicial"
                              />
                              <input
                                type="date"
                                className="form-control"
                                value={dateEnd}
                                min={dateStart || undefined}
                                onChange={(e) => setDateEnd(e.target.value)}
                                title="Data final"
                                aria-label="Data final"
                              />
                            </div>
                          )}

                          {/* 5. Filtro de Funil/Etapa */}
                          <div className="position-relative">
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                              onClick={() => { setIsTagFilterOpen(false); setIsFunnelDropdownOpen(open => !open); }}
                            >
                              <span className="text-truncate" style={{ maxWidth: "90%" }}>
                                {filterStage
                                  ? `Etapa: ${filterStage}`
                                  : filterFunnels.length > 0
                                    ? `Funil: ${filterFunnels.join(", ")}`
                                    : "— Todos Funis/Etapas —"}
                              </span>
                              <i className="ti ti-chevron-down ms-1"></i>
                            </button>

                            {isFunnelDropdownOpen && (
                              <div
                                className="dropdown-menu show"
                                style={{ maxHeight: 300, overflowY: "auto", position: "absolute", zIndex: 1050, left: 0, right: 0, padding: "8px 12px 8px 20px" }}
                              >
                                {/* “Todos” limpa tudo */}
                                <div className="form-check">
                                  <input
                                    id="funnel-all"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={filterFunnels.length === 0 && !filterStage}
                                    onChange={() => {
                                      setFilterFunnels([]);
                                      setFilterStage(null);
                                    }}
                                  />
                                  <label htmlFor="funnel-all" className="form-check-label fw-bold">
                                    — Todos Funis e Etapas —
                                  </label>
                                </div>
                                <hr className="my-2" />

                                {funnels.map(f => (
                                  <div key={f.id} style={{ marginBottom: "10px" }}>
                                    <div className="form-check">
                                      <input
                                        id={`funnel-${f.id}`}
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={filterFunnels.includes(f.nome_funil)}
                                        onChange={() => {
                                          setFilterFunnels(prev => {
                                            if (prev.includes(f.nome_funil)) {
                                              return prev.filter(x => x !== f.nome_funil);
                                            } else {
                                              return [...prev, f.nome_funil];
                                            }
                                          });
                                          setFilterStage(null);
                                        }}
                                      />
                                      <label htmlFor={`funnel-${f.id}`} className="form-check-label fw-bold">
                                        {f.nome_funil}
                                      </label>
                                    </div>

                                    {/* 🔽 Etapas dentro do funil */}
                                    <div style={{ marginLeft: "20px", marginTop: "4px" }}>
                                      {stages
                                        .filter(stage => stage.funil === f.nome_funil)
                                        .map(stage => (
                                          <div key={stage.id} className="form-check">
                                            <input
                                              id={`stage-${stage.id}`}
                                              className="form-check-input"
                                              type="radio"
                                              name="stageFilter"
                                              checked={filterStage === stage.nome_funil}
                                              onChange={() =>
                                                setFilterStage(prev => (prev === stage.nome_funil ? null : stage.nome_funil))
                                              }
                                            />
                                            <label htmlFor={`stage-${stage.id}`} className="form-check-label">
                                              {stage.nome_funil}
                                            </label>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* 6. Filtro de Tags (só clientes liberados) */}
                          {tagsLiberadas && tagsVisiveis.length > 0 && (
                            <div className="position-relative">
                              <button
                                type="button"
                                className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                                onClick={() => { setIsFunnelDropdownOpen(false); setIsTagFilterOpen((v) => !v); }}
                              >
                                <span className="text-truncate d-flex align-items-center gap-1" style={{ maxWidth: "90%" }}>
                                  <i className="ti ti-tags" />
                                  {filterTags.length === 0
                                    ? "— Todas as Tags —"
                                    : `Tags: ${tagsVisiveis.filter((t) => filterTags.includes(t.id)).map((t) => t.nome).join(", ")}`}
                                </span>
                                <i className="ti ti-chevron-down ms-1"></i>
                              </button>
                              {isTagFilterOpen && (
                                <div
                                  className="dropdown-menu show"
                                  style={{ maxHeight: 300, overflowY: "auto", position: "absolute", zIndex: 1050, left: 0, right: 0, padding: "8px 12px" }}
                                  onMouseLeave={() => setIsTagFilterOpen(false)}
                                >
                                  <div className="form-check">
                                    <input
                                      id="tag-filter-all"
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={filterTags.length === 0}
                                      onChange={() => setFilterTags([])}
                                    />
                                    <label htmlFor="tag-filter-all" className="form-check-label fw-bold">— Todas as Tags —</label>
                                  </div>
                                  <hr className="my-2" />
                                  {(() => {
                                    // Admin master: separado por time (mesma ordem do modal de tags).
                                    const grupos: { nome: string; tags: ContactTag[] }[] = [];
                                    for (const t of tagsVisiveis) {
                                      const nome = tagsAdminMaster ? (t.nome_time || "Sem time") : "";
                                      let g = grupos.find((x) => x.nome === nome);
                                      if (!g) { g = { nome, tags: [] }; grupos.push(g); }
                                      g.tags.push(t);
                                    }
                                    return grupos.map((g) => (
                                      <div key={g.nome || "meu-time"} className="mb-1">
                                        {g.nome && <div className="small fw-bold text-uppercase text-muted mt-1 mb-1">{g.nome}</div>}
                                        {g.tags.map((t) => (
                                          <div key={t.id} className="form-check">
                                            <input
                                              id={`tag-filter-${t.id}`}
                                              className="form-check-input"
                                              type="checkbox"
                                              checked={filterTags.includes(t.id)}
                                              onChange={() => setFilterTags((prev) =>
                                                prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id])}
                                            />
                                            <label htmlFor={`tag-filter-${t.id}`} className="form-check-label">
                                              <TagChip tag={t} />
                                              <small className="text-muted ms-2">{usoPorTag[t.id] || 0}</small>
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    ));
                                  })()}
                                </div>
                              )}
                            </div>
                          )}

                          {/* 4. Botão Conectar (só mostra se o modo ativo é "unofficial") */}
                          {!isConnected && !isCheckingConnection && (() => {
                            // Admin master gerencia múltiplos times — conexão é per-time, não global
                            if (isAdminMaster) return null;
                            // Login com nº oficial (Meta) não usa a Uazapi — o aviso de
                            // desconexão dela não se aplica. Antes isso vinha do apiModePerTime;
                            // agora lê a origem do dado direto (mesmo critério, sem o state).
                            const instOficial = localStorage.getItem("instanciaoficial");
                            if (instOficial && instOficial !== "0") return null;
                            return (
                              <div className="alert alert-warning py-1 px-3 mb-2 d-flex align-items-center justify-content-between" style={{ fontSize: '12px', borderRadius: '10px' }}>
                                <span><i className="ti ti-alert-circle me-1"></i> WhatsApp Desconectado</span>
                                <Link to={routes.teamUserManagement} className="btn btn-sm btn-primary py-0" style={{ fontSize: '10px' }}>Conectar</Link>
                              </div>
                            );
                          })()}

                          </div>
                          {/* / Wrapper colapsável */}

                        </div>
                      </div>
                    {/* Filtro Todos / Não lidas — logo acima dos cards de contato */}
                    <div className="d-flex gap-2 px-3 pb-2 pt-1">
                      <button
                        type="button"
                        className={`btn btn-sm flex-fill ${!filtroNaoLidas && !filtroFinalizados ? "btn-primary" : "btn-outline-secondary"}`}
                        style={{ borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}
                        onClick={() => { setFiltroNaoLidas(false); setFiltroFinalizados(false); }}
                      >
                        Todos
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm flex-fill ${filtroNaoLidas && !filtroFinalizados ? "btn-primary" : "btn-outline-secondary"}`}
                        style={{ borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}
                        onClick={() => { setFiltroNaoLidas(true); setFiltroFinalizados(false); }}
                      >
                        Não lidas
                      </button>
                      {(existeEtapaFinalizada || filtroFinalizados) && (
                        <button
                          type="button"
                          className={`btn btn-sm flex-fill ${filtroFinalizados ? "btn-primary" : "btn-outline-secondary"}`}
                          style={{ borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}
                          onClick={() => { setFiltroFinalizados(true); setFiltroNaoLidas(false); }}
                          title="Atendimentos finalizados (não aparecem na lista normal)"
                        >
                          Finalizados
                        </button>
                      )}
                    </div>
                    </div>
                    {/* / coluna de controles */}
                    <div className="chats-col-lista" style={{ display: "contents" }}>
                    <Scrollbars style={{ flex: 1 }} ref={contactsListScrollRef} onScrollFrame={handleContactsScroll}>
                      <div className="sidebar-body chat-body" id="chatsidebar">
                        <div className="chat-users-wrap">
                          {/* Spinner de Carregamento */}
                          {loadingContacts && contacts.length === 0 ? (
                            <div className="text-center p-5">
                              <div className="spinner-border text-primary mb-2" role="status"></div>
                              <p className="text-muted">Carregando contatos...</p>
                            </div>
                          ) : null}

                          {/* Mensagem de Vazio */}
                          {!loadingContacts && contacts.length === 0 && (
                            <div className="text-center p-5">
                              <i className="ti ti-users-off fs-24 text-muted mb-2 d-block"></i>
                              <p className="text-muted">Nenhum contato encontrado.</p>
                              <button
                                className="btn btn-sm btn-outline-primary mt-2"
                                onClick={() => fetchContactsFull()}
                              >
                                Recarregar
                              </button>
                            </div>
                          )}

                          {visibleContacts.map((contact) => (
                            <ContactRow
                              key={contact.id}
                              contact={contact}
                              isActive={selectedContact == contact.id}
                              isDark={isDark}
                              stages={stages}
                              onSelect={handleSelectContact}
                              onPrefetch={prefetchConversa}
                              tags={tagsDoContato(contact.id_time, contact.fone)}
                            />
                          ))}

                          {/* Indicador de carregando mais contatos. Antes exigia
                              contactsLimit > 200, então só aparecia depois que o
                              operador já tinha rolado a lista — no refill automático
                              (filtro recém-aplicado) a tela ficava parada, sem sinal
                              de que ainda havia coisa vindo. */}
                          {(carregandoMais || buscandoContatos) && (
                            <div className="text-center py-3">
                              <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                              <span className="ms-2 text-muted" style={{ fontSize: 12 }}>Carregando mais...</span>
                            </div>
                          )}

                        </div>
                      </div>
                    </Scrollbars>
                    </div>
                    {/* / coluna da lista */}
                  </div>
                </div>
              )}
              {/* / Chats Sidebar */}
              {/* Chat Window */}
              {(!isMobile || showChatMobile) && selectedContact && (
                <div
                  className={`chat chat-messages show card`}
                  id="middle"
                  // Fora da área de mensagens o arquivo solto é apenas engolido: sem isso o
                  // navegador abriria o arquivo na aba e o usuário perderia a conversa.
                  onDragOver={(e) => { if (arrastaArquivos(e)) e.preventDefault(); }}
                  onDrop={(e) => { if (arrastaArquivos(e)) e.preventDefault(); }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: isMobile ? mobileHeight : "100%",
                    width: isMobile ? "calc(100vw - 30px)" : "100%",
                    minHeight: 0,
                    backgroundColor: isDark ? "#0b141a" : "#ece5dd",
                    backgroundImage: isDark
                      ? "url('/assets/img/chatbackground-dark.png')"
                      : "url('/assets/img/chatbackground-light.png')",
                    backgroundSize: "330px",
                    backgroundRepeat: "repeat",
                    backgroundPosition: "center",
                    backgroundBlendMode: "normal",
                    border: "none",
                    borderRadius: 0,
                    overflow: "hidden",
                  }}
                >
                    {/* 🔹 Header Refatorado Mobile & Desktop */}
                    <div className="chat-header card-header d-flex align-items-center gap-2" style={{ flexWrap: "wrap", rowGap: 8, overflow: "visible", padding: "10px 16px", minHeight: "60px", height: "auto", background: isDark ? "#111111" : "#ffffff", borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", borderRadius: 0, position: "relative", zIndex: 40 }}>

                      {selectedContact && (
                        isMobile ? (
                          <div className="d-flex align-items-center w-100" style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", gap: "8px" }}>
                            {mobileSearchOpen ? (
                              <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 8, width: "100%" }}>
                                <span
                                  role="button"
                                  style={{ width: 32, minWidth: 32, height: 38, flex: "0 0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? '#e0e0e0' : '#333' }}
                                  onClick={() => { setChatSearchTerm(""); setMobileSearchOpen(false); }}
                                  title="Fechar busca"
                                >
                                  <i className="ti ti-arrow-left fs-20" />
                                </span>
                                <div
                                  className="d-flex align-items-center gap-1 px-3"
                                  style={{ flex: "1 1 0", minWidth: 0, height: 38, borderRadius: 19, background: isDark ? "#1f1f1f" : "#f0f2f5", border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)" }}
                                >
                                  <i className="ti ti-search text-muted flex-shrink-0" style={{ fontSize: 16 }} />
                                  <input
                                    type="text"
                                    autoFocus
                                    className="border-0 shadow-none flex-grow-1"
                                    placeholder="Buscar na conversa..."
                                    value={chatSearchTerm}
                                    onChange={(e) => setChatSearchTerm(e.target.value)}
                                    style={{ fontSize: 14, background: "transparent", outline: "none", minWidth: 0, color: isDark ? "#e0e0e0" : "#1a1a2e" }}
                                  />
                                  {chatSearchTerm && (
                                    <>
                                      <span className="text-muted flex-shrink-0" style={{ fontSize: 11, minWidth: 26, textAlign: "right" }}>
                                        {chatSearchMatches.length > 0 ? `${currentMatchIndex + 1}/${chatSearchMatches.length}` : "0"}
                                      </span>
                                      <span role="button" onClick={prevMatch} className="text-dark flex-shrink-0" style={{ width: 22, minWidth: 22, cursor: "pointer", textAlign: "center" }} title="Anterior"><i className="ti ti-chevron-up" /></span>
                                      <span role="button" onClick={nextMatch} className="text-dark flex-shrink-0" style={{ width: 22, minWidth: 22, cursor: "pointer", textAlign: "center" }} title="Próxima"><i className="ti ti-chevron-down" /></span>
                                      <span role="button" onClick={() => setChatSearchTerm("")} className="text-danger flex-shrink-0" style={{ width: 22, minWidth: 22, cursor: "pointer", textAlign: "center" }} title="Limpar"><i className="ti ti-x" /></span>
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                            <>
                            {/* 1. Botão Voltar */}
                            <div className="flex-shrink-0">
                              <button
                                className="btn btn-sm border-0 bg-transparent text-dark p-0 text-decoration-none"
                                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowChatMobile(false);
                                }}
                              >
                                <i className="ti ti-arrow-left fs-20" />
                              </button>
                            </div>

                            {/* 2. Nome e Fone (Centro/Esquerda) */}
                            <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                              <h6 className="mb-0 text-truncate" style={{ fontSize: "16px", lineHeight: "1.2" }}>
                                {selectedContactData?.nome || selectedContactData?.fone}
                              </h6>
                              {selectedContactData?.nome && (
                                <small className="text-muted text-truncate d-block" style={{ fontSize: "12px" }}>
                                  {selectedContactData.fone}
                                </small>
                              )}
                              {tagsLiberadas && selectedContactData && (
                                <HeaderTags
                                  tagsDoContato={tagsDoContato(selectedContactData.id_time, selectedContactData.fone) || SEM_TAGS}
                                  todasTags={tagsDoTime(selectedContactData.id_time)}
                                  onAlternar={(t, ativo) => alternarTagContato(selectedContactData.fone, t, ativo)}
                                  onGerenciar={() => setShowTagsModal(true)}
                                />
                              )}
                              {/* Indicador + filtro de instância (time 159) — mobile */}
                              {ehTimeNumeroPorDono(selectedContactData?.id_time) && (() => {
                                const envioUserId = selectedContactData?.id_user ?? Number(localStorage.getItem("id_user"));
                                const donoUser = usuariosDoTime.find((u) => u.id === envioUserId);
                                const instanciasContato = Array.from(new Set((selectedContactData?.messages || [])
                                  .map((m) => (m.ins == null ? null : String(m.ins)))
                                  .filter((v): v is string => v != null)));
                                const mostrarSeletor = isAnyAdmin && instanciasContato.length > 1;
                                if (!donoUser && !mostrarSeletor) return null;
                                return (
                                  <div className="d-flex align-items-center gap-1 mt-1" style={{ flexWrap: "wrap" }}>
                                    {donoUser && (
                                      <span style={{ fontSize: 10.5, fontWeight: 600, color: "#16a34a", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>
                                        <i className="ti ti-user" style={{ fontSize: 11 }} /> {donoUser.nome}
                                      </span>
                                    )}
                                    {donoUser?.fone && (
                                      <span style={{ fontSize: 10.5, fontWeight: 600, color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", background: isDark ? "rgba(77,216,232,0.12)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.08)", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>
                                        <i className="ti ti-device-mobile" style={{ fontSize: 11 }} /> {formatNumeroWhats(donoUser.fone)}
                                      </span>
                                    )}
                                    {mostrarSeletor && (
                                      <select
                                        className="form-select form-select-sm"
                                        value={filtroInstancia ?? ""}
                                        onChange={(e) => setFiltroInstancia(e.target.value || null)}
                                        style={{ width: "auto", maxWidth: 130, fontSize: 10.5, height: 26, padding: "0 4px" }}
                                        title="Filtrar mensagens por número"
                                      >
                                        <option value="">Todos</option>
                                        {instanciasContato.map((ins) => (
                                          <option key={ins} value={ins}>{labelInstancia(ins)}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Compartilhar conversa (deep-link) — logo após o nome/número */}
                            <div className="flex-shrink-0">
                              <button
                                className={`btn btn-sm border-0 bg-transparent p-0 text-decoration-none ${shareCopied ? "text-success" : "text-dark"}`}
                                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => compartilharConversa(selectedContactData?.fone)}
                                title="Copiar link desta conversa"
                              >
                                <i className={`${shareCopied ? "ti ti-check" : "ti ti-share"} fs-22`} />
                              </button>
                            </div>

                            {/* Marcar como NÃO lido — marca as últimas mensagens recebidas */}
                            <div className="flex-shrink-0">
                              <button
                                className={`btn btn-sm border-0 bg-transparent p-0 text-decoration-none ${naoLidoMarcado ? "text-success" : "text-dark"}`}
                                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={marcarComoNaoLido}
                                title="Marcar como não lido"
                              >
                                <i className={`${naoLidoMarcado ? "ti ti-check" : "ti ti-mail-opened"} fs-22`} />
                              </button>
                            </div>

                            {/* Botão Enviar Pix (Clientes habilitados - Mobile) */}
                            {PIX_ENABLED_TIMES.includes(localStorage.getItem("id_cliente") || "") && (
                              <div className="flex-shrink-0">
                                <button
                                  className="btn btn-sm border-0 bg-transparent text-success p-0 text-decoration-none"
                                  style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  onClick={() => setShowPixModal(true)}
                                  title="Enviar Pix"
                                >
                                  <i className="ti ti-qrcode fs-24" />
                                </button>
                              </div>
                            )}

                            {/* 3. Botão Buscar mensagem */}
                            <div className="flex-shrink-0">
                              <button
                                className="btn btn-sm border-0 bg-transparent text-dark p-0 text-decoration-none"
                                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setMobileSearchOpen(true)}
                                title="Buscar na conversa"
                              >
                                <i className="ti ti-search fs-22" />
                              </button>
                            </div>

                            {/* Botão Info */}
                            <div className="flex-shrink-0">
                              <button
                                className="btn btn-sm border-0 bg-transparent text-primary p-0 text-decoration-none"
                                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setShowInfoModal(true)}
                              >
                                <i className="ti ti-info-circle fs-24" />
                              </button>
                            </div>

                            </>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2" style={{ flex: "1 1 auto", minWidth: 0 }}>
                            <img
                              src={avatarSrc}
                              alt="Avatar"
                              className="rounded-circle flex-shrink-0"
                              style={{ width: "42px", height: "42px", objectFit: "cover", border: "2px solid rgba(var(--brand-primary-rgb, 0, 126, 148),0.15)", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}
                            />

                            <div className="d-flex flex-column overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="d-flex align-items-center gap-2">
                                {isEditingName ? (
                                  <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={nomeEditado}
                                      onChange={(e) => setNomeEditado(e.target.value)}
                                      style={{ maxWidth: 180 }}
                                    />
                                    <button
                                      className="btn btn-sm btn-success p-1"
                                      style={{ lineHeight: 1 }}
                                      onClick={async () => {
                                        const id_cliente = localStorage.getItem("id_cliente");
                                        await fetch(`${API_URL}/editar_nome_contato`, {
                                          method: "POST",
                                          headers: { "Content-Type": "application/json" },
                                          body: JSON.stringify({ fone: selectedContactData?.fone, nome: nomeEditado }),
                                        });
                                        setContacts(prev =>
                                          prev.map(c =>
                                            c.id === selectedContactData?.id
                                              ? { ...c, nome: nomeEditado }
                                              : c
                                          )
                                        );
                                        setIsEditingName(false);
                                      }}
                                    >
                                      ✔
                                    </button>
                                    <button
                                      className="btn btn-sm btn-secondary p-1"
                                      style={{ lineHeight: 1 }}
                                      onClick={() => setIsEditingName(false)}
                                    >
                                      ✖
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <h6 className="mb-0 text-truncate" title={selectedContactData?.nome}>
                                      {selectedContactData?.nome || "(sem nome)"}
                                    </h6>
                                    <button
                                      className="btn btn-sm p-0 border-0 text-secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNomeEditado(selectedContactData?.nome || "");
                                        setIsEditingName(true);
                                      }}
                                    >
                                      ✏️
                                    </button>
                                  </>
                                )}
                              </div>
                              <p className="mb-0 text-muted text-truncate" style={{ fontSize: "13px" }}>
                                {selectedContactData?.fone || ""}
                              </p>
                              {tagsLiberadas && selectedContactData && (
                                <HeaderTags
                                  tagsDoContato={tagsDoContato(selectedContactData.id_time, selectedContactData.fone) || SEM_TAGS}
                                  todasTags={tagsDoTime(selectedContactData.id_time)}
                                  onAlternar={(t, ativo) => alternarTagContato(selectedContactData.fone, t, ativo)}
                                  onGerenciar={() => setShowTagsModal(true)}
                                />
                              )}
                            </div>

                            {/* Compartilhar conversa (deep-link) — logo após o nome/número */}
                            <button
                              className={`btn btn-sm border-0 bg-transparent p-0 text-decoration-none flex-shrink-0 ${shareCopied ? "text-success" : "text-secondary"}`}
                              style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => compartilharConversa(selectedContactData?.fone)}
                              title="Copiar link desta conversa"
                            >
                              <i className={shareCopied ? "ti ti-check" : "ti ti-share"} style={{ fontSize: 19 }} />
                            </button>

                            {/* Marcar como NÃO lido — marca as últimas mensagens recebidas */}
                            <button
                              className={`btn btn-sm border-0 bg-transparent p-0 text-decoration-none flex-shrink-0 ${naoLidoMarcado ? "text-success" : "text-secondary"}`}
                              style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={marcarComoNaoLido}
                              title="Marcar como não lido"
                            >
                              <i className={naoLidoMarcado ? "ti ti-check" : "ti ti-mail-opened"} style={{ fontSize: 19 }} />
                            </button>

                            {/* Indicador: atendente atual (nome) + número que atende (time 159) */}
                            {ehTimeNumeroPorDono(selectedContactData?.id_time) && (() => {
                              const envioUserId = selectedContactData?.id_user ?? Number(localStorage.getItem("id_user"));
                              const donoUser = usuariosDoTime.find((u) => u.id === envioUserId);
                              if (!donoUser) return null;
                              return (
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  {/* Label do atendente atual (mesmo visual verde do seletor de troca de atendente) */}
                                  <span
                                    className="d-flex align-items-center gap-1"
                                    title={`Atendente: ${donoUser.nome}`}
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#16a34a",
                                      background: "rgba(34,197,94,0.08)",
                                      border: "1px solid rgba(0,0,0,0.08)",
                                      borderRadius: 8,
                                      padding: "3px 10px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <i className="ti ti-user" style={{ fontSize: 14 }} />
                                    {donoUser.nome}
                                  </span>
                                  {/* Número que atende */}
                                  {donoUser.fone && (
                                    <span
                                      className="d-flex align-items-center gap-1"
                                      title={`Atendendo por ${donoUser.nome} — ${formatNumeroWhats(donoUser.fone)}`}
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)",
                                        background: isDark ? "rgba(77,216,232,0.12)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.08)",
                                        border: isDark ? "1px solid rgba(77,216,232,0.25)" : "1px solid rgba(var(--brand-primary-rgb, 0, 126, 148),0.2)",
                                        borderRadius: 8,
                                        padding: "3px 10px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <i className="ti ti-device-mobile" style={{ fontSize: 14 }} />
                                      {formatNumeroWhats(donoUser.fone)}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Grupo 192 (TNP): NÚMERO vinculado ao lead (resolve a ins do lead -> número) */}
                            {ehGrupoNumeroNoHeader() && (() => {
                              const insLead = String(
                                (selectedContactData?.messages || [])
                                  .map((m) => (m.ins == null ? null : String(m.ins)))
                                  .filter((v): v is string => v != null)
                                  .pop() ?? ""
                              );
                              const numeroLead = insLead ? mapaInstanciaNumero[insLead] : "";
                              if (!numeroLead) return null;
                              // Cor + rótulo pela saúde da Meta do número do lead (fallback p/ a
                              // cor da marca enquanto carrega / saúde desconhecida).
                              const saudeLead = insLead ? mapaInstanciaSaude[insLead] : "";
                              const c =
                                saudeLead === "vermelho"
                                  ? { fg: isDark ? "#f87171" : "#dc2626", bg: "rgba(239,68,68,0.12)", bd: "rgba(239,68,68,0.3)", lbl: "Em risco" }
                                  : saudeLead === "amarelo"
                                  ? { fg: isDark ? "#fbbf24" : "#d97706", bg: "rgba(245,158,11,0.12)", bd: "rgba(245,158,11,0.3)", lbl: "Atenção" }
                                  : saudeLead === "verde"
                                  ? { fg: isDark ? "#4ade80" : "#16a34a", bg: "rgba(34,197,94,0.10)", bd: "rgba(34,197,94,0.3)", lbl: "Saudável" }
                                  : { fg: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", bg: isDark ? "rgba(77,216,232,0.12)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.08)", bd: isDark ? "rgba(77,216,232,0.25)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.2)", lbl: "" };
                              return (
                                <span
                                  className="d-flex align-items-center gap-1 flex-shrink-0"
                                  title={`Número do lead — ${formatNumeroWhats(numeroLead)}${c.lbl ? ` · ${c.lbl} na Meta` : ""}`}
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: c.fg,
                                    background: c.bg,
                                    border: `1px solid ${c.bd}`,
                                    borderRadius: 8,
                                    padding: "3px 10px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <i className="ti ti-device-mobile" style={{ fontSize: 14 }} />
                                  {formatNumeroWhats(numeroLead)}
                                </span>
                              );
                            })()}
                          </div>
                        )
                      )}

                      {/* 4. Área de Ações (Desktop Only) — popover recolhível p/ não tampar o nome */}
                      {!isMobile && selectedContact && (
                        <div ref={headerActionsRef} className="position-relative d-flex align-items-center flex-wrap gap-2" style={{ marginLeft: "auto", justifyContent: "flex-end", rowGap: 8, minWidth: 0, flexShrink: 1 }}>
                          {/* Filtro de instância (time 159, somente admin): só filtra a VISÃO das mensagens, não afeta o envio */}
                          {isAnyAdmin && ehTimeNumeroPorDono(selectedContactData?.id_time) && (() => {
                            const instanciasContato = Array.from(new Set((selectedContactData?.messages || [])
                              .map((m) => (m.ins == null ? null : String(m.ins)))
                              .filter((v): v is string => v != null)));
                            if (instanciasContato.length <= 1) return null;
                            return (
                              <select
                                className="form-select form-select-sm"
                                value={filtroInstancia ?? ""}
                                onChange={(e) => setFiltroInstancia(e.target.value || null)}
                                title="Filtrar mensagens por número (instância)"
                                style={{ width: "auto", maxWidth: 180, fontSize: 12, height: 32 }}
                              >
                                <option value="">Todos os números</option>
                                {instanciasContato.map((ins) => (
                                  <option key={ins} value={ins}>{labelInstancia(ins)}</option>
                                ))}
                              </select>
                            );
                          })()}
                          {/* === SEMPRE VISÍVEIS (fora do popover): atendente, buscar msg, atendimento humano === */}

                          {/* Transferência de atendente (admin only) */}
                          {isAnyAdmin && usuariosDoTime.length > 0 && (() => {
                            const atual = usuariosDoTime.find(
                              (u) => u.id === selectedContactData?.id_user
                            );
                            return (
                              <div className="d-flex align-items-center gap-1">
                                <span className="text-muted small d-none d-lg-inline" style={{ fontSize: 11 }}>
                                  Atendente:
                                </span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{
                                    height: 32,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    minWidth: 130,
                                    maxWidth: 180,
                                    background: atual ? "rgba(34,197,94,0.08)" : "rgba(148,163,184,0.12)",
                                    color: atual ? "#16a34a" : "#64748b",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                  }}
                                  value={selectedContactData?.id_user ?? ""}
                                  onChange={(e) => {
                                    const novoId = parseInt(e.target.value, 10);
                                    const u = usuariosDoTime.find((x) => x.id === novoId);
                                    if (u) transferirAtendente(u.id, u.nome, u.id_time);
                                  }}
                                  title="Transferir atendente"
                                >
                                  <option value="" disabled>
                                    Sem atendente
                                  </option>
                                  {usuariosDoTime.map((u) => (
                                    <option key={u.id} value={u.id}>
                                      {rotuloAtendenteChat(u)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })()}

                          {/* --- Busca Interna de Mensagens --- */}
                          <div className="d-flex align-items-center border rounded px-2 bg-white" style={{ height: "32px" }}>
                            <input
                              type="text"
                              className="border-0 shadow-none p-0"
                              placeholder="Buscar msg..."
                              value={chatSearchTerm}
                              onChange={(e) => setChatSearchTerm(e.target.value)}
                              style={{ width: "100px", fontSize: "13px", outline: "none" }}
                            />
                            {chatSearchTerm && (
                              <>
                                <span className="text-muted small mx-1" style={{ fontSize: "11px" }}>
                                  {chatSearchMatches.length > 0 ? `${currentMatchIndex + 1}/${chatSearchMatches.length}` : "0"}
                                </span>
                                <div className="d-flex gap-1">
                                  <button onClick={prevMatch} className="btn btn-sm btn-link p-0 text-dark" title="Anterior">
                                    <i className="ti ti-chevron-up" />
                                  </button>
                                  <button onClick={nextMatch} className="btn btn-sm btn-link p-0 text-dark" title="Próxima">
                                    <i className="ti ti-chevron-down" />
                                  </button>
                                  <button onClick={() => setChatSearchTerm("")} className="btn btn-sm btn-link p-0 text-danger" title="Limpar">
                                    <i className="ti ti-x" />
                                  </button>
                                </div>
                              </>
                            )}
                            {!chatSearchTerm && <i className="ti ti-search text-muted small" />}
                          </div>

                          {/* Switch de Atendimento Humano */}
                          {(() => {
                            const humanStage = stages.find(
                              (s) =>
                                s.funil === selectedContactData?.funil &&
                                s.atendimento_humano === "Sim"
                            );

                            if (humanStage) {
                              const isHuman = humanStage.nome_funil === selectedContactData?.nome_funil;
                              
                              return (
                                <div className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                                  <div 
                                    className="form-check form-switch m-0 d-flex align-items-center gap-2"
                                                                        onClick={(e) => {
                                                                          e.stopPropagation();
                                                                          if (!isHuman) {
                                                                            // Ativar sem confirmação
                                                                            if (selectedContactData?.fone) {
                                                                              updateFunnelStage(selectedContactData.fone, humanStage.nome_funil);
                                                                            }
                                                                          } else {                                                                                    // Desativar (sair)
                                                                                    const id_cliente = localStorage.getItem("id_cliente");
                                                                                    if (!selectedContactData?.fone || !id_cliente) return;
                                      
                                                                                    fetch(`${API_URL}/voltar_etapa_anterior`, {
                                                                                      method: "POST",
                                                                                      headers: { "Content-Type": "application/json" },
                                                                                      body: JSON.stringify({
                                                                                        fone: selectedContactData.fone,
                                                                                        id_cliente
                                                                                      })
                                                                                    })
                                                                                    .then(res => res.json())
                                                                                                                                  .then(data => {
                                                                                                                                    if (data.found) {
                                                                                                                                      // Se a etapa anterior está em outro funil, atualiza ambos atomicamente.
                                                                                                                                      // Caso contrário (ou se o backend não retornou funnel_name), atualiza só etapa.
                                                                                                                                      const targetFunnel = data.funnel_name as string | undefined;
                                                                                                                                      if (targetFunnel && targetFunnel !== selectedContactData?.funil) {
                                                                                                                                        updateFunnelCombined(selectedContactData.fone, data.stage_name, targetFunnel);
                                                                                                                                      } else {
                                                                                                                                        updateFunnelStage(selectedContactData.fone, data.stage_name);
                                                                                                                                      }
                                                                                                                                    } else {
                                                                                                                                      alert("Não foi possível encontrar uma etapa anterior automaticamente. Por favor, selecione manualmente no dropdown 'Etapa'.");
                                                                                                                                    }
                                                                                                                                  })                                                                                    .catch(err => {
                                                                                      console.error(err);
                                                                                      alert("Erro ao buscar etapa anterior.");
                                                                                    });
                                                                                  }                                    }}
                                  >
                                    <input 
                                      className="form-check-input" 
                                      type="checkbox" 
                                      role="switch" 
                                      checked={isHuman} 
                                      readOnly 
                                      style={{ cursor: "pointer" }}
                                    />
                                    <label 
                                      className="form-check-label m-0 fw-bold" 
                                      style={{ 
                                        color: isHuman ? "#28a745" : "#6c757d", 
                                        cursor: "pointer",
                                        fontSize: "13px"
                                      }}
                                    >
                                      Atendimento Humano
                                    </label>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Finalizar / reabrir atendimento — só quando o funil do contato tem etapa
                              marcada como "atendimento finalizado" (funil.atendimento_finalizado). */}
                          {(() => {
                            const etapaFin = stages.find(
                              (s) => s.funil === selectedContactData?.funil && s.atendimento_finalizado === "Sim"
                            );
                            if (!etapaFin || !selectedContactData?.fone) return null;
                            return (
                              <BotaoFinalizarAtendimento
                                finalizado={etapaFin.nome_funil === selectedContactData?.nome_funil}
                                onFinalizar={() => finalizarAtendimento(etapaFin.nome_funil)}
                                onReabrir={reabrirAtendimento}
                              />
                            );
                          })()}

                          {/* Promessa — registra o que o cliente prometeu enviar e para quando.
                              Só para os times de RETENÇÃO do TNP; carrega os checklists do card. */}
                          {ehTimeRetencao(timeDoLeadAtual()) && (
                            <button
                              type="button"
                              className="dr-escopo dr-btn-linha"
                              style={{ height: 32 }}
                              onClick={() => abrirPromessaModal()}
                              title="Registrar promessa do cliente"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3.2 1.8" />
                              </svg>
                              Promessa
                            </button>
                          )}

                          {/* Uploads internos (TNP, grupo 192) — arquivos anexados a ESTE contato.
                              O contador sai das mensagens já carregadas; a lista do modal vem do
                              banco (não depende do filtro de data da conversa). */}
                          {ehGrupoTnpUpload() && (() => {
                            const qtd = (selectedContactData?.messages || []).filter(
                              (m: Message) => m.type === "tnpupload"
                            ).length;
                            return (
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                style={{ height: 32, whiteSpace: "nowrap" }}
                                onClick={abrirTnpUploads}
                                title="Arquivos internos deste contato"
                              >
                                <i className="ti ti-cloud-upload" /> Uploads
                                {qtd > 0 && (
                                  <span
                                    style={{
                                      background: isDark ? "#3d4448" : "#e9edf1",
                                      color: isDark ? "#c3cad0" : "#5d666e",
                                      borderRadius: 10,
                                      padding: "0 6px",
                                      fontSize: 11,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {qtd}
                                  </span>
                                )}
                              </button>
                            );
                          })()}

                          {/* Ligação (TNP, grupo 192) — um clique marca no thread que foi feita
                              uma ligação para este lead (type='call'). Não envia nada ao cliente. */}
                          {ehGrupoLigacao() && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                              style={{ height: 32, whiteSpace: "nowrap" }}
                              onClick={registrarLigacao}
                              disabled={!selectedContactData || salvandoLigacao}
                              title="Registrar que foi feita uma ligação para este contato"
                            >
                              <i className={salvandoLigacao ? "ti ti-dots" : "ti ti-phone"} /> Ligação
                            </button>
                          )}

                          {/* Cards — sempre visível no header (não depende de expandir "Informações") */}
                          <button
                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                            style={{ height: 32, whiteSpace: "nowrap" }}
                            onClick={handleOpenCardModal}
                            title="Visualizar Cards"
                          >
                            <i className="ti ti-id" /> Cards
                          </button>

                          {/* Relógio de envios programados. Fosco = nada marcado.
                              Laranja = tem coisa agendada. Vermelho = sai em até 24h. */}
                          {(() => {
                            const nivel = agenda?.nivel || "nenhum";
                            const qtd = (agenda?.itens || []).filter((i) => !i.inativo).length;
                            const cor = nivel === "alerta" ? "#dc2626" : nivel === "aviso" ? "#ea580c" : (isDark ? "#5b6167" : "#adb5bd");
                            const aceso = nivel !== "nenhum";
                            return (
                              <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1 border"
                                style={{
                                  height: 32, whiteSpace: "nowrap", color: cor, borderColor: cor,
                                  background: aceso ? (nivel === "alerta" ? "rgba(220,38,38,0.08)" : "rgba(234,88,12,0.08)") : "transparent",
                                  opacity: aceso ? 1 : 0.55,
                                }}
                                onClick={() => setShowAgendaModal(true)}
                                title={aceso
                                  ? `${qtd} envio(s) programado(s) para este lead`
                                  : "Nenhum envio programado para este lead"}
                              >
                                <i className={agendaCarregando ? "ti ti-dots" : "ti ti-clock"} />
                                {aceso && <span style={{ fontSize: 12, fontWeight: 700 }}>{qtd}</span>}
                              </button>
                            );
                          })()}


                          {/* === Toggle "Informações" + popover com o RESTANTE (Pix, Funil, Etapa, detalhes) === */}
                          {/* Toggle recolher/expandir (sempre visível) */}
                          <button
                            className="btn btn-sm btn-light border d-flex align-items-center gap-1"
                            style={{ height: 32, whiteSpace: "nowrap" }}
                            onClick={() => setHeaderActionsExpanded((v) => !v)}
                            title={headerActionsExpanded ? "Fechar" : "Informações"}
                          >
                            <i className={headerActionsExpanded ? "ti ti-x" : "ti ti-info-circle"} />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>
                              {headerActionsExpanded ? "Fechar" : "Informações"}
                            </span>
                          </button>
                          {headerActionsExpanded && (
                          <div
                            className="d-flex flex-wrap align-items-center gap-2"
                            style={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              marginTop: 6,
                              zIndex: 1000,
                              background: isDark ? "#1b1b1b" : "#ffffff",
                              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.1)",
                              borderRadius: 10,
                              padding: 10,
                              boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
                              width: "max-content",
                              maxWidth: "min(640px, 90vw)",
                              maxHeight: "80vh",
                              overflowY: "auto",
                            }}
                          >

                          {/* Botão Enviar Pix (Clientes habilitados) */}
                          {PIX_ENABLED_TIMES.includes(localStorage.getItem("id_cliente") || "") && (
                            <button
                              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                              onClick={() => setShowPixModal(true)}
                              title="Enviar Pix"
                            >
                              <i className="ti ti-qrcode" /> Enviar Pix
                            </button>
                          )}

                          {/* Funil + Etapa (admin master vê os funis de todos os times do grupo) */}
                          {renderFunilEtapaSelects(
                            { height: 32, fontSize: 12, fontWeight: 600, minWidth: 120, maxWidth: 170, color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", border: "1px solid rgba(0,0,0,0.08)" },
                            "form-select form-select-sm"
                          )}

                          {/* Detalhes do contato (migrados do antigo modal Info):
                              Idioma, Observação e Agendamento — direto no popover. */}
                          <div style={{ flexBasis: "100%", width: "100%", borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", marginTop: 4, paddingTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>

                            {/* Idioma (só quando o time usa idiomas) */}
                            {temIdiomas && (
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: isDark ? "#8696a0" : "#8a94a6", minWidth: 64 }}>
                                  <i className="ti ti-language me-1" />Idioma
                                </span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ height: 30, fontSize: 12, maxWidth: 170 }}
                                  value={valorDoSeletor(selectedContactData?.idioma)}
                                  onChange={async (e) => {
                                    const novoIdioma = e.target.value;
                                    const fone = selectedContactData?.fone || "";
                                    await atualizarIdioma(fone, novoIdioma);
                                    setContacts((prev) => prev.map((c) => c.id === selectedContactData?.id ? { ...c, idioma: novoIdioma } : c));
                                  }}
                                >
                                  {opcoesParaContato(selectedContactData?.idioma).map((o) => (
                                    <option key={o.valor} value={o.valor}>{o.rotulo}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Observação */}
                            <div>
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: isDark ? "#8696a0" : "#8a94a6" }}>
                                  <i className="ti ti-notes me-1" />Observação
                                </span>
                                {!isEditingObs && (
                                  <button
                                    type="button"
                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", fontSize: 12, fontWeight: 600 }}
                                    onClick={() => { setObsValue(selectedContactData?.observacao || ""); setIsEditingObs(true); }}
                                  >
                                    <i className="ti ti-pencil me-1" />Editar
                                  </button>
                                )}
                              </div>
                              {isEditingObs ? (
                                <>
                                  <textarea
                                    className="form-control"
                                    rows={3}
                                    value={obsValue}
                                    onChange={(e) => setObsValue(e.target.value)}
                                    style={{ fontSize: 13, borderRadius: 8, marginBottom: 6 }}
                                  />
                                  <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={savingObs} onClick={() => setIsEditingObs(false)}>Cancelar</button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      disabled={savingObs}
                                      onClick={async () => {
                                        setSavingObs(true);
                                        try {
                                          const id_cliente = localStorage.getItem("id_cliente");
                                          await fetch(`${API_URL}/marcarobs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fone: selectedContactData?.fone, observacao: obsValue, id_cliente }) });
                                          setContacts(prev => prev.map(c => c.id === selectedContactData?.id ? { ...c, observacao: obsValue } : c));
                                          setIsEditingObs(false);
                                        } catch (err) {
                                          console.error("Erro ao salvar observacao:", err);
                                          alert("Falha ao salvar.");
                                        } finally {
                                          isFetchingContacts.current = false;
                                          setSavingObs(false);
                                        }
                                      }}
                                    >
                                      {savingObs ? "Salvando..." : "Salvar"}
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div style={{ padding: "8px 10px", borderRadius: 8, fontSize: 13, whiteSpace: "pre-wrap", background: isDark ? "#1a1a1a" : "#f8f9fa", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)", color: isDark ? "#ccc" : "#444", maxHeight: 120, overflowY: "auto" }}>
                                  {selectedContactData?.observacao || "Nenhuma observação registrada."}
                                </div>
                              )}
                            </div>

                            {/* Agendamento */}
                            <button
                              type="button"
                              className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                              onClick={() => setShowScheduleModal(true)}
                              style={{ background: isDark ? "rgba(77,216,232,0.1)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.06)", color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", border: "none", fontWeight: 600 }}
                            >
                              <i className="ti ti-calendar-event" />Agendar retorno
                            </button>

                            {/* Anotações do lead (TNP, grupo 192) — todas as notas internas da
                                conversa, reunidas aqui para não ter de rolar o thread atrás delas.
                                Mesmas notas do balão amarelo (type='anotacao'), mais recentes no
                                topo. Só leitura: criar/excluir continua no compositor do rodapé. */}
                            {ehGrupoAnotacoesInfo() && (() => {
                              const notas = ((selectedContactData?.messages || []) as Message[])
                                .filter((m) => m.type === "anotacao")
                                .slice()
                                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                              return (
                                <div style={{ borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", paddingTop: 8 }}>
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: isDark ? "#8696a0" : "#8a94a6" }}>
                                      <i className="ti ti-notes me-1" />Anotações
                                    </span>
                                    {notas.length > 0 && (
                                      <span
                                        style={{
                                          background: isDark ? "#3a3320" : "#FEF9E7",
                                          color: isDark ? "#e9d98a" : "#9a7d0a",
                                          border: isDark ? "1px solid #5c4f28" : "1px solid #f5e6a8",
                                          borderRadius: 10,
                                          padding: "0 6px",
                                          fontSize: 11,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {notas.length}
                                      </span>
                                    )}
                                  </div>
                                  {notas.length === 0 ? (
                                    <div style={{ padding: "8px 10px", borderRadius: 8, fontSize: 13, background: isDark ? "#1a1a1a" : "#f8f9fa", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)", color: isDark ? "#ccc" : "#444" }}>
                                      Nenhuma anotação registrada.
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                                      {notas.map((n) => {
                                        const autorNome =
                                          (n.id_user != null && nomePorUserId[n.id_user]) || "Alguém";
                                        return (
                                          <div
                                            key={String(n.id)}
                                            style={{
                                              background: isDark ? "#3a3320" : "#FEF9E7",
                                              border: isDark ? "1px solid #5c4f28" : "1px solid #f5e6a8",
                                              borderLeft: "3px solid #f1c40f",
                                              borderRadius: "4px 8px 8px 8px",
                                              padding: "6px 10px",
                                            }}
                                          >
                                            <div className="d-flex align-items-center justify-content-between gap-2" style={{ marginBottom: 2 }}>
                                              <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#e9d98a" : "#9a7d0a" }}>
                                                {autorNome}
                                              </span>
                                              <span style={{ fontSize: 10.5, fontWeight: 500, color: isDark ? "#9a8f6a" : "#b3a24d", whiteSpace: "nowrap" }}>
                                                {new Date(n.timestamp).toLocaleString("pt-BR", {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            </div>
                                            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.45, color: isDark ? "#ece7d2" : "#5c5320" }}>
                                              {parseNotaSegmentos(n.mensagem).map((seg, i) =>
                                                seg.tipo === "mencao" ? (
                                                  <span key={i} style={{ color: "var(--brand-primary, #007e94)", fontWeight: 600 }}>
                                                    {seg.valor}
                                                  </span>
                                                ) : (
                                                  <span key={i}>{seg.valor}</span>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className="chat-body chat-page-group slimscroll card-body"
                      onDragEnter={handleDropAreaEnter}
                      onDragOver={handleDropAreaOver}
                      onDragLeave={handleDropAreaLeave}
                      onDrop={handleDropAreaDrop}
                      style={{
                        flex: 1,
                        overflow: "hidden", // Garante que o scroll fique dentro deste container
                        padding: 0,
                        minHeight: 0,
                        position: "relative", // âncora do overlay de arrastar e da prévia
                        // Fundo da conversa estilo WhatsApp direto na área das msgs (dark e claro).
                        backgroundColor: isDark ? "#0b141a" : "#ece5dd",
                        backgroundImage: isDark ? "url('/assets/img/chatbackground-dark.png')" : "url('/assets/img/chatbackground-light.png')",
                        backgroundSize: "330px",
                        backgroundRepeat: "repeat",
                        backgroundPosition: "center",
                      }}
                    >
                      <Scrollbars 
                        onScroll={handleChatScroll} 
                        ref={scrollbarsRef} 
                        style={{ width: "100%", height: "100%" }} // Força preenchimento total
                        renderView={props => (
                          <div {...props} style={{ ...props.style, overflowX: "hidden" }} />
                        )}
                      >
                        <div className="messages">
                          {(() => {
                            // 1) pega o contato selecionado
                            const selected = contacts.find(c => c.id === selectedContact);
                            const selectedMsgs = selected?.messages ?? [];
                            // 2) Otimista de mídia (áudio/imagem/vídeo): mantém o balão temp
                            // (blob, instantâneo) e ESCONDE a versão real equivalente do banco
                            // enquanto o temp existe — assim não some/pisca nem recarrega o
                            // <audio> na transição (igual ao envio de texto). O temp é limpo ao
                            // trocar de contato; aí a versão persistida (banco) aparece.
                            const reaisOcultar = new Set<string | number>();
                            const usadosMidia = new Set<string | number>();
                            const pendentesAqui = tempsDoFone(pendingMessages, selected?.fone);
                            pendentesAqui.forEach((temp) => {
                              if (!String(temp.id).startsWith("temp-media-")) return;
                              const real = selectedMsgs.find((r) =>
                                !usadosMidia.has(r.id) &&
                                r.status === "send" &&
                                r.type === temp.type &&
                                String(r.fone) === String(temp.fone) &&
                                new Date(r.timestamp).getTime() >= new Date(temp.timestamp).getTime() - 10000
                              );
                              if (real) { usadosMidia.add(real.id); reaisOcultar.add(real.id); }
                            });
                            // 3) combina (estáticas - ocultadas) + pendentes, ordena por timestamp
                            const allMsgsCombined = [
                              ...selectedMsgs.filter((m) => !reaisOcultar.has(m.id)),
                              ...pendentesAqui
                            ]
                            .sort((a,b) =>
                              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                            );

                            // Rede de segurança: nunca renderizar a mesma mensagem duas vezes,
                            // qualquer que seja a origem da duplicata em `selected.messages`
                            // (merge do polling, reidratação, corrida entre realtime e
                            // refetch). Mantém sempre a 1ª ocorrência.
                            //
                            // Duas camadas, porque uma só não basta:
                            //  a) por ID — resolve cópia da MESMA linha do banco.
                            //  b) por CONTEÚDO (status+tipo+texto, dentro de 2s) — em 10/09/2026
                            //     a duplicata voltou com o dedup por id já em produção: o banco
                            //     tinha 1 linha de cada ("Qualquer duvida durante esse tempo..."
                            //     aparecia 5x na tela, id único no banco), ou seja, as cópias
                            //     chegam ao render com ids DIFERENTES (temp órfão / reinserção)
                            //     e o filtro por id não podia pegá-las por construção.
                            //
                            // 🔴 A janela é curta de propósito: envio repetido de verdade tem de
                            // continuar aparecendo duas vezes. Na mesma conversa do relato havia
                            // dois vídeos iguais enviados com 34s de diferença (wamids distintos,
                            // dois envios reais) — precisam sobreviver ao filtro, e sobrevivem.
                            // Comparação por DIFERENÇA de tempo, não por "balde" arredondado:
                            // balde de 2s separa mensagens a 0,1s que caiam em lados opostos da
                            // fronteira e junta outras a 1,9s.
                            const DEDUP_CONTEUDO_MS = 2000;
                            const idsVistosAllMsgs = new Set<string>();
                            const tsPorAssinatura = new Map<string, number[]>();
                            const allMsgsRaw = allMsgsCombined.filter((m) => {
                              const k = String(m.id);
                              if (idsVistosAllMsgs.has(k)) return false;

                              const ts = new Date(m.timestamp).getTime();
                              const assinatura = `${m.status ?? ""}|${m.type ?? ""}|${m.mensagem ?? ""}`;
                              if (!Number.isNaN(ts)) {
                                const jaVistos = tsPorAssinatura.get(assinatura);
                                if (jaVistos && jaVistos.some((t) => Math.abs(t - ts) <= DEDUP_CONTEUDO_MS)) {
                                  return false;
                                }
                                if (jaVistos) { jaVistos.push(ts); } else { tsPorAssinatura.set(assinatura, [ts]); }
                              }

                              idsVistosAllMsgs.add(k);
                              return true;
                            });

                            // Filtro de instância (time 159, somente admin): mostra só as
                            // mensagens da instância (número) escolhida no header do contato.
                            const allMsgs = (isAnyAdmin && filtroInstancia && ehTimeNumeroPorDono(selected?.id_time))
                              ? allMsgsRaw.filter((m) => String(m.ins ?? "") === filtroInstancia)
                              : allMsgsRaw;

                            // ── Corpo vazio: carregando, falhou, ou vazio de verdade ──────
                            //
                            // 🔴 A regra aqui é assimétrica DE PROPÓSITO: afirmar "não tem
                            // mensagem" numa conversa que tem é um erro caro (o operador
                            // conclui que o lead nunca respondeu e age em cima disso), e
                            // mostrar carregamento onde já não há o que carregar é inofensivo.
                            // Por isso o aviso de vazio exige PROVA — o veredito "vazia", que
                            // só vem de uma resposta 200 do backend com `messages: []`. Todo
                            // o resto, inclusive não sabermos ainda, mostra o carregamento.
                            //
                            // O teste antigo era `allMsgs.length === 0 && !carregandoHistorico`,
                            // e `carregandoHistorico` só liga no fetch COM indicador. No
                            // prefetch de hover — que marca a conversa como carregada e busca
                            // sem indicador — clicar durante o voo fazia o carregador sair cedo
                            // e o corpo anunciava "nenhuma mensagem" numa conversa cheia.
                            const chaveAberta = chaveConversa(selected?.fone, selected?.id_time);
                            const veredicto = veredictoConversa[chaveAberta];
                            // Busca em voo PARA ESTA conversa. Usado em dois lugares: a
                            // linha "Tentando novamente…" (que seria mentira sem busca em
                            // andamento) e, mais abaixo, o badge de histórico em voo quando
                            // já há bolhas na tela.
                            const carregandoHistorico = chaveConversaCarregando === chaveAberta;
                            if (allMsgs.length === 0) {
                              // Ocupa a área inteira da conversa: o placeholder fica no CENTRO
                              // do painel, não colado no topo. `inset: 0` se ancora na viewport
                              // do scroll (o Scrollbars posiciona o view), que é exatamente a
                              // área visível da conversa.
                              const centro: React.CSSProperties = {
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                textAlign: "center",
                                padding: 24,
                                pointerEvents: "none",   // não bloqueia o arrastar-e-soltar
                                color: isDark ? "#8696a0" : "#64748b",
                              };

                              if (veredicto === "vazia") {
                                return (
                                  <div style={centro}>
                                    <i className="ti ti-message-off" style={{ fontSize: 30, opacity: 0.6 }} />
                                    <div style={{ fontSize: 13 }}>Nenhuma mensagem nesta conversa ainda.</div>
                                  </div>
                                );
                              }

                              if (veredicto === "erro") {
                                // Honesto: não sabemos se há mensagens, e dizer "nenhuma" seria
                                // inventar. O carregador tenta de novo sozinho (o catch devolve
                                // a chave ao conversasCarregadasRef).
                                return (
                                  <div style={centro}>
                                    <i className="ti ti-alert-triangle" style={{ fontSize: 30, opacity: 0.6 }} />
                                    <div style={{ fontSize: 13 }}>Não foi possível carregar as mensagens.</div>
                                    {carregandoHistorico && (
                                      <div style={{ fontSize: 12, opacity: 0.8 }}>Tentando novamente…</div>
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div style={centro}>
                                  <div className="spinner-border text-primary" role="status" style={{ width: 30, height: 30 }} />
                                  <div style={{ fontSize: 13 }}>Carregando a conversa…</div>
                                </div>
                              );
                            }

                            // Helper para formatar a data
                            const getDateLabel = (dateStr: string) => {
                              const d = new Date(dateStr);
                              const now = new Date();
                              const dZero = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                              const nowZero = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                              
                              const diffTime = nowZero.getTime() - dZero.getTime();
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                              if (diffDays === 0) return "Hoje";
                              if (diffDays === 1) return "Ontem";
                              
                              return d.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
                            };

                            // 4) Agrupa por Data
                            const groupedMsgs: { [key: string]: typeof allMsgs } = {};
                            allMsgs.forEach(msg => {
                              const label = getDateLabel(msg.timestamp);
                              if (!groupedMsgs[label]) groupedMsgs[label] = [];
                              groupedMsgs[label].push(msg);
                            });

                            // 5) Renderiza os grupos
                            const grupos = Object.entries(groupedMsgs).map(([dateLabel, group]) => (
                              <div key={dateLabel} className="date-group" style={{ position: 'relative', width: '100%' }}>
                                
                                {/* Tag de Data Sticky */}
                                <div 
                                  className="d-flex justify-content-center w-100"
                                  style={{
                                    position: 'sticky',
                                    top: '10px',
                                    zIndex: 5,
                                    marginBottom: '10px',
                                    pointerEvents: 'none' // para clicar através da tag se necessário
                                  }}
                                >
                                  <span
                                    className="badge shadow-sm"
                                    style={{
                                      fontSize: '11.5px',
                                      padding: '6px 16px',
                                      borderRadius: '20px',
                                      fontWeight: 600,
                                      opacity: 0.95,
                                      letterSpacing: '0.3px',
                                      background: isDark ? 'rgba(26,26,46,0.9)' : 'rgba(255,255,255,0.95)',
                                      color: isDark ? '#aaa' : '#5a6678',
                                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                                      backdropFilter: 'blur(8px)',
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                                    }}
                                  >
                                    {dateLabel}
                                  </span>
                                </div>

                                {group.map((message) => {
                                  const isLastMessage = allMsgs.length > 0 && message.id === allMsgs[allMsgs.length - 1].id;

                                  // ── Transferência de atendente: NÃO é mensagem do WhatsApp; renderiza
                                  //    como uma tag centralizada (estilo do separador de data "Hoje/Ontem").
                                  //    Destino = id_user da própria linha (novo dono). Origem = deduzida
                                  //    da última mensagem ANTERIOR que tenha um id_user diferente do destino
                                  //    (o atendente que estava atuando antes). Sem isso, cai no texto salvo.
                                  // ── Ligação registrada (type='call'): evento interno, não é mensagem
                                  //    do WhatsApp. Tag central com ícone de fone. O texto vem do banco
                                  //    ("Ligacao realizada em DD/MM/AAAA HH:MM", já em BRT); a regex só
                                  //    reformata para exibição, com fallback no texto cru.
                                  if (message.type === "call") {
                                    const mm = /(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/.exec(message.mensagem || "");
                                    const quandoTxt = mm ? `${mm[1]} às ${mm[2]}` : "";
                                    const autorLig =
                                      (message.id_user != null && nomePorUserId[Number(message.id_user)]) || null;
                                    return (
                                      <div
                                        key={message.clientKey ?? String(message.id)}
                                        className="d-flex justify-content-center w-100"
                                        style={{ marginTop: "6px", marginBottom: isLastMessage ? "20px" : "6px" }}
                                      >
                                        <span
                                          className="badge shadow-sm"
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            fontSize: "11.5px",
                                            padding: "6px 14px",
                                            borderRadius: "20px",
                                            fontWeight: 600,
                                            letterSpacing: "0.2px",
                                            maxWidth: "88%",
                                            whiteSpace: "normal",
                                            textAlign: "center",
                                            lineHeight: 1.4,
                                            background: isDark ? "rgba(93,74,199,0.28)" : "rgba(93,74,199,0.10)",
                                            color: isDark ? "#c3b5f5" : "#5d4ac7",
                                            border: isDark ? "1px solid rgba(195,181,245,0.22)" : "1px solid rgba(93,74,199,0.22)",
                                          }}
                                        >
                                          <i className="ti ti-phone" style={{ fontSize: 14, flex: "0 0 auto" }} />
                                          <span>{quandoTxt ? `Ligação realizada em ${quandoTxt}` : (message.mensagem || "Ligação realizada")}</span>
                                          {autorLig && (
                                            <span style={{ opacity: 0.7, fontWeight: 500 }}>· {autorLig}</span>
                                          )}
                                        </span>
                                      </div>
                                    );
                                  }

                                  if (message.type === "transfer") {
                                    const toId = message.id_user;
                                    const toName = (toId != null && nomePorUserId[Number(toId)]) || null;
                                    let fromName: string | null = null;
                                    const idxAtual = allMsgs.findIndex((m) => String(m.id) === String(message.id));
                                    for (let i = idxAtual - 1; i >= 0; i--) {
                                      const prev = allMsgs[i];
                                      if (prev?.type === "transfer" || prev?.type === "anotacao") continue;
                                      const uid = prev?.id_user;
                                      if (uid != null && String(uid) !== String(toId ?? "")) {
                                        fromName = nomePorUserId[Number(uid)] || null;
                                        break;
                                      }
                                    }
                                    const hora = new Date(message.timestamp).toLocaleTimeString("pt-BR", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    });
                                    const textoTransfer = (fromName && toName)
                                      ? `Transferido de ${fromName} para ${toName}`
                                      : (toName
                                        ? `Transferido para ${toName}`
                                        : (message.mensagem || "Lead transferido"));
                                    return (
                                      <div
                                        key={message.clientKey ?? String(message.id)}
                                        className="d-flex justify-content-center w-100"
                                        style={{ marginTop: "6px", marginBottom: isLastMessage ? "20px" : "6px" }}
                                      >
                                        <span
                                          className="badge shadow-sm"
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            fontSize: "11.5px",
                                            padding: "6px 14px",
                                            borderRadius: "20px",
                                            fontWeight: 600,
                                            letterSpacing: "0.2px",
                                            maxWidth: "88%",
                                            whiteSpace: "normal",
                                            textAlign: "center",
                                            lineHeight: 1.4,
                                            background: isDark ? "rgba(0,94,75,0.28)" : "rgba(0,126,148,0.10)",
                                            color: isDark ? "#8fe3d6" : "#00707f",
                                            border: isDark ? "1px solid rgba(143,227,214,0.22)" : "1px solid rgba(0,126,148,0.20)",
                                          }}
                                        >
                                          <i className="ti ti-arrows-exchange" style={{ fontSize: 14, flex: "0 0 auto" }} />
                                          <span>{textoTransfer}</span>
                                          <span style={{ opacity: 0.7, fontWeight: 500 }}>· {hora}</span>
                                        </span>
                                      </div>
                                    );
                                  }

                                  // ── Anotação interna: balão diferenciado (não é mensagem do WhatsApp) ──
                                  if (message.type === "anotacao") {
                                    const autorNome =
                                      (message.id_user != null && nomePorUserId[message.id_user]) || "Alguém";
                                    const isAuthor =
                                      String(message.id_user ?? "") === (localStorage.getItem("id_user") || "");
                                    const isTemp = String(message.id).startsWith("temp-");
                                    return (
                                      <div
                                        key={message.clientKey ?? String(message.id)}
                                        className="chats chats-right"
                                        style={{
                                          marginBottom: isLastMessage ? "20px" : "4px",
                                          marginLeft: "auto",
                                          marginRight: 0,
                                        }}
                                      >
                                        <div className="chat-content" style={{ maxWidth: "80%" }}>
                                          <div
                                            style={{
                                              background: isDark ? "#3a3320" : "#FEF9E7",
                                              border: isDark ? "1px solid #5c4f28" : "1px solid #f5e6a8",
                                              borderLeft: "3px solid #f1c40f",
                                              borderRadius: "4px 10px 10px 10px",
                                              padding: "8px 12px 6px",
                                              // Anotação otimista instantânea (igual aos envios): bolha sólida na
                                              // hora; se falhar, a função de criação remove a bolha e avisa.
                                              opacity: 1,
                                              fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                            }}
                                          >
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                              <i className="ti ti-notes" style={{ fontSize: 14, color: "#caa20a" }} />
                                              <span
                                                style={{
                                                  fontSize: 12.5,
                                                  fontWeight: 700,
                                                  color: isDark ? "#e9d98a" : "#9a7d0a",
                                                }}
                                              >
                                                {autorNome} anotou:
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                fontSize: 14,
                                                lineHeight: 1.5,
                                                color: isDark ? "#ece7d2" : "#5c5320",
                                              }}
                                            >
                                              {parseNotaSegmentos(message.mensagem).map((seg, i) =>
                                                seg.tipo === "mencao" ? (
                                                  <span key={i} style={{ color: "var(--brand-primary, #007e94)", fontWeight: 600 }}>
                                                    {seg.valor}
                                                  </span>
                                                ) : (
                                                  <span key={i}>{seg.valor}</span>
                                                )
                                              )}
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                gap: 8,
                                                marginTop: 4,
                                              }}
                                            >
                                              {isAuthor && !isTemp && (
                                                <button
                                                  type="button"
                                                  onClick={() => excluirAnotacao(message)}
                                                  title="Excluir anotação"
                                                  style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    color: isDark ? "#9a8f6a" : "#b3a24d",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    lineHeight: 1,
                                                  }}
                                                >
                                                  <i className="ti ti-trash" style={{ fontSize: 13 }} />
                                                </button>
                                              )}
                                              <span
                                                style={{
                                                  fontSize: 10.5,
                                                  color: isDark ? "#9a8f6a" : "#b3a24d",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {new Date(message.timestamp).toLocaleString("pt-BR", {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // ── Upload interno (TNP, grupo 192): balão CINZA ────────────────
                                  //    Mesma estrutura da anotação (que é amarela): não é mensagem
                                  //    do WhatsApp, o cliente não recebeu este arquivo.
                                  if (message.type === "tnpupload") {
                                    const autorNome =
                                      (message.id_user != null && nomePorUserId[message.id_user]) || "Alguém";
                                    const nomeArq = tnpNomeArquivo(message);
                                    return (
                                      <div
                                        key={message.clientKey ?? String(message.id)}
                                        className="chats chats-right"
                                        style={{
                                          marginBottom: isLastMessage ? "20px" : "4px",
                                          marginLeft: "auto",
                                          marginRight: 0,
                                        }}
                                      >
                                        <div className="chat-content" style={{ maxWidth: "80%" }}>
                                          <div
                                            style={{
                                              background: isDark ? "#2a2f32" : "#F1F3F5",
                                              border: isDark ? "1px solid #3d4448" : "1px solid #e2e6ea",
                                              borderLeft: "3px solid #9aa3ab",
                                              borderRadius: "4px 10px 10px 10px",
                                              padding: "8px 12px 6px",
                                              fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                            }}
                                          >
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                              <i className="ti ti-cloud-upload" style={{ fontSize: 14, color: "#8a939b" }} />
                                              <span
                                                style={{
                                                  fontSize: 12.5,
                                                  fontWeight: 700,
                                                  color: isDark ? "#c3cad0" : "#5d666e",
                                                }}
                                              >
                                                {autorNome} anexou um arquivo:
                                              </span>
                                            </div>
                                            <DocumentBubble href={message.mensagem} fileName={nomeArq} isDark={isDark} />
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                marginTop: 4,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: 10.5,
                                                  color: isDark ? "#8a939b" : "#9aa3ab",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {new Date(message.timestamp).toLocaleString("pt-BR", {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // Identificação do status de entrega do TEMPLATE (API Oficial).
                                  // Só mensagens de template carregam status_template_wp; nas demais
                                  // fica null. Normaliza p/ minúsculas: 'sent' | 'delivered' | 'read' | 'failed'.
                                  // (O tratamento visual a partir do 'delivered' será feito numa etapa seguinte.)
                                  // Status de entrega de TODAS as msgs enviadas (logins com API
                                  // oficial), não só templates: 'sent'|'delivered'|'read'|'failed'.
                                  // Só aparece quando há status real (msg oficial grava 'sent' no
                                  // envio); nulo (antigas/uazapi sem tracking) → sem check.
                                  const entregaStatus = (temApiOficial && message.status === "send")
                                    ? ((message.status_template_wp || "").toLowerCase() || null)
                                    : null;

                                  // Figurinha (sticker): SÓ a imagem, sem balão (card/borda/fundo),
                                  // mas com a MESMA estrutura das mensagens (.chats + .chat-avatar +
                                  // .chat-content) para herdar margens e alinhamento idênticos.
                                  // Link do bucket vem em message.mensagem.
                                  if (message.type === "sticker") {
                                    const isSend = message.status === "send";
                                    const isPublicUrl = /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(message.mensagem);
                                    const src = isPublicUrl
                                      ? message.mensagem
                                      : `data:image/webp;base64,${message.mensagem}`;
                                    return (
                                      <div
                                        key={message.clientKey ?? String(message.id)}
                                        className={`chats ${isSend ? "chats-right" : ""}`}
                                        style={{
                                          marginBottom: isLastMessage ? "20px" : "2px",
                                          marginLeft: isSend ? "auto" : "0",
                                          marginRight: isSend ? "0" : "auto",
                                        }}
                                      >
                                        <div
                                          className="chat-avatar"
                                          style={{
                                            width: "40px",
                                            marginLeft: isSend ? "10px" : "6px",
                                            marginRight: isSend ? "6px" : "10px",
                                          }}
                                        />
                                        <div className="chat-content">
                                          <img
                                            src={src}
                                            alt="Figurinha"
                                            loading="lazy"
                                            onClick={() => setFullscreenMedia({ tipo: "image", src })}
                                            style={{
                                              width: 128,
                                              height: 128,
                                              objectFit: "contain",
                                              cursor: "zoom-in",
                                              display: "block",
                                              background: "transparent",
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={message.clientKey ?? String(message.id)}
                                      data-template-status={entregaStatus ?? undefined}
                                      className={`chats ${message.status === "send" ? "chats-right" : ""}`}
                                      style={{
                                        marginBottom: isLastMessage ? "20px" : "2px",
                                        marginLeft: message.status === "send" ? "auto" : "0",
                                        marginRight: message.status === "send" ? "0" : "auto"
                                      }}
                                    >
                                      <div
                                        className="chat-avatar"
                                        style={{
                                          width: "40px",
                                          marginLeft: message.status === "send" ? "10px" : "6px",
                                          marginRight: message.status === "send" ? "6px" : "10px",
                                        }}
                                      />

                                      {/* Indicador de falha à ESQUERDA do balão (além do "Falhou" no rodapé) */}
                                      {message.status === "send" && entregaStatus === "failed" && (
                                        <FailedTemplateTooltip motivo={message.motivo_template_wp} isDark={isDark}>
                                          <div
                                            title={message.motivo_template_wp ? undefined : "Falha no envio"}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              alignSelf: "center",
                                              flex: "0 0 auto",
                                              color: "#ef4444",
                                              fontSize: "20px",
                                              marginRight: "6px",
                                              lineHeight: 1,
                                              cursor: "pointer",
                                              pointerEvents: "auto",
                                            }}
                                          >
                                            <i className="ti ti-alert-circle" />
                                          </div>
                                        </FailedTemplateTooltip>
                                      )}

                                      <div className="chat-content">
                                        <div
                                          className="message-content card p-2 mb-1"
                                          style={{
                                            backgroundColor: entregaStatus === "failed"
                                              ? (isDark ? "#3a1d1f" : "#fff1f1")
                                              : isDark
                                              ? (message.status === "send" ? "#005c4b" : "#1f2c34")
                                              : (message.status === "send" ? "#DCF8C6" : "#ffffff"),
                                            color: isDark ? "#e9edef" : "#1a1a2e",
                                            borderRadius: message.status === "send" ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                                            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                            fontSize: "14px",
                                            lineHeight: "1.5",
                                            // Envio otimista instantâneo: a bolha aparece SÓLIDA (como enviada)
                                            // na hora. A maioria dos envios funciona; se algum falhar, a própria
                                            // função de envio remove a bolha temp e mostra o aviso de erro.
                                            opacity: 1,
                                            position: "relative",
                                            padding: "10px 14px 8px",
                                            minWidth: "unset",
                                            border: entregaStatus === "failed" ? "1px solid #ef4444" : "none",
                                            boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.04)",
                                          }}>

                                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "6px" }}>
                                          {/* Conteúdo da Mensagem */}
                                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "100%" }}>
                                            {(() => {
                                              // Guarda: mídia sem conteúdo (mensagem null/vazia) — evita crash
                                              // em .split/.test/base64 nos casos de mídia (ex.: mídia recebida
                                              // sem URL/conteúdo salvo). Texto usa comQuebras, que já trata null.
                                              if (
                                                (message.mensagem == null || message.mensagem === "") &&
                                                ["imagem", "image", "video", "document", "audio"].includes(message.type)
                                              ) {
                                                return (
                                                  <span style={{ fontStyle: "italic", opacity: 0.7 }}>
                                                    [mídia indisponível]
                                                  </span>
                                                );
                                              }
                                              switch (message.type) {
                                                case "conversation": {
                                                  // Template unificado: identifica mídia/botões pelo template_nome (tabela
                                                  // templates via templatesInfo), com fallback ao gravado na própria mensagem.
                                                  const info = (message.template && message.template_nome) ? templatesInfo[message.template_nome] : undefined;
                                                  const mid = message.template_midia
                                                    || ((info && (info.headerFormat === "IMAGE" || info.headerFormat === "VIDEO")) ? (info.headerImage || "") : "");
                                                  const botoes = (message.template_botoes && message.template_botoes.length)
                                                    ? message.template_botoes
                                                    : (info?.botoes || []);
                                                  const ehTemplateRico = message.template && (mid || (botoes && botoes.length));
                                                  if (!ehTemplateRico) return <span>{comQuebras(message.mensagem)}</span>;
                                                  const ehVideo = /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(mid);
                                                  return (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                      {mid && (ehVideo ? (
                                                        <video src={mid} controls preload="metadata" onError={(e) => { const v = e.currentTarget; if (v.error && v.error.code === 1) return; }} style={{ maxWidth: 260, maxHeight: 300, borderRadius: 8, display: "block", background: "#000" }} />
                                                      ) : (
                                                        <img src={mid} alt="" loading="lazy" onClick={() => setFullscreenMedia({ tipo: "image", src: mid })} style={{ maxWidth: 260, maxHeight: 300, objectFit: "contain", borderRadius: 8, cursor: "zoom-in", display: "block" }} />
                                                      ))}
                                                      {message.mensagem && <span style={{ whiteSpace: "pre-wrap" }}>{comQuebras(message.mensagem)}</span>}
                                                      {botoes && botoes.length > 0 && (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 6 }}>
                                                          {botoes.map((b, i) => (
                                                            <div key={i} style={{ textAlign: "center", color: "#0a7cff", fontWeight: 500, fontSize: 13, padding: "6px 8px", background: "rgba(10,124,255,0.06)", borderRadius: 6 }}>
                                                              {b.type === "URL" && <i className="ti ti-external-link me-1" style={{ fontSize: 12 }} />}
                                                              {b.type === "PHONE_NUMBER" && <i className="ti ti-phone me-1" style={{ fontSize: 12 }} />}
                                                              {b.type === "QUICK_REPLY" && <i className="ti ti-arrow-back-up me-1" style={{ fontSize: 12 }} />}
                                                              {b.text || "Botão"}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                case "imagem":
                                                case "image": {
                                                  const isPublicUrl = /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(message.mensagem);
                                                  const src = isPublicUrl
                                                    ? message.mensagem
                                                    : `data:image/png;base64,${message.mensagem}`;
                                                  // Girar só faz sentido no que o LEAD mandou (foto de documento deitada)
                                                  // e só quando o arquivo está no bucket — base64 não tem o que regravar.
                                                  const podeGirarEsta = podeGirarImagem && message.status !== "send" && isPublicUrl;
                                                  const girandoEsta = girandoImagem.includes(String(message.id));
                                                  return (
                                                    <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
                                                      <img
                                                        src={src}
                                                        alt="Imagem enviada"
                                                        loading="lazy"
                                                        onDragStart={(e) => iniciarArrasteMidia(e, src, (src.split("?")[0].split("/").pop() || "imagem.jpeg"))}
                                                        onClick={() => setFullscreenMedia({ tipo: "image", src, msgId: podeGirarEsta ? String(message.id) : undefined })}
                                                        style={{ maxWidth: 260, maxHeight: 300, width: "auto", height: "auto", objectFit: "contain", borderRadius: 8, cursor: "zoom-in", display: "block", opacity: girandoEsta ? 0.5 : 1, transition: "opacity .15s" }}
                                                      />
                                                      <button type="button" title="Tela cheia" onClick={() => setFullscreenMedia({ tipo: "image", src, msgId: podeGirarEsta ? String(message.id) : undefined })}
                                                        style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                                        <i className="ti ti-arrows-maximize" style={{ fontSize: 16 }} />
                                                      </button>
                                                      {podeGirarEsta && (
                                                        <div style={{ position: "absolute", top: 6, left: 6, display: "flex", gap: 4 }}>
                                                          <button type="button" title="Girar 90° para a esquerda" disabled={girandoEsta}
                                                            onClick={() => girarImagem(message, "antihorario")}
                                                            style={{ background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: girandoEsta ? "wait" : "pointer" }}>
                                                            <i className="ti ti-rotate-2" style={{ fontSize: 16 }} />
                                                          </button>
                                                          <button type="button" title="Girar 90° para a direita" disabled={girandoEsta}
                                                            onClick={() => girarImagem(message, "horario")}
                                                            style={{ background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: girandoEsta ? "wait" : "pointer" }}>
                                                            <i className="ti ti-rotate-clockwise-2" style={{ fontSize: 16 }} />
                                                          </button>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                case "video": {
                                                  const isPublicUrl = /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(message.mensagem);
                                                  const src = isPublicUrl
                                                    ? message.mensagem
                                                    : `data:video/mp4;base64,${message.mensagem}`;
                                                  return (
                                                    <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
                                                      <video
                                                        controls
                                                        preload="metadata"
                                                        src={src}
                                                        onError={(e) => { const v = e.currentTarget; if (v.error && v.error.code === 1) return; }}
                                                        style={{ maxWidth: 260, maxHeight: 300, width: "auto", height: "auto", borderRadius: 8, display: "block", background: "#000" }}
                                                      />
                                                      <button type="button" title="Tela cheia" onClick={() => setFullscreenMedia({ tipo: "video", src })}
                                                        style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                                        <i className="ti ti-arrows-maximize" style={{ fontSize: 16 }} />
                                                      </button>
                                                    </div>
                                                  );
                                                }
                                                case "document": {
                                                  const isPublicUrl = /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(message.mensagem);
                                                  const href = isPublicUrl
                                                    ? message.mensagem
                                                    : `data:application/octet-stream;base64,${message.mensagem}`;
                                                  // Nome do documento: usa doc_name (nome original salvo no envio);
                                                  // fallback p/ o nome derivado da URL (docs antigos sem doc_name).
                                                  const fileName = (message.doc_name && message.doc_name.trim())
                                                    || (message.mensagem.split("/").pop() || "documento").replace(/\?.*$/, "");
                                                  return <DocumentBubble href={href} fileName={fileName} isDark={isDark} />;
                                                }
                                                case "audio": {
                                                  const isPublicUrl = /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(message.mensagem);
                                                  const src = isPublicUrl
                                                    ? message.mensagem
                                                    : `data:audio/mp3;base64,${message.mensagem}`;
                                                  return (
                                                    <div style={{ width: "100%", minWidth: "250px" }}>
                                                      <audio controls src={src} style={{ width: "100%" }} />
                                                      {/* Reenviar (só áudio ENVIADO com URL): rebusca e manda de novo */}
                                                      {message.status === "send" && isPublicUrl && (
                                                        <div style={{ marginTop: 4 }}>
                                                          <button
                                                            type="button"
                                                            onClick={() => reenviarAudio(message)}
                                                            disabled={uploading}
                                                            title="Reenviar este áudio para o cliente"
                                                            style={{ fontSize: 11, padding: "2px 8px", color: "#007e94", background: "transparent", border: "1px solid #007e94", borderRadius: 6, cursor: uploading ? "default" : "pointer", opacity: uploading ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 4 }}
                                                          >
                                                            <i className="ti ti-refresh" style={{ fontSize: 12 }} /> Reenviar
                                                          </button>
                                                        </div>
                                                      )}
                                                      {/* Transcrição: só os áudios que a IA atende chegam transcritos
                                                          (chats.audio_transc). Sem texto, o botão transcreve sob demanda
                                                          (/chat/transcrever_audio grava no banco: o próximo clique é grátis). */}
                                                      {isPublicUrl && (() => {
                                                        const textoTransc = (message.audio_transc || "").trim() || transcricoesAudio[message.id] || "";
                                                        const carregando = !!transcrevendoAudio[message.id];
                                                        const aberto = !!mostrarTranscricaoAudio[message.id];
                                                        return (
                                                          <div className="mt-2">
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-outline-secondary"
                                                              style={{ fontSize: 12, padding: "2px 10px" }}
                                                              disabled={carregando}
                                                              onClick={() => (textoTransc || aberto)
                                                                ? setMostrarTranscricaoAudio(prev => ({ ...prev, [message.id]: !prev[message.id] }))
                                                                : transcreverAudio(message.id)}
                                                            >
                                                              <i className="ti ti-file-text" style={{ fontSize: 13, marginRight: 4 }} />
                                                              {carregando ? "Transcrevendo..." : aberto ? "Ocultar transcrição" : textoTransc ? "Ver transcrição" : "Transcrever"}
                                                            </button>
                                                            {aberto && textoTransc && (
                                                              <div className="alert alert-light mt-2 mb-0" style={{ whiteSpace: "pre-wrap", fontSize: "14px", border: "1px solid rgba(0,0,0,0.08)" }}>
                                                                {textoTransc}
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })()}
                                                      {message.audio_trad && (
                                                        <div className="mt-2">
                                                          <button
                                                            className="btn btn-sm btn-outline-info"
                                                            onClick={() => setMostrarTraducaoAudio(prev => ({
                                                              ...prev,
                                                              [message.id]: !prev[message.id]
                                                            }))}
                                                          >
                                                            {mostrarTraducaoAudio[message.id] ? "Ocultar Tradução" : "Ver Tradução"}
                                                          </button>
                                                          {mostrarTraducaoAudio[message.id] && (
                                                            <div className="alert alert-secondary mt-2" style={{ whiteSpace: "pre-wrap", fontSize: "14px" }}>
                                                              {message.audio_trad}
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                case "contact": {
                                                  // O conteúdo vem como JSON da Cloud API:
                                                  // {name:{first_name,last_name,formatted_name}, phones:[{phone,wa_id,type}]}.
                                                  // A Meta manda `contacts` como ARRAY (o WhatsApp encaminha vários
                                                  // contatos numa mensagem só), mas parte do histórico está gravada
                                                  // como objeto único — aceita os dois e renderiza um card por contato.
                                                  // Array caindo no acesso direto (info?.name) era o que deixava o
                                                  // card sem nome e sem fone. Fallback p/ formato antigo "nome\nfone".
                                                  const extraiContato = (info: any) => ({
                                                    nome: info?.name?.formatted_name
                                                      || [info?.name?.first_name, info?.name?.last_name].filter(Boolean).join(" ")
                                                      || "",
                                                    phones: Array.isArray(info?.phones) ? info.phones : [],
                                                  });
                                                  let lista: { nome: string; phones: any[] }[] = [];
                                                  try {
                                                    const bruto = JSON.parse(message.mensagem);
                                                    lista = (Array.isArray(bruto) ? bruto : [bruto]).map(extraiContato);
                                                  } catch {
                                                    const [n, p] = (message.mensagem || "").split("\n");
                                                    lista = [{ nome: (n || "").trim(), phones: p && p.trim() ? [{ phone: p.trim() }] : [] }];
                                                  }
                                                  if (!lista.length) lista = [{ nome: "", phones: [] }];
                                                  return (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                    {lista.map((ct, idx) => {
                                                    const nome = ct.nome || "Contato";
                                                    const phones = ct.phones;
                                                    const iniciais = nome.split(/\s+/).filter(Boolean).slice(0, 2)
                                                      .map((s: string) => s[0]).join("").toUpperCase() || "?";
                                                    const fonePrincipal = phones[0]?.wa_id || phones[0]?.phone || "";
                                                    return (
                                                    <div key={idx} style={{
                                                      border: isDark ? "1px solid #2F343C" : "1px solid rgba(0,126,148,0.20)",
                                                      borderRadius: 12,
                                                      padding: 12,
                                                      maxWidth: 280,
                                                      background: isDark ? "#1f2c34" : "rgba(0,126,148,0.06)",
                                                    }}>
                                                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        {/* avatar com iniciais */}
                                                        <span style={{
                                                          flex: "0 0 auto", width: 44, height: 44, borderRadius: "50%",
                                                          background: "var(--brand-primary, #007e94)", color: "#fff",
                                                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                          fontWeight: 700, fontSize: 16,
                                                        }}>{iniciais}</span>
                                                        <div style={{ minWidth: 0, flex: 1 }}>
                                                          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, opacity: 0.65 }}>
                                                            <i className="ti ti-user" />
                                                            <span>Contato</span>
                                                          </div>
                                                          <div style={{
                                                            fontWeight: 700, fontSize: 14,
                                                            color: isDark ? "#e9edef" : "#1a1a2e",
                                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                          }}>{nome}</div>
                                                          {phones.map((p, i) => {
                                                            // tipo do telefone (CELL/WORK/…) traduzido p/ PT; desconhecido = omite
                                                            const tipo = ({ CELL: "Celular", MOBILE: "Celular", WORK: "Trabalho",
                                                              HOME: "Casa", MAIN: "Principal", IPHONE: "iPhone" } as Record<string, string>
                                                            )[String(p.type || "").toUpperCase()];
                                                            return (
                                                              <div key={i} style={{
                                                                fontSize: 12.5, color: isDark ? "#9CA3AF" : "#555",
                                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                              }}>
                                                                <i className="ti ti-phone" style={{ fontSize: 12, marginRight: 4, opacity: 0.7 }} />
                                                                {p.phone || p.wa_id}{tipo ? ` · ${tipo}` : ""}
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      </div>
                                                      {!bloqueiaContatoManual && fonePrincipal && (
                                                        <button
                                                          type="button"
                                                          className="btn btn-sm btn-primary w-100 mt-2"
                                                          style={{ borderRadius: 8, fontWeight: 600 }}
                                                          onClick={() => createNewContact(nome, fonePrincipal)}
                                                        >
                                                          <i className="ti ti-user-plus me-1" /> Adicionar contato
                                                        </button>
                                                      )}
                                                    </div>
                                                    );
                                                    })}
                                                    </div>
                                                  );
                                                }
                                                default:
                                                  return <span>{message.mensagem}</span>;
                                              }
                                            })()}
                                          </div>

                                          {/* Barra de validação do anexo (TNP, grupo 192).
                                              Porta de qualidade binária: "este arquivo serve?".
                                              NÃO classifica a que documento do checklist o arquivo
                                              corresponde — só responde se é legível/completo/válido.
                                              Só em bolha RECEBIDA de `document`, imagem ou vídeo:
                                              áudio, texto e mensagem enviada não têm barra.
                                              `width: 100%` quebra a linha do flex e posiciona a barra
                                              entre o anexo e a hora. */}
                                          {ehGrupoDocsRetencao() &&
                                            message.status !== "send" &&
                                            ehAnexoValidavel(message.type) &&
                                            !String(message.id).startsWith("temp-") && (
                                              <div className="dr-escopo dr-validar" style={{ width: "100%" }}>
                                                <button
                                                  type="button"
                                                  className={`dr-acao dr-primeira ${message.doc_validado === false ? "dr-negado" : ""}`}
                                                  aria-label="Marcar arquivo como não aceitável"
                                                  aria-pressed={message.doc_validado === false}
                                                  title="Não serve (tremido, cortado, ilegível, vencido ou incompleto)"
                                                  disabled={validandoAnexo.includes(String(message.id))}
                                                  onClick={() => validarAnexo(message, false)}
                                                >
                                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" style={{ width: "9.5px", height: "9.5px" }}>
                                                    <path d="M6 6l12 12M18 6 6 18" />
                                                  </svg>
                                                </button>
                                                <button
                                                  type="button"
                                                  className={`dr-acao ${message.doc_validado === true ? "dr-aceito" : ""}`}
                                                  aria-label="Marcar arquivo como aceitável"
                                                  aria-pressed={message.doc_validado === true}
                                                  title="Aceitável (legível, completo e dentro da validade)"
                                                  disabled={validandoAnexo.includes(String(message.id))}
                                                  onClick={() => validarAnexo(message, true)}
                                                >
                                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" style={{ width: "9.5px", height: "9.5px" }}>
                                                    <path d="m4 12 5.5 5.5L20 7" />
                                                  </svg>
                                                </button>
                                              </div>
                                            )}

                                          {/* Hora + status de entrega do template (estilo WhatsApp):
                                              1 check = enviado (send/sent), 2 checks = entregue (delivered/read),
                                              indicador vermelho = falha no envio (failed).
                                              Só aparece em mensagens de template enviadas. */}
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "3px",
                                              fontSize: "10.5px",
                                              color: isDark
                                                ? (message.status === "send" ? "#7bb8a6" : "#8696a0")
                                                : (message.status === "send" ? "#6d8a5e" : "#a0a8b8"),
                                              pointerEvents: "none",
                                              lineHeight: "1",
                                              marginLeft: "auto",
                                              marginBottom: "2px",
                                              fontWeight: 500
                                            }}
                                          >
                                            <span>
                                              {new Date(message.timestamp).toLocaleString("pt-BR", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })}
                                            </span>
                                            {message.status === "send" && entregaStatus && (
                                              entregaStatus === "failed" ? (
                                                <FailedTemplateTooltip motivo={message.motivo_template_wp} isDark={isDark}>
                                                  <span
                                                    title={message.motivo_template_wp ? undefined : "Falha no envio"}
                                                    style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                      color: "#ef4444",
                                                      fontWeight: 700,
                                                      cursor: "pointer",
                                                      pointerEvents: "auto",
                                                    }}
                                                  >
                                                    <i className="ti ti-alert-circle" style={{ fontSize: "13px", lineHeight: 1 }} />
                                                    <span style={{ fontSize: "10px", letterSpacing: "0.2px" }}>Falhou</span>
                                                  </span>
                                                </FailedTemplateTooltip>
                                              ) : (
                                                <i
                                                  className={
                                                    entregaStatus === "delivered" || entregaStatus === "read"
                                                      ? "ti ti-checks"
                                                      : "ti ti-check"
                                                  }
                                                  title={
                                                    entregaStatus === "read"
                                                      ? "Lida"
                                                      : entregaStatus === "delivered"
                                                      ? "Entregue"
                                                      : "Enviado"
                                                  }
                                                  style={{
                                                    fontSize: "13px",
                                                    lineHeight: 1,
                                                    pointerEvents: "auto",
                                                    // 2 checks azuis = lida (read); sent/delivered herdam a cor do rodapé.
                                                    color: entregaStatus === "read" ? "#53bdeb" : undefined,
                                                  }}
                                                />
                                              )
                                            )}
                                          </div>
                                          </div>
                                          {/* Faixa do motivo do erro do template (igual à página /templates-com-erro) */}
                                          {message.status === "send" && entregaStatus === "failed" && (
                                            <div style={{ marginTop: 6, background: isDark ? "#2a1416" : "#fff5f5", border: "1px solid #ef4444", borderRadius: 6, padding: "6px 9px", color: isDark ? "#fecaca" : "#7f1d1d" }}>
                                              <TemplateErrorDetails motivo={message.motivo_template_wp} isDark={isDark} fontSize={11} />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ));

                            // Aviso de histórico em voo. Quando a conversa é aberta a partir
                            // de um card LEVE (sem histórico), o realtime pode já ter anexado
                            // a mensagem que acabou de chegar: sem este aviso o painel mostra
                            // essa mensagem SOZINHA por ~750 ms e se lê como o bug antigo de
                            // "sumiram as mensagens anteriores". As bolhas não são escondidas —
                            // só ganham o rótulo de que ainda falta carregar o que veio antes.
                            return (
                              <>
                                {carregandoHistorico && (
                                  <div className="d-flex justify-content-center w-100" style={{ marginBottom: 10 }}>
                                    <span
                                      className="badge shadow-sm d-inline-flex align-items-center gap-2"
                                      style={{
                                        fontSize: '11.5px',
                                        padding: '6px 16px',
                                        borderRadius: '20px',
                                        fontWeight: 600,
                                        letterSpacing: '0.3px',
                                        background: isDark ? 'rgba(26,26,46,0.9)' : 'rgba(255,255,255,0.95)',
                                        color: isDark ? '#aaa' : '#5a6678',
                                        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                                        backdropFilter: 'blur(8px)',
                                      }}
                                    >
                                      <span className="spinner-border" role="status"
                                            style={{ width: 11, height: 11, borderWidth: 1.5 }} />
                                      carregando histórico anterior…
                                    </span>
                                  </div>
                                )}
                                {grupos}
                              </>
                            );
                          })()}
                          <div ref={messagesEndRef} />
                        </div>
                      </Scrollbars>

                      {/* Overlay "solte aqui" — aparece enquanto o arquivo está sendo
                          arrastado por cima da área de mensagens. pointerEvents: none
                          p/ não roubar o próprio evento de drop do container. */}
                      {isDraggingFiles && dropPreview.length === 0 && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 8,
                            zIndex: 30,
                            pointerEvents: "none",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 10,
                            border: `2px dashed ${isDark ? "#25d366" : "#0f9d58"}`,
                            borderRadius: 14,
                            background: isDark ? "rgba(11,20,26,0.82)" : "rgba(255,255,255,0.86)",
                            color: isDark ? "#e9edef" : "#1a1a2e",
                          }}
                        >
                          <i className="ti ti-cloud-upload" style={{ fontSize: 40, color: isDark ? "#25d366" : "#0f9d58" }} />
                          <div style={{ fontSize: 16, fontWeight: 700 }}>Solte os arquivos aqui</div>
                          <div style={{ fontSize: 12, opacity: 0.75 }}>Você confirma o envio na próxima tela</div>
                        </div>
                      )}

                      {/* Prévia dos arquivos soltos — nada é enviado até confirmar. */}
                      {dropPreview.length > 0 && (
                        <div
                          onClick={limparPreviaDrop}
                          style={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 60,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 12,
                            background: isDark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.35)",
                          }}
                        >
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: "min(520px, 100%)",
                              maxHeight: "100%",
                              display: "flex",
                              flexDirection: "column",
                              background: isDark ? "#111b21" : "#ffffff",
                              color: isDark ? "#e9edef" : "#1a1a2e",
                              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                              borderRadius: 12,
                              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                              overflow: "hidden",
                            }}
                          >
                            <div style={{ padding: "12px 14px", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
                              <i className="ti ti-paperclip" />
                              <strong style={{ fontSize: 14 }}>
                                {dropPreview.length === 1 ? "1 arquivo" : `${dropPreview.length} arquivos`}
                              </strong>
                              <span style={{ marginLeft: "auto", fontSize: 12, opacity: 0.7 }}>
                                {selectedContactData?.nome || selectedContactData?.fone || ""}
                              </span>
                            </div>

                            <div style={{ overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                              {dropPreview.map((item, idx) => {
                                const tipo = tipoMidiaDoArquivo(item.file);
                                const ext = (item.file.name.split(".").pop() || "").toLowerCase();
                                const problema = problemaDoArquivo(item.file);
                                return (
                                  <div
                                    key={`${item.file.name}-${idx}`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      padding: 8,
                                      borderRadius: 8,
                                      background: isDark ? "#1f2c34" : "#f4f6f8",
                                    }}
                                  >
                                    <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 6, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: isDark ? "#0b141a" : "#e6eaee" }}>
                                      {tipo === "image" && item.previewUrl ? (
                                        <img src={item.previewUrl} alt={item.file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                      ) : tipo === "video" ? (
                                        <i className="ti ti-video" style={{ fontSize: 22 }} />
                                      ) : (
                                        <FileTypeIcon ext={ext} />
                                      )}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {item.file.name}
                                      </div>
                                      <div style={{ fontSize: 11, opacity: 0.7 }}>
                                        {formatBytes(item.file.size)} · {tipo === "image" ? "imagem" : tipo === "video" ? "vídeo" : "documento"}
                                        {problema && (
                                          <span style={{ color: "#d93025", fontWeight: 600, marginLeft: 6 }}>
                                            <i className="ti ti-alert-circle me-1" />{problema}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removerArquivoDrop(idx)}
                                      title="Remover da lista"
                                      style={{ border: "none", background: "transparent", color: isDark ? "#8696a0" : "#667781", cursor: "pointer", padding: 4, lineHeight: 1 }}
                                    >
                                      <i className="ti ti-x" style={{ fontSize: 16 }} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>

                            <div style={{ padding: 12, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                onClick={limparPreviaDrop}
                                style={{ border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)", background: "transparent", color: "inherit", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
                              >
                                Cancelar
                              </button>
                              {/* Grupo TNP (192): a mesma prévia oferece o caminho interno,
                                  que grava na conversa sem mandar nada ao cliente. */}
                              {ehGrupoTnpUpload() && (
                                <button
                                  type="button"
                                  onClick={enviarPreviaDropInterno}
                                  disabled={tnpUploading}
                                  title="Anexa à conversa sem enviar ao cliente"
                                  style={{ border: "none", background: isDark ? "#2a3942" : "#e6eaee", color: isDark ? "#e9edef" : "#1a1a2e", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: tnpUploading ? "not-allowed" : "pointer" }}
                                >
                                  {tnpUploading ? "Enviando…" : "Upload interno"}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={enviarPreviaDrop}
                                disabled={uploading || shouldUseTemplate()}
                                title={shouldUseTemplate() ? "Janela de 24h expirada: o cliente só recebe template" : undefined}
                                style={{ border: "none", background: uploading || shouldUseTemplate() ? "#7fbfa0" : "#0f9d58", color: "#fff", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: uploading || shouldUseTemplate() ? "not-allowed" : "pointer" }}
                              >
                                {uploading ? "Enviando…" : "Enviar ao cliente"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className="chat-footer card-footer"
                      style={{
                        padding: "12px 16px",
                        margin: 0,
                        marginTop: "auto",
                        backgroundColor: isDark ? "#111111" : "#ffffff",
                        borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
                        flexShrink: 0,
                        width: "100%",
                        boxShadow: "0 -2px 8px rgba(0,0,0,0.02)",
                        position: "relative",
                      }}
                    >
                      {newMessageIndicator && (
                        <div style={{ marginBottom: "8px", textAlign: "center" }}>
                          <Button
                            variant="success"
                            style={{
                              backgroundColor: "green",
                              border: "none",
                              borderRadius: "50px",
                              padding: "8px 16px",
                              boxShadow: "none"
                            }}
                            onClick={() => {
                              const scrollHeight = scrollbarsRef.current?.getScrollHeight() || 0;
                              scrollbarsRef.current?.scrollTop(scrollHeight);
                              setNewMessageIndicator(false);
                              markMessagesAsRead();
                            }}
                          >
                            Ver novas mensagens
                          </Button>
                        </div>
                      )}
                      {/* Picker de respostas rápidas (abre ao digitar "/") */}
                      {showRespostas && (() => {
                        const f = newMessage.slice(1).toLowerCase();
                        const matches = respostasRapidas.filter(
                          (r: any) => (r.titulo || "").toLowerCase().includes(f) || (r.texto || "").toLowerCase().includes(f)
                        );
                        return (
                          <div
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                              position: "absolute", bottom: "calc(100% - 4px)", left: 16, right: 16,
                              background: isDark ? "#1f2c34" : "#fff",
                              border: isDark ? "1px solid #33424c" : "1px solid #e3e8ef",
                              borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
                              maxHeight: 280, overflowY: "auto", zIndex: 1085,
                            }}
                          >
                            <div style={{ padding: "6px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: isDark ? "#8696a0" : "#94a3b8", borderBottom: isDark ? "1px solid #33424c" : "1px solid #f1f5f9" }}>
                              Respostas rápidas{matches.length > 0 ? ` (${matches.length})` : ""}
                            </div>
                            {matches.length === 0 ? (
                              <div style={{ padding: 12, fontSize: 13, color: isDark ? "#8696a0" : "#94a3b8" }}>
                                Nada encontrado. Cadastre em "Respostas Rápidas".
                              </div>
                            ) : (
                              matches.map((r: any) => (
                                <div
                                  key={r.id}
                                  onMouseDown={(e) => { e.preventDefault(); inserirRespostaRapida(r); }}
                                  style={{ padding: "8px 12px", cursor: "pointer", borderBottom: isDark ? "1px solid #2a3942" : "1px solid #f5f7fa" }}
                                >
                                  <div style={{ fontWeight: 700, fontSize: 13, color: isDark ? "#e9edef" : "#1a1a2e" }}>{r.titulo}</div>
                                  <div style={{ fontSize: 12, color: isDark ? "#8696a0" : "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.texto}</div>
                                </div>
                              ))
                            )}
                          </div>
                        );
                      })()}
                      {(() => {
                        const templateMode = shouldUseTemplate();
                        const hasText = newMessage.trim().length > 0;
                        return (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (templateMode) {
                            openTemplateModal();
                          } else if (hasText) {
                            sendMessage();
                          }
                        }}
                        style={{ width: "100%" }}
                      >
                        {/* Aviso de mídia em voo NESTA conversa. Antes o progresso morava no
                            placeholder do campo, que ficava desabilitado — o operador lia
                            "Enviando..." e não conseguia escrever. Agora o campo continua
                            livre e o status vem aqui em cima, sem tomar a mão de ninguém. */}
                        {uploading && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "2px 4px 6px",
                              fontSize: 12,
                              fontWeight: 600,
                              color: isDark ? "#8696a0" : "#64748b",
                            }}
                          >
                            <i className="ti ti-cloud-upload" style={{ fontSize: 14 }} />
                            {progressoEnvio &&
                            progressoEnvio.fone === String(selectedContactData?.fone || "") &&
                            progressoEnvio.total > 1
                              ? `Enviando arquivo ${Math.min(progressoEnvio.feitos + 1, progressoEnvio.total)} de ${progressoEnvio.total}…`
                              : "Enviando arquivo…"}
                          </div>
                        )}
                        {/* 🔹 Correção Definitiva Mobile: Usar .input-group para forçar linha única */}
                        <div className="input-group flex-nowrap">
                          {/* ADD: Botão de clipe (oculto durante gravação) */}
                          <div className="position-relative" style={{ display: isRecording ? "none" : undefined }}>
                            <button
                              type="button"
                              className="btn btn-outline-secondary border-end-0 rounded-0 rounded-start"
                              onClick={() => setShowMediaMenu((v) => !v)}
                              title={templateMode ? "Anexar arquivo (indisponível fora da janela de 24h)" : "Anexar arquivo"}
                              disabled={!selectedContact || templateMode}
                              style={{ height: "40px" }}
                            >
                              <i className="ti ti-paperclip" />
                            </button>

                            {/* Menu para escolher tipo de mídia */}
                            {showMediaMenu && (
                              <div
                                className="dropdown-menu show p-2"
                                style={{ position: "absolute", bottom: "100%", left: 0, marginBottom: "5px" }}
                                onMouseLeave={() => setShowMediaMenu(false)}
                              >
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setPendingUploadType("image");
                                    setShowMediaMenu(false);
                                    const el = fileInputRef.current;
                                    if (el) {
                                      el.accept = "image/*";
                                      el.click();
                                    }
                                  }}
                                >
                                  📷 Imagem
                                </button>

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setPendingUploadType("video");
                                    setShowMediaMenu(false);
                                    const el = fileInputRef.current;
                                    if (el) {
                                      el.accept = "video/*";
                                      el.click();
                                    }
                                  }}
                                >
                                  🎬 Vídeo
                                </button>

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setPendingUploadType("document");
                                    setShowMediaMenu(false);
                                    const el = fileInputRef.current;
                                    if (el) {
                                      el.accept = "*/*";
                                      el.click();
                                    }
                                  }}
                                >
                                  📎 Documento
                                </button>

                                <div className="dropdown-divider my-1" />

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={abrirBiblioteca}
                                >
                                  📚 Biblioteca
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Upload interno (TNP, grupo 192) — anexa o arquivo à conversa e
                              grava como type='tnpupload'. NÃO passa pelo WhatsApp: o cliente
                              não recebe nada, por isso fica fora do menu do clipe (que envia). */}
                          {ehGrupoTnpUpload() && !isRecording && (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary border-end-0 rounded-0"
                                onClick={() => tnpFileInputRef.current?.click()}
                                title="Anexar arquivo interno (não é enviado ao cliente)"
                                disabled={!selectedContact || tnpUploading}
                                style={{ height: "40px" }}
                              >
                                <i className={tnpUploading ? "ti ti-dots" : "ti ti-cloud-upload"} />
                              </button>
                              <input
                                type="file"
                                ref={tnpFileInputRef}
                                multiple
                                style={{ display: "none" }}
                                onChange={async (e) => {
                                  const inputEl = e.currentTarget;
                                  const arquivos = Array.from(inputEl.files || []);
                                  if (arquivos.length === 0) return;
                                  try {
                                    await enviarTnpUploads(arquivos);
                                  } finally {
                                    inputEl.value = "";
                                  }
                                }}
                              />
                            </>
                          )}

                          {/* Template a qualquer momento: o botão verde grande só aparece
                              FORA da janela de 24h (templateMode). Este atalho cobre o caso
                              inverso — a janela está aberta pela conta do /chat (que conta
                              qualquer número), mas a Meta recusa a mensagem livre porque o
                              cliente respondeu por OUTRO número. Aí o template é a única
                              saída, e o operador não precisa esperar a janela fechar. */}
                          {canSendTemplate() && !templateMode && !isRecording && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary border-end-0 rounded-0"
                              onClick={openTemplateModal}
                              title="Enviar um template (disponível mesmo dentro da janela de 24h)"
                              disabled={!selectedContact || sendingTemplate}
                              style={{ height: "40px" }}
                            >
                              <i className="ti ti-template" />
                            </button>
                          )}

                          {/* Botão de Anotação interna (habilitado para todos os grupos) */}
                          {ANOTACOES_HABILITADO && !isRecording && (
                            <div ref={noteComposerRef} className="position-relative" style={{ display: "flex", order: 1 }}>
                              <button
                                type="button"
                                className="btn btn-outline-secondary border-end-0 rounded-0"
                                onClick={() => { setShowNoteComposer((v) => !v); setMentionOpen(false); }}
                                title="Adicionar anotação interna"
                                disabled={!selectedContact}
                                style={{ height: "40px", color: showNoteComposer ? "#caa20a" : undefined }}
                              >
                                <i className="ti ti-notes" />
                              </button>
                              {showNoteComposer && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "100%",
                                    right: 0,
                                    marginBottom: 8,
                                    width: 340,
                                    maxWidth: "90vw",
                                    background: isDark ? "#1f2c34" : "#fff",
                                    border: isDark ? "1px solid #33424c" : "1px solid #e3e8ef",
                                    borderRadius: 12,
                                    boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
                                    zIndex: 1080,
                                    padding: 12,
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                                    <i className="ti ti-notes" style={{ color: "#caa20a" }} />
                                    <span style={{ fontWeight: 700, fontSize: 13, color: isDark ? "#e9edef" : "#1a1a2e" }}>
                                      Nova anotação
                                    </span>
                                    <span style={{ fontSize: 11, color: isDark ? "#8696a0" : "#94a3b8", marginLeft: "auto" }}>
                                      @ para mencionar
                                    </span>
                                  </div>
                                  <div style={{ position: "relative" }}>
                                    <textarea
                                      ref={noteTextareaRef}
                                      value={noteText}
                                      onChange={handleNoteTextareaChange}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey && !mentionOpen) {
                                          e.preventDefault();
                                          saveNote();
                                        }
                                        if (e.key === "Escape") { setShowNoteComposer(false); setMentionOpen(false); }
                                      }}
                                      autoFocus
                                      rows={3}
                                      placeholder="Escreva uma anotação interna…"
                                      style={{
                                        width: "100%",
                                        resize: "none",
                                        border: isDark ? "1px solid #33424c" : "1px solid #f1c40f",
                                        background: isDark ? "#2a3942" : "#FEFCF3",
                                        color: isDark ? "#e9edef" : "#5c5320",
                                        borderRadius: 8,
                                        padding: "8px 10px",
                                        fontSize: 14,
                                        lineHeight: 1.5,
                                        outline: "none",
                                        maxHeight: 160,
                                        overflowY: "auto",
                                      }}
                                    />
                                    {mentionOpen && usuariosMencaoFiltrados().length > 0 && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          bottom: "100%",
                                          left: 0,
                                          width: "100%",
                                          marginBottom: 4,
                                          background: isDark ? "#111b21" : "#fff",
                                          border: isDark ? "1px solid #33424c" : "1px solid #e3e8ef",
                                          borderRadius: 8,
                                          boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
                                          maxHeight: 180,
                                          overflowY: "auto",
                                          zIndex: 1090,
                                        }}
                                      >
                                        {usuariosMencaoFiltrados().map((u) => (
                                          <button
                                            key={u.id}
                                            type="button"
                                            onClick={() => selecionarMencao(u)}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              width: "100%",
                                              border: "none",
                                              background: "transparent",
                                              padding: "8px 10px",
                                              cursor: "pointer",
                                              textAlign: "left",
                                              color: isDark ? "#e9edef" : "#1a1a2e",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "#2a3942" : "#f1f5f9")}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                          >
                                            <span style={{
                                              width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                                              background: "var(--brand-primary, #007e94)", color: "#fff", fontSize: 11, fontWeight: 700,
                                              display: "flex", alignItems: "center", justifyContent: "center",
                                            }}>
                                              {u.nome.charAt(0).toUpperCase()}
                                            </span>
                                            <span style={{ fontSize: 13 }}>{u.nome}</span>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                                    <button
                                      type="button"
                                      onClick={() => { setShowNoteComposer(false); setMentionOpen(false); }}
                                      style={{
                                        border: "none", background: "transparent",
                                        color: isDark ? "#8696a0" : "#64748b", fontSize: 13, fontWeight: 600,
                                        cursor: "pointer", padding: "6px 10px",
                                      }}
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={saveNote}
                                      disabled={!noteText.trim() || noteSaving}
                                      style={{
                                        border: "none",
                                        background: !noteText.trim() || noteSaving ? "#b9c2cc" : "linear-gradient(135deg, #f1c40f 0%, #e0aa0a 100%)",
                                        color: "#3a2f05", fontSize: 13, fontWeight: 700,
                                        cursor: !noteText.trim() || noteSaving ? "not-allowed" : "pointer",
                                        padding: "6px 14px", borderRadius: 8,
                                      }}
                                    >
                                      {noteSaving ? "Salvando…" : "Salvar anotação"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* input invisível para escolher o arquivo */}
                          <input
                            type="file"
                            ref={fileInputRef}
                            multiple
                            style={{ display: "none" }}
                            onChange={async (e) => {
                              const inputEl = e.currentTarget;
                              const files = Array.from(inputEl.files || []);
                              if (files.length === 0 || !pendingUploadType) return;

                              try {
                                // fila: envia vários arquivos do mesmo tipo de uma vez
                                await enfileirarMidias(files, pendingUploadType);
                              } finally {
                                inputEl.value = "";
                                setPendingUploadType(null);
                              }
                            }}
                          />

                          {isRecording ? (
                            // ── Barra de gravação (estilo WhatsApp Web) ──
                            <div
                              className="form-control border-start-0 rounded-0 shadow-none d-flex align-items-center"
                              style={{
                                height: "40px",
                                padding: "0 8px",
                                background: isDark ? "#1f2c34" : "#f7f9fa",
                                color: isDark ? "#e0e0e0" : "#1a1a2e",
                                gap: "8px",
                              }}
                            >
                              <style>{`
                                @keyframes mic-rec-pulse {
                                  0%, 100% { opacity: 1; transform: scale(1); }
                                  50%      { opacity: 0.45; transform: scale(0.85); }
                                }
                              `}</style>
                              {/* Lixeira — cancelar */}
                              <button
                                type="button"
                                className="btn btn-sm p-1"
                                onClick={cancelRecording}
                                title="Cancelar gravação"
                                style={{
                                  color: "#dc3545",
                                  border: "none",
                                  background: "transparent",
                                  lineHeight: 1,
                                }}
                              >
                                <i className="ti ti-trash" style={{ fontSize: "18px" }} />
                              </button>

                              {/* Bolinha pulsando (só quando gravando, não pausado) */}
                              <span
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  background: isPaused ? (isDark ? "#666" : "#aaa") : "#dc3545",
                                  animation: isPaused ? "none" : "mic-rec-pulse 1s ease-in-out infinite",
                                  flexShrink: 0,
                                }}
                              />

                              {/* Timer */}
                              <span
                                style={{
                                  fontVariantNumeric: "tabular-nums",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  minWidth: "44px",
                                }}
                              >
                                {formatRecordingTime(recordingDuration)}
                              </span>

                              {/* Espaço flexível */}
                              <div className="flex-grow-1" />

                              {/* Pausa / Retomar */}
                              <button
                                type="button"
                                className="btn btn-sm p-1"
                                onClick={isPaused ? resumeRecording : pauseRecording}
                                title={isPaused ? "Retomar" : "Pausar"}
                                style={{
                                  color: isDark ? "#e0e0e0" : "#555",
                                  border: "none",
                                  background: "transparent",
                                  lineHeight: 1,
                                }}
                              >
                                <i
                                  className={isPaused ? "ti ti-player-play-filled" : "ti ti-player-pause-filled"}
                                  style={{ fontSize: "18px" }}
                                />
                              </button>
                            </div>
                          ) : (
                            <textarea
                              ref={messageInputRef}
                              rows={1}
                              className="form-control border-start-0 border-end-0 rounded-0 shadow-none"
                              placeholder={
                                templateMode
                                  ? "Janela de 24h expirada — envie um template"
                                  : "Digite sua mensagem..."
                              }
                              value={newMessage}
                              onChange={(e) => {
                                const val = e.target.value;
                                setNewMessage(val);
                                const ta = e.currentTarget;
                                ta.style.height = "auto";
                                const maxHeight = 120;
                                ta.style.height = Math.min(ta.scrollHeight, maxHeight) + "px";
                                // Respostas rápidas: começa com "/" → abre o picker
                                if (val.startsWith("/")) {
                                  carregarRespostasRapidas();
                                  setShowRespostas(true);
                                } else if (showRespostas) {
                                  setShowRespostas(false);
                                }
                              }}
                              onKeyDown={(e) => {
                                // Picker de respostas rápidas aberto: Esc fecha; Enter escolhe a 1ª.
                                if (showRespostas) {
                                  if (e.key === "Escape") { e.preventDefault(); setShowRespostas(false); return; }
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    const f = newMessage.slice(1).toLowerCase();
                                    const matches = respostasRapidas.filter(
                                      (r: any) => (r.titulo || "").toLowerCase().includes(f) || (r.texto || "").toLowerCase().includes(f)
                                    );
                                    if (matches.length > 0) { e.preventDefault(); inserirRespostaRapida(matches[0]); return; }
                                  }
                                }
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  if (templateMode) {
                                    openTemplateModal();
                                  } else if (hasText) {
                                    sendMessage();
                                  }
                                }
                              }}
                              // Envio de mídia em voo NÃO trava mais o texto: são caminhos
                              // independentes (o arquivo vive em `pendingMessages`, a mensagem
                              // de texto entra direto em `contacts`), então dá para escrever e
                              // mandar enquanto o documento sobe. Só o templateMode bloqueia.
                              disabled={templateMode}
                              style={{
                                fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "14px",
                                lineHeight: "1.5",
                                minWidth: "50px",
                                minHeight: "40px",
                                maxHeight: "120px",
                                padding: "9px 12px",
                                resize: "none",
                                overflowY: "auto",
                                color: templateMode
                                  ? (isDark ? "#777" : "#999")
                                  : (isDark ? "#e0e0e0" : "#1a1a2e"),
                                background: templateMode
                                  ? (isDark ? "#1a1a1a" : "#f0f0f0")
                                  : "transparent",
                                cursor: templateMode ? "not-allowed" : "text",
                              }}
                            />
                          )}

                          {/* Botão Emoji — visível quando NÃO está gravando.
                              Mantém o input-group horizontal (height 40px) e
                              abre o picker num popover ancorado acima/à direita. */}
                          {!isRecording && (
                            <div ref={emojiPickerRef} className="position-relative" style={{ order: 2 }}>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setShowEmojiPicker((v) => !v)}
                                disabled={templateMode}
                                title="Emojis"
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: showEmojiPicker
                                    ? (isDark ? "#2a2a2a" : "#f1f5f9")
                                    : "transparent",
                                  border: "none",
                                  color: showEmojiPicker
                                    ? "#f59e0b"
                                    : (isDark ? "#94a3b8" : "#64748b"),
                                  borderRadius: 0,
                                  transition: "background 0.15s, color 0.15s",
                                  cursor: templateMode ? "not-allowed" : "pointer",
                                }}
                              >
                                <i className="ti ti-mood-smile" style={{ fontSize: 22 }} />
                              </button>
                              {showEmojiPicker && !templateMode && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: 48,
                                    right: 0,
                                    zIndex: 1060,
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                                    borderRadius: 12,
                                    overflow: "hidden",
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <EmojiPicker
                                    onEmojiClick={(data) => {
                                      insertEmoji(data.emoji);
                                    }}
                                    autoFocusSearch={false}
                                    emojiStyle={EmojiStyle.NATIVE}
                                    theme={isDark ? EmojiTheme.DARK : EmojiTheme.LIGHT}
                                    width={320}
                                    height={380}
                                    lazyLoadEmojis
                                    searchPlaceHolder="Buscar emoji…"
                                    previewConfig={{ showPreview: false }}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {isRecording ? (
                            <button
                              type="button"
                              className="btn rounded-0 rounded-end"
                              onClick={sendRecording}
                              title="Enviar áudio"
                              style={{
                                order: 3,
                                height: "40px",
                                padding: "0 16px",
                                background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                                color: "#ffffff",
                                border: "none",
                                boxShadow: "0 2px 6px rgba(37, 211, 102, 0.35)",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <i className="ti ti-send" style={{ fontSize: "16px" }} />
                            </button>
                          ) : templateMode ? (
                            <button
                              className="btn rounded-0 rounded-end"
                              type="button"
                              onClick={openTemplateModal}
                              disabled={!selectedContact || sendingTemplate}
                              style={{
                                order: 3,
                                height: "40px",
                                padding: "0 16px",
                                background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                                color: "#ffffff",
                                border: "none",
                                fontSize: "13px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                boxShadow: "0 2px 6px rgba(37, 211, 102, 0.35)",
                                transition: "all 0.2s ease",
                                opacity: sendingTemplate ? 0.7 : 1,
                              }}
                              title="Escolher e enviar um template para reabrir a conversa (fora da janela de 24h)"
                            >
                              <i className="ti ti-template me-1" />
                              {sendingTemplate ? "Enviando..." : "Enviar template"}
                            </button>
                          ) : hasText ? (
                            <button
                              className="btn btn-primary rounded-0 rounded-end"
                              type="submit"
                              style={{
                                order: 3,
                                height: "40px",
                                padding: "0 16px",
                                background: "linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #006577 100%)",
                                border: "none",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <i className="ti ti-send" style={{ fontSize: "16px" }} />
                            </button>
                          ) : (
                            <button
                              className="btn rounded-0 rounded-end"
                              type="button"
                              onClick={startRecording}
                              disabled={!selectedContact || uploading}
                              title="Gravar áudio"
                              style={{
                                order: 3,
                                height: "40px",
                                padding: "0 16px",
                                background: "linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #006577 100%)",
                                color: "#ffffff",
                                border: "none",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <i className="ti ti-microphone" style={{ fontSize: "18px" }} />
                            </button>
                          )}
                        </div>
                      </form>
                        );
                      })()}
                    </div>
                </div>
              )}
              {/* / Chat Window */}

              {/* Carregando a conversa vinda da notificação de anotação (o contato
                  não é do usuário, então é buscado/injetado — mostra um spinner) */}
              {(!isMobile || showChatMobile) && !selectedContact && (carregandoDeepLink || carregandoConversa) && (
                <div
                  className="chat chat-messages show card"
                  id="middle-deeplink-loading"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                    height: isMobile ? mobileHeight : "100%",
                    width: isMobile ? "calc(100vw - 30px)" : "100%",
                    minHeight: 0,
                    backgroundColor: isDark ? "#060a0e" : "#f0f2f5",
                    border: "none",
                    borderRadius: 0,
                  }}
                >
                  <div className="spinner-border text-primary" role="status" style={{ width: 42, height: 42 }} />
                  <div style={{ color: isDark ? "#8696a0" : "#64748b", fontSize: 15, fontWeight: 600 }}>
                    Abrindo conversa marcada…
                  </div>
                  <div style={{ color: isDark ? "#5a6b76" : "#94a3b8", fontSize: 12.5 }}>
                    Carregando o contato em que você foi mencionado
                  </div>
                </div>
              )}
            </div>
          </div>
          <audio ref={notificationSoundRef} src="/assets/sounds/notification.mp3" preload="auto" />

          {/* Aviso discreto de versão nova — só aparece quando o bundle do servidor
              mudou depois que esta aba carregou. Recarregar é escolha do operador. */}
          {novaVersaoDisponivel && (
            <div style={{
              position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
              zIndex: 2000, background: "#0f5b68", color: "#fff", padding: "10px 14px",
              borderRadius: 10, display: "flex", alignItems: "center", gap: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25)", fontSize: 13, maxWidth: "92vw",
            }}>
              <i className="ti ti-refresh" style={{ fontSize: 16 }} />
              <span>Nova versão do CRM disponível.</span>
              <button type="button" onClick={() => window.location.reload()}
                style={{ background: "#fff", color: "#0f5b68", border: "none", borderRadius: 6, padding: "4px 10px", fontWeight: 600, cursor: "pointer" }}>
                Recarregar
              </button>
              <button type="button" title="Agora não" onClick={() => setNovaVersaoDisponivel(false)}
                style={{ background: "transparent", color: "#fff", border: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}>
                ×
              </button>
            </div>
          )}
          {/* Mídia em tela cheia (imagem/vídeo do chat) */}
          <Modal show={!!fullscreenMedia} onHide={() => setFullscreenMedia(null)} centered size="xl" contentClassName="bg-transparent border-0 shadow-none">
            <div style={{ position: "relative", textAlign: "center" }} onClick={() => setFullscreenMedia(null)}>
              <button type="button" title="Fechar" onClick={() => setFullscreenMedia(null)}
                style={{ position: "absolute", top: -6, right: -6, zIndex: 2, background: "rgba(0,0,0,0.65)", color: "#fff", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <i className="ti ti-x" style={{ fontSize: 18 }} />
              </button>
              {/* Girar a imagem sem sair da tela cheia: é aqui que a operadora lê o
                  documento, e fechar o modal a cada 90° tornaria o botão inútil. */}
              {fullscreenMedia?.tipo === "image" && fullscreenMedia.msgId && podeGirarImagem && (() => {
                const msgDaTela = messages.find((mm) => String(mm.id) === fullscreenMedia.msgId);
                if (!msgDaTela) return null;
                const girandoEsta = girandoImagem.includes(String(fullscreenMedia.msgId));
                const estilo = {
                  background: "rgba(0,0,0,0.65)", color: "#fff", border: "none", borderRadius: "50%",
                  width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: girandoEsta ? "wait" : "pointer",
                } as React.CSSProperties;
                return (
                  <div style={{ position: "absolute", top: -6, left: -6, zIndex: 2, display: "flex", gap: 6 }}
                       onClick={(e) => e.stopPropagation()}>
                    <button type="button" title="Girar 90° para a esquerda" disabled={girandoEsta}
                      onClick={() => girarImagem(msgDaTela, "antihorario")} style={estilo}>
                      <i className="ti ti-rotate-2" style={{ fontSize: 18 }} />
                    </button>
                    <button type="button" title="Girar 90° para a direita" disabled={girandoEsta}
                      onClick={() => girarImagem(msgDaTela, "horario")} style={estilo}>
                      <i className="ti ti-rotate-clockwise-2" style={{ fontSize: 18 }} />
                    </button>
                  </div>
                );
              })()}
              {fullscreenMedia?.tipo === "image" ? (
                <img src={fullscreenMedia.src} alt="Imagem" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "86vh", objectFit: "contain", borderRadius: 8, opacity: girandoImagem.includes(String(fullscreenMedia.msgId ?? "")) ? 0.5 : 1, transition: "opacity .15s" }} />
              ) : fullscreenMedia?.tipo === "video" ? (
                <video src={fullscreenMedia.src} controls autoPlay onClick={(e) => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "86vh", borderRadius: 8, background: "#000" }} />
              ) : null}
            </div>
          </Modal>
          {tagsLiberadas && (
            <TagsManagerModal
              show={showTagsModal}
              onHide={() => setShowTagsModal(false)}
              tags={tagsVisiveis}
              times={tagsAdminMaster && timesDoGrupo.length > 0
                ? timesDoGrupo.map((t) => ({ id: String(t.id), nome: t.nome }))
                : [{ id: tagsMeuTime, nome: localStorage.getItem("nome_time") || "Meu time" }]}
              usoPorTag={usoPorTag}
              onCriar={criarTag}
              onEditar={editarTag}
              onApagar={apagarTag}
            />
          )}
          <Modal
            show={showScheduleModal}
            onHide={() => setShowScheduleModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Agendar Retorno Automático</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <label>Data de retorno</label>
              <input
                type="date"
                className="form-control"
                value={scheduleDate}
                min={today}
                onChange={e => setScheduleDate(e.target.value)}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleScheduleSubmit}>
                Agendar
              </Button>
            </Modal.Footer>
          </Modal>
          <Offcanvas
            show={showInfoModal}
            onHide={() => setShowInfoModal(false)}
            placement="end"
            style={{ width: "min(88vw, 380px)" }}
          >
            <Offcanvas.Body style={{ padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{
              height: "100%", display: "flex", flexDirection: "column", overflow: "hidden",
              background: isDark ? "#111" : "#fff",
            }}>
              {/* Header com avatar e info */}
              <div style={{
                background: isDark
                  ? "linear-gradient(135deg, #0a2a30 0%, #111 100%)"
                  : "linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #005f70 100%)",
                padding: "24px 20px 20px",
                position: "relative",
              }}>
                {/* Botão fechar */}
                <button
                  onClick={() => setShowInfoModal(false)}
                  style={{
                    position: "absolute", top: "12px", right: "12px",
                    width: "32px", height: "32px", borderRadius: "50%",
                    border: "none", background: "rgba(255,255,255,0.15)",
                    color: "#fff", display: "flex", alignItems: "center",
                    justifyContent: "center", cursor: "pointer", fontSize: "16px",
                  }}
                >
                  <i className="ti ti-x" />
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{
                    width: "56px", height: "56px", borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: "24px", color: "#fff", fontWeight: 700,
                    border: "2px solid rgba(255,255,255,0.3)",
                    flexShrink: 0,
                  }}>
                    {(selectedContactData?.nome || "?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: "18px", fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedContactData?.nome || "(sem nome)"}
                    </div>
                    <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", marginTop: "2px" }}>
                      {selectedContactData?.fone || ""}
                    </div>
                    <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                      {selectedContactData?.funil && (
                        <span style={{
                          fontSize: "10px", padding: "2px 10px", borderRadius: "12px",
                          background: "rgba(255,255,255,0.2)", color: "#fff",
                          fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase",
                        }}>
                          {selectedContactData.funil}
                        </span>
                      )}
                      {selectedContactData?.nome_funil && (
                        <span style={{
                          fontSize: "10px", padding: "2px 10px", borderRadius: "12px",
                          background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)",
                          fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase",
                        }}>
                          {selectedContactData.nome_funil}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: "20px", flex: 1, overflowY: "auto" }}>

                {/* Atendente (transferência) — admin only. Verde quando o dono está
                    no grupo de atendentes; cinza quando não encontrado. */}
                {isAnyAdmin && usuariosDoTime.length > 0 && (() => {
                  const atual = usuariosDoTime.find(
                    (u) => String(u.id) === String(selectedContactData?.id_user)
                  );
                  return (
                    <div style={{ marginBottom: "16px" }}>
                      <label style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isDark ? "#8696a0" : "#8a94a6", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                        <i className="ti ti-headset" style={{ fontSize: "14px" }} /> Atendente
                      </label>
                      <select
                        className="form-select"
                        value={selectedContactData?.id_user ?? ""}
                        onChange={(e) => {
                          const novoId = parseInt(e.target.value, 10);
                          const u = usuariosDoTime.find((x) => x.id === novoId);
                          if (u) transferirAtendente(u.id, u.nome, u.id_time);
                        }}
                        style={{
                          borderRadius: "10px", fontSize: "13px", padding: "8px 12px", fontWeight: 600,
                          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                          background: atual ? "rgba(34,197,94,0.08)" : "rgba(148,163,184,0.12)",
                          color: atual ? "#16a34a" : "#64748b",
                        }}
                      >
                        <option value="" disabled>Sem atendente</option>
                        {usuariosDoTime.map((u) => (
                          <option key={u.id} value={u.id}>{rotuloAtendenteChat(u)}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Idioma */}
                {temIdiomas && (
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isDark ? "#8696a0" : "#8a94a6", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <i className="ti ti-language" style={{ fontSize: "14px" }} /> Idioma
                    </label>
                    <select
                      className="form-select"
                      value={valorDoSeletor(selectedContactData?.idioma)}
                      style={{
                        borderRadius: "10px", fontSize: "13px", padding: "8px 12px",
                        border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                        background: isDark ? "#1a1a1a" : "#fff",
                        color: isDark ? "#e0e0e0" : "#333",
                      }}
                      onChange={async (e) => {
                        const novoIdioma = e.target.value;
                        const fone = selectedContactData?.fone || "";
                        await atualizarIdioma(fone, novoIdioma);
                        setContacts((prev) =>
                          prev.map((c) =>
                            c.id === selectedContactData?.id
                              ? { ...c, idioma: novoIdioma }
                              : c
                          )
                        );
                      }}
                    >
                      {opcoesParaContato(selectedContactData?.idioma).map((o) => (
                        <option key={o.valor} value={o.valor}>{o.rotulo}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Funil e Etapa lado a lado — mesma lógica do header:
                    o funil só recarrega as etapas, a etapa é que move o lead. */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isDark ? "#8696a0" : "#8a94a6", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <i className="ti ti-filter" style={{ fontSize: "14px" }} /> Funil
                    </label>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isDark ? "#8696a0" : "#8a94a6", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <i className="ti ti-tag" style={{ fontSize: "14px" }} /> Etapa
                    </label>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                  {renderFunilEtapaSelects(
                    {
                      flex: 1, minWidth: 0,
                      borderRadius: "10px", fontSize: "13px", padding: "8px 12px",
                      border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                      background: isDark ? "#1a1a1a" : "#fff",
                      color: isDark ? "#e0e0e0" : "#333",
                    },
                    "form-select"
                  )}
                </div>

                {/* Observacao */}
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                    <label style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isDark ? "#8696a0" : "#8a94a6", display: "flex", alignItems: "center", gap: "6px", margin: 0 }}>
                      <i className="ti ti-notes" style={{ fontSize: "14px" }} /> Observacao
                    </label>
                    {!isEditingObs && (
                      <button
                        style={{
                          border: "none", background: "transparent", cursor: "pointer",
                          color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)", fontSize: "12px", fontWeight: 600,
                          display: "flex", alignItems: "center", gap: "4px", padding: "2px 8px",
                          borderRadius: "6px",
                        }}
                        onClick={() => {
                          setObsValue(selectedContactData?.observacao || "");
                          setIsEditingObs(true);
                        }}
                      >
                        <i className="ti ti-pencil" style={{ fontSize: "13px" }} /> Editar
                      </button>
                    )}
                  </div>
                  {isEditingObs ? (
                    <div>
                      <textarea
                        className="form-control"
                        rows={6}
                        value={obsValue}
                        onChange={e => setObsValue(e.target.value)}
                        style={{
                          borderRadius: "10px", fontSize: "13px", resize: "vertical",
                          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                          background: isDark ? "#1a1a1a" : "#fff",
                          color: isDark ? "#e0e0e0" : "#333",
                          marginBottom: "8px",
                        }}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                        <button
                          disabled={savingObs}
                          onClick={() => setIsEditingObs(false)}
                          style={{
                            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                            background: "transparent", borderRadius: "8px", padding: "6px 16px",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            color: isDark ? "#ccc" : "#555",
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          disabled={savingObs}
                          onClick={async () => {
                            setSavingObs(true);
                            try {
                              const id_cliente = localStorage.getItem("id_cliente");
                              await fetch(`${API_URL}/marcarobs`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  fone: selectedContactData?.fone,
                                  observacao: obsValue,
                                  id_cliente,
                                }),
                              });
                              setContacts(prev =>
                                prev.map(c =>
                                  c.id === selectedContactData?.id
                                    ? { ...c, observacao: obsValue }
                                    : c
                                )
                              );
                              setIsEditingObs(false);
                            } catch (err) {
                              console.error("Erro ao salvar observacao:", err);
                              alert("Falha ao salvar.");
                            } finally {
                              isFetchingContacts.current = false;
                              setSavingObs(false);
                            }
                          }}
                          style={{
                            border: "none", borderRadius: "8px", padding: "6px 16px",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #005f70 100%)",
                            color: "#fff",
                          }}
                        >
                          {savingObs ? "Salvando..." : "Salvar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: "12px 14px", borderRadius: "10px", minHeight: "80px",
                      whiteSpace: "pre-wrap", fontSize: "13px", lineHeight: "1.6",
                      background: isDark ? "#1a1a1a" : "#f8f9fa",
                      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
                      color: isDark ? "#ccc" : "#444",
                    }}>
                      {selectedContactData?.observacao || "Nenhuma observacao registrada."}
                    </div>
                  )}
                </div>

                {(() => {
                  const etapaFin = stages.find(
                    (s) => s.funil === selectedContactData?.funil && s.atendimento_finalizado === "Sim"
                  );
                  if (!etapaFin || !selectedContactData?.fone) return null;
                  return (
                    <div style={{ marginBottom: "16px" }}>
                      <BotaoFinalizarAtendimento
                        finalizado={etapaFin.nome_funil === selectedContactData?.nome_funil}
                        onFinalizar={() => finalizarAtendimento(etapaFin.nome_funil)}
                        onReabrir={reabrirAtendimento}
                      />
                    </div>
                  );
                })()}

                {/* Atendimento Humano (switch) — só quando o funil do contato tem etapa de atendimento humano */}
                {(() => {
                  const humanStage = stages.find(
                    (s) => s.funil === selectedContactData?.funil && s.atendimento_humano === "Sim"
                  );
                  if (!humanStage) return null;
                  const isHuman = humanStage.nome_funil === selectedContactData?.nome_funil;
                  return (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: "16px", padding: "10px 14px", borderRadius: "10px",
                      background: isDark ? "#1a1a1a" : "#f8f9fa",
                      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
                    }}>
                      <span style={{ fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px", color: isHuman ? "#28a745" : (isDark ? "#ccc" : "#6c757d") }}>
                        <i className="ti ti-user-check" style={{ fontSize: "16px" }} /> Atendimento Humano
                      </span>
                      <div
                        className="form-check form-switch m-0"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (!isHuman) {
                            if (selectedContactData?.fone) updateFunnelStage(selectedContactData.fone, humanStage.nome_funil);
                          } else {
                            const id_cliente = localStorage.getItem("id_cliente");
                            if (!selectedContactData?.fone || !id_cliente) return;
                            fetch(`${API_URL}/voltar_etapa_anterior`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ fone: selectedContactData.fone, id_cliente }),
                            })
                              .then((res) => res.json())
                              .then((data) => {
                                if (data.found) {
                                  const targetFunnel = data.funnel_name as string | undefined;
                                  if (targetFunnel && targetFunnel !== selectedContactData?.funil) {
                                    updateFunnelCombined(selectedContactData.fone, data.stage_name, targetFunnel);
                                  } else {
                                    updateFunnelStage(selectedContactData.fone, data.stage_name);
                                  }
                                } else {
                                  alert("Não foi possível encontrar uma etapa anterior automaticamente. Selecione manualmente no campo 'Etapa'.");
                                }
                              })
                              .catch(() => alert("Erro ao buscar etapa anterior."));
                          }
                        }}
                      >
                        <input className="form-check-input" type="checkbox" role="switch" checked={isHuman} readOnly style={{ cursor: "pointer" }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Botão Cards */}
                <button
                  onClick={() => { setShowInfoModal(false); handleOpenCardModal(); }}
                  style={{
                    width: "100%", border: "none", borderRadius: "10px",
                    padding: "10px 16px", fontSize: "14px", fontWeight: 600,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: "8px", marginBottom: "10px",
                    background: isDark ? "rgba(77,216,232,0.1)" : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.06)",
                    color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)",
                  }}
                >
                  <i className="ti ti-id" style={{ fontSize: "18px" }} /> Visualizar Cards
                </button>

                {/* Botao agendamento */}
                <button
                  onClick={() => {
                    setShowInfoModal(false);
                    setShowScheduleModal(true);
                  }}
                  style={{
                    width: "100%", border: "none", borderRadius: "10px",
                    padding: "10px 16px", fontSize: "14px", fontWeight: 600,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: "8px",
                    background: isDark
                      ? "rgba(77,216,232,0.1)"
                      : "rgba(var(--brand-primary-rgb, 0, 126, 148),0.06)",
                    color: isDark ? "#4dd8e8" : "var(--brand-primary, #007e94)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <i className="ti ti-calendar-event" style={{ fontSize: "18px" }} />
                  Agendar Retorno
                </button>
              </div>
            </div>
            </Offcanvas.Body>
          </Offcanvas>
          <Modal
            show={showNewContactModal}
            onHide={() => {
              if (creatingContact) return; // não fecha durante o submit
              setShowNewContactModal(false);
              setCreateContactError(null);
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Criar Novo Contato</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {createContactError && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mb-3" role="alert">
                  <i className="ti ti-alert-circle mt-1" />
                  <div className="flex-grow-1" style={{ fontSize: 13 }}>{createContactError}</div>
                  <button
                    type="button"
                    className="btn-close ms-2"
                    aria-label="Fechar"
                    onClick={() => setCreateContactError(null)}
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Nome</label>
                <input
                  type="text"
                  className="form-control"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="Digite o nome"
                />
              </div>
              {/* Time de destino — so o admin master escolhe; os demais criam no proprio. */}
              {isAdminMaster && timesDoGrupo.length > 1 && (
                <div className="mb-3">
                  <label className="form-label">Time <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={newContactTime}
                    onChange={(e) => setNewContactTime(e.target.value)}
                  >
                    {timesDoGrupo.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.nome}</option>
                    ))}
                  </select>
                  <div className="form-text">O lead entra neste time. Funil, etapa e números abaixo seguem a escolha.</div>
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Telefone</label>
                <div className="d-flex gap-2">
                  {/* DDI separado: o campo ao lado recebe SO DDD + numero. */}
                  <div style={{ flex: "0 0 128px" }}>
                    <div className="input-group">
                      <span className="input-group-text px-2" title="Brasil">🇧🇷</span>
                      <input
                        type="text"
                        className="form-control text-center px-1"
                        value={newContactDDI}
                        onChange={(e) => setNewContactDDI(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="55"
                        title="Código do país"
                      />
                    </div>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
                    placeholder="DDD + número. Ex: 11999999999"
                  />
                </div>
                <div className="form-text">Só DDD e número — o código do país fica no campo ao lado.</div>
              </div>
              {receptora ? (
                <div className="mb-3">
                  <label className="form-label">Destino</label>
                  <div className="form-control bg-light d-flex align-items-center gap-2" style={{ cursor: "default" }}>
                    <i className="ti ti-target-arrow text-primary" />
                    <span><strong>{receptora.funil}</strong> / {receptora.etapa}</span>
                  </div>
                  <div className="form-text">Etapa receptora do time. O lead entra direto aqui.</div>
                  {receptora.boasVindas && (
                    <div className="alert alert-warning py-2 px-3 mt-2 mb-0 d-flex align-items-start gap-2">
                      <i className="ti ti-alert-triangle mt-1" />
                      <span>
                        Esta etapa tem <strong>mensagem de boas-vindas ativa</strong> — ao salvar, o
                        contato recebe uma mensagem automática no WhatsApp.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <>
              <div className="mb-3">
                <label className="form-label">Funil</label>
                <select
                  className="form-select"
                  value={newContactFunnel}
                  onChange={(e) => {
                    setNewContactFunnel(e.target.value);
                    setNewContactStage("");
                  }}
                >
                  <option value="">Selecione um funil...</option>
                  {/* modalFunnels: seguem o time ESCOLHIDO, nao o do login. */}
                  {(modalFunnels.length ? modalFunnels : funnels).map(f => (
                    <option key={f.id} value={f.nome_funil}>{f.nome_funil}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Etapa</label>
                <select
                  className="form-select"
                  value={newContactStage}
                  onChange={(e) => setNewContactStage(e.target.value)}
                  disabled={!newContactFunnel}
                >
                  <option value="">Selecione uma etapa...</option>
                  {(modalStages.length ? modalStages : stages)
                    .filter((stage: any) => stage.funil === newContactFunnel)
                    .map(stage => (
                      <option key={stage.id} value={stage.nome_funil}>
                        {stage.nome_funil}
                      </option>
                    ))}
                </select>
              </div>
                </>
              )}
              {mostraSeletorNumero && (
                <div className="mb-3">
                  <label className="form-label">
                    Número de atendimento<span className="text-danger"> *</span>
                  </label>
                  <select
                    className="form-select"
                    value={newContactIns}
                    onChange={(e) => setNewContactIns(e.target.value)}
                  >
                    <option value="">Selecione o número...</option>
                    {instanciasDoTime.map((i) => (
                      <option key={i.ins_nome} value={String(i.ins_nome)}>
                        {rotuloNumero(i.ins_numero)}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    É por este número que o lead será atendido. O WhatsApp só permite iniciar
                    conversa pelo número que o cliente respondeu, então trocar depois exige
                    template.
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowNewContactModal(false);
                  setCreateContactError(null);
                }}
                disabled={creatingContact}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateManualContact}
                disabled={creatingContact}
              >
                {creatingContact ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Criando…
                  </>
                ) : (
                  "Salvar Contato"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
              
                        <CardViewModal
                          show={cardModalShow}
                          onHide={() => setCardModalShow(false)}
                          leadPhone={selectedLeadPhone}
                          leadName={selectedLeadName}
                          initialFunnelName={selectedFunnelName}
                          // A costura entre as telas: o card mostra o que falta e, no mesmo
                          // gesto, abre a promessa com os pendentes já carregados (sem refetch).
                          // Só os times de retenção recebem o botão; as demais telas que usam
                          // o card (ChatAdmin, kanban, lista de leads) não passam esta prop.
                          onRegistrarPromessa={ehTimeRetencao(timeDoLeadAtual()) ? abrirPromessaComDados : undefined}
                          idTime={timeDoLeadAtual()}
                        />

                        {/* ── Uploads internos do contato (TNP, grupo 192) ────────────────
                            Lista os arquivos anexados a ESTE contato (type='tnpupload').
                            São internos: nenhum deles foi enviado ao cliente. */}
                        <Modal
                          show={showTnpUploads}
                          onHide={() => setShowTnpUploads(false)}
                          centered
                          dialogClassName="dr-dialog-620"
                          contentClassName="dr-escopo dr-conteudo"
                        >
                          <div className="dr-topo">
                            <div className="dr-selo">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 21, height: 21 }}>
                                <path d="M12 16V6M8.5 9.5 12 6l3.5 3.5" />
                                <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                              </svg>
                            </div>
                            <div>
                              <h2>Uploads do contato</h2>
                              <p>
                                Arquivos anexados internamente a{" "}
                                <b>{selectedContactData?.nome || selectedContactData?.fone || "este contato"}</b>.
                                Não foram enviados ao cliente.
                              </p>
                            </div>
                            <button type="button" className="dr-fechar" aria-label="Fechar" onClick={() => setShowTnpUploads(false)}>✕</button>
                          </div>

                          <div className="dr-corpo">
                            {tnpUploadsLoading ? (
                              <div className="dr-dica">Carregando arquivos...</div>
                            ) : tnpUploads.length === 0 ? (
                              <div className="dr-dica">Nenhum arquivo enviado para este contato ainda.</div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "56vh", overflowY: "auto" }}>
                                {tnpUploads.map((u) => {
                                  const nomeArq = tnpNomeArquivo(u);
                                  const ext = (nomeArq.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                                  const autor = (u.id_user != null && nomePorUserId[u.id_user]) || "—";
                                  return (
                                    <div
                                      key={String(u.id)}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "8px 10px",
                                        borderRadius: 10,
                                        background: isDark ? "rgba(255,255,255,0.05)" : "#f7f9fa",
                                        border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid #e6ebf0",
                                      }}
                                    >
                                      <FileTypeIcon ext={ext} />
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div
                                          title={nomeArq}
                                          style={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            color: isDark ? "#e9edef" : "#1a1a2e",
                                          }}
                                        >
                                          {nomeArq}
                                        </div>
                                        <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 2 }}>
                                          {autor} ·{" "}
                                          {new Date(u.timestamp).toLocaleString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </div>
                                      </div>
                                      <a
                                        href={u.mensagem}
                                        download={nomeArq}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                        style={{ flex: "0 0 auto" }}
                                      >
                                        <i className="ti ti-download" /> Abrir
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </Modal>

                        {/* ── Registrar promessa do cliente (TNP, grupo 192) ──────────────
                            Registrar a promessa LIGA UM GATILHO: o aviso verde não é
                            decorativo, é o que a operadora precisa entender. */}
                        <Modal
                          show={showPromessaModal}
                          onHide={() => setShowPromessaModal(false)}
                          centered
                          dialogClassName="dr-dialog-620"
                          contentClassName="dr-escopo dr-conteudo"
                        >
                          <div className="dr-topo">
                            <div className="dr-selo">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 21, height: 21 }}>
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3.2 1.8" />
                              </svg>
                            </div>
                            <div>
                              <h2>Registrar promessa do cliente</h2>
                              <p>Marque o que o cliente se comprometeu a enviar e para quando.</p>
                            </div>
                            <button type="button" className="dr-fechar" aria-label="Fechar" onClick={() => setShowPromessaModal(false)}>✕</button>
                          </div>

                          <div className="dr-corpo">
                            {promessaAtual && (
                              <div className="dr-aviso dr-aviso-existente">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
                                  <circle cx="12" cy="12" r="9" />
                                  <path d="M12 8h.01M11 11h1v5" />
                                </svg>
                                <div style={{ width: "100%" }}>
                                  Este cliente já tem uma promessa em aberto para{" "}
                                  <b>{docsFmtPromessaData(promessaAtual.data_prometida)}</b>. Salvar
                                  uma nova <b>substitui</b> a anterior.

                                  {/* O que ele prometeu: os nomes gravados na promessa,
                                      não o checklist de hoje (o card pode ter mudado). */}
                                  {Array.isArray(promessaAtual.documentos) && promessaAtual.documentos.length > 0 && (
                                    <ul className="dr-promessa-docs">
                                      {promessaAtual.documentos.map((nome: string) => (
                                        <li key={nome}>{nome}</li>
                                      ))}
                                    </ul>
                                  )}

                                  {/* Documentos chegaram: encerra a promessa e o lead
                                      deixa de ter data (e de ser cobrado). */}
                                  <button
                                    type="button"
                                    className="dr-btn-remover-promessa"
                                    onClick={confirmarEntregaPromessa}
                                    disabled={confirmandoEntrega}
                                    title="Confirmar a entrega e encerrar esta promessa"
                                  >
                                    {confirmandoEntrega ? "Limpando..." : "Remover da Promessa"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {promessaCumpridaAviso && (
                              <div className="dr-aviso dr-aviso-cumprida">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
                                  <circle cx="12" cy="12" r="9" />
                                  <path d="m8.5 12.5 2.5 2.5 4.5-5" />
                                </svg>
                                <div>{promessaCumpridaAviso}</div>
                              </div>
                            )}
                            <div className="dr-rotulo-campo">Documentos que ainda faltam</div>
                            {/* Com 0 recebidos a segunda frase fica esquisita — some. */}
                            <div className="dr-dica">
                              Aparecem apenas os pendentes deste cliente.
                              {promessaRecebidos === 1 && " O documento já recebido não entra na lista."}
                              {promessaRecebidos > 1 && ` Os ${promessaRecebidos} já recebidos não entram na lista.`}
                            </div>

                            {promessaCarregando ? (
                              <div className="dr-dica">Carregando documentos do card...</div>
                            ) : promessaPendentes.length === 0 ? (
                              // Ou o cliente entregou tudo, ou o card não tem campo checklist
                              // configurado para este funil.
                              <div className="dr-dica">
                                Nenhum documento pendente no card deste cliente.
                              </div>
                            ) : (
                              <div className="dr-docs">
                                {promessaPendentes.map((nome) => {
                                  const marcado = promessaSelecionados.includes(nome);
                                  return (
                                    <label key={nome} className={`dr-doc ${marcado ? "dr-marcado" : ""}`}>
                                      <input
                                        type="checkbox"
                                        checked={marcado}
                                        onChange={() =>
                                          setPromessaSelecionados((prev) =>
                                            marcado ? prev.filter((x) => x !== nome) : [...prev, nome]
                                          )
                                        }
                                      />
                                      <span className="dr-caixa">
                                        {marcado && (
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: 12, height: 12 }}>
                                            <path d="m4 12 5.5 5.5L20 7" />
                                          </svg>
                                        )}
                                      </span>
                                      <span className="dr-nome">{nome}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            <div className="dr-linha-data">
                              <div className="dr-campo-data">
                                <div className="dr-rotulo-campo">
                                  Prometido para
                                  {/* Documento marcado sem data trava o Salvar. O botão
                                      desabilitado sozinho não explica o porquê. */}
                                  {promessaSelecionados.length > 0 && !promessaData && (
                                    <span className="dr-obrigatorio"> — escolha a data</span>
                                  )}
                                </div>
                                <div className="dr-entrada">
                                  {/* min = hoje: promessa é sempre para frente */}
                                  <input
                                    type="date"
                                    value={promessaData}
                                    min={docsHojeISO()}
                                    onChange={(e) => setPromessaData(e.target.value)}
                                    aria-label="Data prometida"
                                  />
                                </div>
                              </div>
                              <div className="dr-atalhos">
                                {[
                                  { rotulo: "hoje", dias: 0 },
                                  { rotulo: "amanhã", dias: 1 },
                                  { rotulo: "em 3 dias", dias: 3 },
                                  { rotulo: "em 7 dias", dias: 7 },
                                ].map((atalho) => {
                                  const valor = docsSomarDias(atalho.dias);
                                  return (
                                    <button
                                      key={atalho.rotulo}
                                      type="button"
                                      className={promessaData === valor ? "dr-ativo" : ""}
                                      onClick={() => setPromessaData(valor)}
                                    >
                                      {atalho.rotulo}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="dr-aviso">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
                                <path d="m13 2-9 12h7l-1 8 9-12h-7z" />
                              </svg>
                              <div>
                                Se os documentos não chegarem até{" "}
                                <b>{promessaData ? docsFormatarBR(promessaData).slice(0, 5) : "a data escolhida"}</b>
                                , a cadência de cobrança dispara sozinha e o cliente sobe para o topo da fila de retenção como promessa não cumprida.
                              </div>
                            </div>
                          </div>

                          <div className="dr-rodape">
                            <button type="button" className="dr-btn-cancelar" onClick={() => setShowPromessaModal(false)}>
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="dr-btn-salvar"
                              disabled={promessaSelecionados.length === 0 || !promessaData || salvandoPromessa || promessaCarregando}
                              onClick={salvarPromessa}
                            >
                              {salvandoPromessa ? "Salvando..." : "Salvar promessa"}
                            </button>
                          </div>
                        </Modal>

                        {/* Biblioteca de mídias — escolher uma mídia salva e enviar */}
                        {/* Lista do que o lead vai receber se ficar parado na etapa atual */}
                        <Modal show={showAgendaModal} onHide={() => setShowAgendaModal(false)} centered size="lg" scrollable>
                          <Modal.Header closeButton className="border-0 pb-0">
                            <Modal.Title className="d-flex align-items-center gap-2">
                              <i className="ti ti-clock text-primary" style={{ fontSize: 22 }} />
                              <span className="fw-bold">Envios programados</span>
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body className="pt-2">
                            <p className="text-muted fs-13 mb-3">
                              O que este lead recebe <strong>se ficar parado</strong> em
                              {" "}<strong>{agenda?.itens?.[0]?.etapa || selectedContactData?.nome_funil || "—"}</strong>.
                              Responder ou mudar de etapa refaz a conta.
                            </p>
                            {(agenda?.itens || []).length === 0 && (
                              <div className="text-center text-muted py-4">
                                <i className="ti ti-clock-off d-block mb-2" style={{ fontSize: 30 }} />
                                Nenhum envio programado para este lead.
                              </div>
                            )}
                            {(agenda?.itens || []).map((i, idx) => {
                              const rotulo: Record<string, string> = {
                                cadencia: "Cadência", follow: "Follow", promessa: "Promessa", bemvindo: "Boas-vindas",
                              };
                              const icone: Record<string, string> = {
                                cadencia: "ti-repeat", follow: "ti-message-2", promessa: "ti-calendar-check", bemvindo: "ti-hand-move",
                              };
                              return (
                                <div key={idx} className="border rounded-3 p-3 mb-2"
                                     style={{ opacity: i.inativo ? 0.55 : 1, background: isDark ? "#161616" : "#fbfbfc" }}>
                                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span className="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
                                      <i className={`ti ${icone[i.tipo] || "ti-clock"}`} /> {rotulo[i.tipo] || i.tipo}
                                    </span>
                                    <span className="fw-bold fs-13">{dataAgendaBR(i.quando) || "sem data prevista"}</span>
                                    {i.estimado && i.quando && (
                                      <span className="badge bg-secondary-subtle text-secondary">estimativa</span>
                                    )}
                                    {i.canal === "uazapi" && (
                                      <span className="badge bg-warning-subtle text-warning">não oficial (uazapi)</span>
                                    )}
                                    {i.inativo && <span className="badge bg-danger-subtle text-danger">não vai sair</span>}
                                  </div>
                                  <div className="fs-13 fw-medium">{i.titulo}</div>
                                  {i.template && (
                                    <div className="fs-12 text-muted">
                                      <i className="ti ti-file-text me-1" />template <code>{i.template}</code>
                                    </div>
                                  )}
                                  {i.texto && (
                                    <div className="mt-2 p-2 rounded-2 fs-12"
                                         style={{ whiteSpace: "pre-wrap", background: isDark ? "#101010" : "#fff", border: "1px solid rgba(0,0,0,0.06)" }}>
                                      {i.texto}
                                    </div>
                                  )}
                                  {!i.texto && i.tipo === "follow" && (
                                    <div className="fs-12 text-muted mt-1">
                                      O texto é escrito pela IA na hora do envio, então não dá para mostrar antes.
                                    </div>
                                  )}
                                  {i.observacao && <div className="fs-11 text-muted mt-1">{i.observacao}</div>}
                                </div>
                              );
                            })}
                          </Modal.Body>
                          <Modal.Footer className="border-0">
                            <Button variant="light" onClick={() => setShowAgendaModal(false)}>Fechar</Button>
                          </Modal.Footer>
                        </Modal>

                        <Modal show={showBibliotecaModal} onHide={() => setShowBibliotecaModal(false)} centered size="lg" scrollable>
                          <Modal.Header closeButton className="border-0 pb-0">
                            <Modal.Title className="d-flex align-items-center gap-2">
                              <i className="ti ti-photo-video text-primary" style={{ fontSize: 22 }} />
                              <span className="fw-bold">Biblioteca de mídias</span>
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body className="pt-2">
                            <ul className="nav nav-pills gap-2 mb-3">
                              {[
                                { key: "image", label: "Imagens", icon: "ti-photo" },
                                { key: "video", label: "Vídeos", icon: "ti-video" },
                                { key: "audio", label: "Áudios", icon: "ti-music" },
                                { key: "document", label: "Documentos", icon: "ti-file-text" },
                              ].map((t) => (
                                <li className="nav-item" key={t.key}>
                                  <button
                                    className={`nav-link d-flex align-items-center gap-1 fs-13 ${bibliotecaAba === t.key ? "active" : ""}`}
                                    onClick={() => trocarAbaBiblioteca(t.key as any)}
                                  >
                                    <i className={`ti ${t.icon}`} /> {t.label}
                                  </button>
                                </li>
                              ))}
                            </ul>

                            {/* Breadcrumb de pasta */}
                            <div className="d-flex align-items-center gap-1 mb-2 fs-13">
                              <button className="btn btn-link p-0 text-decoration-none fw-medium" onClick={() => { setBibliotecaPasta(null); carregarBibliotecaMidias(bibliotecaAba, null); }}>Raiz</button>
                              {bibliotecaPasta && (<><i className="ti ti-chevron-right text-muted fs-12" /> <span className="fw-bold"><i className="ti ti-folder me-1 text-warning" />{bibliotecaPasta.nome}</span></>)}
                            </div>

                            {/* Pastas (só na raiz) */}
                            {!bibliotecaPasta && bibliotecaPastas.length > 0 && (
                              <div className="row g-2 mb-3">
                                {bibliotecaPastas.map((p: any) => (
                                  <div className="col-6 col-md-4" key={p.id}>
                                    <div className="card border shadow-sm" style={{ cursor: "pointer" }} onClick={() => entrarPastaBiblioteca(p)}>
                                      <div className="card-body p-2 d-flex align-items-center gap-2 overflow-hidden">
                                        <i className="ti ti-folder text-warning" style={{ fontSize: 22 }} />
                                        <span className="fw-medium text-truncate fs-13">{p.nome}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {bibliotecaLoading ? (
                              <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
                            ) : bibliotecaItens.length === 0 ? (
                              (!bibliotecaPasta && bibliotecaPastas.length > 0) ? null : (
                                <div className="text-center text-muted py-5">
                                  Nenhuma mídia aqui. Adicione em <b>Biblioteca de Mídias</b>.
                                </div>
                              )
                            ) : (
                              <div className="row g-3">
                                {bibliotecaItens.map((m: any) => (
                                  <div className="col-6 col-md-4 col-lg-3" key={m.id}>
                                    <div
                                      className="card border shadow-sm h-100 overflow-hidden"
                                      style={{ cursor: "pointer" }}
                                      title="Clique para enviar"
                                      onClick={() => enviarMidiaBiblioteca(m)}
                                    >
                                      {m.tipo === "image" ? (
                                        <img src={m.url} alt={m.nome || ""} loading="lazy" decoding="async" style={{ width: "100%", height: 110, objectFit: "cover" }} />
                                      ) : m.tipo === "video" ? (
                                        <video src={m.url} preload="metadata" muted style={{ width: "100%", height: 110, objectFit: "cover", background: "#000" }} />
                                      ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 110, background: "#f1f5f9", color: "#475569" }}>
                                          <i className={`ti ${m.tipo === "audio" ? "ti-music" : "ti-file-text"}`} style={{ fontSize: 34 }} />
                                        </div>
                                      )}
                                      <div className="card-body p-2">
                                        <span className="small text-truncate d-block" title={m.nome || ""}>{m.nome || "—"}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Modal.Body>
                        </Modal>

                        {/* Modal de Pagamento */}
                        <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} centered size="lg">
                          <Modal.Header closeButton className="border-0 pb-0">
                            <Modal.Title className="d-flex align-items-center gap-2">
                              <i className="ti ti-template text-success" style={{ fontSize: 22 }} />
                              <span className="fw-bold">Enviar template</span>
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body className="pt-2">
                            {loadingTemplates ? (
                              <div className="text-center py-5">
                                <div className="spinner-border text-success" role="status" />
                                <p className="text-muted mt-3 mb-0 small">Carregando templates da Meta…</p>
                              </div>
                            ) : !selectedTpl ? (
                              <>
                                {/* Campo de busca com ícone */}
                                <div className="input-group mb-3">
                                  <span className="input-group-text bg-white border-end-0">
                                    <i className="ti ti-search text-muted" />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Buscar template pelo nome…"
                                    value={templateSearch}
                                    onChange={(e) => setTemplateSearch(e.target.value)}
                                    autoFocus
                                  />
                                </div>

                                {(() => {
                                  const filtered = templatesList.filter((t) =>
                                    t.name.toLowerCase().includes(templateSearch.toLowerCase())
                                  );
                                  if (filtered.length === 0) {
                                    return (
                                      <div className="text-center py-5">
                                        <i className="ti ti-mood-empty text-muted" style={{ fontSize: 36 }} />
                                        <p className="text-muted mt-2 mb-0 small">Nenhum template encontrado.</p>
                                      </div>
                                    );
                                  }
                                  return (
                                    <>
                                      <div className="text-muted small mb-2 ps-1">
                                        {filtered.length} template{filtered.length === 1 ? "" : "s"} aprovado{filtered.length === 1 ? "" : "s"}
                                      </div>
                                      <div className="d-flex flex-column gap-2" style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
                                        {filtered.map((t) => {
                                          const hasImage = t.headerFormat === "IMAGE" && !!t.headerImage;
                                          const hasVideo = t.headerFormat === "VIDEO" && !!t.headerImage;
                                          const previewText = (t.body || t.headerText || "(sem corpo)").slice(0, 120);
                                          const truncated = (t.body || t.headerText || "").length > 120;
                                          return (
                                            <button
                                              type="button"
                                              key={t.name}
                                              onClick={() => selectTemplate(t)}
                                              className="w-100 d-flex align-items-start gap-3 p-3 border rounded-3"
                                              style={{
                                                background: isDark ? "#1a1a1a" : "#ffffff",
                                                color: isDark ? "#e0e0e0" : "#1a1a2e",
                                                textAlign: "left",
                                                cursor: "pointer",
                                                transition: "transform .12s, box-shadow .12s, border-color .12s",
                                                borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = "#22c55e";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(34,197,94,0.12)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = isDark
                                                  ? "rgba(255,255,255,0.08)"
                                                  : "rgba(0,0,0,0.08)";
                                                e.currentTarget.style.boxShadow = "none";
                                              }}
                                            >
                                              {/* Thumbnail (imagem real ou ícone) */}
                                              <div
                                                className="flex-shrink-0 d-flex align-items-center justify-content-center"
                                                style={{
                                                  width: 48,
                                                  height: 48,
                                                  borderRadius: 10,
                                                  background: hasImage
                                                    ? "transparent"
                                                    : isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                {hasImage ? (
                                                  <img
                                                    src={t.headerImage}
                                                    alt={t.name}
                                                    loading="lazy"
                                                    decoding="async"
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                  />
                                                ) : hasVideo ? (
                                                  <i className="ti ti-video" style={{ fontSize: 22, color: "#16a34a" }} />
                                                ) : (
                                                  <i className="ti ti-message-circle" style={{ fontSize: 20, color: "#16a34a" }} />
                                                )}
                                              </div>

                                              {/* Texto + badges */}
                                              <div className="flex-grow-1 overflow-hidden">
                                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                  <span className="fw-bold" style={{ fontSize: 14 }}>
                                                    {t.name}
                                                  </span>
                                                  {hasImage && (
                                                    <span className="badge rounded-pill" style={{
                                                      background: "rgba(34,197,94,0.12)", color: "#16a34a",
                                                      fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                                    }}>
                                                      <i className="ti ti-photo me-1" />IMAGEM
                                                    </span>
                                                  )}
                                                  {hasVideo && (
                                                    <span className="badge rounded-pill" style={{
                                                      background: "rgba(59,130,246,0.12)", color: "#2563eb",
                                                      fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                                    }}>
                                                      <i className="ti ti-video me-1" />VÍDEO
                                                    </span>
                                                  )}
                                                  {t.vars.length > 0 && (
                                                    <span className="badge rounded-pill" style={{
                                                      background: "rgba(99,102,241,0.12)", color: "#6366f1",
                                                      fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                                    }}>
                                                      <i className="ti ti-variable me-1" />
                                                      {t.vars.length} var.
                                                    </span>
                                                  )}
                                                  {(() => {
                                                    const selo = seloCategoria(t.categoria);
                                                    if (!selo) return null;
                                                    return (
                                                      <span className="badge rounded-pill" style={{
                                                        background: selo.fundo, color: selo.cor,
                                                        fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                                      }}>
                                                        <i className={`ti ${selo.icone} me-1`} />
                                                        {selo.texto}
                                                      </span>
                                                    );
                                                  })()}
                                                  {(() => {
                                                    const st = seloStatus(t.status);
                                                    if (!st) return null;
                                                    return (
                                                      <span className="badge rounded-pill" title={st.aviso} style={{
                                                        background: st.fundo, color: st.cor,
                                                        fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                                      }}>
                                                        <i className={`ti ${st.icone} me-1`} />
                                                        {st.texto}
                                                      </span>
                                                    );
                                                  })()}
                                                </div>
                                                <div className="text-muted" style={{
                                                  fontSize: 12, lineHeight: 1.4,
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: "vertical",
                                                  overflow: "hidden",
                                                }}>
                                                  {previewText}{truncated && "…"}
                                                </div>
                                              </div>

                                              <i className="ti ti-chevron-right text-muted flex-shrink-0 align-self-center" style={{ fontSize: 18 }} />
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              <>
                                {/* Header com voltar + nome */}
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <button
                                    type="button"
                                    className="btn btn-sm d-flex align-items-center gap-1 p-1 border-0"
                                    onClick={() => setSelectedTpl(null)}
                                    style={{ background: "transparent", color: isDark ? "#e0e0e0" : "#475569" }}
                                  >
                                    <i className="ti ti-arrow-left" style={{ fontSize: 18 }} />
                                  </button>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <span className="fw-bold" style={{ fontSize: 16 }}>{selectedTpl.name}</span>
                                      {(() => {
                                        const selo = seloCategoria(selectedTpl.categoria);
                                        if (!selo) return null;
                                        return (
                                          <span className="badge rounded-pill" style={{
                                            background: selo.fundo, color: selo.cor,
                                            fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                          }}>
                                            <i className={`ti ${selo.icone} me-1`} />
                                            {selo.texto}
                                          </span>
                                        );
                                      })()}
                                      {(() => {
                                        const st = seloStatus(selectedTpl.status);
                                        if (!st) return null;
                                        return (
                                          <span className="badge rounded-pill" style={{
                                            background: st.fundo, color: st.cor,
                                            fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                          }}>
                                            <i className={`ti ${st.icone} me-1`} />
                                            {st.texto}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                      <i className="ti ti-language me-1" />
                                      {selectedTpl.language}
                                    </div>
                                  </div>
                                </div>

                                {/* Aviso quando o template NÃO está aprovado. O selo sozinho é fácil
                                    de não ver; aqui vai o motivo e a consequência, antes do envio. */}
                                {(() => {
                                  const st = seloStatus(selectedTpl.status);
                                  if (!st) return null;
                                  return (
                                    <div className="d-flex align-items-start gap-2 mb-3 p-2 rounded"
                                         style={{ background: st.fundo, color: st.cor, fontSize: 12 }}>
                                      <i className={`ti ${st.icone}`} style={{ fontSize: 16, marginTop: 1 }} />
                                      <span>{st.aviso}</span>
                                    </div>
                                  );
                                })()}

                                {/* Preview estilo WhatsApp */}
                                <div className="mb-3" style={{
                                  background: "#e5ddd5",
                                  borderRadius: 12,
                                  padding: 16,
                                }}>
                                  <div style={{
                                    background: "#dcf8c6",
                                    borderRadius: 10,
                                    padding: 10,
                                    marginLeft: "auto",
                                    maxWidth: "92%",
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                                  }}>
                                    {selectedTpl.headerFormat === "IMAGE" && selectedTpl.headerImage && (
                                      <img
                                        src={selectedTpl.headerImage}
                                        alt="header"
                                        loading="lazy"
                                        decoding="async"
                                        style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 6, marginBottom: 8 }}
                                      />
                                    )}
                                    {selectedTpl.headerFormat === "VIDEO" && selectedTpl.headerImage && (
                                      <video
                                        src={selectedTpl.headerImage}
                                        controls
                                        style={{ width: "100%", maxHeight: 220, borderRadius: 6, marginBottom: 8 }}
                                      />
                                    )}
                                    {selectedTpl.headerText && (
                                      <div className="fw-bold mb-1" style={{ color: "#303030", fontSize: 13 }}>
                                        {selectedTpl.headerText.replace(/\{\{(\d+)\}\}/g, (_m, n) => tplVars["h" + n] || `{{${n}}}`)}
                                      </div>
                                    )}
                                    <div style={{
                                      color: "#303030",
                                      fontSize: 13,
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-wrap",
                                    }}>
                                      {(selectedTpl.body || "(sem corpo)").replace(/\{\{(\d+)\}\}/g, (_m, n) => tplVars[n] || `{{${n}}}`)}
                                    </div>
                                    <div className="text-end mt-1" style={{ fontSize: 9, color: "#6b7280" }}>
                                      {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                      <i className="ti ti-checks text-info ms-1" />
                                    </div>
                                  </div>
                                </div>

                                {/* Variáveis */}
                                {(selectedTpl.vars.length + (selectedTpl.headerVars || []).length) > 0 && (
                                  <div className="mb-2">
                                    <label className="form-label fw-bold small text-muted text-uppercase mb-2" style={{ letterSpacing: 0.5 }}>
                                      <i className="ti ti-variable me-1" />
                                      Variáveis ({selectedTpl.vars.length + (selectedTpl.headerVars || []).length})
                                    </label>
                                    <div className="d-flex flex-column gap-2">
                                      {[
                                        ...(selectedTpl.headerVars || []).map((n) => ({ chave: "h" + n, rotulo: `Título {{${n}}}` })),
                                        ...selectedTpl.vars.map((n) => ({ chave: n, rotulo: `{{${n}}}` })),
                                      ].map(({ chave, rotulo }) => (
                                        <div key={chave} className="d-flex align-items-center gap-2">
                                          <span className="badge rounded-pill flex-shrink-0" style={{
                                            background: "rgba(99,102,241,0.12)", color: "#6366f1",
                                            fontSize: 11, fontWeight: 700, padding: "5px 10px", minWidth: 50,
                                          }}>
                                            {rotulo}
                                          </span>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={tplVars[chave] || ""}
                                            onChange={(e) => setTplVars((prev) => ({ ...prev, [chave]: e.target.value }))}
                                            placeholder={`Valor para ${rotulo}`}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </Modal.Body>
                          <Modal.Footer className="border-0 pt-0">
                            <Button variant="light" onClick={() => setShowTemplateModal(false)} className="rounded-pill px-4">
                              Cancelar
                            </Button>
                            <Button
                              variant="success"
                              disabled={!selectedTpl || sendingTemplate}
                              onClick={sendSelectedTemplate}
                              className="rounded-pill px-4 fw-bold d-flex align-items-center gap-2"
                            >
                              {sendingTemplate ? (
                                <>
                                  <span className="spinner-border spinner-border-sm" role="status" />
                                  Enviando…
                                </>
                              ) : (
                                <>
                                  <i className="ti ti-send" />
                                  Enviar template
                                </>
                              )}
                            </Button>
                          </Modal.Footer>
                        </Modal>

                        <Modal show={showPixModal} onHide={() => setShowPixModal(false)} centered>
                          <Modal.Header closeButton>
                            <Modal.Title>Gerar e Enviar Pagamento</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="mb-3">
                              <label className="form-label">Provedor de Pagamento</label>
                              <select 
                                className="form-select" 
                                value={paymentProvider} 
                                onChange={(e) => setPaymentProvider(e.target.value as any)}
                              >
                                <option value="pix">PIX (Direto)</option>
                                <option value="mercadopago">Mercado Pago (Link)</option>
                                <option value="stripe">Stripe (Cartão - Link)</option>
                              </select>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Descrição / Item</label>
                              <input
                                type="text"
                                className="form-control"
                                value={paymentDescription}
                                onChange={(e) => setPaymentDescription(e.target.value)}
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Valor (R$)</label>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Ex: 150.00"
                                step="0.01"
                                value={pixAmount}
                                onChange={(e) => setPixAmount(e.target.value)}
                              />
                              {paymentProvider === "pix" && (
                                <small className="text-muted">
                                  Beneficiário: {PIX_CONFIG.NOME} (Chave: {PIX_CONFIG.CHAVE})
                                </small>
                              )}
                            </div>
                            
                            {sendingPix && (
                              <div className="text-center text-primary">
                                <div className="spinner-border spinner-border-sm me-2" role="status"/>
                                {paymentProvider === "pix" ? "Enviando sequência de mensagens..." : "Gerando e enviando link..."}
                              </div>
                            )}
                          </Modal.Body>
                          <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowPixModal(false)} disabled={sendingPix}>
                              Cancelar
                            </Button>
                            <Button variant="success" onClick={handleSendPaymentConfirm} disabled={sendingPix}>
                              Enviar Pagamento
                            </Button>
                          </Modal.Footer>
                        </Modal>

                        {/* ── Aviso (falha de envio e afins) ─────────────────────────────
                            O estado `showModal`/`modalMessage` já era usado em 19 pontos do
                            arquivo, mas NENHUM JSX o renderizava: todo aviso de erro era
                            silencioso — o operador via a bolha sumir e não sabia o motivo.
                            `pre-line` preserva as quebras do texto (motivo em linha própria). */}
                        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                          <Modal.Header closeButton className="border-0 pb-2">
                            <Modal.Title className="fs-16 fw-bold">
                              <i className="ti ti-alert-triangle text-warning me-2" />
                              Aviso
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body className="pt-0">
                            <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{modalMessage}</p>
                          </Modal.Body>
                          <Modal.Footer className="border-0">
                            <Button variant="primary" onClick={() => setShowModal(false)}>
                              Entendi
                            </Button>
                          </Modal.Footer>
                        </Modal>

                      </div>
                    );
                  };
                  
                  export default Chat;
              